// Сгенерировано из shared/openapi/openapi.json (@hey-api/openapi-ts) — руками не править.
// Перегенерировать: make -C admin api-generate; проверка рассинхрона: make -C admin api-check.

import * as z from 'zod';

import { AccuracyKind, ActivityModule, AgingPotential, AlertSeverity, AlertStatus, AnswerType, ApplicantRole, AppMarketStatus, AppModulesCatModelsArchiveReason, AppModulesGeoModelsArchiveReason, AromaGroup, AuditActorRole, AuditActorType, AuditCategory, AuditExportStatus, AuthCodePurpose, AuthorKind, AuthorProfileStatus, AuthProviderCode, BlindClimate, BookingStatus, BookingTarget, BookingType, BottleVolume, CalibrationArchiveReason, CalibrationStatus, CampaignChannel, CampaignLinkType, CampaignMode, CampaignStatus, CampaignWeekday, CancelReason, CartGroup, CartItemType, CatalogStatus, CatalogType, CheckoutStatus, ChildAgeGroup, ClientPlatform, Climate, ConsentKind, ContentArchiveReason, ContentLanguage, ContentStatus, CourseKind, Currency, Decanting, Decision, DeclineReason, DeliveryCarrier, DeliveryChannel, DeliveryMethod, DeliveryOption, DeliveryProvider, DeliveryStatus, DestinationScope, DiarySource, DigitalBillingChannel, DispatchStatus, DocKey, DocumentOwnerKind, DocumentStatus, DocumentVisibility, DropSalesState, DropTargetType, EventArchiveReason, EventLifecycle, ExcursionCancelPolicy, ExecutorType, ExperienceType, ExpertApplicationStatus, ExpertExperience, ExpertFormat, ExpertQualification, ExpertSpecialization, FieldType, FileStatus, FileVariantsStatus, Flow, GamDeclineReason, HideReason, IdentificationField, InboxKind, IntegrationMode, IntegrationPort, IntegrationStatus, ItemType, Language, LearnArchiveReason, LearnMediaPlacement, LearnRevisionState, LikeObjectType, LinkSource, LogActorType, LogCategory, LogDirection, LogResult, MapProvider, MediaKind, MediaPlacement, MediaRole, MediaSlot, MediaType, MessageAuthorKind, MessageKind, MinNotice, ModerationDecisionKind, ModerationEntityStatus, ModerationGroup, ModerationQueueState, ModerationRevisionStatus, ModerationStatus, NotificationAudience, NotificationChannels, NotificationSection, NotifyTopic, OAuthFlow, OAuthProvider, OAuthStateStatus, OfferState, OnboardingScreenFixed, OnboardingScreenKind, OnboardingScreenStatus, OneTimeRewardType, OrderDeliveryStatus, OrderGroup, OrderStatus, OrderType, OriginLevel, PairingCategory, PartnerRole, PaymentGateway, PaymentMethod, PaymentMethodCode, PaymentStatus, PayoutStatus, PreferenceCategory, PriceChangeState, PublicationKind, PublicationSource, PublicationStatus, PushProvider, ReactionKind, RecognitionStatus, RecognitionTranslation, RefClimate, RefundReason, RegionStatus, RegistrationMethod, RevisionState, RewardKind, RewardStatus, RoomKind, RoomType, SalesChannel, ScheduleMode, SeenSection, SensoryParam, ServeTemp, SetAuthorType, SetFormat, SetPortion, ShareTarget, ShippingCarrier, ShippingCostToBuyer, SkipReason, SlaUnit, Soil, SpecCategoryKind, SpecStatus, StatusActorKind, StoreCode, StoryItemKind, StoryLinkTarget, StoryStatus, StoryTtl, SupportChannel, SupportPriorityCode, SupportTopicCode, SuppressionReason, SuppressionScope, TasteChar, TastingFormat, TastingKind, TeamMemberSource, TeamMemberStatus, TeamRole, TestKind, TicketStatus, TierGateKey, TranslationBatchStatus, TranslationJobStatus, TranslationMode, TranslationStatus, TripCancelPolicy, TripSeason, UserStatus, VideoOwnerType, VideoProvider, VideoSource, VideoStatus, VisitMode, Weekday, WineColor, WineKind, WinePartnerStatus, WineryStatus, WineSalesMode, WineStyle, WineSugar, WineType, XpEventType, XpRuleStatus, ZoneType } from './types.gen';

/**
 * AcceptedOut
 *
 * `202`: событие для SRV (`exp.booking.reminder_due`) записано; доставку выполняет SRV.
 */
export const zAcceptedOut = z.object({
    booking_id: z.uuid(),
    queued: z.boolean(),
    delivery: z.string()
});

/**
 * AccuracyKind
 */
export const zAccuracyKind = z.enum(AccuracyKind);

/**
 * AchievementOut
 */
export const zAchievementOut = z.object({
    key: z.string(),
    title: z.string(),
    condition: z.string(),
    target: z.int(),
    progress: z.int(),
    awarded_at: z.iso.datetime({ offset: true }).nullable(),
    progress_label: z.string(),
    toast: z.string()
});

/**
 * AchievementsOut
 */
export const zAchievementsOut = z.object({
    items: z.array(zAchievementOut),
    awarded: z.int(),
    total: z.int(),
    header: z.string()
});

/**
 * ActionResultOut
 */
export const zActionResultOut = z.object({
    ok: z.boolean(),
    message: z.string(),
    data: z.record(z.string(), z.unknown()).nullish()
});

/**
 * ActivityModule
 *
 * Модуль-источник записи «Истории действий» (4.3.23; значения — lower_snake_case).
 */
export const zActivityModule = z.enum(ActivityModule);

/**
 * AdminAccountOut
 */
export const zAdminAccountOut = z.object({
    country_editable: z.boolean(),
    deletion_sla_days: z.int()
});

/**
 * AdminContentOut
 */
export const zAdminContentOut = z.object({
    translation: z.boolean()
});

/**
 * AdminLoginIn
 */
export const zAdminLoginIn = z.object({
    email: z.string(),
    password: z.string().max(500)
});

/**
 * AdminReviewOut
 */
export const zAdminReviewOut = z.object({
    id: z.uuid(),
    author_name: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    edited_at: z.iso.datetime({ offset: true }).nullable(),
    format_title: z.string(),
    rating: z.int(),
    text: z.string()
});

/**
 * AdminReviewSummaryOut
 */
export const zAdminReviewSummaryOut = z.object({
    avg: z.number().nullable(),
    count: z.int(),
    below_4: z.int()
});

/**
 * AdminReviewsOut
 */
export const zAdminReviewsOut = z.object({
    summary: zAdminReviewSummaryOut,
    items: z.array(zAdminReviewOut),
    next_cursor: z.string().nullable()
});

/**
 * AdminSupportOut
 */
export const zAdminSupportOut = z.object({
    email: z.string()
});

/**
 * AgeGateIn
 */
export const zAgeGateIn = z.object({
    answer: z.enum(['adult', 'underage'])
});

/**
 * AgeGateOut
 */
export const zAgeGateOut = z.object({
    age_confirmed: z.boolean(),
    underage_locked: z.boolean(),
    changes_left: z.int()
});

/**
 * AgingPotential
 */
export const zAgingPotential = z.enum(AgingPotential);

/**
 * AlertCan
 */
export const zAlertCan = z.object({
    resolve: z.boolean().optional().default(false)
});

/**
 * AlertResolveIn
 */
export const zAlertResolveIn = z.object({
    comment: z.string().max(500).nullish()
});

/**
 * AlertSeverity
 */
export const zAlertSeverity = z.enum(AlertSeverity);

/**
 * AlertStatus
 */
export const zAlertStatus = z.enum(AlertStatus);

/**
 * AlertOut
 */
export const zAlertOut = z.object({
    id: z.uuid(),
    integration_key: z.string(),
    integration_title: z.string(),
    type: z.string(),
    type_label: z.string(),
    severity: zAlertSeverity,
    severity_label: z.string(),
    message: z.string(),
    first_seen_at: z.iso.datetime({ offset: true }),
    last_seen_at: z.iso.datetime({ offset: true }),
    occurrences: z.int(),
    status: zAlertStatus,
    status_label: z.string(),
    resolved_at: z.iso.datetime({ offset: true }).nullable(),
    resolved_by: z.uuid().nullable(),
    resolve_comment: z.string().nullable(),
    can: zAlertCan
});

/**
 * AnswerIn
 */
export const zAnswerIn = z.object({
    question_id: z.uuid(),
    option_ids: z.array(z.uuid()).min(1).max(10)
});

/**
 * AnswerOut
 */
export const zAnswerOut = z.object({
    is_correct: z.boolean(),
    correct_option_ids: z.array(z.uuid()),
    correct_count: z.int(),
    answered_count: z.int(),
    label: z.string()
});

/**
 * AnswerType
 */
export const zAnswerType = z.enum(AnswerType);

/**
 * AppAccountOut
 */
export const zAppAccountOut = z.object({
    country_editable: z.boolean(),
    country_note: z.string().nullable()
});

/**
 * AppBrandOut
 */
export const zAppBrandOut = z.object({
    name: z.string(),
    bonus_name: z.string(),
    logo_url: z.string().nullish()
});

/**
 * AppCountryOut
 */
export const zAppCountryOut = z.object({
    code: z.string(),
    name: z.string(),
    sort_order: z.int(),
    picture_url: z.string().nullable(),
    regions_count: z.int(),
    is_live: z.boolean()
});

/**
 * AppCountryListOut
 */
export const zAppCountryListOut = z.object({
    items: z.array(zAppCountryOut)
});

/**
 * AppGeoOut
 */
export const zAppGeoOut = z.object({
    you_here_radius_km: z.int(),
    position_max_age_min: z.int(),
    location_ttl_hours: z.int()
});

/**
 * AppLinksOut
 */
export const zAppLinksOut = z.object({
    web_base_url: z.string(),
    app_web_base_url: z.string(),
    legal_documents_base_url: z.string(),
    account_deletion_web_url: z.string(),
    return_scheme_app: z.string()
});

/**
 * AppMarketCan
 */
export const zAppMarketCan = z.object({
    update: z.boolean().optional().default(false),
    set_status: z.boolean().optional().default(false),
    deactivate: z.boolean().optional().default(false)
});

/**
 * AppMarketPatchIn
 */
export const zAppMarketPatchIn = z.object({
    sort_order: z.int().nullish(),
    note: z.string().max(300).nullish()
});

/**
 * AppMarketStatus
 *
 * Страна приложения (ACC 4.8.2 `status`): `active` — доступна для выбора, `soon` — «скоро».
 */
export const zAppMarketStatus = z.enum(AppMarketStatus);

/**
 * AppDictionaryCountryOut
 *
 * Страна приложения (ACC 4.8.2 `GET /dictionaries/countries`).
 */
export const zAppDictionaryCountryOut = z.object({
    code: z.string(),
    name: z.string(),
    status: zAppMarketStatus,
    currency: z.string(),
    phone_code: z.string().nullable()
});

/**
 * AppDictionaryCountriesOut
 */
export const zAppDictionaryCountriesOut = z.object({
    items: z.array(zAppDictionaryCountryOut)
});

/**
 * AppMarketCreateIn
 */
export const zAppMarketCreateIn = z.object({
    country_code: z.string().length(2),
    status: zAppMarketStatus.optional().default('active'),
    sort_order: z.int().gte(1).nullish(),
    note: z.string().max(300).nullish()
});

/**
 * AppMarketStatusIn
 */
export const zAppMarketStatusIn = z.object({
    status: zAppMarketStatus
});

/**
 * AppPair
 */
export const zAppPair = z.object({
    label: z.string(),
    value: z.string()
});

/**
 * AppRefreshIn
 */
export const zAppRefreshIn = z.object({
    refresh_token: z.string().max(300)
});

/**
 * AppSearchOut
 */
export const zAppSearchOut = z.object({
    popular_queries: z.array(z.string())
});

/**
 * AppStep
 */
export const zAppStep = z.object({
    title: z.string(),
    text: z.string()
});

/**
 * AppVideoOut
 */
export const zAppVideoOut = z.object({
    playback_rates: z.array(z.number()),
    progress_heartbeat_sec: z.int(),
    complete_threshold: z.number()
});

/**
 * AppleUserIn
 *
 * Имя и почта, которые Apple передаёт только при первой авторизации (BR-INT-SVC-25).
 */
export const zAppleUserIn = z.object({
    first_name: z.string().max(100).nullish(),
    last_name: z.string().max(100).nullish(),
    email: z.string().max(254).nullish()
});

/**
 * ApplicantRole
 */
export const zApplicantRole = z.enum(ApplicantRole);

/**
 * ApplicationActionsOut
 */
export const zApplicationActionsOut = z.object({
    withdraw: z.boolean(),
    edit: z.boolean(),
    reapply: z.boolean(),
    reapply_from: z.iso.date().nullable(),
    open_studio: z.boolean()
});

/**
 * ApplicationDecisionIn
 */
export const zApplicationDecisionIn = z.object({
    decision: z.enum([
        'approve',
        'needs_info',
        'reject'
    ]),
    comment: z.string().max(600).nullish()
});

/**
 * AromaGroup
 */
export const zAromaGroup = z.enum(AromaGroup);

/**
 * AttachIn
 */
export const zAttachIn = z.object({
    target_type: z.enum([
        'set',
        'tasting',
        'lesson',
        'course',
        'publication'
    ]),
    target_id: z.uuid()
});

/**
 * AttachmentOut
 */
export const zAttachmentOut = z.object({
    id: z.uuid(),
    url: z.string().nullable(),
    content_type: z.string().nullable()
});

/**
 * AttemptOptionOut
 */
export const zAttemptOptionOut = z.object({
    id: z.uuid(),
    text: z.string()
});

/**
 * AttemptQuestionOut
 */
export const zAttemptQuestionOut = z.object({
    id: z.uuid(),
    text: z.string(),
    answer_type: zAnswerType,
    topic: z.string().nullable(),
    options: z.array(zAttemptOptionOut)
});

/**
 * AttemptStartIn
 */
export const zAttemptStartIn = z.object({
    lesson_id: z.uuid().nullish(),
    country_code: z.string().length(2).nullish()
});

/**
 * AuditActorRole
 */
export const zAuditActorRole = z.enum(AuditActorRole);

/**
 * AuditActorType
 */
export const zAuditActorType = z.enum(AuditActorType);

/**
 * AuditCategory
 *
 * BR-SRV-63: категория — по коду действия, для быстрых фильтров реестра.
 */
export const zAuditCategory = z.enum(AuditCategory);

/**
 * AuditChangeOut
 */
export const zAuditChangeOut = z.object({
    field: z.string(),
    label: z.string(),
    before: z.unknown().optional(),
    after: z.unknown().optional(),
    before_text: z.string(),
    after_text: z.string(),
    masked: z.boolean().optional().default(false)
});

/**
 * AuditExportIn
 *
 * Фильтры экспорта — как у `GET /audit` (13.8.2).
 */
export const zAuditExportIn = z.object({
    category: zAuditCategory.nullish(),
    actor_id: z.uuid().nullish(),
    entity_type: z.string().max(64).nullish(),
    entity_id: z.uuid().nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    q: z.string().max(200).nullish(),
    filters: z.record(z.string(), z.string()).optional(),
    sort: z.enum(['occurred_at', '-occurred_at']).nullish()
});

/**
 * AuditExportStatus
 */
export const zAuditExportStatus = z.enum(AuditExportStatus);

/**
 * AuditExportOut
 */
export const zAuditExportOut = z.object({
    export_id: z.uuid(),
    status: zAuditExportStatus,
    url: z.string().nullable(),
    rows: z.int(),
    expires_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * AuditHistoryItemOut
 *
 * Запись журнала аудита (SRV 13.3.17), где участник — исполнитель.
 */
export const zAuditHistoryItemOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    action: z.string(),
    action_label: z.string(),
    entity_type: z.string().nullable(),
    entity_label: z.string().nullable(),
    summary: z.string()
});

/**
 * AuditRowOut
 */
export const zAuditRowOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    actor_type: zAuditActorType,
    actor_id: z.uuid().nullable(),
    actor_name: z.string(),
    actor_role: zAuditActorRole,
    actor_role_label: z.string(),
    category: zAuditCategory,
    category_label: z.string(),
    action: z.string(),
    action_label: z.string(),
    entity_type: z.string().nullable(),
    entity_type_label: z.string().nullable(),
    entity_id: z.uuid().nullable(),
    entity_label: z.string().nullable(),
    entity: z.string(),
    summary: z.string()
});

/**
 * AuthCodePurpose
 */
export const zAuthCodePurpose = z.enum(AuthCodePurpose);

/**
 * AuthProviderCode
 */
export const zAuthProviderCode = z.enum(AuthProviderCode);

/**
 * AdminAuthOut
 */
export const zAdminAuthOut = z.object({
    providers: z.array(zAuthProviderCode)
});

/**
 * AuthorBriefOut
 */
export const zAuthorBriefOut = z.object({
    member_id: z.uuid().nullable(),
    name: z.string(),
    role_label: z.string().nullable(),
    label: z.string(),
    target: z.string().nullable()
});

/**
 * AuthorKind
 */
export const zAuthorKind = z.enum(AuthorKind);

/**
 * AuthorOut
 */
export const zAuthorOut = z.object({
    member_id: z.uuid().nullable(),
    name: z.string(),
    is_platform: z.boolean()
});

/**
 * AuthorProfileCan
 */
export const zAuthorProfileCan = z.object({
    update: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    hide: z.boolean().optional().default(false)
});

/**
 * AuthorProfileStatus
 */
export const zAuthorProfileStatus = z.enum(AuthorProfileStatus);

/**
 * AvailabilityErrorOut
 */
export const zAvailabilityErrorOut = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).nullish()
});

/**
 * AvailabilityItemIn
 */
export const zAvailabilityItemIn = z.object({
    object_type: z.enum([
        'wine',
        'set',
        'set_item',
        'drop'
    ]),
    object_id: z.uuid(),
    qty: z.int().gte(1).lte(999)
});

/**
 * AvailabilityIn
 */
export const zAvailabilityIn = z.object({
    items: z.array(zAvailabilityItemIn).min(1).max(50)
});

/**
 * AvailabilityItemOut
 */
export const zAvailabilityItemOut = z.object({
    object_type: z.string(),
    object_id: z.uuid(),
    ok: z.boolean(),
    available: z.int().nullable(),
    max_qty: z.int(),
    error: zAvailabilityErrorOut.nullish()
});

/**
 * AvailabilityOut
 */
export const zAvailabilityOut = z.object({
    items: z.array(zAvailabilityItemOut)
});

/**
 * AvatarIn
 */
export const zAvatarIn = z.object({
    file_id: z.uuid()
});

/**
 * AvatarOut
 */
export const zAvatarOut = z.object({
    avatar_url: z.string().nullable(),
    avatar_thumb_url: z.string().nullable()
});

/**
 * BillingAppStorePurchaseIn
 */
export const zBillingAppStorePurchaseIn = z.object({
    signed_transaction: z.string().min(10).max(20000),
    transaction_id: z.string().min(1).max(100)
});

/**
 * BillingGooglePlayPurchaseIn
 */
export const zBillingGooglePlayPurchaseIn = z.object({
    product_id: z.string().min(2).max(100),
    purchase_token: z.string().min(8).max(4096),
    order_id: z.string().max(100).nullish(),
    price_amount_micros: z.int().gte(0).nullish(),
    price_currency: z.string().length(3).nullish()
});

/**
 * BillingIntentIn
 */
export const zBillingIntentIn = z.object({
    item_type: z.enum([
        'lesson',
        'region_course',
        'expert_course'
    ]),
    item_id: z.uuid()
});

/**
 * BillingProductOut
 */
export const zBillingProductOut = z.object({
    item_type: z.enum([
        'lesson',
        'region_course',
        'expert_course'
    ]),
    item_id: z.uuid(),
    store_product_id: z.string().nullable(),
    active: z.boolean()
});

/**
 * BillingProductUsedByOut
 */
export const zBillingProductUsedByOut = z.object({
    item_type: z.enum([
        'lesson',
        'region_course',
        'expert_course'
    ]),
    item_id: z.uuid(),
    title: z.string()
});

/**
 * BillingProductCheckOut
 */
export const zBillingProductCheckOut = z.object({
    available: z.boolean(),
    used_by: zBillingProductUsedByOut.nullish()
});

/**
 * BillingPurchaseOut
 */
export const zBillingPurchaseOut = z.object({
    status: z.enum([
        'granted',
        'pending',
        'revoked'
    ]),
    order_id: z.uuid(),
    item_type: z.enum([
        'lesson',
        'region_course',
        'expert_course'
    ]),
    item_id: z.uuid()
});

/**
 * BillingRuStorePurchaseIn
 */
export const zBillingRuStorePurchaseIn = z.object({
    product_id: z.string().min(2).max(100),
    purchase_id: z.string().min(1).max(100),
    invoice_id: z.string().max(100).nullish(),
    order_id: z.string().max(150).nullish()
});

/**
 * BillingSeenItemIn
 */
export const zBillingSeenItemIn = z.object({
    store_product_id: z.string().min(2).max(100),
    available: z.boolean(),
    display_price: z.string().max(64).nullish(),
    price_amount_micros: z.int().gte(0).nullish(),
    currency: z.string().length(3).nullish()
});

/**
 * BillingSyncPurchaseIn
 */
export const zBillingSyncPurchaseIn = z.object({
    app_store: zBillingAppStorePurchaseIn.nullish(),
    google_play: zBillingGooglePlayPurchaseIn.nullish(),
    rustore: zBillingRuStorePurchaseIn.nullish(),
    intent_id: z.uuid().nullish()
});

/**
 * BillingSyncResultOut
 */
export const zBillingSyncResultOut = z.object({
    store_transaction_id: z.string().nullable(),
    status: z.enum([
        'granted',
        'pending',
        'revoked',
        'failed'
    ]),
    order_id: z.uuid().nullish(),
    error_code: z.string().nullish()
});

/**
 * BillingSyncOut
 */
export const zBillingSyncOut = z.object({
    results: z.array(zBillingSyncResultOut)
});

/**
 * BlindClimate
 *
 * Климат в определении вина вслепую (имя схемы уникально: у GEO свой `Climate`).
 */
export const zBlindClimate = z.enum(BlindClimate);

/**
 * BlockOut
 */
export const zBlockOut = z.object({
    type: z.enum([
        'heading',
        'paragraph',
        'quote',
        'list',
        'image'
    ]),
    text: z.string().nullish(),
    items: z.array(z.string()).nullish(),
    url: z.string().nullish(),
    caption: z.string().nullish()
});

/**
 * BodyBlockOut
 */
export const zBodyBlockOut = z.object({
    type: z.string(),
    inline: z.array(z.record(z.string(), z.unknown())).nullish(),
    items: z.array(z.array(z.record(z.string(), z.unknown()))).nullish(),
    media_id: z.uuid().nullish(),
    caption: z.string().nullish(),
    url: z.string().nullish()
});

/**
 * BookingAdminCan
 */
export const zBookingAdminCan = z.object({
    approve: z.boolean().optional().default(false),
    decline: z.boolean().optional().default(false),
    cancel: z.boolean().optional().default(false),
    remind_payment: z.boolean().optional().default(false),
    mark_attended: z.boolean().optional().default(false),
    mark_no_show: z.boolean().optional().default(false),
    offer_seat: z.boolean().optional().default(false),
    remove_from_waitlist: z.boolean().optional().default(false)
});

/**
 * BookingCan
 */
export const zBookingCan = z.object({
    cancel: z.boolean().optional().default(false),
    pay: z.boolean().optional().default(false),
    edit: z.boolean().optional().default(false),
    conference: z.boolean().optional().default(false)
});

/**
 * BookingStatus
 */
export const zBookingStatus = z.enum(BookingStatus);

/**
 * BookingTarget
 */
export const zBookingTarget = z.enum(BookingTarget);

/**
 * AlertIn
 */
export const zAlertIn = z.object({
    kind: zBookingTarget,
    region_id: z.uuid().nullish()
});

/**
 * BookingCreateIn
 */
export const zBookingCreateIn = z.object({
    target_type: zBookingTarget,
    target_id: z.uuid(),
    slot_starts_at: z.iso.datetime({ offset: true }).nullish(),
    adults: z.int().gte(1).lte(20).optional().default(1),
    sober_guests: z.int().gte(0).lte(20).optional().default(0)
});

/**
 * BookingType
 */
export const zBookingType = z.enum(BookingType);

/**
 * BottleVolume
 */
export const zBottleVolume = z.enum(BottleVolume);

/**
 * BrandLogoFileOut
 */
export const zBrandLogoFileOut = z.object({
    id: z.uuid(),
    url: z.string(),
    name: z.string().nullable(),
    content_type: z.string().nullable(),
    size: z.int().nullable()
});

/**
 * BrandLogoIn
 */
export const zBrandLogoIn = z.object({
    file_id: z.uuid(),
    file_name: z.string().max(255).nullish()
});

/**
 * BrandLogoOut
 */
export const zBrandLogoOut = z.object({
    platform: z.string(),
    file: zBrandLogoFileOut.nullable(),
    updated_at: z.iso.datetime({ offset: true }).nullable(),
    updated_by: z.string().nullable(),
    hint: z.string(),
    can: z.record(z.string(), z.boolean()),
    toast: z.string().nullish()
});

/**
 * BreakdownLessonOut
 */
export const zBreakdownLessonOut = z.object({
    lesson_id: z.uuid(),
    number: z.string(),
    title: z.string(),
    open: z.boolean(),
    duration_min: z.int(),
    right_label: z.string()
});

/**
 * CalibrationArchiveReason
 *
 * Общий механизм архива админки: Не актуально / Дубликат / Партнёр расторг / Ошибка данных.
 */
export const zCalibrationArchiveReason = z.enum(CalibrationArchiveReason);

/**
 * CalibrationStatus
 */
export const zCalibrationStatus = z.enum(CalibrationStatus);

/**
 * CampaignCan
 */
export const zCampaignCan = z.object({
    update: z.boolean().optional().default(false),
    schedule: z.boolean().optional().default(false),
    launch: z.boolean().optional().default(false),
    stop: z.boolean().optional().default(false),
    resume: z.boolean().optional().default(false),
    cancel: z.boolean().optional().default(false)
});

/**
 * CampaignChannel
 */
export const zCampaignChannel = z.enum(CampaignChannel);

/**
 * CampaignAudienceEstimateOut
 */
export const zCampaignAudienceEstimateOut = z.object({
    count: z.int(),
    label: z.string(),
    channel: zCampaignChannel,
    segment: z.string().nullable(),
    available: z.boolean(),
    email_days: z.int().nullish(),
    email_days_label: z.string().nullish()
});

/**
 * CampaignChannelOptionOut
 */
export const zCampaignChannelOptionOut = z.object({
    value: z.string(),
    label: z.string(),
    hint: z.string(),
    title_label: z.string(),
    title_max: z.int(),
    text_max: z.int(),
    link_hint: z.string(),
    image_hint: z.string()
});

/**
 * CampaignCountersOut
 */
export const zCampaignCountersOut = z.object({
    total: z.int(),
    push: z.int(),
    email: z.int()
});

/**
 * CampaignDeliveryStatsOut
 */
export const zCampaignDeliveryStatsOut = z.object({
    sent: z.int().optional().default(0),
    failed: z.int().optional().default(0),
    dropped: z.int().optional().default(0),
    pending: z.int().optional().default(0)
});

/**
 * CampaignHistoryOut
 */
export const zCampaignHistoryOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    action_label: z.string(),
    summary: z.string()
});

/**
 * CampaignLinkOptionOut
 */
export const zCampaignLinkOptionOut = z.object({
    id: z.uuid(),
    label: z.string()
});

/**
 * CampaignLinkType
 *
 * «Что открыть» (13.3.15).
 */
export const zCampaignLinkType = z.enum(CampaignLinkType);

/**
 * CampaignLinkOptionsOut
 */
export const zCampaignLinkOptionsOut = z.object({
    link_type: zCampaignLinkType,
    available: z.boolean(),
    items: z.array(zCampaignLinkOptionOut)
});

/**
 * CampaignLinkOut
 */
export const zCampaignLinkOut = z.object({
    type: zCampaignLinkType,
    type_label: z.string(),
    id: z.uuid(),
    title: z.string().nullable(),
    label: z.string(),
    section: z.string().nullable(),
    live: z.boolean().nullable(),
    available: z.boolean()
});

/**
 * CampaignLinkTypeOptionOut
 */
export const zCampaignLinkTypeOptionOut = z.object({
    value: z.string(),
    label: z.string(),
    available: z.boolean(),
    section: z.string().nullable()
});

/**
 * CampaignMode
 */
export const zCampaignMode = z.enum(CampaignMode);

/**
 * CampaignOptionOut
 */
export const zCampaignOptionOut = z.object({
    value: z.string(),
    label: z.string()
});

/**
 * CampaignRunReportOut
 *
 * Отчёт о запуске: получатели отобраны в момент запуска; доставки — по факту (push — статус
 * SRV, письмо — статус Mailer).
 */
export const zCampaignRunReportOut = z.object({
    id: z.uuid(),
    run_date: z.iso.date(),
    started_at: z.iso.datetime({ offset: true }),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    state: z.enum(['running', 'finished']),
    state_label: z.string(),
    recipients: z.int(),
    sent: z.int(),
    failed: z.int(),
    skipped: z.int(),
    pending: z.int(),
    cancelled: z.int(),
    feed_only: z.int(),
    skipped_by_reason: z.record(z.string(), z.int()),
    push: zCampaignDeliveryStatsOut,
    email: zCampaignDeliveryStatsOut,
    notes: z.array(z.string())
});

/**
 * CampaignSegmentOut
 */
export const zCampaignSegmentOut = z.object({
    key: z.string(),
    label: z.string(),
    params: z.record(z.string(), z.string()),
    params_label: z.string().nullable(),
    available: z.boolean()
});

/**
 * CampaignSegmentParamOut
 */
export const zCampaignSegmentParamOut = z.object({
    name: z.string(),
    label: z.string(),
    kind: z.enum(['choice', 'ref']),
    ref: z.string().nullable(),
    choices: z.array(zCampaignOptionOut)
});

/**
 * CampaignSegmentOptionOut
 */
export const zCampaignSegmentOptionOut = z.object({
    key: z.string(),
    label: z.string(),
    hint: z.string(),
    available: z.boolean(),
    params: z.array(zCampaignSegmentParamOut)
});

/**
 * CampaignFormOut
 *
 * Справочник модалки «Новое уведомление» (13.7.3.3).
 */
export const zCampaignFormOut = z.object({
    overline: z.string(),
    text: z.string(),
    footer: z.string(),
    channels: z.array(zCampaignChannelOptionOut),
    link_types: z.array(zCampaignLinkTypeOptionOut),
    modes: z.array(zCampaignOptionOut),
    weekdays: z.array(zCampaignOptionOut),
    timezones: z.array(zCampaignOptionOut),
    default_timezone: z.string(),
    time_hint: z.string(),
    min_lead_minutes: z.int(),
    image_types: z.array(z.string()),
    image_max_size: z.int(),
    audience_label: z.string(),
    segments: z.array(zCampaignSegmentOptionOut)
});

/**
 * CampaignStatus
 */
export const zCampaignStatus = z.enum(CampaignStatus);

/**
 * CampaignWeekday
 */
export const zCampaignWeekday = z.enum(CampaignWeekday);

/**
 * CancelEventIn
 */
export const zCancelEventIn = z.object({
    reason: z.string().min(1).max(300)
});

/**
 * CancelReason
 */
export const zCancelReason = z.enum(CancelReason);

/**
 * AdminCancelIn
 */
export const zAdminCancelIn = z.object({
    reason: zCancelReason,
    comment: z.string().max(300).nullish()
});

/**
 * CapacityOut
 */
export const zCapacityOut = z.object({
    daily_limit: z.int(),
    used_24h: z.int(),
    available_now: z.int(),
    estimated_days: z.int(),
    marketing_daily_quota: z.int(),
    marketing_used_24h: z.int()
});

/**
 * CardCan
 */
export const zCardCan = z.object({
    update: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    cancel: z.boolean().optional().default(false),
    manage_bookings: z.boolean().optional().default(false)
});

/**
 * CareRowCan
 */
export const zCareRowCan = z.object({
    view: z.boolean().optional().default(false),
    update: z.boolean().optional().default(false),
    take: z.boolean().optional().default(false),
    reply: z.boolean().optional().default(false),
    resolve: z.boolean().optional().default(false),
    wait_user: z.boolean().optional().default(false),
    reopen: z.boolean().optional().default(false),
    close: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false)
});

/**
 * CareTicketCan
 */
export const zCareTicketCan = z.object({
    view: z.boolean().optional().default(false),
    update: z.boolean().optional().default(false),
    take: z.boolean().optional().default(false),
    reply: z.boolean().optional().default(false),
    resolve: z.boolean().optional().default(false),
    wait_user: z.boolean().optional().default(false),
    reopen: z.boolean().optional().default(false),
    close: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false)
});

/**
 * CartGroup
 */
export const zCartGroup = z.enum(CartGroup);

/**
 * CartItemType
 */
export const zCartItemType = z.enum(CartItemType);

/**
 * CatalogStatus
 */
export const zCatalogStatus = z.enum(CatalogStatus);

/**
 * CatalogType
 *
 * Тип каталожной записи: владелец медиа, ревизии, истории (6.3.6, 6.3.11, 6.3.12).
 */
export const zCatalogType = z.enum(CatalogType);

/**
 * CheckResultOut
 */
export const zCheckResultOut = z.object({
    ok: z.boolean(),
    message: z.string(),
    duration_ms: z.int().optional().default(0)
});

/**
 * CheckoutStatus
 */
export const zCheckoutStatus = z.enum(CheckoutStatus);

/**
 * ChildAgeGroup
 */
export const zChildAgeGroup = z.enum(ChildAgeGroup);

/**
 * ClientCityOut
 */
export const zClientCityOut = z.object({
    name: z.string(),
    lat: z.number(),
    lon: z.number()
});

/**
 * ClientPlatform
 */
export const zClientPlatform = z.enum(ClientPlatform);

/**
 * AdminGamOut
 */
export const zAdminGamOut = z.object({
    learning_discount_channels: z.array(zClientPlatform),
    wine_discount_applies: z.boolean()
});

/**
 * Climate
 */
export const zClimate = z.enum(Climate);

/**
 * ClubConfigOut
 */
export const zClubConfigOut = z.object({
    level_id: z.uuid(),
    club_min_xp: z.int(),
    step_xp: z.int()
});

/**
 * ClubGoalOut
 */
export const zClubGoalOut = z.object({
    reward_id: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    cost: z.int(),
    icon_url: z.string().nullable(),
    have: z.int(),
    left: z.int(),
    reached: z.boolean(),
    progress_label: z.string(),
    text: z.string()
});

/**
 * ClubOut
 */
export const zClubOut = z.object({
    open: z.boolean(),
    club_min_xp: z.int().nullable(),
    step_xp: z.int().nullable(),
    bonus_earned: z.int(),
    bonus_spent: z.int(),
    bonus_balance: z.int(),
    xp_to_next_bonus: z.int().nullable(),
    text: z.string().nullable(),
    card_header: z.string().nullable(),
    card_title: z.string().nullable(),
    card_text: z.string().nullable()
});

/**
 * CodeLabel
 */
export const zCodeLabel = z.object({
    code: z.string(),
    label: z.string()
});

/**
 * CodeLoginIn
 */
export const zCodeLoginIn = z.object({
    email: z.string(),
    code: z.string().regex(/^[0-9]{4,8}$/)
});

/**
 * CodeRequestIn
 */
export const zCodeRequestIn = z.object({
    email: z.string(),
    purpose: zAuthCodePurpose
});

/**
 * CodeSentOut
 */
export const zCodeSentOut = z.object({
    ttl_sec: z.int(),
    resend_after_sec: z.int()
});

/**
 * CodeVerifyIn
 */
export const zCodeVerifyIn = z.object({
    email: z.string(),
    purpose: zAuthCodePurpose,
    code: z.string().regex(/^[0-9]{4,8}$/)
});

/**
 * CollectedOut
 */
export const zCollectedOut = z.object({
    level: z.enum(['bronze', 'gold']),
    title: z.string(),
    label: z.string()
});

/**
 * CommentAuthorOut
 */
export const zCommentAuthorOut = z.object({
    name: z.string(),
    label: z.string()
});

/**
 * CommentIn
 */
export const zCommentIn = z.object({
    comment: z.string().min(1).max(600)
});

/**
 * CommentReplyOut
 */
export const zCommentReplyOut = z.object({
    id: z.uuid(),
    author: zCommentAuthorOut,
    text: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    time_label: z.string()
});

/**
 * CommentOut
 */
export const zCommentOut = z.object({
    id: z.uuid(),
    author: zCommentAuthorOut,
    text: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    time_label: z.string(),
    reply: zCommentReplyOut.nullable()
});

/**
 * CommentListOut
 */
export const zCommentListOut = z.object({
    items: z.array(zCommentOut),
    total: z.int(),
    total_label: z.string(),
    next_cursor: z.string().nullable(),
    more_label: z.string().nullable()
});

/**
 * CompleteIn
 */
export const zCompleteIn = z.object({
    course_id: z.uuid().nullish()
});

/**
 * ConferenceOut
 */
export const zConferenceOut = z.object({
    url: z.string()
});

/**
 * ConfirmIn
 */
export const zConfirmIn = z.object({
    confirm: z.literal(true)
});

/**
 * ConsentKind
 */
export const zConsentKind = z.enum(ConsentKind);

/**
 * ConsentItemIn
 */
export const zConsentItemIn = z.object({
    kind: zConsentKind,
    document_version_id: z.uuid().nullish()
});

/**
 * ContentArchiveReason
 *
 * Причина архивирования (12.3.2, 12.3.9 — общая модалка).
 */
export const zContentArchiveReason = z.enum(ContentArchiveReason);

/**
 * ContentArchiveIn
 */
export const zContentArchiveIn = z.object({
    reason: zContentArchiveReason,
    comment: z.string().max(300).nullish()
});

/**
 * ContentAuthorOut
 */
export const zContentAuthorOut = z.object({
    member_id: z.uuid().nullable(),
    name: z.string(),
    role_label: z.string().nullable(),
    kicker: z.string().nullable(),
    photo_url: z.string().nullable(),
    initial: z.string(),
    target: z.string().nullable()
});

/**
 * ContentGateTierOut
 */
export const zContentGateTierOut = z.object({
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    xp_min: z.int().nullish(),
    accuracy_min: z.int().nullish()
});

/**
 * ContentGateOut
 */
export const zContentGateOut = z.object({
    gate: z.string(),
    gate_title: z.string(),
    passed: z.boolean(),
    required_tier: zContentGateTierOut.nullable(),
    current_tier: zContentGateTierOut.nullable(),
    progress: z.record(z.string(), z.unknown()).nullable(),
    note: z.string().nullable()
});

/**
 * ContentHistoryOut
 */
export const zContentHistoryOut = z.object({
    at: z.iso.datetime({ offset: true }),
    actor: z.string(),
    text: z.string()
});

/**
 * ContentLanguage
 */
export const zContentLanguage = z.enum(ContentLanguage);

/**
 * ContentMediaOut
 */
export const zContentMediaOut = z.object({
    id: z.uuid(),
    kind: z.string(),
    url: z.string().nullable(),
    preview_url: z.string().nullish(),
    caption: z.string().nullish(),
    status: z.string()
});

/**
 * ContentOptionOut
 */
export const zContentOptionOut = z.object({
    id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullish(),
    type: z.string().nullish()
});

/**
 * ContentRefOut
 *
 * Отметка или связанный объект: название и переход.
 */
export const zContentRefOut = z.object({
    type: z.string(),
    id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullish(),
    target: z.string().nullish()
});

/**
 * ContentStatus
 */
export const zContentStatus = z.enum(ContentStatus);

/**
 * ContentStepOut
 */
export const zContentStepOut = z.object({
    title: z.string(),
    done: z.boolean(),
    current: z.boolean(),
    note: z.string()
});

/**
 * ContentTargetOut
 *
 * Переход на экран приложения: `target` — ключ экрана (`bottle:{id}`, `region:{id}`,
 * `post:{id}`, `path-lesson:{регион}.{nn}`…).
 */
export const zContentTargetOut = z.object({
    screen: z.string(),
    id: z.uuid().nullish(),
    target: z.string()
});

/**
 * CountryBrief
 */
export const zCountryBrief = z.object({
    code: z.string(),
    name: z.string()
});

/**
 * CountryCan
 */
export const zCountryCan = z.object({
    update: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * CountryCardOut
 */
export const zCountryCardOut = z.object({
    code: z.string(),
    name: z.string(),
    image_url: z.string().nullable(),
    your_country: z.boolean(),
    has_update: z.boolean(),
    state: z.enum([
        'done',
        'progress',
        'new'
    ]),
    subtitle: z.string(),
    regions_total: z.int(),
    collected: z.int(),
    stamps: z.int(),
    lessons_count: z.int(),
    target: z.string()
});

/**
 * CountryOut
 */
export const zCountryOut = z.object({
    code: z.string(),
    name: z.string()
});

/**
 * CountryPatchIn
 */
export const zCountryPatchIn = z.object({
    sort_order: z.int().nullish(),
    note: z.string().max(300).nullish(),
    illustration_file_id: z.uuid().nullish(),
    picture_file_id: z.uuid().nullish()
});

/**
 * CountryRefOut
 */
export const zCountryRefOut = z.object({
    code: z.string(),
    name: z.string()
});

/**
 * CountsOut
 */
export const zCountsOut = z.object({
    bottles: z.int(),
    sets: z.int(),
    drops: z.int()
});

/**
 * CourseAccessOut
 */
export const zCourseAccessOut = z.object({
    opened: z.boolean(),
    purchased: z.boolean(),
    free: z.boolean()
});

/**
 * CourseCan
 */
export const zCourseCan = z.object({
    update: z.boolean().optional().default(false),
    submit: z.boolean().optional().default(false),
    submit_edits: z.boolean().optional().default(false),
    waiting: z.boolean().optional().default(false),
    decide: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    edit_price: z.boolean().optional().default(false),
    edit_store_products: z.boolean().optional().default(false),
    edit_program: z.boolean().optional().default(false)
});

/**
 * CourseKind
 */
export const zCourseKind = z.enum(CourseKind);

/**
 * CourseLikeOut
 */
export const zCourseLikeOut = z.object({
    liked: z.boolean(),
    likes_count: z.int()
});

/**
 * CourseOptionOut
 */
export const zCourseOptionOut = z.object({
    id: z.uuid(),
    title: z.string()
});

/**
 * CourseProgressBriefOut
 */
export const zCourseProgressBriefOut = z.object({
    done: z.int(),
    all: z.int(),
    pct: z.int(),
    completed: z.boolean(),
    next_lesson_id: z.uuid().nullable()
});

/**
 * CredentialsOut
 */
export const zCredentialsOut = z.object({
    login: z.string(),
    temporary_password: z.string(),
    where: z.string()
});

/**
 * CtaOut
 */
export const zCtaOut = z.object({
    label: z.string(),
    action: z.enum([
        'open',
        'complete',
        'test',
        'cart',
        'checkout',
        'store',
        'none'
    ]),
    target: z.string().nullish(),
    test_id: z.uuid().nullish(),
    enabled: z.boolean().optional().default(true)
});

/**
 * Currency
 */
export const zCurrency = z.enum(Currency);

/**
 * CurrentOwnerOut
 */
export const zCurrentOwnerOut = z.object({
    id: z.uuid().nullable(),
    name: z.string().nullable()
});

/**
 * DashboardCatalogTileOut
 */
export const zDashboardCatalogTileOut = z.object({
    key: z.string(),
    group: z.string(),
    group_label: z.string(),
    label: z.string(),
    available: z.boolean(),
    total: z.int().nullable(),
    published: z.int().nullable(),
    on_moderation: z.int().nullable(),
    added: z.int().nullable(),
    value_label: z.string(),
    sub: z.string(),
    link: z.string()
});

/**
 * DashboardFigureOut
 *
 * Плитка воронки и вклада «Сообщества».
 */
export const zDashboardFigureOut = z.object({
    key: z.string(),
    label: z.string(),
    available: z.boolean(),
    value: z.int().nullable(),
    value_label: z.string(),
    sub: z.string().nullable(),
    link: z.string().nullable(),
    unavailable_reason: z.string().nullish()
});

/**
 * DashboardCommunityOut
 */
export const zDashboardCommunityOut = z.object({
    title: z.string(),
    funnel: z.array(zDashboardFigureOut),
    tiles: z.array(zDashboardFigureOut)
});

/**
 * DashboardHeaderOut
 */
export const zDashboardHeaderOut = z.object({
    overline: z.string(),
    title: z.string(),
    lead: z.string(),
    period_dates: z.string(),
    data_as_of: z.iso.datetime({ offset: true }).nullable()
});

/**
 * DashboardLinkOut
 */
export const zDashboardLinkOut = z.object({
    section: z.string(),
    id: z.uuid().nullish()
});

/**
 * DashboardAttentionRowOut
 */
export const zDashboardAttentionRowOut = z.object({
    rule: z.string(),
    tone: z.enum(['bad', 'warn']),
    title: z.string(),
    note: z.string(),
    link: zDashboardLinkOut.nullable()
});

/**
 * DashboardAttentionOut
 */
export const zDashboardAttentionOut = z.object({
    title: z.string(),
    count: z.int(),
    count_label: z.string(),
    items: z.array(zDashboardAttentionRowOut)
});

/**
 * DashboardModerationRowOut
 */
export const zDashboardModerationRowOut = z.object({
    number: z.int(),
    title: z.string(),
    kind_label: z.string(),
    author_label: z.string(),
    status_label: z.string(),
    age_label: z.string(),
    returned: z.boolean(),
    entity_type: z.string(),
    entity_id: z.uuid(),
    link: zDashboardLinkOut.nullable()
});

/**
 * DashboardModerationOut
 */
export const zDashboardModerationOut = z.object({
    title: z.string(),
    count: z.int(),
    items: z.array(zDashboardModerationRowOut),
    action_label: z.string().nullable(),
    action_link: zDashboardLinkOut.nullable()
});

/**
 * DashboardOptionOut
 */
export const zDashboardOptionOut = z.object({
    value: z.string(),
    label: z.string()
});

/**
 * DashboardPeriodOut
 */
export const zDashboardPeriodOut = z.object({
    kind: z.enum([
        'week',
        'month',
        'year',
        'all',
        'custom'
    ]),
    start: z.iso.date(),
    end: z.iso.date(),
    label: z.string(),
    dates_label: z.string(),
    compare_start: z.iso.date().nullable(),
    compare_end: z.iso.date().nullable(),
    compare_note: z.string().nullable(),
    month: z.string().nullish(),
    year: z.int().nullish()
});

/**
 * DashboardScheduleRowOut
 */
export const zDashboardScheduleRowOut = z.object({
    starts_at: z.iso.datetime({ offset: true }),
    day_label: z.string(),
    title: z.string(),
    kind: z.string(),
    kind_label: z.string(),
    taken: z.int(),
    seats: z.int().nullable(),
    fill_pct: z.int().nullable(),
    fill_label: z.string(),
    full: z.boolean(),
    link: zDashboardLinkOut
});

/**
 * DashboardScheduleOut
 */
export const zDashboardScheduleOut = z.object({
    title: z.string(),
    available: z.boolean(),
    count: z.int(),
    count_label: z.string(),
    items: z.array(zDashboardScheduleRowOut)
});

/**
 * DashboardNowOut
 */
export const zDashboardNowOut = z.object({
    moderation: zDashboardModerationOut,
    schedule: zDashboardScheduleOut,
    attention: zDashboardAttentionOut
});

/**
 * Decanting
 */
export const zDecanting = z.enum(Decanting);

/**
 * Decision
 *
 * Решение модерации (8.8.2 `decision`); у ревизии — каким решением она закрыта.
 */
export const zDecision = z.enum(Decision);

/**
 * DecisionIn
 */
export const zDecisionIn = z.object({
    decision: zDecision,
    comment: z.string().max(600).nullish()
});

/**
 * DeclineReason
 */
export const zDeclineReason = z.enum(DeclineReason);

/**
 * DeclineIn
 */
export const zDeclineIn = z.object({
    reason: zDeclineReason,
    comment: z.string().max(300).nullish()
});

/**
 * DefaultPointOut
 */
export const zDefaultPointOut = z.object({
    lat: z.number(),
    lng: z.number(),
    city: z.string()
});

/**
 * DeleteIn
 */
export const zDeleteIn = z.object({
    confirm_title: z.string().min(1).max(200)
});

/**
 * DeleteMeIn
 */
export const zDeleteMeIn = z.object({
    password: z.string().max(500).nullish(),
    code: z.string().regex(/^[0-9]{4,8}$/).nullish(),
    confirm: z.boolean()
});

/**
 * DeletionItemOut
 */
export const zDeletionItemOut = z.object({
    type: z.string(),
    id: z.uuid(),
    title: z.string(),
    status: z.string()
});

/**
 * DeletionCheckOut
 */
export const zDeletionCheckOut = z.object({
    allowed: z.boolean(),
    reason: z.enum(['team_member', 'active_orders']).nullable(),
    message: z.string().nullable(),
    items: z.array(zDeletionItemOut),
    confirm_with: z.enum(['password', 'code'])
});

/**
 * DeliveryCarrier
 */
export const zDeliveryCarrier = z.enum(DeliveryCarrier);

/**
 * DeliveryChannel
 */
export const zDeliveryChannel = z.enum(DeliveryChannel);

/**
 * DeliveryMethod
 */
export const zDeliveryMethod = z.enum(DeliveryMethod);

/**
 * DeliveryOption
 */
export const zDeliveryOption = z.enum(DeliveryOption);

/**
 * DeliveryProvider
 */
export const zDeliveryProvider = z.enum(DeliveryProvider);

/**
 * DeliveryStatus
 */
export const zDeliveryStatus = z.enum(DeliveryStatus);

/**
 * DeliveryOut
 */
export const zDeliveryOut = z.object({
    id: z.uuid(),
    channel: zDeliveryChannel,
    channel_label: z.string(),
    provider: zDeliveryProvider.nullable(),
    status: zDeliveryStatus.nullable(),
    status_label: z.string(),
    attempts: z.int(),
    last_error_code: z.string().nullable(),
    sent_at: z.iso.datetime({ offset: true }).nullable(),
    device_id: z.uuid().nullish(),
    email: z.string().nullish(),
    mail_message_id: z.uuid().nullish()
});

/**
 * DepartureApp
 */
export const zDepartureApp = z.object({
    id: z.uuid(),
    date_from: z.iso.date(),
    date_to: z.iso.date(),
    dates_label: z.string(),
    seats_free: z.int(),
    label: z.string(),
    available: z.boolean()
});

/**
 * DepartureIn
 */
export const zDepartureIn = z.object({
    id: z.uuid().nullish(),
    date_from: z.iso.date(),
    date_to: z.iso.date(),
    seats: z.int().gte(1).lte(100000).nullish()
});

/**
 * DepartureOut
 */
export const zDepartureOut = z.object({
    id: z.uuid(),
    date_from: z.iso.date(),
    date_to: z.iso.date(),
    dates_label: z.string(),
    seats: z.int(),
    seats_taken: z.int(),
    seats_free: z.int(),
    taken_label: z.string(),
    free_label: z.string()
});

/**
 * DepartureRef
 */
export const zDepartureRef = z.object({
    id: z.uuid(),
    date_from: z.iso.date(),
    date_to: z.iso.date()
});

/**
 * DeparturesIn
 */
export const zDeparturesIn = z.object({
    departures: z.array(zDepartureIn).max(100)
});

/**
 * DestinationScope
 */
export const zDestinationScope = z.enum(DestinationScope);

/**
 * DevicesSummaryOut
 */
export const zDevicesSummaryOut = z.object({
    active_count: z.int(),
    push_enabled: z.boolean()
});

/**
 * DiarySource
 */
export const zDiarySource = z.enum(DiarySource);

/**
 * DigitalBillingChannel
 *
 * `billing.digital`: чем оплачивается обучение на платформе клиента.
 */
export const zDigitalBillingChannel = z.enum(DigitalBillingChannel);

/**
 * DispatchStatus
 */
export const zDispatchStatus = z.enum(DispatchStatus);

/**
 * DocKey
 */
export const zDocKey = z.enum(DocKey);

/**
 * DocumentCan
 */
export const zDocumentCan = z.object({
    replace: z.boolean().optional().default(false),
    rename: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    decide: z.boolean().optional().default(false)
});

/**
 * DocumentCreateIn
 */
export const zDocumentCreateIn = z.object({
    name: z.string().max(200),
    winery_id: z.uuid().nullish(),
    media_id: z.uuid().nullish(),
    url: z.string().max(500).nullish(),
    file_name: z.string().max(255).nullish()
});

/**
 * DocumentDecisionIn
 */
export const zDocumentDecisionIn = z.object({
    decision: z.enum(['accept', 'reject']),
    comment: z.string().max(600).nullish()
});

/**
 * DocumentOwnerKind
 */
export const zDocumentOwnerKind = z.enum(DocumentOwnerKind);

/**
 * DocumentPatchIn
 */
export const zDocumentPatchIn = z.object({
    name: z.string().max(200)
});

/**
 * DocumentStatus
 *
 * `draft` — расширение (docs/decisions.md): документ платформы из `make seed` без
 * загруженной версии («файл загружает администратор»).
 */
export const zDocumentStatus = z.enum(DocumentStatus);

/**
 * DocumentUsageOut
 */
export const zDocumentUsageOut = z.object({
    applications: z.array(zContentRefOut),
    consents: z.array(z.record(z.string(), z.unknown()))
});

/**
 * DocumentVersionIn
 */
export const zDocumentVersionIn = z.object({
    media_id: z.uuid().nullish(),
    url: z.string().max(500).nullish(),
    file_name: z.string().max(255).nullish(),
    name: z.string().max(200).nullish()
});

/**
 * DocumentVersionOut
 */
export const zDocumentVersionOut = z.object({
    id: z.uuid(),
    version: z.string(),
    file_label: z.string().nullable(),
    file_url: z.string().nullable(),
    url: z.string().nullable(),
    effective_from: z.iso.date(),
    uploaded_by: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    current: z.boolean()
});

/**
 * DocumentVisibility
 */
export const zDocumentVisibility = z.enum(DocumentVisibility);

/**
 * DropCan
 */
export const zDropCan = z.object({
    update: z.boolean().optional().default(false),
    edit_target: z.boolean().optional().default(false),
    edit_schedule: z.boolean().optional().default(false),
    edit_media: z.boolean().optional().default(false),
    submit: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    return: z.boolean().optional().default(false),
    reject: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    awaiting_decision: z.boolean().optional().default(false)
});

/**
 * DropSalesState
 */
export const zDropSalesState = z.enum(DropSalesState);

/**
 * DropTargetAppOut
 */
export const zDropTargetAppOut = z.object({
    type: z.enum(['wine', 'set']),
    id: z.uuid(),
    name: z.string(),
    vintage: z.int().nullish()
});

/**
 * DropTargetType
 */
export const zDropTargetType = z.enum(DropTargetType);

/**
 * DropTargetOptionOut
 */
export const zDropTargetOptionOut = z.object({
    type: zDropTargetType,
    id: z.uuid(),
    label: z.string()
});

/**
 * DropTargetOut
 */
export const zDropTargetOut = z.object({
    type: zDropTargetType,
    id: z.uuid(),
    label: z.string(),
    status: zCatalogStatus,
    status_label: z.string()
});

/**
 * DropTargetsOut
 */
export const zDropTargetsOut = z.object({
    items: z.array(zDropTargetOptionOut)
});

/**
 * EarlyAccessOut
 */
export const zEarlyAccessOut = z.object({
    enabled: z.boolean(),
    starts_at: z.iso.datetime({ offset: true }).nullable(),
    required_tier: z.record(z.string(), z.unknown()).nullable(),
    user_passes: z.boolean()
});

/**
 * EmailChangeIn
 */
export const zEmailChangeIn = z.object({
    new_email: z.string()
});

/**
 * EmailConfirmIn
 */
export const zEmailConfirmIn = z.object({
    new_email: z.string(),
    code: z.string().regex(/^[0-9]{4,8}$/)
});

/**
 * ErrorBody
 */
export const zErrorBody = z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()).nullish(),
    trace_id: z.string().nullish()
});

/**
 * ErrorResponse
 */
export const zErrorResponse = z.object({
    error: zErrorBody
});

/**
 * EventArchiveReason
 */
export const zEventArchiveReason = z.enum(EventArchiveReason);

/**
 * EventArchiveIn
 */
export const zEventArchiveIn = z.object({
    reason: zEventArchiveReason,
    comment: z.string().max(300).nullish()
});

/**
 * EventLifecycle
 */
export const zEventLifecycle = z.enum(EventLifecycle);

/**
 * CancelEventOut
 */
export const zCancelEventOut = z.object({
    id: z.uuid(),
    lifecycle: zEventLifecycle.nullable(),
    cancelled_bookings: z.int(),
    refunds_requested: z.int()
});

/**
 * ExchangeCan
 */
export const zExchangeCan = z.object({
    view: z.boolean(),
    complete: z.boolean(),
    decline: z.boolean()
});

/**
 * ExchangeCompleteIn
 */
export const zExchangeCompleteIn = z.object({
    given_text: z.string().min(1).max(200),
    executor_winery_id: z.uuid().nullish(),
    executor_expert_id: z.uuid().nullish(),
    team_note: z.string().max(300).nullish()
});

/**
 * ExchangeCreateIn
 */
export const zExchangeCreateIn = z.object({
    reward_id: z.uuid()
});

/**
 * ExchangeHistoryOut
 */
export const zExchangeHistoryOut = z.object({
    at: z.iso.datetime({ offset: true }).nullable(),
    who: z.string(),
    text: z.string()
});

/**
 * ExchangeItemOut
 */
export const zExchangeItemOut = z.object({
    id: z.uuid(),
    number: z.string(),
    reward_id: z.uuid(),
    reward_title: z.string(),
    icon_url: z.string().nullable(),
    cost: z.int(),
    status: z.enum([
        'requested',
        'paid',
        'declined'
    ]),
    status_label: z.string(),
    requested_at: z.iso.datetime({ offset: true }),
    due_date: z.iso.date(),
    paid_at: z.iso.datetime({ offset: true }).nullable(),
    decline_reason: z.string().nullable(),
    decline_reason_text: z.string().nullable(),
    subtitle: z.string(),
    text: z.string(),
    order_id: z.uuid().nullable(),
    order_number: z.string().nullable()
});

/**
 * ExchangeCreatedOut
 */
export const zExchangeCreatedOut = z.object({
    exchange: zExchangeItemOut,
    bonus_balance: z.int(),
    toast: z.string()
});

/**
 * ExchangeOrderOut
 */
export const zExchangeOrderOut = z.object({
    id: z.uuid(),
    number: z.string().nullable()
});

/**
 * ExchangeRewardOut
 */
export const zExchangeRewardOut = z.object({
    id: z.uuid(),
    title: z.string()
});

/**
 * ExchangeRewardPanelOut
 */
export const zExchangeRewardPanelOut = z.object({
    id: z.uuid(),
    kind_label: z.string(),
    cost_label: z.string(),
    executor_label: z.string(),
    status: z.string().nullable(),
    status_label: z.string().nullable(),
    note: z.string()
});

/**
 * ExchangeStepOut
 */
export const zExchangeStepOut = z.object({
    key: z.string(),
    title: z.string(),
    text: z.string(),
    state: z.enum([
        'done',
        'current',
        'next'
    ])
});

/**
 * ExcursionCancelPolicy
 */
export const zExcursionCancelPolicy = z.enum(ExcursionCancelPolicy);

/**
 * ExcursionReviewOut
 */
export const zExcursionReviewOut = z.object({
    author: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    rating: z.int(),
    text: z.string()
});

/**
 * ExcursionSlotOut
 */
export const zExcursionSlotOut = z.object({
    starts_at: z.iso.datetime({ offset: true }),
    ends_at: z.iso.datetime({ offset: true }),
    capacity: z.int(),
    free: z.int(),
    label: z.string()
});

/**
 * ExecutorType
 */
export const zExecutorType = z.enum(ExecutorType);

/**
 * ExpAlertOut
 */
export const zExpAlertOut = z.object({
    kind: zBookingTarget,
    region_id: z.uuid().nullable()
});

/**
 * AlertsOut
 */
export const zAlertsOut = z.object({
    items: z.array(zExpAlertOut)
});

/**
 * ExpLinkOut
 *
 * Переход в раздел админки с фильтрами реестра.
 */
export const zExpLinkOut = z.object({
    section: z.string(),
    filters: z.record(z.string(), z.string()).optional()
});

/**
 * ExpTileOut
 *
 * Плитка «Сводки» карточки события.
 */
export const zExpTileOut = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string(),
    caption: z.string().nullish(),
    link: zExpLinkOut.nullish()
});

/**
 * ExperienceType
 */
export const zExperienceType = z.enum(ExperienceType);

/**
 * ExperienceTagIn
 */
export const zExperienceTagIn = z.object({
    type: zExperienceType,
    id: z.uuid()
});

/**
 * ExpertApplicationDecisionIn
 */
export const zExpertApplicationDecisionIn = z.object({
    decision: z.enum([
        'approve',
        'needs_info',
        'reject'
    ]),
    comment: z.string().max(600).nullish()
});

/**
 * ExpertApplicationStatus
 */
export const zExpertApplicationStatus = z.enum(ExpertApplicationStatus);

/**
 * ApplicationAdminOut
 */
export const zApplicationAdminOut = z.object({
    id: z.uuid(),
    member_id: z.uuid(),
    user_id: z.uuid(),
    email: z.string().nullable(),
    status: zExpertApplicationStatus,
    status_label: z.string(),
    status_line: z.string(),
    full_name: z.string(),
    city: z.string(),
    applicant_role: z.string(),
    applicant_role_label: z.string(),
    experience: z.string(),
    experience_label: z.string(),
    specializations: z.array(z.string()),
    specializations_label: z.string(),
    qualifications: z.array(z.string()),
    qualifications_label: z.string(),
    formats: z.array(z.string()),
    formats_label: z.string(),
    diploma: zContentRefOut.nullable(),
    links: z.string().nullable(),
    about: z.string(),
    author_terms_label: z.string().nullable(),
    passport_snapshot: z.string().nullable(),
    submitted_at: z.iso.datetime({ offset: true }),
    decided_at: z.iso.datetime({ offset: true }).nullable(),
    decided_by: z.string().nullable(),
    decision_comment: z.string().nullable(),
    can_decide: z.boolean()
});

/**
 * ApplicationDecisionOut
 */
export const zApplicationDecisionOut = z.object({
    id: z.uuid(),
    status: zExpertApplicationStatus,
    member_status: z.string(),
    decided_at: z.iso.datetime({ offset: true }).nullable(),
    decided_by: z.string().nullable(),
    status_line: z.string(),
    toast: z.string()
});

/**
 * ExpertExperience
 */
export const zExpertExperience = z.enum(ExpertExperience);

/**
 * ExpertFormat
 */
export const zExpertFormat = z.enum(ExpertFormat);

/**
 * ExpertOptionOut
 */
export const zExpertOptionOut = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * ExpertQualification
 */
export const zExpertQualification = z.enum(ExpertQualification);

/**
 * ExpertSpecialization
 */
export const zExpertSpecialization = z.enum(ExpertSpecialization);

/**
 * ApplicationIn
 */
export const zApplicationIn = z.object({
    full_name: z.string().max(100),
    city: z.string().max(80),
    applicant_role: zApplicantRole,
    experience: zExpertExperience,
    specializations: z.array(zExpertSpecialization).max(8),
    qualifications: z.array(zExpertQualification).max(6),
    diploma_media_id: z.uuid().nullish(),
    diploma_file_name: z.string().max(255).nullish(),
    links: z.string().max(500).nullish(),
    formats: z.array(zExpertFormat).max(5),
    about: z.string().max(2000),
    author_terms_version_id: z.uuid().nullish()
});

/**
 * FacetGrapeOut
 */
export const zFacetGrapeOut = z.object({
    id: z.uuid(),
    label: z.string(),
    names: z.array(z.string())
});

/**
 * FacetOptionOut
 */
export const zFacetOptionOut = z.object({
    value: z.string(),
    label: z.string()
});

/**
 * FacetRegionOut
 */
export const zFacetRegionOut = z.object({
    id: z.uuid(),
    name: z.string(),
    country: zCountryOut
});

/**
 * FacetsOut
 */
export const zFacetsOut = z.object({
    regions: z.array(zFacetRegionOut),
    grapes: z.array(zFacetGrapeOut),
    kinds: z.array(zFacetOptionOut),
    tastes: z.array(zFacetOptionOut),
    author_types: z.array(zFacetOptionOut),
    formats: z.array(zFacetOptionOut),
    states: z.array(zFacetOptionOut)
});

/**
 * FactItemOut
 */
export const zFactItemOut = z.object({
    title: z.string(),
    text: z.string()
});

/**
 * FeedAuthorOut
 */
export const zFeedAuthorOut = z.object({
    member_id: z.uuid().nullish(),
    name: z.string(),
    sub: z.string().nullish(),
    kicker: z.string().nullish(),
    photo_url: z.string().nullish(),
    initial: z.string().nullish()
});

/**
 * FeedImageOut
 */
export const zFeedImageOut = z.object({
    type: z.string(),
    url: z.string().nullish()
});

/**
 * FeedLikeOut
 */
export const zFeedLikeOut = z.object({
    owner: z.enum([
        'CNT',
        'CAT',
        'LRN'
    ]),
    liked: z.boolean(),
    count: z.int()
});

/**
 * FeedActionsOut
 */
export const zFeedActionsOut = z.object({
    like: zFeedLikeOut.nullish(),
    save: z.record(z.string(), z.boolean()).nullish(),
    share: z.boolean().optional().default(false),
    to_diary: z.boolean().optional().default(false)
});

/**
 * FieldOptionOut
 */
export const zFieldOptionOut = z.object({
    value: z.string(),
    label: z.string()
});

/**
 * FieldType
 */
export const zFieldType = z.enum(FieldType);

/**
 * FileStatus
 */
export const zFileStatus = z.enum(FileStatus);

/**
 * FileVariantOut
 */
export const zFileVariantOut = z.object({
    name: z.string(),
    url: z.string(),
    width: z.int(),
    height: z.int()
});

/**
 * FileVariantsStatus
 */
export const zFileVariantsStatus = z.enum(FileVariantsStatus);

/**
 * FileOut
 */
export const zFileOut = z.object({
    upload_id: z.uuid(),
    purpose: z.string(),
    status: zFileStatus,
    content_type: z.string().nullable(),
    size: z.int().nullable(),
    url: z.string().nullable(),
    variants_status: zFileVariantsStatus,
    variants: z.array(zFileVariantOut)
});

/**
 * CampaignImageOut
 */
export const zCampaignImageOut = z.object({
    file: zFileOut,
    label: z.string()
});

/**
 * CampaignRowOut
 */
export const zCampaignRowOut = z.object({
    id: z.uuid(),
    channel: zCampaignChannel,
    channel_label: z.string(),
    title: z.string(),
    text: z.string(),
    image: zCampaignImageOut.nullable(),
    link: zCampaignLinkOut.nullable(),
    mode: zCampaignMode,
    mode_label: z.string(),
    schedule_label: z.string(),
    send_at: z.iso.datetime({ offset: true }).nullable(),
    send_at_timezone: z.string().nullable(),
    weekdays: z.array(zCampaignWeekday),
    local_time: z.string().nullable(),
    status: zCampaignStatus,
    status_label: z.string(),
    runs_count: z.int(),
    last_run_recipients: z.int().nullable(),
    sent_label: z.string(),
    next_run_at: z.iso.datetime({ offset: true }).nullable(),
    row_action: z.enum([
        'schedule',
        'launch',
        'stop',
        'resume',
        'cancel'
    ]).nullable(),
    row_action_label: z.string().nullable(),
    audience_label: z.string(),
    segment: zCampaignSegmentOut.nullable(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
    can: zCampaignCan
});

/**
 * FirstRegionOut
 */
export const zFirstRegionOut = z.object({
    status: z.enum([
        'unavailable',
        'available',
        'reserved',
        'used'
    ]),
    pct: z.number().nullable(),
    used_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * Flow
 */
export const zFlow = z.enum(Flow);

/**
 * FollowStateOut
 */
export const zFollowStateOut = z.object({
    following: z.boolean(),
    subscribers_count: z.int(),
    subscribers_label: z.string(),
    toast: z.string()
});

/**
 * GalleryItemOut
 */
export const zGalleryItemOut = z.object({
    url: z.string(),
    caption: z.string().nullable()
});

/**
 * GamDeclineReason
 */
export const zGamDeclineReason = z.enum(GamDeclineReason);

/**
 * ExchangeDeclineIn
 */
export const zExchangeDeclineIn = z.object({
    reason: zGamDeclineReason,
    comment: z.string().max(300).nullish()
});

/**
 * GamExchangesPageOut
 */
export const zGamExchangesPageOut = z.object({
    items: z.array(zExchangeItemOut),
    next_cursor: z.string().nullable(),
    total: z.int(),
    total_label: z.string()
});

/**
 * GamGateTierOut
 */
export const zGamGateTierOut = z.object({
    id: z.uuid(),
    roman: z.string(),
    name: z.string(),
    rank: z.int(),
    title: z.string()
});

/**
 * GamHistoryItemOut
 */
export const zGamHistoryItemOut = z.object({
    id: z.uuid(),
    type: z.string(),
    occurred_at: z.iso.datetime({ offset: true }),
    day: z.int(),
    month_short: z.string(),
    group: z.string(),
    title: z.string(),
    change: z.string(),
    link: z.string()
});

/**
 * GamMePatchIn
 */
export const zGamMePatchIn = z.object({
    coins_onboarding_seen: z.boolean().nullish(),
    club_goal_reward_id: z.uuid().nullish()
});

/**
 * GamRowCan
 */
export const zGamRowCan = z.object({
    update: z.boolean(),
    delete: z.boolean()
});

/**
 * GamRuleHistoryEntryOut
 */
export const zGamRuleHistoryEntryOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    action_label: z.string(),
    summary: z.string()
});

/**
 * GamSectionLinkOut
 */
export const zGamSectionLinkOut = z.object({
    section: z.string(),
    filters: z.record(z.string(), z.string())
});

/**
 * GamStampOut
 */
export const zGamStampOut = z.object({
    region_id: z.uuid(),
    region_name: z.string(),
    level: z.enum(['bronze', 'gold']),
    awarded_at: z.iso.datetime({ offset: true }),
    date_label: z.string(),
    image_url: z.string().nullable(),
    basis: z.string(),
    line: z.string(),
    received_label: z.string(),
    explored_label: z.string(),
    explain: z.string(),
    target: z.string()
});

/**
 * GamStepOut
 */
export const zGamStepOut = z.object({
    done: z.boolean(),
    n: z.int(),
    all: z.int()
});

/**
 * GamTierOut
 */
export const zGamTierOut = z.object({
    id: z.uuid(),
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    title: z.string(),
    xp_min: z.int(),
    accuracy_min: z.number(),
    unlocks: z.string().nullable(),
    image_url: z.string().nullable(),
    xp_label: z.string(),
    accuracy_label: z.string()
});

/**
 * GamUserRefOut
 */
export const zGamUserRefOut = z.object({
    id: z.uuid().nullable(),
    name: z.string().nullable()
});

/**
 * ExchangeRowOut
 */
export const zExchangeRowOut = z.object({
    id: z.uuid(),
    number: z.string(),
    user: zGamUserRefOut,
    type_label: z.string(),
    reward: zExchangeRewardOut,
    executor_type: zExecutorType,
    executor: z.string(),
    when_label: z.string(),
    persons: z.int(),
    cost: z.int(),
    amount_label: z.string(),
    status: z.enum([
        'requested',
        'paid',
        'declined'
    ]),
    status_label: z.string(),
    requested_at: z.iso.datetime({ offset: true }),
    due_date: z.iso.date(),
    overdue: z.boolean(),
    paid_at: z.iso.datetime({ offset: true }).nullable(),
    declined_at: z.iso.datetime({ offset: true }).nullable(),
    order: zExchangeOrderOut.nullable(),
    can: zExchangeCan
});

/**
 * GateInfoOut
 */
export const zGateInfoOut = z.object({
    key: z.string(),
    title: z.string(),
    description: z.string(),
    owner_module: z.string(),
    min_rank: z.int().nullable(),
    min_tier_id: z.uuid().nullable()
});

/**
 * GateStateOut
 */
export const zGateStateOut = z.object({
    key: z.string(),
    title: z.string(),
    min_rank: z.int().nullable(),
    allowed: z.boolean()
});

/**
 * GateTierOut
 */
export const zGateTierOut = z.object({
    rank: z.int(),
    roman: z.string(),
    name: z.string()
});

/**
 * GateOut
 *
 * Гейт ступени (BR-EXP-20): без GAM — закрыт, порог неизвестен (`required_tier = null`).
 */
export const zGateOut = z.object({
    gate: z.string(),
    allowed: z.boolean(),
    required_tier: zGateTierOut.nullish(),
    current_tier: zGateTierOut.nullish()
});

/**
 * GateTitleOut
 */
export const zGateTitleOut = z.object({
    key: z.string(),
    title: z.string()
});

/**
 * GeoClientOut
 */
export const zGeoClientOut = z.object({
    default_city: zClientCityOut
});

/**
 * GeoPointIn
 *
 * Также строка «широта · долгота», BR-GEO-58
 */
export const zGeoPointIn = z.object({
    lat: z.number(),
    lng: z.number()
});

/**
 * GeoPointOut
 */
export const zGeoPointOut = z.object({
    lat: z.number(),
    lng: z.number()
});

/**
 * GeoPolygonIn
 *
 * GeoJSON Polygon (WGS 84): одно внешнее кольцо `[[lng, lat], …]`, ≥ 3 различные вершины.
 */
export const zGeoPolygonIn = z.object({
    type: z.string(),
    coordinates: z.array(z.array(z.array(z.number())))
});

/**
 * ContourIn
 */
export const zContourIn = z.object({
    polygon: zGeoPolygonIn.nullable()
});

/**
 * GeoPolygonOut
 */
export const zGeoPolygonOut = z.object({
    type: z.string().optional().default('Polygon'),
    coordinates: z.array(z.array(z.array(z.number())))
});

/**
 * GeocodeCandidateOut
 */
export const zGeocodeCandidateOut = z.object({
    formatted_address: z.string(),
    lat: z.number(),
    lon: z.number(),
    precision: z.enum([
        'exact',
        'street',
        'locality',
        'other'
    ]),
    precision_label: z.string(),
    country_code: z.string().nullable(),
    provider_ref: z.string().nullish()
});

/**
 * GeocodeDetailsOut
 */
export const zGeocodeDetailsOut = z.object({
    item: zGeocodeCandidateOut.nullable()
});

/**
 * GeocodeListOut
 *
 * `GET /admin/geo/geocode`: первый — точка «Найти по адресу», остальные (до 5) — выбор.
 */
export const zGeocodeListOut = z.object({
    items: z.array(zGeocodeCandidateOut)
});

/**
 * GeocodeReverseOut
 */
export const zGeocodeReverseOut = z.object({
    item: zGeocodeCandidateOut.nullable(),
    warning: z.string().nullish()
});

/**
 * GeocodeSuggestionOut
 */
export const zGeocodeSuggestionOut = z.object({
    id: z.string(),
    text: z.string(),
    secondary: z.string().nullable()
});

/**
 * GeocodeSuggestionsOut
 */
export const zGeocodeSuggestionsOut = z.object({
    items: z.array(zGeocodeSuggestionOut)
});

/**
 * GoogleClientIdsOut
 */
export const zGoogleClientIdsOut = z.object({
    web: z.string().nullish(),
    android: z.string().nullish(),
    ios: z.string().nullish()
});

/**
 * GrapeOptionOut
 */
export const zGrapeOptionOut = z.object({
    id: z.uuid(),
    name: z.string(),
    short_name: z.string().nullable(),
    aliases: z.array(z.string())
});

/**
 * GrapeShareAppOut
 */
export const zGrapeShareAppOut = z.object({
    name: z.string(),
    share_pct: z.int()
});

/**
 * GrapeShareIn
 */
export const zGrapeShareIn = z.object({
    grape_id: z.uuid(),
    share_pct: z.int().nullish()
});

/**
 * GrapeShareOut
 */
export const zGrapeShareOut = z.object({
    grape_id: z.uuid(),
    name: z.string(),
    share_pct: z.int()
});

/**
 * GrapesIn
 */
export const zGrapesIn = z.object({
    items: z.array(zGrapeShareIn).max(20)
});

/**
 * GuestConsentsIn
 */
export const zGuestConsentsIn = z.object({
    items: z.array(zConsentItemIn).min(1).max(3)
});

/**
 * GuestGateOut
 */
export const zGuestGateOut = z.object({
    age_confirmed: z.boolean(),
    underage_locked: z.boolean(),
    onboarding_completed: z.boolean()
});

/**
 * DeviceOut
 */
export const zDeviceOut = z.object({
    device_id: z.uuid(),
    guest_profile_id: z.uuid().nullable(),
    guest_gate: zGuestGateOut
});

/**
 * GuestLocationIn
 */
export const zGuestLocationIn = z.object({
    country_code: z.string().regex(/^[A-Za-z]{2}$/),
    city_name: z.string().max(80),
    city_suggestion_id: z.uuid().nullish()
});

/**
 * GuestLocationOut
 */
export const zGuestLocationOut = z.object({
    country_code: z.string(),
    city_name: z.string().nullable(),
    city_suggestion_id: z.uuid().nullable()
});

/**
 * GuestMergeOut
 */
export const zGuestMergeOut = z.object({
    performed: z.boolean(),
    guest_profile_id: z.uuid().nullable(),
    mode: z.enum(['registration', 'login']).nullable()
});

/**
 * GuestsSummaryOut
 */
export const zGuestsSummaryOut = z.object({
    schedule: z.string(),
    stay: z.string().nullable(),
    warnings: z.array(z.string())
});

/**
 * HealthOut
 */
export const zHealthOut = z.object({
    status: z.enum(['ok', 'degraded']),
    database: z.boolean(),
    redis: z.boolean()
});

/**
 * HeldLevelOut
 */
export const zHeldLevelOut = z.object({
    id: z.uuid(),
    name: z.string(),
    held_label: z.string()
});

/**
 * HideReason
 */
export const zHideReason = z.enum(HideReason);

/**
 * HistoryMonthOut
 */
export const zHistoryMonthOut = z.object({
    year: z.int(),
    month: z.int(),
    label: z.string(),
    count: z.int(),
    count_label: z.string()
});

/**
 * GamHistoryPageOut
 */
export const zGamHistoryPageOut = z.object({
    items: z.array(zGamHistoryItemOut),
    next_cursor: z.string().nullable(),
    years: z.array(z.int()),
    months: z.array(zHistoryMonthOut),
    total: z.int(),
    first_at: z.iso.datetime({ offset: true }).nullable(),
    header: z.string().nullable()
});

/**
 * HistoryOut
 */
export const zHistoryOut = z.object({
    at: z.iso.datetime({ offset: true }),
    actor: z.string(),
    action: z.string(),
    text: z.string()
});

/**
 * HostOut
 */
export const zHostOut = z.object({
    id: z.uuid(),
    name: z.string(),
    role: z.string(),
    role_label: z.string().nullish(),
    photo_url: z.string().nullish()
});

/**
 * HowToGetOut
 */
export const zHowToGetOut = z.object({
    distance_km: z.int(),
    road: z.string().nullable(),
    transfer: z.string().nullable()
});

/**
 * IdentificationField
 */
export const zIdentificationField = z.enum(IdentificationField);

/**
 * InboxKind
 *
 * Вид записи ленты (13.3.10); `none` — в ленту не попадает.
 */
export const zInboxKind = z.enum(InboxKind);

/**
 * FeedItemOut
 */
export const zFeedItemOut = z.object({
    id: z.uuid(),
    kind: zInboxKind,
    kind_label: z.string(),
    title: z.string(),
    meta: z.string().nullish(),
    image: zFeedImageOut.nullish(),
    deeplink: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    read: z.boolean(),
    day_group: z.enum([
        'today',
        'yesterday',
        'earlier'
    ]),
    day_label: z.string(),
    time_label: z.string()
});

/**
 * FeedPageOut
 */
export const zFeedPageOut = z.object({
    items: z.array(zFeedItemOut),
    next_cursor: z.string().nullable(),
    unread_count: z.int(),
    unread_label: z.string()
});

/**
 * IntPackagePresetIn
 */
export const zIntPackagePresetIn = z.object({
    name: z.string().min(1).max(60),
    bottles_075_equivalent: z.int().gte(1).lte(60),
    gross_weight_kg: z.union([
        z.number().gt(0).lte(9999.99),
        z.string()
    ]),
    length_cm: z.int().gte(1).lte(300),
    width_cm: z.int().gte(1).lte(300),
    height_cm: z.int().gte(1).lte(300),
    active: z.boolean().optional().default(true)
});

/**
 * IntPartnerCatalogStateRow
 */
export const zIntPartnerCatalogStateRow = z.object({
    item_type: z.string(),
    item_type_label: z.string(),
    item_id: z.uuid(),
    title: z.string(),
    sku: z.string(),
    partner_sku: z.string().nullable(),
    partner_available: z.string(),
    partner_available_label: z.string(),
    partner_stock: z.int().nullable(),
    partner_price: z.record(z.string(), z.unknown()).nullable(),
    feed_status: z.string(),
    feed_status_label: z.string(),
    feed_version: z.int(),
    in_feed_since: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
    reported_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * IntPartnerCatalogStatePage
 */
export const zIntPartnerCatalogStatePage = z.object({
    items: z.array(zIntPartnerCatalogStateRow),
    total: z.int(),
    last_catalog_pull_at: z.iso.datetime({ offset: true }).nullable(),
    stale_warning: z.string().nullable()
});

/**
 * IntPartnerCreateAllowed
 */
export const zIntPartnerCreateAllowed = z.object({
    test: z.boolean(),
    prod: z.boolean(),
    hint: z.string().nullable()
});

/**
 * IntPartnerHandoffRow
 */
export const zIntPartnerHandoffRow = z.object({
    id: z.uuid(),
    created_at: z.iso.datetime({ offset: true }),
    user_id: z.uuid(),
    user_name: z.string().nullable(),
    item_type: z.string(),
    item_type_label: z.string(),
    item_id: z.uuid(),
    qty: z.int(),
    amount_hint: z.record(z.string(), z.unknown()),
    eligible_early_access: z.boolean(),
    status: z.string(),
    status_label: z.string(),
    order_id: z.uuid().nullable(),
    order_number: z.int().nullable(),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * IntPartnerHandoffPage
 */
export const zIntPartnerHandoffPage = z.object({
    items: z.array(zIntPartnerHandoffRow),
    total: z.int()
});

/**
 * IntPartnerNotifyAllOut
 */
export const zIntPartnerNotifyAllOut = z.object({
    events: z.int()
});

/**
 * IntPartnerSignatureCheckIn
 *
 * «Проверить подпись» (16.10.1.1): строка запроса партнёра и его подпись.
 */
export const zIntPartnerSignatureCheckIn = z.object({
    method: z.string().min(3).max(7),
    path: z.string().min(1).max(2000),
    timestamp: z.string().min(1).max(20),
    nonce: z.string().min(1).max(128),
    body: z.string().max(200000).optional().default(''),
    key_id: z.string().min(1).max(40),
    signature: z.string().min(1).max(200)
});

/**
 * IntPartnerSignatureCheckOut
 */
export const zIntPartnerSignatureCheckOut = z.object({
    valid: z.boolean(),
    message: z.string(),
    string_to_sign: z.string()
});

/**
 * IntPartnerSigningKeyCreated
 */
export const zIntPartnerSigningKeyCreated = z.object({
    kid: z.string(),
    previous_kid: z.string().nullable()
});

/**
 * IntPartnerSigningKeyOut
 */
export const zIntPartnerSigningKeyOut = z.object({
    kid: z.string(),
    status: z.enum([
        'active',
        'retiring',
        'retired'
    ]),
    status_label: z.string(),
    activated_at: z.iso.datetime({ offset: true }),
    retiring_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * IntPartnerSkuIn
 */
export const zIntPartnerSkuIn = z.object({
    partner_sku: z.string().max(64).nullable()
});

/**
 * IntPartnerTestLinkIn
 */
export const zIntPartnerTestLinkIn = z.object({
    item_type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]),
    item_id: z.uuid(),
    qty: z.int().gte(1).lte(99).optional().default(1)
});

/**
 * IntPartnerTestLinkOut
 */
export const zIntPartnerTestLinkOut = z.object({
    redirect_url: z.string(),
    handoff_id: z.uuid(),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * IntPartnerWebhookOut
 */
export const zIntPartnerWebhookOut = z.object({
    event_id: z.uuid(),
    event_type: z.string(),
    event_label: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    attempts: z.int(),
    last_http_status: z.int().nullable(),
    last_error: z.string().nullable(),
    status: z.string(),
    status_label: z.string(),
    next_attempt_at: z.iso.datetime({ offset: true }).nullable(),
    can_resend: z.boolean()
});

/**
 * IntPartnerWebhookBlock
 */
export const zIntPartnerWebhookBlock = z.object({
    url: z.string().nullable(),
    events: z.array(z.string()),
    secret_set: z.boolean(),
    failed_count: z.int(),
    recent: z.array(zIntPartnerWebhookOut)
});

/**
 * IntPartnerWebhookTestOut
 */
export const zIntPartnerWebhookTestOut = z.object({
    http_status: z.int().nullable(),
    duration_ms: z.int(),
    error: z.string().nullable(),
    message: z.string()
});

/**
 * IntPickupIn
 */
export const zIntPickupIn = z.object({
    shipper_location_id: z.uuid(),
    date: z.iso.date()
});

/**
 * IntPickupOut
 */
export const zIntPickupOut = z.object({
    id: z.uuid(),
    shipper_location_id: z.uuid(),
    shipper_location_name: z.string(),
    pickup_date: z.iso.date(),
    ready_at: z.iso.datetime({ offset: true }),
    close_at: z.iso.datetime({ offset: true }),
    dispatch_confirmation_number: z.string().nullable(),
    status: z.string(),
    status_label: z.string(),
    shipments_count: z.int(),
    error_message: z.string().nullable()
});

/**
 * IntReferenceCardOut
 *
 * Справочная карточка без API (СДЭК, 16.10.1.2).
 */
export const zIntReferenceCardOut = z.object({
    description: z.string(),
    tracking_url_template: z.string().nullable(),
    source_integration_key: z.string().nullable()
});

/**
 * IntShipmentAccepted
 */
export const zIntShipmentAccepted = z.object({
    id: z.uuid(),
    status: z.string(),
    status_label: z.string()
});

/**
 * IntShipmentCan
 */
export const zIntShipmentCan = z.object({
    download_label: z.boolean().optional().default(false),
    retry: z.boolean().optional().default(false),
    update_address: z.boolean().optional().default(false),
    cancel: z.boolean().optional().default(false),
    refresh_tracking: z.boolean().optional().default(false),
    update_excise: z.boolean().optional().default(false),
    request_pickup: z.boolean().optional().default(false),
    cancel_pickup: z.boolean().optional().default(false)
});

/**
 * IntShipmentEventOut
 */
export const zIntShipmentEventOut = z.object({
    occurred_at: z.iso.datetime({ offset: true }),
    source: z.string(),
    code: z.string().nullable(),
    status_code: z.string().nullable(),
    description: z.string().nullable(),
    location: z.string().nullable(),
    mapped_status: z.string().nullable(),
    mapped_status_label: z.string().nullable()
});

/**
 * IntShipmentPatchIn
 */
export const zIntShipmentPatchIn = z.object({
    excise_document_ref: z.string().max(40).nullable()
});

/**
 * IntShipmentPickupOut
 */
export const zIntShipmentPickupOut = z.object({
    id: z.uuid(),
    pickup_date: z.iso.date(),
    dispatch_confirmation_number: z.string().nullable(),
    status: z.string(),
    status_label: z.string()
});

/**
 * IntShipmentReceiverIn
 */
export const zIntShipmentReceiverIn = z.object({
    full_name: z.string().min(1).max(255),
    phone: z.string().max(70).regex(/^\+\d{6,15}$/),
    email: z.string().min(3).max(70),
    address_line1: z.string().min(1).max(45),
    address_line2: z.string().max(45).nullish(),
    address_line3: z.string().max(45).nullish(),
    postal_code: z.string().min(1).max(12),
    city: z.string().min(1).max(45),
    province: z.string().max(35).nullish(),
    country_code: z.string().length(2).regex(/^[A-Za-z]{2}$/),
    delivery_note: z.string().max(70).nullish()
});

/**
 * IntShipmentRetryIn
 */
export const zIntShipmentRetryIn = z.object({
    receiver: zIntShipmentReceiverIn.nullish()
});

/**
 * IntShipmentTrackingOut
 */
export const zIntShipmentTrackingOut = z.object({
    status: z.string(),
    status_label: z.string(),
    events: z.array(zIntShipmentEventOut)
});

/**
 * IntShipperLocationCan
 */
export const zIntShipperLocationCan = z.object({
    update: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * IntPackagePresetOut
 */
export const zIntPackagePresetOut = z.object({
    id: z.uuid(),
    name: z.string(),
    bottles_075_equivalent: z.int(),
    gross_weight_kg: z.union([
        z.number(),
        z.string()
    ]),
    length_cm: z.int(),
    width_cm: z.int(),
    height_cm: z.int(),
    dimensions_label: z.string(),
    active: z.boolean(),
    can: zIntShipperLocationCan
});

/**
 * IntShipperLocationIn
 */
export const zIntShipperLocationIn = z.object({
    name: z.string().min(1).max(100),
    winery_id: z.uuid().nullish(),
    company_name: z.string().min(1).max(100),
    contact_name: z.string().min(1).max(255),
    phone: z.string().max(70).regex(/^\+\d{6,15}$/),
    email: z.string().min(3).max(70),
    address_line1: z.string().min(1).max(45),
    address_line2: z.string().max(45).nullish(),
    address_line3: z.string().max(45).nullish(),
    postal_code: z.string().min(1).max(12),
    city: z.string().min(1).max(45),
    province_code: z.string().max(35).nullish(),
    country_code: z.string().length(2).regex(/^[A-Za-z]{2}$/),
    excise_warehouse_ref: z.string().max(32).nullish(),
    pickup_mode: z.enum(['regular', 'on_demand']),
    pickup_ready_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    pickup_close_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    pickup_cutoff_time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
    timezone: z.string().max(64).nullish(),
    business_days: z.array(z.int()).min(1).max(7),
    active: z.boolean().optional().default(true)
});

/**
 * IntShipperLocationOut
 */
export const zIntShipperLocationOut = z.object({
    id: z.uuid(),
    name: z.string(),
    winery_id: z.uuid().nullable(),
    winery_name: z.string().nullable(),
    company_name: z.string(),
    contact_name: z.string(),
    phone: z.string(),
    email: z.string(),
    address_line1: z.string(),
    address_line2: z.string().nullable(),
    address_line3: z.string().nullable(),
    postal_code: z.string(),
    city: z.string(),
    province_code: z.string().nullable(),
    country_code: z.string(),
    excise_warehouse_ref: z.string().nullable(),
    pickup_mode: z.string(),
    pickup_mode_label: z.string(),
    pickup_ready_time: z.string(),
    pickup_close_time: z.string(),
    pickup_cutoff_time: z.string(),
    timezone: z.string(),
    business_days: z.array(z.int()),
    active: z.boolean(),
    can: zIntShipperLocationCan
});

/**
 * IntShippingMoney
 */
export const zIntShippingMoney = z.object({
    amount: z.int().gte(0),
    currency: z.string().length(3)
});

/**
 * IntShipmentOut
 */
export const zIntShipmentOut = z.object({
    id: z.uuid(),
    order_id: z.uuid(),
    order_label: z.string(),
    delivery_id: z.uuid(),
    carrier: z.string(),
    carrier_label: z.string(),
    status: z.string(),
    status_label: z.string(),
    tracking_number: z.string().nullable(),
    tracking_url: z.string().nullable(),
    product_code: z.string().nullable(),
    planned_shipping_at: z.iso.datetime({ offset: true }).nullable(),
    estimated_delivery_at: z.iso.datetime({ offset: true }).nullable(),
    delivered_at: z.iso.datetime({ offset: true }).nullable(),
    shipper_location_id: z.uuid(),
    shipper_location_name: z.string(),
    receiver: z.record(z.string(), z.unknown()),
    packages: z.array(z.record(z.string(), z.unknown())),
    items: z.array(z.record(z.string(), z.unknown())),
    service_codes: z.array(z.string()),
    declared_value: zIntShippingMoney,
    carrier_cost: zIntShippingMoney.nullable(),
    excise_document_ref: z.string().nullable(),
    excise_required: z.boolean(),
    pickup: zIntShipmentPickupOut.nullable(),
    last_error_code: z.string().nullable(),
    last_error_message: z.string().nullable(),
    attempts: z.int(),
    events: z.array(zIntShipmentEventOut),
    can: zIntShipmentCan
});

/**
 * IntShippingPushOut
 */
export const zIntShippingPushOut = z.object({
    enabled: z.boolean(),
    active_subscriptions: z.int(),
    failed_subscriptions: z.int(),
    last_push_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * IntShippingCardOut
 *
 * Карточка «DHL Express» (16.10.1.3): предусловия и состояние отправлений.
 */
export const zIntShippingCardOut = z.object({
    alcohol_approved: z.boolean(),
    label_errors: z.int(),
    shipper_locations_active: z.int(),
    package_presets_active: z.int(),
    countries_enabled: z.int(),
    push: zIntShippingPushOut.nullable()
});

/**
 * IntTaxProfileIn
 */
export const zIntTaxProfileIn = z.object({
    valid_from: z.iso.date(),
    vat_rate_standard: z.union([
        z.number().gte(0).lte(100),
        z.string()
    ]),
    excise_still_wine_per_hl: zIntShippingMoney,
    excise_sparkling_wine_per_hl: zIntShippingMoney,
    excise_intermediate_per_hl: zIntShippingMoney.nullish(),
    excise_basis: z.enum(['per_hl_product', 'per_hl_alcohol']),
    tax_representative: z.string().max(255).nullish(),
    registration_ref: z.string().max(64).nullish(),
    enabled_for_sales: z.boolean()
});

/**
 * IntTaxProfileOut
 */
export const zIntTaxProfileOut = z.object({
    id: z.uuid(),
    country_code: z.string(),
    country_name: z.string(),
    valid_from: z.iso.date(),
    vat_rate_standard: z.union([
        z.number(),
        z.string()
    ]),
    excise_still_wine_per_hl: zIntShippingMoney,
    excise_sparkling_wine_per_hl: zIntShippingMoney,
    excise_intermediate_per_hl: zIntShippingMoney.nullable(),
    excise_basis: z.string(),
    tax_representative: z.string().nullable(),
    registration_ref: z.string().nullable(),
    enabled_for_sales: z.boolean(),
    current: z.boolean(),
    dhl_allowed: z.boolean()
});

/**
 * IntTaxProfilesPage
 */
export const zIntTaxProfilesPage = z.object({
    items: z.array(zIntTaxProfileOut),
    total: z.int()
});

/**
 * IntegrationActionIn
 *
 * Тело действия провайдера: `send_test_email` — `{to}`, `send_test_push` — `{user_email}`.
 */
export const zIntegrationActionIn = z.object({
    to: z.string().max(320).nullish(),
    user_email: z.string().max(320).nullish(),
    shipper_location_id: z.uuid().nullish(),
    country_code: z.string().length(2).nullish(),
    postal_code: z.string().max(12).nullish(),
    city: z.string().max(45).nullish(),
    bottles: z.int().gte(1).lte(60).nullish()
});

/**
 * IntegrationCan
 */
export const zIntegrationCan = z.object({
    update: z.boolean().optional().default(false),
    test: z.boolean().optional().default(false),
    enable: z.boolean().optional().default(false),
    disable: z.boolean().optional().default(false),
    activate: z.boolean().optional().default(false),
    actions: z.boolean().optional().default(false),
    open_log: z.boolean().optional().default(false)
});

/**
 * IntegrationMode
 */
export const zIntegrationMode = z.enum(IntegrationMode);

/**
 * IntPartnerCredentialCreated
 */
export const zIntPartnerCredentialCreated = z.object({
    key_id: z.string(),
    secret: z.string(),
    mode: zIntegrationMode,
    expires_at: z.iso.datetime({ offset: true }).nullable(),
    message: z.string()
});

/**
 * IntPartnerCredentialIn
 */
export const zIntPartnerCredentialIn = z.object({
    mode: zIntegrationMode,
    expires_at: z.iso.datetime({ offset: true }).nullish()
});

/**
 * IntPartnerCredentialOut
 *
 * API-ключ партнёра (16.3.2): секрет не отдаётся — только последние 4 символа.
 */
export const zIntPartnerCredentialOut = z.object({
    key_id: z.string(),
    mode: zIntegrationMode,
    mode_label: z.string(),
    secret_last4: z.string(),
    status: z.enum([
        'active',
        'revoked',
        'expired'
    ]),
    status_label: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    last_used_at: z.iso.datetime({ offset: true }).nullable(),
    expires_at: z.iso.datetime({ offset: true }).nullable(),
    revoked_at: z.iso.datetime({ offset: true }).nullable(),
    can_revoke: z.boolean()
});

/**
 * IntPartnerCardOut
 *
 * Вкладки карточки «Партнёр ТБН» (16.10.1.1) сверх общих полей интеграции.
 */
export const zIntPartnerCardOut = z.object({
    credentials: z.array(zIntPartnerCredentialOut),
    can_create_credential: zIntPartnerCreateAllowed,
    signing_keys: z.array(zIntPartnerSigningKeyOut),
    jwks_url: z.string(),
    return_url: z.string(),
    webhook: zIntPartnerWebhookBlock,
    last_inbound_at: z.iso.datetime({ offset: true }).nullable(),
    last_catalog_pull_at: z.iso.datetime({ offset: true }).nullable(),
    catalog_stale: z.boolean(),
    feed_items: z.int(),
    test_link_available: z.boolean()
});

/**
 * IntegrationPort
 */
export const zIntegrationPort = z.enum(IntegrationPort);

/**
 * IntegrationStatus
 *
 * Вычисляемый статус (14.6.1, 17.15.1) — в БД не хранится.
 */
export const zIntegrationStatus = z.enum(IntegrationStatus);

/**
 * IntegrationUpdateIn
 */
export const zIntegrationUpdateIn = z.object({
    mode: zIntegrationMode.nullish(),
    settings: z.record(z.string(), z.unknown()).optional(),
    secrets: z.record(z.string(), z.string().nullable()).optional()
});

/**
 * IntroLessonItemOut
 */
export const zIntroLessonItemOut = z.object({
    lesson_id: z.uuid(),
    number: z.string(),
    title: z.string(),
    duration_min: z.int(),
    state: z.enum([
        'done',
        'next',
        'open'
    ]),
    xp: z.int().nullable(),
    likes: z.int(),
    set_label: z.string().nullable(),
    target: z.string()
});

/**
 * IntroSectionOut
 */
export const zIntroSectionOut = z.object({
    id: z.uuid(),
    number: z.int(),
    title: z.string(),
    result_text: z.string().nullable(),
    achievement_title: z.string().nullable(),
    done: z.int(),
    all: z.int(),
    pct: z.int(),
    xp: z.int().nullable(),
    completed: z.boolean(),
    label: z.string(),
    target: z.string(),
    lessons: z.array(zIntroLessonItemOut)
});

/**
 * ItemType
 *
 * Объект права доступа (8.3.20) и предложения цены.
 */
export const zItemType = z.enum(ItemType);

/**
 * EntitlementItemOut
 */
export const zEntitlementItemOut = z.object({
    item_type: zItemType,
    item_id: z.uuid(),
    title: z.string(),
    granted_at: z.iso.datetime({ offset: true }),
    source: z.string(),
    source_label: z.string()
});

/**
 * EntitlementPageOut
 */
export const zEntitlementPageOut = z.object({
    items: z.array(zEntitlementItemOut),
    next_cursor: z.string().nullable()
});

/**
 * LadderTierOut
 */
export const zLadderTierOut = z.object({
    id: z.uuid(),
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    title: z.string(),
    xp_min: z.int(),
    accuracy_min: z.number(),
    unlocks: z.string().nullable(),
    image_url: z.string().nullable(),
    xp_label: z.string(),
    accuracy_label: z.string(),
    reached: z.boolean()
});

/**
 * Language
 */
export const zLanguage = z.enum(Language);

/**
 * AdminMePatchIn
 *
 * `{admin_locale?, phone?}`. Отсутствующее поле не меняется; `phone: null` очищает телефон;
 * `admin_locale: null` недопустим (колонка обязательна).
 */
export const zAdminMePatchIn = z.object({
    admin_locale: zLanguage.nullish(),
    phone: z.string().max(30).nullish()
});

/**
 * AppLocaleOut
 */
export const zAppLocaleOut = z.object({
    languages: z.array(zLanguage),
    default: zLanguage,
    current: zLanguage
});

/**
 * DevicePatchIn
 *
 * `{locale?, push_token?, push_enabled?}`; `push_token: null` — токен удалён.
 */
export const zDevicePatchIn = z.object({
    locale: zLanguage.nullish(),
    push_token: z.string().max(4096).nullish(),
    push_enabled: z.boolean().nullish()
});

/**
 * DocumentAdminOut
 */
export const zDocumentAdminOut = z.object({
    id: z.uuid(),
    name: z.string(),
    doc_key: zDocKey,
    is_platform: z.boolean(),
    owner_kind: zDocumentOwnerKind,
    owner_label: z.string(),
    lang: zLanguage,
    status: zDocumentStatus,
    status_label: z.string(),
    visibility: zDocumentVisibility,
    version: z.string().nullable(),
    effective_from: z.iso.date().nullable(),
    file_label: z.string().nullable(),
    file_url: z.string().nullable(),
    url: z.string().nullable(),
    decision_comment: z.string().nullable(),
    notice: z.string().nullable(),
    archive_reason: zContentArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    versions: z.array(zDocumentVersionOut),
    usage: zDocumentUsageOut,
    history: z.array(zContentHistoryOut),
    can: zDocumentCan,
    toast: z.string().nullish(),
    notification_queued: z.boolean().nullish()
});

/**
 * DocumentRowOut
 */
export const zDocumentRowOut = z.object({
    id: z.uuid(),
    name: z.string(),
    doc_key: zDocKey,
    file_label: z.string().nullable(),
    version: z.string().nullable(),
    effective_from: z.iso.date().nullable(),
    lang: zLanguage,
    owner_kind: zDocumentOwnerKind,
    owner_label: z.string(),
    status: zDocumentStatus,
    status_label: z.string(),
    visibility: zDocumentVisibility,
    can: zDocumentCan
});

/**
 * LearnArchiveReason
 */
export const zLearnArchiveReason = z.enum(LearnArchiveReason);

/**
 * LearnArchiveIn
 */
export const zLearnArchiveIn = z.object({
    reason: zLearnArchiveReason,
    comment: z.string().max(300).nullish()
});

/**
 * LearnHistoryItemOut
 */
export const zLearnHistoryItemOut = z.object({
    id: z.uuid(),
    at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    text: z.string()
});

/**
 * LearnHistoryPageOut
 */
export const zLearnHistoryPageOut = z.object({
    items: z.array(zLearnHistoryItemOut),
    total: z.int()
});

/**
 * LearnMediaPlacement
 */
export const zLearnMediaPlacement = z.enum(LearnMediaPlacement);

/**
 * LearnPriceIn
 */
export const zLearnPriceIn = z.object({
    amount: z.int().gt(0),
    currency: zCurrency
});

/**
 * LearnRefOut
 */
export const zLearnRefOut = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * CourseInfoOut
 */
export const zCourseInfoOut = z.object({
    id: z.uuid(),
    kind: zCourseKind,
    title: z.string(),
    region: zLearnRefOut.nullable(),
    description: z.array(zBlockOut),
    theses: z.array(z.string()),
    cover_url: z.string().nullable(),
    lessons_count: z.int(),
    duration_min: z.int(),
    duration_label: z.string(),
    xp_total: z.int().nullable(),
    header: z.string()
});

/**
 * LearnRevisionState
 */
export const zLearnRevisionState = z.enum(LearnRevisionState);

/**
 * LearnRevisionOut
 */
export const zLearnRevisionOut = z.object({
    id: z.uuid(),
    version: z.int(),
    state: zLearnRevisionState,
    is_edit: z.boolean(),
    decision: zDecision.nullable(),
    submitted_at: z.iso.datetime({ offset: true }).nullable(),
    decided_at: z.iso.datetime({ offset: true }).nullable(),
    note: z.string().nullable()
});

/**
 * LearnStatusOut
 */
export const zLearnStatusOut = z.object({
    status: zContentStatus,
    status_label: z.string(),
    display_status: zContentStatus,
    display_status_label: z.string(),
    revision: zLearnRevisionOut.nullable()
});

/**
 * LearnUsageItemOut
 */
export const zLearnUsageItemOut = z.object({
    kind: z.string(),
    label: z.string(),
    count: z.int().nullish()
});

/**
 * LearningDiscountOut
 */
export const zLearningDiscountOut = z.object({
    pct: z.number(),
    applies_on_platform: z.boolean(),
    visible: z.boolean()
});

/**
 * LedgerItemOut
 */
export const zLedgerItemOut = z.object({
    id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullable(),
    amount: z.int(),
    kind: z.enum(['accrual', 'reversal']),
    occurred_at: z.iso.datetime({ offset: true }),
    date_label: z.string(),
    link: z.string().nullable(),
    event_type: z.string()
});

/**
 * LedgerObjectOut
 */
export const zLedgerObjectOut = z.object({
    type: z.string(),
    id: z.uuid(),
    name: z.string().nullable()
});

/**
 * LedgerPageOut
 */
export const zLedgerPageOut = z.object({
    items: z.array(zLedgerItemOut),
    next_cursor: z.string().nullable(),
    total: z.int(),
    subtitle: z.string()
});

/**
 * LegalAcceptIn
 */
export const zLegalAcceptIn = z.object({
    version_ids: z.array(z.uuid()).min(1).max(4)
});

/**
 * LegalDocumentOut
 */
export const zLegalDocumentOut = z.object({
    doc_key: zDocKey,
    name: z.string(),
    lang: z.string(),
    version_id: z.uuid(),
    version: z.string(),
    effective_from: z.iso.date(),
    url: z.string(),
    file_url: z.string().nullable(),
    acceptance_required: z.boolean().nullable()
});

/**
 * LessonAccessOut
 */
export const zLessonAccessOut = z.object({
    open: z.boolean(),
    reason: z.enum([
        'free',
        'purchased',
        'course',
        'paid'
    ])
});

/**
 * LessonCan
 */
export const zLessonCan = z.object({
    update: z.boolean().optional().default(false),
    submit: z.boolean().optional().default(false),
    submit_edits: z.boolean().optional().default(false),
    waiting: z.boolean().optional().default(false),
    decide: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    edit_price: z.boolean().optional().default(false),
    edit_store_products: z.boolean().optional().default(false),
    edit_tests: z.boolean().optional().default(false)
});

/**
 * LessonCourseOut
 */
export const zLessonCourseOut = z.object({
    id: z.uuid(),
    title: z.string(),
    kind: zCourseKind,
    kind_label: z.string(),
    can_edit: z.boolean()
});

/**
 * LessonCourseRefOut
 */
export const zLessonCourseRefOut = z.object({
    id: z.uuid(),
    kind: zCourseKind,
    title: z.string(),
    region_id: z.uuid().nullable()
});

/**
 * LessonNavItemOut
 */
export const zLessonNavItemOut = z.object({
    number: z.string(),
    lesson_id: z.uuid(),
    state: z.enum([
        'done',
        'current',
        'open',
        'locked'
    ])
});

/**
 * LessonOptionOut
 */
export const zLessonOptionOut = z.object({
    id: z.uuid(),
    title: z.string(),
    course_id: z.uuid().nullable(),
    is_intro: z.boolean()
});

/**
 * LessonTestIn
 */
export const zLessonTestIn = z.object({
    test_id: z.uuid(),
    required: z.boolean().optional().default(true),
    order: z.int().gte(0).nullish()
});

/**
 * LessonTestItemOut
 */
export const zLessonTestItemOut = z.object({
    test_id: z.uuid(),
    title: z.string(),
    required: z.boolean(),
    questions: z.int(),
    pass_from: z.int(),
    state: z.enum([
        'not_started',
        'passed',
        'perfect',
        'failed'
    ]),
    label: z.string(),
    state_label: z.string(),
    target: z.string()
});

/**
 * LessonTestOut
 */
export const zLessonTestOut = z.object({
    test_id: z.uuid(),
    title: z.string(),
    is_required: z.boolean(),
    mode_label: z.string(),
    position: z.int(),
    questions: z.int(),
    status: zContentStatus,
    status_label: z.string()
});

/**
 * LessonTestsOut
 */
export const zLessonTestsOut = z.object({
    items: z.array(zLessonTestOut)
});

/**
 * LessonVideoOut
 */
export const zLessonVideoOut = z.object({
    has_video: z.boolean(),
    status: z.string().nullable(),
    duration_sec: z.int().nullable(),
    thumbnail_url: z.string().nullable(),
    position_sec: z.int().nullish()
});

/**
 * LevelTileOut
 */
export const zLevelTileOut = z.object({
    key: z.enum([
        'lessons',
        'wine',
        'bonuses'
    ]),
    title: z.string(),
    value: z.string(),
    locked: z.boolean()
});

/**
 * LevelBarOut
 */
export const zLevelBarOut = z.object({
    overline: z.string(),
    tiles: z.array(zLevelTileOut),
    scale_max: z.int().nullable(),
    scale_marks: z.array(z.int()),
    next_perk: z.string().nullable()
});

/**
 * LikeLogRowOut
 */
export const zLikeLogRowOut = z.object({
    id: z.uuid(),
    user_id: z.uuid().nullable(),
    user_name: z.string(),
    object_type: z.string(),
    object_type_label: z.string(),
    object_id: z.uuid(),
    object_title: z.string(),
    at: z.iso.datetime({ offset: true })
});

/**
 * LikeObjectType
 */
export const zLikeObjectType = z.enum(LikeObjectType);

/**
 * LikeIn
 */
export const zLikeIn = z.object({
    object_type: zLikeObjectType,
    object_id: z.uuid()
});

/**
 * LikeOut
 */
export const zLikeOut = z.object({
    liked: z.boolean(),
    likes_count: z.int()
});

/**
 * LikeStateOut
 */
export const zLikeStateOut = z.object({
    liked: z.boolean(),
    likes_count: z.int()
});

/**
 * LikesOut
 */
export const zLikesOut = z.object({
    total: z.int(),
    last_30_days: z.int()
});

/**
 * LineOut
 */
export const zLineOut = z.object({
    label: z.string(),
    value: z.string()
});

/**
 * LinkAttachIn
 */
export const zLinkAttachIn = z.object({
    entity_type: z.string().max(40),
    entity_id: z.uuid(),
    confirm_move: z.boolean().optional().default(false)
});

/**
 * LinkCandidateOut
 */
export const zLinkCandidateOut = z.object({
    entity_type: z.string(),
    entity_id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullable(),
    current_region: zCurrentOwnerOut.nullable()
});

/**
 * LinkCandidateListOut
 */
export const zLinkCandidateListOut = z.object({
    tab: z.string(),
    items: z.array(zLinkCandidateOut)
});

/**
 * LinkOut
 */
export const zLinkOut = z.object({
    label: z.string(),
    target: z.string()
});

/**
 * LinkSource
 *
 * Метка связи с регионом (BR-GEO-52): по полю сущности или ручная привязка.
 */
export const zLinkSource = z.enum(LinkSource);

/**
 * LinkedIdentityOut
 */
export const zLinkedIdentityOut = z.object({
    provider: z.string(),
    provider_label: z.string(),
    linked_at: z.iso.datetime({ offset: true }),
    last_used_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * ListCan
 *
 * Права на уровне реестра (fail-closed: по умолчанию всё запрещено).
 */
export const zListCan = z.object({
    create: z.boolean().optional().default(false),
    export: z.boolean().optional().default(false)
});

/**
 * AuditPageOut
 */
export const zAuditPageOut = z.object({
    items: z.array(zAuditRowOut),
    total: z.int(),
    can: zListCan
});

/**
 * CampaignsPageOut
 */
export const zCampaignsPageOut = z.object({
    items: z.array(zCampaignRowOut),
    total: z.int(),
    counters: zCampaignCountersOut,
    lead: z.string(),
    empty_title: z.string(),
    empty_text: z.string(),
    can: zListCan.optional()
});

/**
 * CntPage[DocumentRowOut]
 */
export const zCntPageDocumentRowOut = z.object({
    items: z.array(zDocumentRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * CntPage[LikeLogRowOut]
 */
export const zCntPageLikeLogRowOut = z.object({
    items: z.array(zLikeLogRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * GamAdminExchangesPageOut
 */
export const zGamAdminExchangesPageOut = z.object({
    items: z.array(zExchangeRowOut),
    total: z.int(),
    can: zListCan
});

/**
 * IntPackagePresetsPage
 */
export const zIntPackagePresetsPage = z.object({
    items: z.array(zIntPackagePresetOut),
    total: z.int(),
    can: zListCan
});

/**
 * IntShipperLocationsPage
 */
export const zIntShipperLocationsPage = z.object({
    items: z.array(zIntShipperLocationOut),
    total: z.int(),
    can: zListCan
});

/**
 * LogActorType
 */
export const zLogActorType = z.enum(LogActorType);

/**
 * LogCategory
 */
export const zLogCategory = z.enum(LogCategory);

/**
 * LogDirection
 */
export const zLogDirection = z.enum(LogDirection);

/**
 * LogResult
 */
export const zLogResult = z.enum(LogResult);

/**
 * LogBriefOut
 */
export const zLogBriefOut = z.object({
    id: z.uuid(),
    requested_at: z.iso.datetime({ offset: true }),
    operation: z.string(),
    result: zLogResult,
    result_label: z.string(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable()
});

/**
 * LogCard
 */
export const zLogCard = z.object({
    id: z.uuid(),
    integration_key: z.string(),
    integration_title: z.string(),
    provider: z.string(),
    environment: zIntegrationMode,
    direction: zLogDirection,
    direction_label: z.string(),
    operation: z.string(),
    provider_method: z.string().nullable(),
    http_method: z.string().nullable(),
    response_status: z.int().nullable(),
    result: zLogResult,
    result_label: z.string(),
    error_code: z.string().nullable(),
    category: zLogCategory,
    requested_at: z.iso.datetime({ offset: true }),
    responded_at: z.iso.datetime({ offset: true }).nullable(),
    duration_ms: z.int().nullable(),
    attempt: z.int(),
    entity_type: z.string().nullable(),
    entity_id: z.string().nullable(),
    entity_title: z.string().nullable(),
    trace_id: z.string(),
    url: z.string().nullable(),
    request_headers: z.record(z.string(), z.unknown()).nullable(),
    request_body: z.string().nullable(),
    response_headers: z.record(z.string(), z.unknown()).nullable(),
    response_body: z.string().nullable(),
    error_message: z.string().nullable(),
    idempotency_key: z.string().nullable(),
    correlation: z.record(z.string(), z.unknown()).nullable(),
    actor_type: zLogActorType,
    actor_id: z.uuid().nullable()
});

/**
 * LogRow
 */
export const zLogRow = z.object({
    id: z.uuid(),
    integration_key: z.string(),
    integration_title: z.string(),
    provider: z.string(),
    environment: zIntegrationMode,
    direction: zLogDirection,
    direction_label: z.string(),
    operation: z.string(),
    provider_method: z.string().nullable(),
    http_method: z.string().nullable(),
    response_status: z.int().nullable(),
    result: zLogResult,
    result_label: z.string(),
    error_code: z.string().nullable(),
    category: zLogCategory,
    requested_at: z.iso.datetime({ offset: true }),
    responded_at: z.iso.datetime({ offset: true }).nullable(),
    duration_ms: z.int().nullable(),
    attempt: z.int(),
    entity_type: z.string().nullable(),
    entity_id: z.string().nullable(),
    entity_title: z.string().nullable(),
    trace_id: z.string()
});

/**
 * LogSubjectOut
 */
export const zLogSubjectOut = z.object({
    user_id: z.uuid().nullable(),
    name: z.string(),
    is_guest: z.boolean()
});

/**
 * LearnLogRowOut
 */
export const zLearnLogRowOut = z.object({
    id: z.string(),
    lesson: zLearnRefOut,
    course: zLearnRefOut.nullable(),
    subject: zLogSubjectOut,
    at: z.iso.datetime({ offset: true }),
    test_passed: z.boolean().nullable(),
    test_label: z.string()
});

/**
 * LearnPage[LearnLogRowOut]
 */
export const zLearnPageLearnLogRowOut = z.object({
    items: z.array(zLearnLogRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * LogsPage
 */
export const zLogsPage = z.object({
    items: z.array(zLogRow),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * MailTilesOut
 *
 * Плитки карточки почтовой интеграции (17.9.8).
 */
export const zMailTilesOut = z.object({
    sent_24h: z.int(),
    daily_limit: z.int(),
    queued: z.int(),
    deferred: z.int(),
    errors_24h: z.int()
});

/**
 * MapProvider
 */
export const zMapProvider = z.enum(MapProvider);

/**
 * MapsClientOut
 */
export const zMapsClientOut = z.object({
    provider: zMapProvider,
    js_api_key: z.string().nullish(),
    mapkit_key: z.string().nullish(),
    google_android_key: z.string().nullish(),
    google_ios_key: z.string().nullish()
});

/**
 * MarketInfoRowOut
 */
export const zMarketInfoRowOut = z.object({
    label: z.string(),
    value: z.string()
});

/**
 * AppMarketInfoOut
 */
export const zAppMarketInfoOut = z.object({
    title: z.string(),
    rows: z.array(zMarketInfoRowOut),
    summary: z.string()
});

/**
 * MarketingIn
 */
export const zMarketingIn = z.object({
    push: z.boolean().nullish(),
    email: z.boolean().nullish()
});

/**
 * MarketingOut
 */
export const zMarketingOut = z.object({
    push: z.boolean(),
    email: z.boolean(),
    text_version: z.string(),
    text: z.string()
});

/**
 * MeLocationIn
 */
export const zMeLocationIn = z.object({
    country_code: z.string().regex(/^[A-Za-z]{2}$/).nullish(),
    city_name: z.string().max(80),
    city_suggestion_id: z.uuid().nullish()
});

/**
 * MePasswordIn
 */
export const zMePasswordIn = z.object({
    current_password: z.string().max(500).nullish(),
    code: z.string().regex(/^[0-9]{4,8}$/).nullish(),
    new_password: z.string().max(500)
});

/**
 * MeTierOut
 */
export const zMeTierOut = z.object({
    id: z.uuid(),
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    title: z.string(),
    xp_min: z.int(),
    accuracy_min: z.number(),
    unlocks: z.string().nullable(),
    image_url: z.string().nullable(),
    xp_label: z.string(),
    accuracy_label: z.string(),
    reached_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * MediaKind
 */
export const zMediaKind = z.enum(MediaKind);

/**
 * MediaPlacement
 */
export const zMediaPlacement = z.enum(MediaPlacement);

/**
 * AppMedia
 */
export const zAppMedia = z.object({
    url: z.string(),
    kind: zMediaKind,
    placement: zMediaPlacement,
    caption: z.string().nullish()
});

/**
 * MediaRole
 *
 * MediaItem.role (5.3.1).
 */
export const zMediaRole = z.enum(MediaRole);

/**
 * MediaSlot
 *
 * Место медиа (5.8.2 `slot`): MediaItem (cover/gallery/video) и одиночные изображения.
 */
export const zMediaSlot = z.enum(MediaSlot);

/**
 * MediaType
 */
export const zMediaType = z.enum(MediaType);

/**
 * MediaAppOut
 */
export const zMediaAppOut = z.object({
    type: zMediaType,
    placement: zMediaPlacement,
    url: z.string(),
    caption: z.string().nullable()
});

/**
 * MemberBlockIn
 */
export const zMemberBlockIn = z.object({
    reason: z.string().max(300).nullish()
});

/**
 * MemberCan
 */
export const zMemberCan = z.object({
    view: z.boolean().optional().default(false),
    update: z.boolean().optional().default(false),
    change_role: z.boolean().optional().default(false),
    reset_password: z.boolean().optional().default(false),
    block: z.boolean().optional().default(false),
    unblock: z.boolean().optional().default(false),
    decide_application: z.boolean().optional().default(false),
    edit_author_profile: z.boolean().optional().default(false)
});

/**
 * MessageAuthorKind
 */
export const zMessageAuthorKind = z.enum(MessageAuthorKind);

/**
 * MessageKind
 */
export const zMessageKind = z.enum(MessageKind);

/**
 * MessageOut
 */
export const zMessageOut = z.object({
    id: z.uuid(),
    author: zMessageAuthorKind,
    kind: zMessageKind,
    author_name: z.string().nullable(),
    text: z.string(),
    delivered_channels: z.array(z.string()),
    created_at: z.iso.datetime({ offset: true }),
    attachments: z.array(zAttachmentOut)
});

/**
 * MetricsOut
 */
export const zMetricsOut = z.object({
    calls_24h: z.int(),
    error_rate_24h: z.number(),
    p50_ms: z.int().nullable(),
    p95_ms: z.int().nullable(),
    calls_7d: z.int(),
    error_rate_7d: z.number(),
    p50_ms_7d: z.int().nullable(),
    p95_ms_7d: z.int().nullable()
});

/**
 * MinNotice
 */
export const zMinNotice = z.enum(MinNotice);

/**
 * ModerationActionIn
 */
export const zModerationActionIn = z.record(z.string(), z.never());

/**
 * ModerationCountersOut
 */
export const zModerationCountersOut = z.object({
    waiting: z.int(),
    returned: z.int(),
    overdue: z.int(),
    decided_in_session: z.int(),
    by_group: z.record(z.string(), z.int()),
    returned_by_group: z.record(z.string(), z.int())
});

/**
 * ModerationDecisionKind
 *
 * Решение администратора (13.3.5).
 */
export const zModerationDecisionKind = z.enum(ModerationDecisionKind);

/**
 * ModerationDecisionIn
 */
export const zModerationDecisionIn = z.object({
    decision: zModerationDecisionKind,
    comment: z.string().max(600).nullish()
});

/**
 * ModerationDecisionOptionOut
 *
 * Кнопка модалки «Решение» (13.7.2), слева направо.
 */
export const zModerationDecisionOptionOut = z.object({
    decision: zModerationDecisionKind,
    label: z.string(),
    comment_required: z.boolean()
});

/**
 * ModerationDiffItemOut
 */
export const zModerationDiffItemOut = z.object({
    field: z.string(),
    label: z.string(),
    before: z.unknown().optional(),
    after: z.unknown().optional()
});

/**
 * ModerationEntityStatus
 *
 * Статус модерируемой сущности в словаре SRV: общая модель публикации (13.5.1), заявка
 * эксперта и документ (13.3.6) и состояния записи (архив, отзыв). Модули-владельцы со своими
 * статусами (GEO) переводят их в этот словарь в адаптере.
 */
export const zModerationEntityStatus = z.enum(ModerationEntityStatus);

/**
 * ModerationGroup
 *
 * Группа очереди: Каталог / Обучение / Впечатления / Люди / Документы (13.3.4).
 */
export const zModerationGroup = z.enum(ModerationGroup);

/**
 * ModerationLinkOut
 */
export const zModerationLinkOut = z.object({
    section: z.string(),
    id: z.uuid()
});

/**
 * AuditCardOut
 */
export const zAuditCardOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    actor_type: zAuditActorType,
    actor_id: z.uuid().nullable(),
    actor_name: z.string(),
    actor_role: zAuditActorRole,
    actor_role_label: z.string(),
    category: zAuditCategory,
    category_label: z.string(),
    action: z.string(),
    action_label: z.string(),
    entity_type: z.string().nullable(),
    entity_type_label: z.string().nullable(),
    entity_id: z.uuid().nullable(),
    entity_label: z.string().nullable(),
    entity: z.string(),
    summary: z.string(),
    reason: z.string().nullable(),
    ip: z.string().nullable(),
    user_agent: z.string().nullable(),
    session_id: z.uuid().nullable(),
    trace_id: z.string().nullable(),
    changes: z.array(zAuditChangeOut),
    actor_link: zModerationLinkOut.nullable(),
    entity_link: zModerationLinkOut.nullable()
});

/**
 * ModerationDecisionOut
 */
export const zModerationDecisionOut = z.object({
    id: z.uuid(),
    entity_type: z.string(),
    entity_id: z.uuid(),
    kind_label: z.string(),
    group: zModerationGroup,
    title: z.string(),
    decision: zModerationDecisionKind,
    decision_label: z.string(),
    resulting_status: zModerationEntityStatus,
    resulting_status_label: z.string(),
    comment: z.string().nullable(),
    decided_by: z.uuid().nullable(),
    decided_by_name: z.string(),
    decided_at: z.iso.datetime({ offset: true }),
    wait_hours: z.int(),
    link: zModerationLinkOut.nullable()
});

/**
 * ModerationDecisionsPageOut
 */
export const zModerationDecisionsPageOut = z.object({
    items: z.array(zModerationDecisionOut),
    total: z.int(),
    by_group: z.record(z.string(), z.int())
});

/**
 * ModerationNoteOut
 */
export const zModerationNoteOut = z.object({
    comment: z.string(),
    author: z.string().nullable(),
    at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * ModerationQueueState
 *
 * Состояние элемента очереди (13.3.4, 13.5.5).
 */
export const zModerationQueueState = z.enum(ModerationQueueState);

/**
 * ModerationQueueInfoOut
 */
export const zModerationQueueInfoOut = z.object({
    state: zModerationQueueState,
    queued_at: z.iso.datetime({ offset: true }),
    returned_at: z.iso.datetime({ offset: true }).nullable(),
    age_hours: z.int(),
    age_label: z.string(),
    overdue: z.boolean(),
    what_to_check: z.string().nullable(),
    last_comment: z.string().nullable()
});

/**
 * ModerationQueueItemOut
 */
export const zModerationQueueItemOut = z.object({
    number: z.int(),
    entity_type: z.string(),
    entity_id: z.uuid(),
    kind_label: z.string(),
    group: zModerationGroup,
    group_label: z.string(),
    title: z.string(),
    author_label: z.string(),
    what_to_check: z.string().nullable(),
    last_comment: z.string().nullable(),
    note_label: z.string(),
    comment_label: z.string(),
    status: zModerationEntityStatus,
    status_label: z.string(),
    state: zModerationQueueState,
    queued_at: z.iso.datetime({ offset: true }),
    returned_at: z.iso.datetime({ offset: true }).nullable(),
    age_hours: z.int(),
    age_label: z.string(),
    overdue: z.boolean(),
    queue_note: z.string(),
    revision_id: z.uuid().nullable(),
    decisions: z.array(zModerationDecisionOptionOut),
    link: zModerationLinkOut.nullable()
});

/**
 * ModerationQueuePageOut
 */
export const zModerationQueuePageOut = z.object({
    items: z.array(zModerationQueueItemOut),
    total: z.int(),
    counters: zModerationCountersOut,
    sla_label: z.string()
});

/**
 * ModerationRevisionStatus
 *
 * Ревизия опубликованной записи (13.3.3, 13.5.2).
 */
export const zModerationRevisionStatus = z.enum(ModerationRevisionStatus);

/**
 * ModerationRevisionOut
 */
export const zModerationRevisionOut = z.object({
    id: z.uuid(),
    status: zModerationRevisionStatus,
    status_label: z.string(),
    diff: z.array(zModerationDiffItemOut),
    base_published_at: z.iso.datetime({ offset: true }),
    submitted_at: z.iso.datetime({ offset: true }).nullable(),
    decided_at: z.iso.datetime({ offset: true }).nullable(),
    live_version_label: z.string().nullable()
});

/**
 * ModerationStateOut
 *
 * Состояние модерации записи (13.8.2 `GET /moderation/{entity_type}/{id}`).
 */
export const zModerationStateOut = z.object({
    entity_type: z.string(),
    entity_id: z.uuid(),
    kind_label: z.string(),
    title: z.string(),
    moderation_status: zModerationEntityStatus,
    status_label: z.string(),
    is_live: z.boolean(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    submitted_at: z.iso.datetime({ offset: true }).nullable(),
    moderation_note: z.string().nullable(),
    note_by: z.string().nullable(),
    note_at: z.iso.datetime({ offset: true }).nullable(),
    revision: zModerationRevisionOut.nullable(),
    history: z.array(zModerationDecisionOut),
    available_actions: z.array(z.string()),
    decisions: z.array(zModerationDecisionOptionOut),
    awaiting_decision: z.boolean(),
    queue: zModerationQueueInfoOut.nullable()
});

/**
 * ModerationStatus
 *
 * `moderation_status` рабочей версии (13.3.2).
 */
export const zModerationStatus = z.enum(ModerationStatus);

/**
 * ModerationOut
 */
export const zModerationOut = z.object({
    status: zModerationStatus,
    status_label: z.string(),
    note: z.string().nullable(),
    is_live: z.boolean(),
    has_pending_revision: z.boolean()
});

/**
 * Money
 */
export const zMoney = z.object({
    amount: z.int(),
    currency: zCurrency
});

/**
 * BookingBrief
 */
export const zBookingBrief = z.object({
    id: z.uuid(),
    number: z.string(),
    status: zBookingStatus,
    status_label: z.string(),
    amount: zMoney,
    hold_until: z.iso.datetime({ offset: true }).nullish(),
    waitlist_position: z.int().nullish()
});

/**
 * CancelPreviewOut
 */
export const zCancelPreviewOut = z.object({
    allowed: z.boolean(),
    paid_amount: zMoney.nullable(),
    refund_pct: z.int().nullable(),
    refund_amount: zMoney.nullable(),
    policy_text: z.string(),
    reason_if_not_allowed: z.string().nullable(),
    message: z.string()
});

/**
 * ChannelSalesOut
 */
export const zChannelSalesOut = z.object({
    channel: z.enum(['delivery', 'in_set']),
    label: z.string(),
    bottles: z.int(),
    orders: z.int(),
    amount: zMoney,
    amount_label: z.string(),
    share_pct: z.int()
});

/**
 * ChildCalcOut
 */
export const zChildCalcOut = z.object({
    age_group: zChildAgeGroup,
    pct: z.int(),
    amount: zMoney
});

/**
 * CourseLessonItemOut
 */
export const zCourseLessonItemOut = z.object({
    lesson_id: z.uuid(),
    number: z.string(),
    title: z.string(),
    duration_min: z.int(),
    is_paid: z.boolean(),
    open: z.boolean(),
    state: z.enum([
        'done',
        'next',
        'open',
        'locked'
    ]),
    price: zMoney.nullable(),
    likes: z.int(),
    xp: z.int().nullable(),
    label: z.string(),
    target: z.string()
});

/**
 * CourseRowOut
 */
export const zCourseRowOut = z.object({
    id: z.uuid(),
    kind: zCourseKind,
    kind_label: z.string(),
    title: z.string(),
    author_label: z.string(),
    region_id: z.uuid().nullable(),
    region_label: z.string(),
    access_label: z.string(),
    is_paid: z.boolean(),
    price: zMoney.nullable(),
    lessons_count: z.int(),
    tests_count: z.int(),
    duration_min: z.int().nullable(),
    duration_label: z.string(),
    status: zContentStatus,
    display_status: zContentStatus,
    display_status_label: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * DashboardMiniOut
 */
export const zDashboardMiniOut = z.object({
    key: z.string(),
    label: z.string(),
    available: z.boolean(),
    value: z.union([
        zMoney,
        z.int()
    ]).nullable(),
    value_label: z.string(),
    delta_pct: z.int().nullish(),
    delta_label: z.string().nullish(),
    sub: z.string().nullish(),
    link: z.string().nullish(),
    unavailable_reason: z.string().nullish()
});

/**
 * DashboardSeriesOut
 */
export const zDashboardSeriesOut = z.object({
    title: z.string(),
    scale: z.enum([
        'days',
        'weeks',
        'months',
        'period_weeks',
        'years'
    ]),
    scale_label: z.string(),
    unit: z.enum(['money', 'count']),
    available: z.boolean(),
    unavailable_reason: z.string().nullable(),
    points: z.array(z.tuple([z.string(), z.int().nullable()])),
    total: z.union([
        zMoney,
        z.int()
    ]).nullable(),
    total_label: z.string().nullable(),
    peak_label: z.string().nullable()
});

/**
 * DashboardSplitOut
 */
export const zDashboardSplitOut = z.object({
    key: z.enum([
        'platform',
        'experts',
        'winemakers'
    ]),
    label: z.string(),
    amount: zMoney.nullable(),
    pct: z.int().nullable()
});

/**
 * DashboardRevenueOut
 */
export const zDashboardRevenueOut = z.object({
    title: z.string(),
    link: z.string(),
    link_label: z.string(),
    available: z.boolean(),
    unavailable_reason: z.string().nullable(),
    gross: zMoney.nullable(),
    gross_label: z.string(),
    delta_pct: z.int().nullable(),
    delta_label: z.string().nullable(),
    delta_note: z.string().nullable(),
    period_note: z.string().nullable(),
    split: z.array(zDashboardSplitOut),
    minis: z.array(zDashboardMiniOut)
});

/**
 * DashboardStructureItemOut
 */
export const zDashboardStructureItemOut = z.object({
    key: z.string(),
    label: z.string(),
    pct: z.int().nullable(),
    amount: zMoney.nullish(),
    value: z.int().nullish(),
    value_label: z.string()
});

/**
 * DashboardTileOut
 */
export const zDashboardTileOut = z.object({
    key: z.string(),
    label: z.string(),
    available: z.boolean(),
    value: z.union([
        zMoney,
        z.int()
    ]).nullable(),
    value_label: z.string(),
    delta_pct: z.int().nullable(),
    delta_label: z.string().nullable(),
    note: z.string().nullable(),
    spark: z.array(z.union([z.int(), z.null()])),
    unavailable_reason: z.string().nullish()
});

/**
 * DashboardOut
 */
export const zDashboardOut = z.object({
    role: z.enum([
        'admin',
        'winemaker',
        'expert'
    ]),
    view: z.enum(['overview', 'dynamics']).nullable(),
    period: zDashboardPeriodOut,
    header: zDashboardHeaderOut,
    views: z.array(zDashboardOptionOut),
    periods: z.array(zDashboardOptionOut),
    months: z.array(zDashboardOptionOut),
    years: z.array(zDashboardOptionOut),
    revenue: zDashboardRevenueOut.nullish(),
    community: zDashboardCommunityOut.nullish(),
    catalog: z.array(zDashboardCatalogTileOut).nullish(),
    catalog_title: z.string().nullish(),
    catalog_lead: z.string().nullish(),
    series: zDashboardSeriesOut.nullish(),
    structure: z.array(zDashboardStructureItemOut).nullish(),
    structure_title: z.string().nullish(),
    structure_center: z.string().nullish(),
    structure_available: z.boolean().nullish(),
    tiles: z.array(zDashboardTileOut).nullish(),
    now: zDashboardNowOut.nullish()
});

/**
 * DiscountOut
 */
export const zDiscountOut = z.object({
    kind: z.enum(['level', 'first_region']),
    pct: z.int(),
    amount: zMoney,
    label: z.string()
});

/**
 * DropRowOut
 */
export const zDropRowOut = z.object({
    id: z.uuid(),
    sku: z.string(),
    name: z.string(),
    target_label: z.string(),
    start_at: z.iso.datetime({ offset: true }).nullable(),
    early_start_at: z.iso.datetime({ offset: true }).nullable(),
    timezone: z.string(),
    batch_qty: z.int(),
    remaining: z.int(),
    price: zMoney,
    price_label: z.string(),
    sales_state: zDropSalesState,
    sales_state_label: z.string(),
    status: zCatalogStatus,
    display_status: zCatalogStatus,
    display_status_label: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * CatalogPageOut[DropRowOut]
 */
export const zCatalogPageOutDropRowOut = z.object({
    items: z.array(zDropRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * EventItem
 */
export const zEventItem = z.object({
    type: zBookingTarget,
    id: z.uuid(),
    starts_at: z.iso.datetime({ offset: true }),
    month: z.string(),
    month_label: z.string(),
    day_label: z.string(),
    title: z.string(),
    meta: z.string(),
    price: zMoney.nullable(),
    price_label: z.string(),
    is_full: z.boolean(),
    is_blind: z.boolean().optional().default(false)
});

/**
 * EventsOut
 */
export const zEventsOut = z.object({
    items: z.array(zEventItem)
});

/**
 * FeedEventOut
 */
export const zFeedEventOut = z.object({
    tasting_id: z.uuid(),
    day: z.string(),
    month: z.string(),
    title: z.string(),
    meta: z.string(),
    price: zMoney.nullable(),
    price_label: z.string().nullable(),
    target: z.string()
});

/**
 * FeedCardOut
 *
 * Карточка ленты (12.6.1): регион, урок, сет, винодельня, дегустация, дроп, публикация,
 * рекомендация, хвост; `events_block` — «Афиша недели».
 */
export const zFeedCardOut = z.object({
    item_type: z.string(),
    key: z.string().nullish(),
    topic: z.string().nullish(),
    topic_label: z.string().nullish(),
    rule: z.string().nullish(),
    author: zFeedAuthorOut.nullish(),
    overline: z.string().nullish(),
    kicker: z.string().nullish(),
    title: z.string(),
    text: z.string().nullish(),
    meta: z.string().nullish(),
    pics: z.array(z.string()).optional(),
    price: zMoney.nullish(),
    price_label: z.string().nullish(),
    progress: z.record(z.string(), z.int()).nullish(),
    cta: z.string().nullish(),
    link: zContentTargetOut.nullish(),
    open: zContentTargetOut.nullish(),
    time_label: z.string().nullish(),
    actions: zFeedActionsOut.nullish(),
    events: z.array(zFeedEventOut).nullish()
});

/**
 * FormatOut
 */
export const zFormatOut = z.object({
    type: zBookingTarget,
    id: z.uuid(),
    title: z.string(),
    subtitle: z.string(),
    price: zMoney.nullable(),
    price_label: z.string(),
    starts_at: z.iso.datetime({ offset: true }).nullish()
});

/**
 * KitBottleOut
 */
export const zKitBottleOut = z.object({
    item_id: z.uuid(),
    position: z.int(),
    price: zMoney,
    is_this_broadcast: z.boolean().optional().default(false)
});

/**
 * KitOut
 *
 * Набор к эфиру вслепую (CAT, BR-EXP-25).
 */
export const zKitOut = z.object({
    set_id: z.uuid(),
    title: z.string(),
    price: zMoney,
    bottles: z.array(zKitBottleOut),
    saving: zMoney.nullable(),
    orderable: z.boolean(),
    closed_label: z.string().nullish()
});

/**
 * LearnOfferOut
 */
export const zLearnOfferOut = z.object({
    item: z.string(),
    owned: z.boolean(),
    in_cart: z.boolean(),
    purchasable: z.boolean(),
    price: zMoney.nullish(),
    discount: zDiscountOut.nullish(),
    credit: zMoney.nullish(),
    to_pay: zMoney.nullish(),
    lessons_separately: zMoney.nullish(),
    savings: zMoney.nullish(),
    reference_price: zMoney.nullish(),
    button: z.string().nullish(),
    note: z.string().nullish()
});

/**
 * CourseCardOut
 */
export const zCourseCardOut = z.object({
    course_id: z.uuid(),
    title: z.string(),
    subtitle: z.string(),
    note: z.string().nullable(),
    opened: z.boolean(),
    offer: zLearnOfferOut.nullable(),
    target: z.string()
});

/**
 * LearnOffersOut
 */
export const zLearnOffersOut = z.object({
    channel: z.enum(['gateway', 'store']),
    store: z.string().nullish(),
    items: z.array(zLearnOfferOut)
});

/**
 * LearnPage[CourseRowOut]
 */
export const zLearnPageCourseRowOut = z.object({
    items: z.array(zCourseRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * LessonRowOut
 */
export const zLessonRowOut = z.object({
    id: z.uuid(),
    title: z.string(),
    author_label: z.string(),
    region_id: z.uuid().nullable(),
    region_label: z.string(),
    cost_label: z.string(),
    is_paid: z.boolean(),
    price: zMoney.nullable(),
    duration_min: z.int(),
    duration_label: z.string(),
    courses_count: z.int(),
    courses_label: z.string(),
    tests_count: z.int(),
    in_intro: z.boolean(),
    status: zContentStatus,
    display_status: zContentStatus,
    display_status_label: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * LearnPage[LessonRowOut]
 */
export const zLearnPageLessonRowOut = z.object({
    items: z.array(zLessonRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * MeOut
 *
 * Блок `me` для авторизованного пользователя (9.8.1).
 */
export const zMeOut = z.object({
    booking: zBookingBrief.nullable(),
    waitlisted: z.boolean(),
    attended: z.boolean().optional().default(false),
    gate: zGateOut.nullish()
});

/**
 * MoneyIn
 */
export const zMoneyIn = z.object({
    amount: z.int(),
    currency: zCurrency
});

/**
 * MoreItemOut
 */
export const zMoreItemOut = z.object({
    kind: z.enum(['tasting', 'set']),
    kind_label: z.string(),
    id: z.uuid(),
    title: z.string(),
    price: zMoney.nullable(),
    price_label: z.string().nullable(),
    target: z.string()
});

/**
 * MoreFromAuthorOut
 */
export const zMoreFromAuthorOut = z.object({
    title: z.string(),
    profile_target: z.string(),
    items: z.array(zMoreItemOut)
});

/**
 * MyExperience
 */
export const zMyExperience = z.object({
    booking_id: z.uuid(),
    kind: zBookingTarget,
    target_id: z.uuid(),
    title: z.string(),
    overline: z.string(),
    meta: z.string(),
    status: zBookingStatus,
    status_chip: z.string(),
    amount: zMoney,
    amount_label: z.string(),
    starts_at: z.iso.datetime({ offset: true }).nullable(),
    open: z.string()
});

/**
 * MyOut
 */
export const zMyOut = z.object({
    items: z.array(zMyExperience),
    registered: z.int(),
    attended: z.int()
});

/**
 * NavItemOut
 */
export const zNavItemOut = z.object({
    group_key: z.string(),
    group: z.string(),
    key: z.string(),
    label: z.string(),
    badge: z.int().nullish()
});

/**
 * NearbyOut
 */
export const zNearbyOut = z.object({
    id: z.uuid(),
    author_name: z.string(),
    author_photo_url: z.string().nullable(),
    author_initial: z.string(),
    time_label: z.string(),
    title: z.string(),
    meta: z.string(),
    target: z.string()
});

/**
 * NeedOut
 */
export const zNeedOut = z.object({
    k: z.enum(['xp', 'accuracy']),
    v: z.number(),
    all: z.number(),
    done: z.boolean(),
    label: z.string()
});

/**
 * NextLessonOut
 */
export const zNextLessonOut = z.object({
    lesson_id: z.uuid(),
    number: z.string(),
    title: z.string(),
    locked: z.boolean(),
    likes: z.int(),
    label: z.string()
});

/**
 * NextLevelOut
 */
export const zNextLevelOut = z.object({
    id: z.uuid(),
    name: z.string(),
    min_xp: z.int(),
    xp_left: z.int()
});

/**
 * NextPerkOut
 */
export const zNextPerkOut = z.object({
    kind: z.enum([
        'held',
        'discount',
        'club',
        'bonus',
        'none'
    ]),
    text: z.string().nullable(),
    xp_left: z.int().nullable(),
    label: z.string().nullable()
});

/**
 * NextTierOut
 */
export const zNextTierOut = z.object({
    id: z.uuid(),
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    title: z.string(),
    unlocks: z.string().nullable(),
    need: z.array(zNeedOut),
    done_count: z.int(),
    hint: z.string(),
    card_label: z.string()
});

/**
 * NotificationAudience
 */
export const zNotificationAudience = z.enum(NotificationAudience);

/**
 * NotificationChannels
 */
export const zNotificationChannels = z.enum(NotificationChannels);

/**
 * NotificationItemOut
 */
export const zNotificationItemOut = z.object({
    key: z.enum([
        'drops',
        'events',
        'learning',
        'subscriptions'
    ]),
    label: z.string(),
    hint: z.string(),
    enabled: z.boolean()
});

/**
 * NotificationPreferencesOut
 */
export const zNotificationPreferencesOut = z.object({
    drops: z.boolean(),
    events: z.boolean(),
    learning: z.boolean(),
    subscriptions: z.boolean(),
    marketing: zMarketingOut.nullable(),
    push_permission: z.enum([
        'granted',
        'denied',
        'default'
    ])
});

/**
 * NotificationSection
 *
 * Раздел шаблона для фильтра админки (13.3.7).
 */
export const zNotificationSection = z.enum(NotificationSection);

/**
 * NotificationSettingsIn
 */
export const zNotificationSettingsIn = z.object({
    drops: z.boolean(),
    events: z.boolean(),
    learning: z.boolean(),
    subscriptions: z.boolean()
});

/**
 * NotificationSettingsOut
 */
export const zNotificationSettingsOut = z.object({
    drops: z.boolean(),
    events: z.boolean(),
    learning: z.boolean(),
    subscriptions: z.boolean(),
    push_enabled: z.boolean().nullable()
});

/**
 * NotificationSettingsPatchIn
 *
 * Частичный объект: переданные переключатели сохраняются сразу (13.6.2).
 */
export const zNotificationSettingsPatchIn = z.object({
    drops: z.boolean().nullish(),
    events: z.boolean().nullish(),
    learning: z.boolean().nullish(),
    subscriptions: z.boolean().nullish(),
    marketing: zMarketingIn.nullish()
});

/**
 * NotificationsOut
 */
export const zNotificationsOut = z.object({
    enabled_count: z.int(),
    total: z.int(),
    summary: z.string(),
    items: z.array(zNotificationItemOut)
});

/**
 * NotifyTopic
 */
export const zNotifyTopic = z.enum(NotifyTopic);

/**
 * NotifySubscriptionOut
 */
export const zNotifySubscriptionOut = z.object({
    topic: zNotifyTopic,
    region_id: z.uuid().nullable()
});

/**
 * NotifySubscriptionListOut
 */
export const zNotifySubscriptionListOut = z.object({
    items: z.array(zNotifySubscriptionOut)
});

/**
 * OAuthCompleteIn
 */
export const zOAuthCompleteIn = z.object({
    state: z.string().max(64),
    code: z.string().max(4096).nullish(),
    device_id: z.string().max(255).nullish(),
    access_token: z.string().max(8192).nullish(),
    id_token: z.string().max(8192).nullish(),
    apple_user: zAppleUserIn.nullish(),
    redirect_uri: z.string().max(500).nullish(),
    error: z.string().max(100).nullish()
});

/**
 * OAuthFlow
 */
export const zOAuthFlow = z.enum(OAuthFlow);

/**
 * OAuthHandoffIn
 */
export const zOAuthHandoffIn = z.object({
    handoff_code: z.string().max(64)
});

/**
 * OAuthPollIn
 */
export const zOAuthPollIn = z.object({
    state: z.string().max(64)
});

/**
 * OAuthProvider
 */
export const zOAuthProvider = z.enum(OAuthProvider);

/**
 * OAuthSessionOut
 */
export const zOAuthSessionOut = z.object({
    access_token: z.string(),
    refresh_token: z.string(),
    expires_in: z.int(),
    refresh_expires_in: z.int()
});

/**
 * OAuthStartIn
 */
export const zOAuthStartIn = z.object({
    flow: zOAuthFlow,
    client_platform: zClientPlatform,
    return_route: z.string().max(200).nullish()
});

/**
 * OAuthStartOut
 */
export const zOAuthStartOut = z.object({
    state: z.string(),
    nonce: z.string().nullable(),
    code_challenge: z.string().nullable(),
    authorize_url: z.string().nullable(),
    redirect_uri: z.string(),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * OAuthStateStatus
 */
export const zOAuthStateStatus = z.enum(OAuthStateStatus);

/**
 * OfferState
 */
export const zOfferState = z.enum(OfferState);

/**
 * OfferOut
 */
export const zOfferOut = z.object({
    state: zOfferState,
    state_label: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    price: zMoney.nullable(),
    price_label: z.string().nullable(),
    price_caption: z.string().nullable(),
    available: z.int().nullable(),
    max_qty: z.int(),
    seller_label: z.string().nullable(),
    ship_offer: z.string().nullable(),
    purchase_action: z.enum([
        'cart',
        'partner',
        'none'
    ]),
    purchase_label: z.string().nullable()
});

/**
 * OnboardingGetOut
 */
export const zOnboardingGetOut = z.object({
    title: z.string(),
    label: z.string()
});

/**
 * OnboardingGoalOut
 */
export const zOnboardingGoalOut = z.object({
    position: z.int(),
    title: z.string()
});

/**
 * OnboardingGoalsIn
 */
export const zOnboardingGoalsIn = z.object({
    item_ids: z.array(z.uuid()).max(50)
});

/**
 * OnboardingItemAppOut
 */
export const zOnboardingItemAppOut = z.object({
    id: z.uuid(),
    code: z.string(),
    title: z.string(),
    caption: z.string(),
    icon_url: z.string().nullable()
});

/**
 * OnboardingItemCan
 */
export const zOnboardingItemCan = z.object({
    update: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * OnboardingMoveIn
 */
export const zOnboardingMoveIn = z.object({
    direction: z.enum(['up', 'down'])
});

/**
 * OnboardingScreenCan
 */
export const zOnboardingScreenCan = z.object({
    view: z.boolean().optional().default(false),
    update: z.boolean().optional().default(false),
    move_up: z.boolean().optional().default(false),
    move_down: z.boolean().optional().default(false),
    hide: z.boolean().optional().default(false),
    show: z.boolean().optional().default(false),
    add_item: z.boolean().optional().default(false)
});

/**
 * OnboardingScreenFixed
 */
export const zOnboardingScreenFixed = z.enum(OnboardingScreenFixed);

/**
 * OnboardingScreenKind
 */
export const zOnboardingScreenKind = z.enum(OnboardingScreenKind);

/**
 * OnboardingScreenStatus
 */
export const zOnboardingScreenStatus = z.enum(OnboardingScreenStatus);

/**
 * OnboardingStepAppOut
 */
export const zOnboardingStepAppOut = z.object({
    number: z.int(),
    total: z.int()
});

/**
 * OnboardingScreenAppOut
 */
export const zOnboardingScreenAppOut = z.object({
    id: z.uuid(),
    key: z.string(),
    kind: zOnboardingScreenKind,
    title: z.string(),
    subtitle: z.string().nullable(),
    image_url: z.string().nullable(),
    step: zOnboardingStepAppOut.nullable(),
    items: z.array(zOnboardingItemAppOut)
});

/**
 * OnboardingAppOut
 */
export const zOnboardingAppOut = z.object({
    screens: z.array(zOnboardingScreenAppOut)
});

/**
 * OnboardingStepOut
 */
export const zOnboardingStepOut = z.object({
    number: z.int().nullable(),
    total: z.int(),
    label: z.string()
});

/**
 * OnboardingVisibilityIn
 */
export const zOnboardingVisibilityIn = z.object({
    status: zOnboardingScreenStatus
});

/**
 * OneTimeRewardOut
 */
export const zOneTimeRewardOut = z.object({
    type: z.enum([
        'none',
        'first_region_discount',
        'club_bonuses'
    ]),
    value: z.int().nullable(),
    text: z.string().nullable()
});

/**
 * LevelOut
 */
export const zLevelOut = z.object({
    id: z.uuid(),
    index: z.int(),
    name: z.string(),
    subtitle: z.string().nullable(),
    min_xp: z.int(),
    min_xp_label: z.string(),
    perks: z.array(z.string()),
    learning_pct: z.number(),
    wine_pct: z.number(),
    learning_label: z.string(),
    wine_label: z.string(),
    bonuses_label: z.string(),
    one_time_reward: zOneTimeRewardOut,
    image_url: z.string().nullable(),
    state_label: z.string().nullish()
});

/**
 * MeLevelOut
 */
export const zMeLevelOut = z.object({
    id: z.uuid(),
    index: z.int(),
    name: z.string(),
    subtitle: z.string().nullable(),
    min_xp: z.int(),
    min_xp_label: z.string(),
    perks: z.array(z.string()),
    learning_pct: z.number(),
    wine_pct: z.number(),
    learning_label: z.string(),
    wine_label: z.string(),
    bonuses_label: z.string(),
    one_time_reward: zOneTimeRewardOut,
    image_url: z.string().nullable(),
    state_label: z.string().nullish(),
    of: z.int(),
    held: z.boolean(),
    overline: z.string()
});

/**
 * OneTimeRewardType
 */
export const zOneTimeRewardType = z.enum(OneTimeRewardType);

/**
 * OpenHoursIn
 */
export const zOpenHoursIn = z.object({
    weekday: z.int().gte(1).lte(7),
    from: z.iso.time(),
    to: z.iso.time()
});

/**
 * OpenHoursOut
 */
export const zOpenHoursOut = z.object({
    weekday: z.int(),
    from: z.iso.time(),
    to: z.iso.time()
});

/**
 * OptionOut
 */
export const zOptionOut = z.object({
    value: z.string(),
    label: z.string()
});

/**
 * OrdAcceptedOut
 */
export const zOrdAcceptedOut = z.object({
    accepted: z.boolean().optional().default(true),
    toast: z.string()
});

/**
 * OrdAddressIn
 */
export const zOrdAddressIn = z.object({
    recipient_name: z.string().min(2).max(120),
    phone: z.string().min(5).max(20),
    email: z.string().max(200).nullish(),
    country_code: z.string().length(2),
    city: z.string().min(1).max(80),
    postal_code: z.string().min(1).max(12),
    line1: z.string().min(1).max(120),
    line2: z.string().max(120).nullish(),
    courier_note: z.string().max(200).nullish(),
    is_default: z.boolean().nullish()
});

/**
 * OrdAddressOut
 */
export const zOrdAddressOut = z.object({
    id: z.uuid(),
    recipient_name: z.string(),
    phone: z.string(),
    email: z.string(),
    country_code: z.string(),
    country_name: z.string().nullable(),
    city: z.string(),
    postal_code: z.string(),
    line1: z.string(),
    line2: z.string().nullable(),
    courier_note: z.string().nullable(),
    is_default: z.boolean(),
    validated_at: z.iso.datetime({ offset: true }).nullable(),
    label: z.string()
});

/**
 * OrdAddressPatchIn
 */
export const zOrdAddressPatchIn = z.object({
    recipient_name: z.string().min(2).max(120).nullish(),
    phone: z.string().min(5).max(20).nullish(),
    email: z.string().max(200).nullish(),
    country_code: z.string().length(2).nullish(),
    city: z.string().min(1).max(80).nullish(),
    postal_code: z.string().min(1).max(12).nullish(),
    line1: z.string().min(1).max(120).nullish(),
    line2: z.string().max(120).nullish(),
    courier_note: z.string().max(200).nullish(),
    is_default: z.boolean().nullish()
});

/**
 * OrdAddressSuggestion
 */
export const zOrdAddressSuggestion = z.object({
    city: z.string(),
    postal_code: z.string()
});

/**
 * OrdAddressValidateIn
 */
export const zOrdAddressValidateIn = z.object({
    country_code: z.string().length(2),
    postal_code: z.string().min(1).max(12),
    city: z.string().min(1).max(80)
});

/**
 * OrdAddressValidateOut
 */
export const zOrdAddressValidateOut = z.object({
    valid: z.boolean(),
    suggestions: z.array(zOrdAddressSuggestion)
});

/**
 * OrdAddressesOut
 */
export const zOrdAddressesOut = z.object({
    items: z.array(zOrdAddressOut),
    countries: z.array(z.record(z.string(), z.string()))
});

/**
 * OrdAdminRowCan
 */
export const zOrdAdminRowCan = z.object({
    view: z.boolean().optional().default(true)
});

/**
 * OrdAttemptView
 *
 * `PaymentAttemptView` (INT-PAY 15.13.1).
 */
export const zOrdAttemptView = z.object({
    id: z.uuid(),
    checkout_session_id: z.uuid(),
    order_ids: z.array(z.uuid()),
    order_number: z.string(),
    seq: z.int(),
    provider: z.string(),
    environment: z.string(),
    method: z.string(),
    method_title: z.string(),
    status: z.string(),
    amount: zMoney,
    next_action: z.record(z.string(), z.unknown()).nullable(),
    expires_at: z.iso.datetime({ offset: true }),
    failure: z.record(z.string(), z.string().nullable()).nullable(),
    card: z.record(z.string(), z.string().nullable()).nullable(),
    paid_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * OrdCancelIn
 */
export const zOrdCancelIn = z.object({
    expected_refund_amount: zMoney.nullish()
});

/**
 * OrdCancelQuoteOut
 */
export const zOrdCancelQuoteOut = z.object({
    cancellable: z.boolean(),
    refund_amount: zMoney,
    paid_amount: zMoney,
    rule_text: z.string().nullable(),
    title: z.string(),
    text: z.string()
});

/**
 * OrdCardCan
 */
export const zOrdCardCan = z.object({
    change_status: z.boolean().optional().default(false),
    comment: z.boolean().optional().default(false),
    payment_reminder: z.boolean().optional().default(false),
    link_user: z.boolean().optional().default(false),
    payments: z.boolean().optional().default(false),
    open_booking: z.boolean().optional().default(false)
});

/**
 * OrdCartItemIn
 */
export const zOrdCartItemIn = z.object({
    item_type: zCartItemType,
    ref_id: z.uuid(),
    qty: z.int().gte(1).lte(99).optional().default(1),
    meta: z.string().max(200).nullish()
});

/**
 * OrdCartItemOut
 */
export const zOrdCartItemOut = z.object({
    item_key: z.string(),
    item_type: zCartItemType,
    ref_id: z.uuid(),
    group: zCartGroup,
    title: z.string(),
    meta: z.string().nullable(),
    image_url: z.string().nullable(),
    qty: z.int(),
    fixed_qty: z.boolean(),
    max_qty: z.int().nullable(),
    unit_price: zMoney,
    unit_price_shown: zMoney,
    price_changed: z.boolean(),
    line_total: zMoney,
    badge: z.string().nullable(),
    available: z.boolean(),
    notice: z.string().nullable(),
    target: z.string().nullable()
});

/**
 * OrdCartGroupOut
 */
export const zOrdCartGroupOut = z.object({
    group: zCartGroup,
    title: z.string(),
    rule: z.string(),
    items: z.array(zOrdCartItemOut),
    subtotal: zMoney,
    discount: zMoney,
    total: zMoney,
    discount_label: z.string().nullable(),
    button: z.string()
});

/**
 * OrdCartMergeIn
 */
export const zOrdCartMergeIn = z.object({
    device_id: z.uuid()
});

/**
 * OrdCartOut
 */
export const zOrdCartOut = z.object({
    groups: z.array(zOrdCartGroupOut),
    positions: z.int(),
    groups_count: z.int(),
    total: zMoney,
    discount_total: zMoney,
    header: z.string(),
    title: z.string(),
    note: z.string().nullable(),
    total_label: z.string(),
    empty_title: z.string().nullable(),
    empty_text: z.string().nullable(),
    removed: z.array(z.string()).optional()
});

/**
 * OrdChip
 *
 * Чип статуса в приложении (7.6.6): `act` — активный, `wait` — ожидание, `done` —
 * завершён, `off` — отменён.
 */
export const zOrdChip = z.object({
    text: z.string(),
    tone: z.enum([
        'act',
        'wait',
        'done',
        'off'
    ])
});

/**
 * OrdClientResultIn
 */
export const zOrdClientResultIn = z.object({
    result: z.enum([
        'completed',
        'cancelled',
        'failed'
    ]),
    error_code: z.string().max(64).nullish()
});

/**
 * OrdCommentIn
 */
export const zOrdCommentIn = z.object({
    text: z.string().min(1).max(2000)
});

/**
 * OrdCondition
 */
export const zOrdCondition = z.object({
    label: z.string(),
    value: z.string()
});

/**
 * OrdDeliveryAddressIn
 */
export const zOrdDeliveryAddressIn = z.object({
    country_code: z.string().length(2),
    city: z.string().min(1).max(80),
    postal_code: z.string().min(1).max(12),
    line1: z.string().min(1).max(120),
    line2: z.string().max(120).nullish(),
    courier_note: z.string().max(200).nullish(),
    phone: z.string().max(20).nullish()
});

/**
 * OrdDeliveryCan
 *
 * Форма правки 7.7.3: до передачи в доставку — все поля, после — только «Плановая дата».
 */
export const zOrdDeliveryCan = z.object({
    update: z.boolean(),
    update_recipient: z.boolean(),
    update_address: z.boolean(),
    update_carrier: z.boolean()
});

/**
 * OrdDeliveryInfo
 */
export const zOrdDeliveryInfo = z.object({
    address: z.string().nullable(),
    tracking_number: z.string().nullable(),
    tracking_url: z.string().nullable(),
    planned: z.string().nullable(),
    status: z.string().nullable(),
    carrier: z.string().nullable()
});

/**
 * OrdDeliveryPatchIn
 */
export const zOrdDeliveryPatchIn = z.object({
    recipient_name: z.string().min(2).max(120).nullish(),
    address: zOrdDeliveryAddressIn.nullish(),
    carrier: zDeliveryCarrier.nullish(),
    planned_from: z.iso.date().nullish(),
    planned_to: z.iso.date().nullish()
});

/**
 * OrdDetailItem
 */
export const zOrdDetailItem = z.object({
    id: z.uuid(),
    kind: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    qty: z.int(),
    unit_price: zMoney,
    line_total: zMoney,
    price_label: z.string(),
    target: z.string().nullable()
});

/**
 * OrdDetailRow
 */
export const zOrdDetailRow = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string(),
    copyable: z.boolean().optional().default(false)
});

/**
 * OrdDiscountOut
 */
export const zOrdDiscountOut = z.object({
    level_name: z.string().nullable(),
    pct: z.string(),
    amount: zMoney,
    label: z.string().nullable()
});

/**
 * OrdDiscountsOut
 */
export const zOrdDiscountsOut = z.object({
    level_name: z.string().nullable(),
    learning_pct: z.string(),
    wine_pct: z.string(),
    learning_applies: z.boolean(),
    wine_applies: z.boolean()
});

/**
 * OrdEntryBlock
 */
export const zOrdEntryBlock = z.object({
    title: z.string(),
    name: z.string(),
    caption: z.string(),
    note: z.string()
});

/**
 * OrdHandoffIn
 */
export const zOrdHandoffIn = z.object({
    item_type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]),
    item_id: z.uuid(),
    qty: z.int().gte(1).lte(99).optional().default(1),
    source_screen: z.string().max(40).nullish()
});

/**
 * OrdHandoffOut
 */
export const zOrdHandoffOut = z.object({
    handoff_id: z.uuid(),
    redirect_url: z.string(),
    expires_at: z.iso.datetime({ offset: true }),
    qty: z.int(),
    qty_adjusted: z.boolean(),
    amount_hint: zMoney,
    eligible_early_access: z.boolean()
});

/**
 * OrdHandoffStatusOut
 */
export const zOrdHandoffStatusOut = z.object({
    handoff_id: z.uuid(),
    status: z.string(),
    order_id: z.uuid().nullable(),
    order_status: z.string().nullable(),
    item: z.record(z.string(), z.unknown())
});

/**
 * OrdHistoryRow
 */
export const zOrdHistoryRow = z.object({
    id: z.uuid(),
    at: z.iso.datetime({ offset: true }),
    actor: z.string(),
    actor_kind: z.string(),
    text: z.string()
});

/**
 * OrdLink
 *
 * Переход в раздел админки: `section` — ключ раздела, `id` — запись, `filters` — фильтры.
 */
export const zOrdLink = z.object({
    section: z.string(),
    id: z.uuid().nullish(),
    filters: z.record(z.string(), z.string()).optional()
});

/**
 * OrdAdminItem
 */
export const zOrdAdminItem = z.object({
    id: z.uuid(),
    kind: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    qty: z.int(),
    unit_price: zMoney,
    line_total: zMoney,
    link: zOrdLink.nullish()
});

/**
 * OrdLinkUserIn
 */
export const zOrdLinkUserIn = z.object({
    user_id: z.uuid(),
    comment: z.string().max(300).nullish()
});

/**
 * OrdNextStep
 */
export const zOrdNextStep = z.object({
    title: z.string(),
    text: z.string()
});

/**
 * OrdOrderCounts
 */
export const zOrdOrderCounts = z.object({
    active: z.int(),
    done: z.int()
});

/**
 * OrdOwnedRefsOut
 */
export const zOrdOwnedRefsOut = z.object({
    item_keys: z.array(z.string())
});

/**
 * OrdPartnerBrief
 */
export const zOrdPartnerBrief = z.object({
    name: z.string().nullable(),
    partner_order_number: z.string(),
    status: z.string(),
    tracking_number: z.string().nullable(),
    tracking_url: z.string().nullable()
});

/**
 * OrdPartnerItemIn
 */
export const zOrdPartnerItemIn = z.object({
    item_type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]),
    item_id: z.uuid(),
    qty: z.int().gte(1).lte(99)
});

/**
 * OrdPartnerOrderIn
 *
 * «Добавить заказ партнёра» (INT-DLV 16.10.3).
 */
export const zOrdPartnerOrderIn = z.object({
    wine_partner_id: z.uuid(),
    partner_order_number: z.string().min(1).max(64),
    user_id: z.uuid(),
    items: z.array(zOrdPartnerItemIn).min(1).max(50),
    amount: zMoney,
    status: z.enum([
        'passed_to_partner',
        'paid_at_partner',
        'partner_shipping',
        'delivered'
    ]),
    tracking_number: z.string().max(40).nullish()
});

/**
 * OrdPaymentAttemptRow
 */
export const zOrdPaymentAttemptRow = z.object({
    id: z.uuid(),
    seq: z.int(),
    created_at: z.iso.datetime({ offset: true }),
    provider: z.string(),
    provider_label: z.string(),
    environment: z.string(),
    environment_label: z.string(),
    method: z.string(),
    method_label: z.string(),
    card_mask: z.string().nullable(),
    amount: zMoney,
    status: z.string(),
    status_label: z.string(),
    failure_code: z.string().nullable(),
    failure_provider_code: z.string().nullable(),
    provider_payment_id: z.string().nullable(),
    log_link: zOrdLink
});

/**
 * OrdPaymentRefundRow
 */
export const zOrdPaymentRefundRow = z.object({
    id: z.uuid(),
    requested_at: z.iso.datetime({ offset: true }),
    amount: zMoney,
    initiated_by: z.string(),
    initiated_by_label: z.string(),
    reason: z.string(),
    reason_label: z.string(),
    status: z.string(),
    status_label: z.string(),
    provider_refund_id: z.string().nullable()
});

/**
 * OrdPayoutIn
 */
export const zOrdPayoutIn = z.object({
    entry_ids: z.array(z.uuid()).min(1).max(500),
    paid_on: z.iso.date(),
    comment: z.string().max(300).nullish()
});

/**
 * OrdPayoutOut
 */
export const zOrdPayoutOut = z.object({
    id: z.uuid(),
    partner_member_id: z.uuid(),
    partner_name: z.string(),
    amount: zMoney,
    paid_on: z.iso.date(),
    comment: z.string().nullable(),
    entries: z.int(),
    toast: z.string()
});

/**
 * OrdPill
 *
 * Пилюля реестра админки (7.7.1): тон `ok` / `warn` / `bad` / `mute` / `gold`.
 */
export const zOrdPill = z.object({
    label: z.string(),
    tone: z.enum([
        'ok',
        'warn',
        'bad',
        'mute',
        'gold'
    ])
});

/**
 * OrdPriceChange
 */
export const zOrdPriceChange = z.object({
    item_key: z.string(),
    title: z.string(),
    old: zMoney,
    new: zMoney,
    toast: z.string()
});

/**
 * OrdQuoteIn
 */
export const zOrdQuoteIn = z.object({
    group: zCartGroup,
    address_id: z.uuid().nullish(),
    delivery_method: zDeliveryMethod.nullish()
});

/**
 * OrdQuoteItemOut
 */
export const zOrdQuoteItemOut = z.object({
    item_key: z.string(),
    title: z.string(),
    meta: z.string().nullable(),
    image_url: z.string().nullable(),
    qty: z.int(),
    unit_price: zMoney,
    line_total: zMoney,
    qty_label: z.string()
});

/**
 * OrdQuoteItemRef
 */
export const zOrdQuoteItemRef = z.object({
    item_type: zCartItemType,
    item_id: z.uuid(),
    qty: z.int().gte(1).lte(99).optional().default(1)
});

/**
 * OrdReasonOption
 */
export const zOrdReasonOption = z.object({
    code: z.string(),
    label: z.string()
});

/**
 * OrdReceiptOut
 */
export const zOrdReceiptOut = z.object({
    kind: z.string(),
    stage: z.string(),
    title: z.string(),
    total: zMoney,
    status: z.string(),
    ofd_url: z.string().nullable(),
    registered_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * OrdReceiptRow
 */
export const zOrdReceiptRow = z.object({
    id: z.uuid(),
    kind_label: z.string(),
    total: zMoney,
    status: z.string(),
    fiscal: z.string().nullable(),
    ofd_url: z.string().nullable()
});

/**
 * OrdReceiptsOut
 */
export const zOrdReceiptsOut = z.object({
    items: z.array(zOrdReceiptOut),
    toast: z.string().nullable()
});

/**
 * OrdRefundBrief
 */
export const zOrdRefundBrief = z.object({
    id: z.uuid(),
    amount: zMoney,
    status: z.string(),
    status_label: z.string()
});

/**
 * OrdResultScreen
 *
 * Тексты экранов `pay-ok` / `pay-fail` (7.6.4, 7.6.5).
 */
export const zOrdResultScreen = z.object({
    title: z.string(),
    text: z.string(),
    amount_label: z.string(),
    chip: zOrdChip,
    steps: z.array(zOrdNextStep),
    primary_button: z.string(),
    primary_target: z.string().nullable(),
    secondary_button: z.string()
});

/**
 * OrdRevenueKpi
 */
export const zOrdRevenueKpi = z.object({
    key: z.string(),
    label: z.string(),
    value: zMoney,
    note: z.string()
});

/**
 * OrdRow
 *
 * Строка «подпись — значение» карточки.
 */
export const zOrdRow = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string(),
    link: zOrdLink.nullish(),
    hint: z.string().nullish()
});

/**
 * OrdPanel
 */
export const zOrdPanel = z.object({
    key: z.string(),
    title: z.string(),
    rows: z.array(zOrdRow),
    note: z.string().nullish(),
    link: zOrdLink.nullish()
});

/**
 * OrdSavedCardOut
 */
export const zOrdSavedCardOut = z.object({
    id: z.uuid(),
    title: z.string(),
    brand: z.string(),
    last4: z.string().nullable()
});

/**
 * OrdSessionOut
 */
export const zOrdSessionOut = z.object({
    id: z.uuid(),
    status: zCheckoutStatus,
    group: zCartGroup,
    subtotal: zMoney,
    discount_total: zMoney,
    shipping_total: zMoney,
    total: zMoney,
    hold_expires_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * OrdShareLine
 */
export const zOrdShareLine = z.object({
    group: zCartGroup,
    label: z.string(),
    partner_pct: z.int(),
    platform_pct: z.int(),
    text: z.string()
});

/**
 * OrdSharesIn
 */
export const zOrdSharesIn = z.object({
    learn_pct: z.int().gte(0).lte(100),
    wine_pct: z.int().gte(0).lte(100),
    exp_pct: z.int().gte(0).lte(100)
});

/**
 * OrdShipmentInfo
 *
 * Отправление DHL заказа (INT-DLV 16.9.4): несколько точек отправки — несколько отправлений
 * («Отправление {index} из {total} · {позиции}»).
 */
export const zOrdShipmentInfo = z.object({
    id: z.uuid(),
    index: z.int(),
    total: z.int(),
    status: z.string(),
    status_label: z.string(),
    tracking_number: z.string().nullable(),
    tracking_url: z.string().nullable(),
    estimated_delivery_at: z.iso.datetime({ offset: true }).nullable(),
    delivered_at: z.iso.datetime({ offset: true }).nullable(),
    items: z.array(z.record(z.string(), z.unknown())),
    exception: z.string().nullable(),
    exception_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * OrdShippingOption
 */
export const zOrdShippingOption = z.object({
    carrier: z.string(),
    product_code: z.string(),
    price: zMoney.nullable(),
    customer_price: zMoney,
    estimated_delivery_at: z.iso.datetime({ offset: true }).nullable(),
    transit_text: z.string()
});

/**
 * OrdShippingOut
 */
export const zOrdShippingOut = z.object({
    cost: zMoney,
    cost_label: z.string(),
    eta_from: z.iso.date().nullable(),
    eta_to: z.iso.date().nullable(),
    transit_text: z.string(),
    eta_line: z.string().nullable(),
    term_line: z.string().nullable(),
    fallback: z.boolean(),
    pickup_available: z.boolean(),
    pickup_label: z.string().nullable(),
    courier_label: z.string(),
    delivery_method: zDeliveryMethod,
    address_id: z.uuid().nullable(),
    address_label: z.string().nullable(),
    gift_wrap_label: z.string(),
    age_check: z.string()
});

/**
 * OrdShippingQuoteIn
 */
export const zOrdShippingQuoteIn = z.object({
    address: zOrdAddressValidateIn,
    items: z.array(zOrdQuoteItemRef).max(50).optional()
});

/**
 * OrdShippingQuoteOut
 */
export const zOrdShippingQuoteOut = z.object({
    options: z.array(zOrdShippingOption),
    fallback: z.boolean()
});

/**
 * OrdStorePurchaseRow
 *
 * 15.12.4 «Покупка в сторе»: стор, среда, транзакция, цена в сторе, витрина, статус,
 * подтверждение (Google).
 */
export const zOrdStorePurchaseRow = z.object({
    id: z.uuid(),
    store: z.string(),
    store_label: z.string(),
    environment: z.string(),
    environment_label: z.string(),
    store_transaction_id: z.string(),
    price: z.string().nullable(),
    storefront: z.string().nullable(),
    status: z.string(),
    status_label: z.string(),
    acknowledged: z.boolean().nullable(),
    purchased_at: z.iso.datetime({ offset: true }),
    revoked_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * OrdPaymentsBlockOut
 */
export const zOrdPaymentsBlockOut = z.object({
    attempts: z.array(zOrdPaymentAttemptRow),
    refunds: z.array(zOrdPaymentRefundRow),
    receipts: z.array(zOrdReceiptRow),
    store_purchase: zOrdStorePurchaseRow.nullable()
});

/**
 * OrdTimelineStep
 */
export const zOrdTimelineStep = z.object({
    code: z.string(),
    title: z.string(),
    detail: z.string().nullable(),
    done: z.boolean(),
    at: z.iso.datetime({ offset: true }).nullish()
});

/**
 * OrdTotals
 */
export const zOrdTotals = z.object({
    items_label: z.string(),
    items: zMoney,
    discount: zMoney.nullable(),
    discount_label: z.string().nullable(),
    shipping_label: z.string().nullable(),
    final_label: z.string(),
    final: zMoney,
    payment_line: z.string().nullable()
});

/**
 * OrdTrackingEntry
 */
export const zOrdTrackingEntry = z.object({
    carrier: z.string(),
    tracking_number: z.string().nullable(),
    tracking_url: z.string().nullable(),
    estimated_from: z.iso.date().nullable(),
    estimated_to: z.iso.date().nullable(),
    events: z.array(z.record(z.string(), z.unknown()))
});

/**
 * OrdTrackingOut
 */
export const zOrdTrackingOut = z.object({
    steps: z.array(zOrdTimelineStep),
    tracking: z.array(zOrdTrackingEntry),
    toast: z.string().nullable()
});

/**
 * OrdWinePartnerCan
 */
export const zOrdWinePartnerCan = z.object({
    update: z.boolean(),
    delete: z.boolean()
});

/**
 * OrderBrief
 */
export const zOrderBrief = z.object({
    id: z.uuid(),
    number: z.string().nullable(),
    type: z.string().nullish(),
    amount: zMoney.nullish()
});

/**
 * OrderDeliveryStatus
 */
export const zOrderDeliveryStatus = z.enum(OrderDeliveryStatus);

/**
 * OrdDeliveryCard
 */
export const zOrdDeliveryCard = z.object({
    id: z.uuid(),
    order_id: z.uuid(),
    order_label: z.string(),
    recipient: z.string(),
    phone: z.string().nullable(),
    address: z.string(),
    address_snapshot: z.record(z.string(), z.unknown()),
    carrier: zDeliveryCarrier,
    carrier_label: z.string(),
    planned_from: z.iso.date().nullable(),
    planned_to: z.iso.date().nullable(),
    planned: z.string().nullable(),
    status: zOrderDeliveryStatus,
    status_pill: zOrdPill,
    ship_from: z.string(),
    gift_wrap: z.boolean(),
    data: z.array(zOrdRow),
    shipments: z.array(z.record(z.string(), z.unknown())),
    history: z.array(zOrdHistoryRow),
    can: zOrdDeliveryCan
});

/**
 * OrdDeliveryRow
 */
export const zOrdDeliveryRow = z.object({
    id: z.uuid(),
    order_id: z.uuid(),
    order_number: z.int(),
    order_label: z.string(),
    recipient: z.string(),
    address: z.string(),
    carrier: zDeliveryCarrier,
    carrier_label: z.string(),
    tracking_number: z.string().nullable(),
    planned: z.string().nullable(),
    planned_from: z.iso.date().nullable(),
    planned_to: z.iso.date().nullable(),
    status: zOrderDeliveryStatus,
    status_pill: zOrdPill
});

/**
 * OrdPage[OrdDeliveryRow]
 */
export const zOrdPageOrdDeliveryRow = z.object({
    items: z.array(zOrdDeliveryRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * OrderGroup
 */
export const zOrderGroup = z.enum(OrderGroup);

/**
 * OrderRefOut
 */
export const zOrderRefOut = z.object({
    id: z.uuid(),
    number: z.string().nullable()
});

/**
 * OrderStatus
 *
 * Этапы всех потоков (7.5.1); какие допустимы потоку — `flows.STAGES`.
 */
export const zOrderStatus = z.enum(OrderStatus);

/**
 * OrdStageStep
 */
export const zOrdStageStep = z.object({
    status: zOrderStatus,
    label: z.string(),
    description: z.string().nullable(),
    done: z.boolean(),
    current: z.boolean(),
    caption: z.string().nullish()
});

/**
 * OrdStages
 */
export const zOrdStages = z.object({
    title: z.string(),
    steps: z.array(zOrdStageStep)
});

/**
 * OrdStatusOption
 */
export const zOrdStatusOption = z.object({
    status: zOrderStatus,
    label: z.string(),
    description: z.string().nullable(),
    enabled: z.boolean(),
    hint: z.string().nullable()
});

/**
 * OrdStatusModal
 */
export const zOrdStatusModal = z.object({
    overline: z.string(),
    title: z.string(),
    lead: z.string(),
    note: z.string(),
    options: z.array(zOrdStatusOption),
    refund_reasons: z.array(zOrdReasonOption),
    refund_amount: zMoney.nullable(),
    refund_line: z.string().nullable(),
    reason_required_for: z.array(zOrderStatus).optional()
});

/**
 * OrderType
 */
export const zOrderType = z.enum(OrderType);

/**
 * OrdOrderBrief
 */
export const zOrdOrderBrief = z.object({
    id: z.uuid(),
    number: z.int(),
    number_label: z.string(),
    order_type: zOrderType,
    flow: zFlow,
    status: zOrderStatus,
    seller_label: z.string(),
    total: zMoney
});

/**
 * OrdCheckoutOut
 */
export const zOrdCheckoutOut = z.object({
    session: zOrdSessionOut,
    orders: z.array(zOrdOrderBrief),
    payment: zOrdAttemptView.nullable()
});

/**
 * OrdCheckoutStatusOut
 */
export const zOrdCheckoutStatusOut = z.object({
    id: z.uuid(),
    status: zCheckoutStatus,
    group: zCartGroup,
    total: zMoney,
    orders: z.array(zOrdOrderBrief),
    payment: zOrdAttemptView.nullable(),
    result: zOrdResultScreen.nullable()
});

/**
 * OrdOrderCard
 */
export const zOrdOrderCard = z.object({
    id: z.uuid(),
    number: z.int(),
    number_label: z.string(),
    type_label: z.string(),
    group: zOrderGroup,
    flow: zFlow,
    order_type: zOrderType,
    status: zOrderStatus,
    title: z.string(),
    meta: z.string().nullable(),
    image_url: z.string().nullable(),
    chip: zOrdChip,
    section: z.enum(['active', 'done']),
    total: zMoney,
    total_struck: z.boolean(),
    placed_at: z.iso.datetime({ offset: true }),
    target: z.string(),
    partner: zOrdPartnerBrief.nullish(),
    tracking_line: z.string().nullish()
});

/**
 * OrdOrdersPage
 */
export const zOrdOrdersPage = z.object({
    items: z.array(zOrdOrderCard),
    next_cursor: z.string().nullable(),
    counts: zOrdOrderCounts,
    header: z.string()
});

/**
 * OriginCan
 */
export const zOriginCan = z.object({
    update: z.boolean().optional().default(false)
});

/**
 * OriginLevel
 */
export const zOriginLevel = z.enum(OriginLevel);

/**
 * OriginOptionOut
 */
export const zOriginOptionOut = z.object({
    id: z.uuid(),
    code: z.string(),
    full_name: z.string(),
    label: z.string()
});

/**
 * OwnedItemOut
 */
export const zOwnedItemOut = z.object({
    id: z.uuid(),
    title: z.string(),
    meta: z.string().nullable(),
    status: z.string().nullable()
});

/**
 * Page[AlertOut]
 */
export const zPageAlertOut = z.object({
    items: z.array(zAlertOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * PairingCategory
 */
export const zPairingCategory = z.enum(PairingCategory);

/**
 * PartnerApiBuyerIn
 */
export const zPartnerApiBuyerIn = z.object({
    email: z.string().max(254).nullish(),
    phone: z.string().max(20).regex(/^\+\d{6,19}$/).nullish(),
    name: z.string().max(255).nullish()
});

/**
 * PartnerApiCatalogItem
 *
 * Позиция фида; поля по типу — таблица 16.6.4 (лишние поля партнёр игнорирует).
 */
export const zPartnerApiCatalogItem = z.object({
    id: z.uuid(),
    sku: z.string().nullable(),
    type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]),
    status: z.enum(['active', 'removed']),
    version: z.int(),
    updated_at: z.iso.datetime({ offset: true }),
    title: z.string(),
    description: z.string(),
    images: z.array(z.record(z.string(), z.unknown())),
    price_hint: z.record(z.string(), z.unknown()).nullable(),
    volume_l: z.number().nullable(),
    composition_hidden_from_customer: z.boolean(),
    age_restriction: z.int(),
    restrictions: z.array(z.string()),
    availability: z.string().nullable(),
    app_url: z.string().nullable()
});

/**
 * PartnerApiCatalogPage
 */
export const zPartnerApiCatalogPage = z.object({
    items: z.array(zPartnerApiCatalogItem),
    next_cursor: z.string().nullable(),
    server_time: z.iso.datetime({ offset: true })
});

/**
 * PartnerApiDeliveryIn
 */
export const zPartnerApiDeliveryIn = z.object({
    carrier: z.enum(['cdek', 'other']).nullish(),
    method: z.enum(['pickup_point', 'courier']).nullish(),
    city: z.string().max(100).nullish(),
    point_code: z.string().max(32).nullish(),
    address_display: z.string().max(255).nullish(),
    recipient_name: z.string().max(255).nullish(),
    tracking_number: z.string().regex(/^[A-Za-z0-9]{1,40}$/).nullish(),
    estimated_from: z.iso.date().nullish(),
    estimated_to: z.iso.date().nullish()
});

/**
 * PartnerApiEventOut
 */
export const zPartnerApiEventOut = z.object({
    applied: z.boolean(),
    status: z.string(),
    previous_status: z.string(),
    tracking_url: z.string().nullable()
});

/**
 * PartnerApiHold
 */
export const zPartnerApiHold = z.object({
    hold_id: z.uuid(),
    qty: z.int(),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * PartnerApiJwksOut
 */
export const zPartnerApiJwksOut = z.object({
    keys: z.array(z.record(z.string(), z.unknown()))
});

/**
 * PartnerApiMoney
 */
export const zPartnerApiMoney = z.object({
    amount: z.int().gte(0),
    currency: z.string().length(3)
});

/**
 * PartnerApiAvailabilityItemIn
 */
export const zPartnerApiAvailabilityItemIn = z.object({
    id: z.uuid(),
    partner_sku: z.string().max(64).nullish(),
    availability: z.enum([
        'available',
        'out_of_stock',
        'not_carried'
    ]),
    stock: z.int().gte(0).nullish(),
    partner_price: zPartnerApiMoney.nullish()
});

/**
 * PartnerApiAvailabilityIn
 */
export const zPartnerApiAvailabilityIn = z.object({
    items: z.array(zPartnerApiAvailabilityItemIn).min(1).max(500)
});

/**
 * PartnerApiItemIn
 */
export const zPartnerApiItemIn = z.object({
    catalog_item_id: z.uuid().nullish(),
    partner_sku: z.string().max(64).nullish(),
    type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]),
    title: z.string().max(255).nullish(),
    qty: z.int().gte(1).lte(99),
    unit_price: zPartnerApiMoney,
    line_total: zPartnerApiMoney
});

/**
 * PartnerApiOrderCreated
 */
export const zPartnerApiOrderCreated = z.object({
    order_id: z.uuid(),
    order_number: z.string(),
    partner_order_number: z.string(),
    match_status: z.string(),
    status: z.string(),
    tracking_url: z.string().nullable(),
    warnings: z.array(z.string())
});

/**
 * PartnerApiOrderEventOut
 */
export const zPartnerApiOrderEventOut = z.object({
    event_id: z.string().nullable(),
    source: z.string(),
    status: z.string().nullable(),
    occurred_at: z.iso.datetime({ offset: true }),
    received_at: z.iso.datetime({ offset: true }),
    applied: z.boolean(),
    not_applied_reason: z.string().nullable(),
    comment: z.string().nullable()
});

/**
 * PartnerApiOrderOut
 */
export const zPartnerApiOrderOut = z.object({
    order_id: z.uuid(),
    order_number: z.string(),
    partner_order_number: z.string(),
    ordered_at: z.iso.datetime({ offset: true }),
    status: z.string(),
    status_set_by: z.enum(['partner', 'admin']),
    match_status: z.string(),
    delivered_at: z.iso.datetime({ offset: true }).nullable(),
    buyer: z.record(z.string(), z.unknown()).nullable(),
    items: z.array(z.record(z.string(), z.unknown())),
    totals: z.record(z.string(), z.unknown()),
    payment: z.record(z.string(), z.unknown()),
    delivery: z.record(z.string(), z.unknown()),
    tracking_url: z.string().nullable(),
    receipt_url: z.string().nullable(),
    comment: z.string().nullable(),
    is_test: z.boolean(),
    updated_at: z.iso.datetime({ offset: true }),
    events: z.array(zPartnerApiOrderEventOut)
});

/**
 * PartnerApiOrdersPage
 */
export const zPartnerApiOrdersPage = z.object({
    items: z.array(zPartnerApiOrderOut),
    next_cursor: z.string().nullable()
});

/**
 * PartnerApiPaymentIn
 */
export const zPartnerApiPaymentIn = z.object({
    method: z.enum([
        'card',
        'sbp',
        'sberpay',
        'tpay',
        'cash_on_delivery',
        'other'
    ]),
    status: z.enum(['pending', 'paid']),
    paid_at: z.iso.datetime({ offset: true }).nullish()
});

/**
 * PartnerApiPingOut
 */
export const zPartnerApiPingOut = z.object({
    ok: z.boolean(),
    partner: z.string(),
    mode: zIntegrationMode,
    server_time: z.iso.datetime({ offset: true })
});

/**
 * PartnerApiRefundIn
 */
export const zPartnerApiRefundIn = z.object({
    amount: zPartnerApiMoney,
    refunded_at: z.iso.datetime({ offset: true }).nullish(),
    reason: z.string().max(300).nullish()
});

/**
 * PartnerApiEventIn
 */
export const zPartnerApiEventIn = z.object({
    event_id: z.string().min(1).max(64),
    status: z.enum([
        'awaiting_payment',
        'paid',
        'assembling',
        'handed_to_carrier',
        'in_delivery',
        'delivered',
        'cancelled',
        'refunded'
    ]),
    occurred_at: z.iso.datetime({ offset: true }),
    tracking_number: z.string().regex(/^[A-Za-z0-9]{1,40}$/).nullish(),
    estimated_from: z.iso.date().nullish(),
    estimated_to: z.iso.date().nullish(),
    comment: z.string().max(500).nullish(),
    reason: z.string().max(300).nullish(),
    refund: zPartnerApiRefundIn.nullish(),
    carrier_reported_delivered_at: z.iso.datetime({ offset: true }).nullish()
});

/**
 * PartnerApiRejected
 */
export const zPartnerApiRejected = z.object({
    id: z.string(),
    error: z.string()
});

/**
 * PartnerApiAvailabilityOut
 */
export const zPartnerApiAvailabilityOut = z.object({
    accepted: z.int(),
    rejected: z.array(zPartnerApiRejected)
});

/**
 * PartnerApiTotalsIn
 */
export const zPartnerApiTotalsIn = z.object({
    items_total: zPartnerApiMoney,
    delivery_total: zPartnerApiMoney,
    discount_total: zPartnerApiMoney,
    grand_total: zPartnerApiMoney
});

/**
 * PartnerApiOrderIn
 */
export const zPartnerApiOrderIn = z.object({
    ref: z.string().max(4096).nullish(),
    partner_order_number: z.string().min(1).max(64),
    ordered_at: z.iso.datetime({ offset: true }),
    status: z.enum([
        'awaiting_payment',
        'paid',
        'assembling',
        'handed_to_carrier',
        'in_delivery'
    ]),
    buyer: zPartnerApiBuyerIn.nullish(),
    items: z.array(zPartnerApiItemIn).min(1).max(50),
    totals: zPartnerApiTotalsIn,
    payment: zPartnerApiPaymentIn,
    delivery: zPartnerApiDeliveryIn.nullish(),
    receipt_url: z.string().max(500).regex(/^https:\/\/\S+$/).nullish(),
    hold_id: z.uuid().nullish(),
    comment: z.string().max(500).nullish()
});

/**
 * PartnerApiOrderPatchIn
 */
export const zPartnerApiOrderPatchIn = z.object({
    delivery: zPartnerApiDeliveryIn.nullish(),
    receipt_url: z.string().max(500).regex(/^https:\/\/\S+$/).nullish(),
    buyer: zPartnerApiBuyerIn.nullish(),
    comment: z.string().max(500).nullish(),
    items: z.array(zPartnerApiItemIn).min(1).max(50).nullish(),
    totals: zPartnerApiTotalsIn.nullish()
});

/**
 * PartnerApiVerifyIn
 */
export const zPartnerApiVerifyIn = z.object({
    ref: z.string().min(10).max(4096),
    qty: z.int().gte(1).lte(99).nullish(),
    hold: z.boolean().optional().default(false)
});

/**
 * PartnerApiVerifyItem
 */
export const zPartnerApiVerifyItem = z.object({
    id: z.uuid(),
    type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]),
    availability: z.string()
});

/**
 * PartnerApiVerifyOut
 */
export const zPartnerApiVerifyOut = z.object({
    valid: z.boolean(),
    handoff_id: z.uuid(),
    partner_user_ref: z.string(),
    item: zPartnerApiVerifyItem,
    qty_allowed: z.int(),
    eligible_early_access: z.boolean(),
    sale_window: z.enum(['early', 'general']).nullable(),
    hold: zPartnerApiHold.nullable(),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * PartnerRole
 */
export const zPartnerRole = z.enum(PartnerRole);

/**
 * OrdRevenueRecipient
 */
export const zOrdRevenueRecipient = z.object({
    member_id: z.uuid().nullable(),
    name: z.string(),
    role: zPartnerRole.nullable(),
    role_label: z.string().nullable(),
    sellers: z.string().nullable(),
    positions: z.int(),
    turnover: zMoney,
    received: zMoney,
    platform: zMoney.nullable(),
    to_pay: zMoney.nullable()
});

/**
 * OrdRevenueSummaryOut
 */
export const zOrdRevenueSummaryOut = z.object({
    period_label: z.string(),
    kpis: z.array(zOrdRevenueKpi),
    recipients: z.array(zOrdRevenueRecipient),
    footer: z.string(),
    months: z.array(z.string()),
    recipients_filter: z.array(z.record(z.string(), z.string()))
});

/**
 * OrdSharesOut
 */
export const zOrdSharesOut = z.object({
    member_id: z.uuid(),
    role: zPartnerRole,
    custom: z.boolean(),
    note: z.string(),
    learn_pct: z.int(),
    wine_pct: z.int(),
    exp_pct: z.int(),
    lines: z.array(zOrdShareLine),
    can_update: z.boolean(),
    revenue_link: zOrdLink
});

/**
 * PassportCellOut
 */
export const zPassportCellOut = z.object({
    value: z.string(),
    caption: z.string(),
    target: z.string().nullish()
});

/**
 * PassportCellsOut
 */
export const zPassportCellsOut = z.object({
    xp: zPassportCellOut,
    accuracy: zPassportCellOut,
    stamps: zPassportCellOut,
    specializations: zPassportCellOut
});

/**
 * PassportLevelOut
 */
export const zPassportLevelOut = z.object({
    id: z.uuid(),
    index: z.int(),
    name: z.string(),
    subtitle: z.string().nullable(),
    min_xp: z.int(),
    min_xp_label: z.string(),
    perks: z.array(z.string()),
    learning_pct: z.number(),
    wine_pct: z.number(),
    learning_label: z.string(),
    wine_label: z.string(),
    bonuses_label: z.string(),
    one_time_reward: zOneTimeRewardOut,
    image_url: z.string().nullable(),
    state_label: z.string().nullish(),
    held: z.boolean(),
    privileges: z.array(z.string()),
    progress_label: z.string()
});

/**
 * PassportTierOut
 */
export const zPassportTierOut = z.object({
    id: z.uuid(),
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    title: z.string(),
    xp_min: z.int(),
    accuracy_min: z.number(),
    unlocks: z.string().nullable(),
    image_url: z.string().nullable(),
    xp_label: z.string(),
    accuracy_label: z.string(),
    reached_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * PasswordChangeIn
 */
export const zPasswordChangeIn = z.object({
    current_password: z.string().max(500),
    new_password: z.string().max(500)
});

/**
 * PasswordLoginIn
 */
export const zPasswordLoginIn = z.object({
    email: z.string(),
    password: z.string().max(500)
});

/**
 * PasswordResetIn
 */
export const zPasswordResetIn = z.object({
    verification_token: z.string().max(2000),
    new_password: z.string().max(500)
});

/**
 * PasswordResetOut
 */
export const zPasswordResetOut = z.object({
    credentials: zCredentialsOut
});

/**
 * PathCountryOut
 */
export const zPathCountryOut = z.object({
    code: z.string(),
    name: z.string(),
    illustration_url: z.string().nullable(),
    picture_url: z.string().nullable(),
    short_label: z.string(),
    regions_count: z.int(),
    lessons_count: z.int(),
    meta: z.string()
});

/**
 * PathCountryQuizOut
 */
export const zPathCountryQuizOut = z.object({
    test_id: z.uuid(),
    title: z.string(),
    questions: z.int(),
    state: z.enum(['not_started', 'done']),
    score: z.int().nullable(),
    total: z.int().nullable(),
    xp: z.int().nullable(),
    xp_received: z.boolean(),
    label: z.string(),
    target: z.string()
});

/**
 * PathCountryRefOut
 */
export const zPathCountryRefOut = z.object({
    code: z.string(),
    name: z.string()
});

/**
 * PathIntroOut
 */
export const zPathIntroOut = z.object({
    course_id: z.uuid(),
    title: z.string(),
    completed: z.boolean(),
    lessons_total: z.int(),
    done: z.int(),
    pct: z.int(),
    next_lesson_id: z.uuid().nullable(),
    next_number: z.string().nullable(),
    free_label: z.string(),
    label: z.string(),
    button: z.string(),
    xp_per_lesson: z.int().nullable(),
    xp_earned: z.int().nullable(),
    right_label: z.string(),
    target: z.string()
});

/**
 * PathLevelCheckOut
 */
export const zPathLevelCheckOut = z.object({
    test_id: z.uuid(),
    questions: z.int(),
    done: z.boolean(),
    score: z.int().nullable(),
    total: z.int().nullable(),
    label: z.string(),
    target: z.string()
});

/**
 * PayOut
 */
export const zPayOut = z.object({
    order_id: z.uuid(),
    order_number: z.string(),
    amount: zMoney,
    hold_until: z.iso.datetime({ offset: true }).nullable()
});

/**
 * PaymentGateway
 */
export const zPaymentGateway = z.enum(PaymentGateway);

/**
 * PaymentMethod
 */
export const zPaymentMethod = z.enum(PaymentMethod);

/**
 * AdminPaymentsOut
 */
export const zAdminPaymentsOut = z.object({
    gateway: z.array(zPaymentGateway),
    methods: z.array(zPaymentMethod),
    min_amount: zMoney
});

/**
 * PaymentMethodCode
 *
 * Способ оплаты попытки (15.4.1 `method`, ORD `CheckoutSession.payment_method`).
 */
export const zPaymentMethodCode = z.enum(PaymentMethodCode);

/**
 * OrdCheckoutIn
 */
export const zOrdCheckoutIn = z.object({
    group: zCartGroup,
    payment_method: zPaymentMethodCode,
    saved_card_id: z.uuid().nullish(),
    address_id: z.uuid().nullish(),
    delivery_method: zDeliveryMethod.nullish(),
    gift_wrap: z.boolean().optional().default(false)
});

/**
 * OrdMethodOut
 */
export const zOrdMethodOut = z.object({
    id: z.string(),
    title: z.string(),
    kind: z.enum(['method', 'saved_card']),
    method: zPaymentMethodCode
});

/**
 * OrdPaymentIn
 */
export const zOrdPaymentIn = z.object({
    payment_method: zPaymentMethodCode,
    saved_card_id: z.uuid().nullish()
});

/**
 * OrdPaymentMethodsOut
 */
export const zOrdPaymentMethodsOut = z.object({
    provider: z.string().nullable(),
    environment: z.string().nullable(),
    methods: z.array(zOrdMethodOut),
    saved_cards: z.array(zOrdSavedCardOut),
    default_method_id: z.string().nullable(),
    available: z.boolean(),
    notice: z.string().nullable(),
    test_mode_notice: z.string().nullable()
});

/**
 * OrdQuoteOut
 */
export const zOrdQuoteOut = z.object({
    group: zCartGroup,
    overline: z.string(),
    title: z.string(),
    lead: z.string(),
    items: z.array(zOrdQuoteItemOut),
    subtotal: zMoney,
    discount: zOrdDiscountOut.nullable(),
    shipping: zOrdShippingOut.nullable(),
    total: zMoney,
    payment_methods: z.array(zOrdMethodOut),
    saved_cards: z.array(zOrdSavedCardOut),
    default_method_id: z.string().nullable(),
    payments_available: z.boolean(),
    payments_notice: z.string().nullable(),
    test_mode_notice: z.string().nullable(),
    conditions: z.array(zOrdCondition),
    price_changes: z.array(zOrdPriceChange),
    pay_button: z.string()
});

/**
 * PaymentStatus
 */
export const zPaymentStatus = z.enum(PaymentStatus);

/**
 * OrdAdminOrderCard
 */
export const zOrdAdminOrderCard = z.object({
    id: z.uuid(),
    number: z.int(),
    number_label: z.string(),
    overline: z.string(),
    title: z.string(),
    subtitle: z.string(),
    group: zOrderGroup,
    order_type: zOrderType,
    type_label: z.string(),
    flow: zFlow,
    status: zOrderStatus,
    status_pill: zOrdPill,
    payment_status: zPaymentStatus,
    payment: zOrdPill,
    placed_at: z.iso.datetime({ offset: true }),
    paid_at: z.iso.datetime({ offset: true }).nullable(),
    total: zMoney,
    data: z.array(zOrdRow),
    items: z.array(zOrdAdminItem),
    items_total: zMoney,
    stages: zOrdStages,
    panels: z.array(zOrdPanel),
    history: z.array(zOrdHistoryRow),
    modal: zOrdStatusModal.nullable(),
    can: zOrdCardCan,
    notice: z.string().nullish()
});

/**
 * OrdAdminOrderRow
 */
export const zOrdAdminOrderRow = z.object({
    id: z.uuid(),
    number: z.int(),
    number_label: z.string(),
    placed_at: z.iso.datetime({ offset: true }),
    buyer: z.string(),
    buyer_id: z.uuid().nullable(),
    group: zOrderGroup,
    order_type: zOrderType,
    type_label: z.string(),
    flow: zFlow,
    composition: z.string(),
    seller: z.string(),
    total: zMoney,
    total_label: z.string(),
    payment: zOrdPill,
    payment_status: zPaymentStatus,
    status: zOrderStatus,
    status_pill: zOrdPill,
    can: zOrdAdminRowCan
});

/**
 * OrdCardResult
 */
export const zOrdCardResult = z.object({
    toast: z.string(),
    card: zOrdAdminOrderCard
});

/**
 * OrdPage[OrdAdminOrderRow]
 */
export const zOrdPageOrdAdminOrderRow = z.object({
    items: z.array(zOrdAdminOrderRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * OrdPaymentInfo
 */
export const zOrdPaymentInfo = z.object({
    label: z.string().nullable(),
    paid_at: z.iso.datetime({ offset: true }).nullable(),
    status: zPaymentStatus
});

/**
 * OrdOrderDetail
 */
export const zOrdOrderDetail = z.object({
    id: z.uuid(),
    number: z.int(),
    number_label: z.string(),
    type_label: z.string(),
    group: zOrderGroup,
    flow: zFlow,
    order_type: zOrderType,
    status: zOrderStatus,
    chip: zOrdChip,
    placed_at: z.iso.datetime({ offset: true }),
    placed_label: z.string(),
    head: z.string(),
    sub: z.string().nullable(),
    timeline: z.array(zOrdTimelineStep),
    timeline_title: z.string().nullable(),
    items_title: z.string(),
    items: z.array(zOrdDetailItem),
    details: z.array(zOrdDetailRow),
    entry: zOrdEntryBlock.nullable(),
    totals: zOrdTotals,
    payment: zOrdPaymentInfo,
    delivery: zOrdDeliveryInfo.nullable(),
    shipments: z.array(zOrdShipmentInfo).optional(),
    partner: z.record(z.string(), z.unknown()).nullable(),
    booking_id: z.uuid().nullable(),
    event_id: z.uuid().nullable(),
    hold_expires_at: z.iso.datetime({ offset: true }).nullable(),
    pay_amount: zMoney.nullable(),
    checkout_session_id: z.uuid().nullable(),
    actions: z.array(z.string()),
    cancel_confirm: z.string().nullable(),
    receipt_mode: z.enum([
        'ofd',
        'email',
        'store',
        'partner',
        'none'
    ]),
    receipt_toast: z.string().nullable()
});

/**
 * OrdStatusResult
 */
export const zOrdStatusResult = z.object({
    id: z.uuid(),
    status: zOrderStatus,
    payment_status: zPaymentStatus,
    refund: zOrdRefundBrief.nullable(),
    toast: z.string(),
    card: zOrdAdminOrderCard
});

/**
 * PaymentTilesOut
 *
 * Плитки карточки платёжного шлюза (15.12.1).
 */
export const zPaymentTilesOut = z.object({
    methods: z.array(z.string()),
    pending_count: z.int(),
    last_webhook_at: z.iso.datetime({ offset: true }).nullable(),
    has_recent_payments: z.boolean()
});

/**
 * PaymentsClientOut
 *
 * Публичные параметры оплаты для клиента (15.7: `Stripe.publishableKey`,
 * `Stripe.merchantIdentifier`, `Stripe.urlScheme`; Google Pay `testEnv`). Пока Stripe не
 * включён и не настроен — `null`.
 */
export const zPaymentsClientOut = z.object({
    stripe_publishable_key: z.string().nullish(),
    apple_pay_merchant_id: z.string().nullish(),
    google_pay_test_env: z.boolean().nullish(),
    url_scheme: z.string()
});

/**
 * PayoutStatus
 *
 * BR-ORD-105 — вычисляется, не хранится.
 */
export const zPayoutStatus = z.enum(PayoutStatus);

/**
 * OrdRevenueRow
 */
export const zOrdRevenueRow = z.object({
    id: z.uuid(),
    sold_at: z.iso.datetime({ offset: true }),
    order_id: z.uuid(),
    order_number: z.int(),
    order_label: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    group: zCartGroup,
    group_label: z.string(),
    is_service_line: z.boolean(),
    partner_member_id: z.uuid().nullable(),
    partner_name: z.string(),
    partner_role: zPartnerRole.nullable(),
    partner_link: zOrdLink.nullable(),
    amount: zMoney,
    share_pct: z.int(),
    share_label: z.string().nullable(),
    partner_amount: zMoney.nullable(),
    platform_amount: zMoney.nullable(),
    payout_status: zPayoutStatus,
    payout_pill: zOrdPill,
    payout_id: z.uuid().nullable(),
    selectable: z.boolean()
});

/**
 * OrdPage[OrdRevenueRow]
 */
export const zOrdPageOrdRevenueRow = z.object({
    items: z.array(zOrdRevenueRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * PaywallOut
 */
export const zPaywallOut = z.object({
    title: z.string(),
    text: z.string(),
    price: zMoney.nullable(),
    lesson_offer: zLearnOfferOut.nullable(),
    course_card: zCourseCardOut.nullable()
});

/**
 * PeopleRegionOut
 */
export const zPeopleRegionOut = z.object({
    id: z.uuid(),
    title: z.string()
});

/**
 * PersonRowOut
 */
export const zPersonRowOut = z.object({
    member_id: z.uuid(),
    name: z.string(),
    role_label: z.string(),
    kicker: z.string(),
    photo_url: z.string().nullable(),
    initial: z.string(),
    meta: z.string().nullable(),
    subscribers_count: z.int(),
    subscribers_label: z.string(),
    following: z.boolean(),
    target: z.string()
});

/**
 * PeopleOut
 */
export const zPeopleOut = z.object({
    tab: z.enum(['experts', 'makers']),
    regions: z.array(zPeopleRegionOut),
    following: z.array(zPersonRowOut),
    items: z.array(zPersonRowOut),
    total: z.int(),
    following_total: z.int(),
    next_cursor: z.string().nullable(),
    empty_text: z.string().nullable()
});

/**
 * PhoneCodeOut
 */
export const zPhoneCodeOut = z.object({
    country_code: z.string(),
    name: z.string(),
    dial_code: z.string(),
    mask: z.string().nullable(),
    length: z.int().nullable(),
    is_default: z.boolean()
});

/**
 * PhoneCodesOut
 */
export const zPhoneCodesOut = z.object({
    default_dial_code: z.string(),
    items: z.array(zPhoneCodeOut)
});

/**
 * PhoneIn
 *
 * Телефон: код страны (лист выбора) + номер; маска и длина — BR-ACC-13.
 */
export const zPhoneIn = z.object({
    dial_code: z.string().max(4),
    number: z.string().max(20)
});

/**
 * MePatchIn
 *
 * `{full_name?, birth_date?, phone?, city_name?, city_suggestion_id?, locale?, expert_mode?}`;
 * отсутствующее поле не меняется, `null` у обязательных полей профиля — 422. `consents` —
 * согласия при дозаполнении аккаунта, созданного в «Команде» (BR-ACC-09, 4.6.4).
 */
export const zMePatchIn = z.object({
    full_name: z.string().max(100).nullish(),
    birth_date: z.iso.date().nullish(),
    phone: zPhoneIn.nullish(),
    city_name: z.string().max(80).nullish(),
    city_suggestion_id: z.uuid().nullish(),
    locale: zLanguage.nullish(),
    expert_mode: z.boolean().nullish(),
    consents: z.array(zConsentItemIn).max(3).nullish()
});

/**
 * PhoneOut
 */
export const zPhoneOut = z.object({
    dial_code: z.string(),
    number: z.string()
});

/**
 * AdminUserOut
 */
export const zAdminUserOut = z.object({
    id: z.uuid(),
    email: z.string(),
    full_name: z.string().nullable(),
    avatar_url: z.string().nullable(),
    locale: zLanguage,
    phone: zPhoneOut.nullable(),
    password_changed_at: z.iso.datetime({ offset: true }).nullable(),
    password_is_temporary: z.boolean()
});

/**
 * PickerItemOut
 */
export const zPickerItemOut = z.object({
    id: z.uuid(),
    label: z.string(),
    winery: z.string(),
    region: z.string(),
    price: zMoney,
    price_label: z.string(),
    volume: zBottleVolume,
    volume_label: z.string(),
    status: zCatalogStatus,
    status_label: z.string()
});

/**
 * PickerOut
 */
export const zPickerOut = z.object({
    items: z.array(zPickerItemOut)
});

/**
 * PlaybackOut
 */
export const zPlaybackOut = z.object({
    video_asset_id: z.uuid(),
    hls_url: z.string(),
    expires_at: z.iso.datetime({ offset: true }),
    duration_sec: z.int().nullable(),
    resume_from_sec: z.int(),
    thumbnail_url: z.string().nullable()
});

/**
 * PointsRegionOut
 */
export const zPointsRegionOut = z.object({
    id: z.uuid(),
    name: z.string(),
    center: zGeoPointOut,
    distance_km: z.int()
});

/**
 * PreferenceCategory
 *
 * BR-SRV-25: `service` нельзя отключить.
 */
export const zPreferenceCategory = z.enum(PreferenceCategory);

/**
 * PrefillOut
 *
 * Предзаполнение экрана «О вас» данными провайдера (BR-INT-SVC-18) — пользователь
 * подтверждает их сам.
 */
export const zPrefillOut = z.object({
    full_name: z.string().nullable(),
    birth_date: z.iso.date().nullable(),
    avatar_url: z.string().nullable()
});

/**
 * PriceChangeIn
 */
export const zPriceChangeIn = z.object({
    new_price: zMoneyIn,
    effective_at: z.iso.datetime({ offset: true }).nullish()
});

/**
 * PriceChangeState
 */
export const zPriceChangeState = z.enum(PriceChangeState);

/**
 * PriceChangeOut
 */
export const zPriceChangeOut = z.object({
    id: z.uuid(),
    wine_id: z.uuid(),
    old_price: zMoney,
    new_price: zMoney,
    old_price_label: z.string(),
    new_price_label: z.string(),
    effective_at: z.iso.datetime({ offset: true }).nullable(),
    applied_at: z.iso.datetime({ offset: true }).nullable(),
    state: zPriceChangeState,
    state_label: z.string(),
    created_at: z.iso.datetime({ offset: true })
});

/**
 * ProgramSavedOut
 */
export const zProgramSavedOut = z.object({
    sections: z.int(),
    lessons: z.int(),
    tests: z.int(),
    message: z.string()
});

/**
 * ProgramTestIn
 */
export const zProgramTestIn = z.object({
    test_id: z.uuid(),
    required: z.boolean().optional().default(true)
});

/**
 * ProgramLessonIn
 */
export const zProgramLessonIn = z.object({
    lesson_id: z.uuid(),
    order: z.int().gte(0).nullish(),
    required: z.boolean().optional().default(true),
    tests: z.array(zProgramTestIn).nullish()
});

/**
 * ProgramTestOut
 */
export const zProgramTestOut = z.object({
    test_id: z.uuid(),
    title: z.string(),
    is_required: z.boolean(),
    mode_label: z.string(),
    questions: z.int(),
    status: zContentStatus,
    status_label: z.string()
});

/**
 * ProgramLessonOut
 */
export const zProgramLessonOut = z.object({
    lesson_id: z.uuid(),
    title: z.string(),
    position: z.int(),
    is_required: z.boolean(),
    duration_min: z.int(),
    status: zContentStatus,
    status_label: z.string(),
    is_paid: z.boolean(),
    price: zMoney.nullable(),
    access_label: z.string(),
    used_in: z.int(),
    used_in_label: z.string(),
    has_video: z.boolean(),
    can_edit_tests: z.boolean(),
    tests: z.array(zProgramTestOut)
});

/**
 * ProgressOut
 */
export const zProgressOut = z.object({
    done: z.int(),
    all: z.int(),
    pct: z.int(),
    completed: z.boolean(),
    next_lesson_id: z.uuid().nullable(),
    next_number: z.string().nullable(),
    label: z.string(),
    xp_earned: z.int().nullable()
});

/**
 * CourseScreenOut
 */
export const zCourseScreenOut = z.object({
    course: zCourseInfoOut,
    author: zAuthorBriefOut,
    access: zCourseAccessOut,
    progress: zProgressOut,
    offer: zLearnOfferOut.nullable(),
    lessons: z.array(zCourseLessonItemOut),
    cta: zCtaOut,
    subtitle: z.string(),
    reactions: zCourseLikeOut
});

/**
 * IntroCourseOut
 */
export const zIntroCourseOut = z.object({
    course: zCourseInfoOut,
    author: zAuthorBriefOut,
    progress: zProgressOut,
    label: z.string(),
    cta: zCtaOut,
    sections: z.array(zIntroSectionOut),
    reactions: zCourseLikeOut
});

/**
 * PublicationCan
 */
export const zPublicationCan = z.object({
    update: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * PublicationCommentIn
 */
export const zPublicationCommentIn = z.object({
    text: z.string().max(600)
});

/**
 * PublicationDeleteIn
 */
export const zPublicationDeleteIn = z.object({
    confirm_title: z.string().max(200)
});

/**
 * PublicationFactsOut
 */
export const zPublicationFactsOut = z.object({
    author: z.string(),
    region: z.string(),
    wines: z.string(),
    experiences: z.string(),
    date: z.iso.datetime({ offset: true }).nullable(),
    likes_label: z.string(),
    views: z.int(),
    comments: z.int(),
    source_label: z.string()
});

/**
 * PublicationKind
 */
export const zPublicationKind = z.enum(PublicationKind);

/**
 * PublicationCardOut
 *
 * Карточка публикации (BR-CNT-37; блок «Публикации» региона и винодельни 12.6.7).
 */
export const zPublicationCardOut = z.object({
    id: z.uuid(),
    kind: zPublicationKind,
    kind_label: z.string(),
    author: zContentAuthorOut,
    title: z.string(),
    excerpt: z.string(),
    pics: z.array(z.string()),
    has_video: z.boolean(),
    region: zContentRefOut.nullable(),
    winery: zContentRefOut.nullable(),
    wine: zContentRefOut.nullable(),
    experience: zContentRefOut.nullable(),
    published_at: z.iso.datetime({ offset: true }),
    time_label: z.string(),
    likes_count: z.int(),
    liked: z.boolean(),
    comments_count: z.int(),
    views_count: z.int(),
    own: z.boolean(),
    target: z.string()
});

/**
 * PublicationListOut
 */
export const zPublicationListOut = z.object({
    items: z.array(zPublicationCardOut),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * PublicationScreenOut
 */
export const zPublicationScreenOut = z.object({
    id: z.uuid(),
    kind: zPublicationKind,
    kind_label: z.string(),
    author: zContentAuthorOut,
    title: z.string(),
    excerpt: z.string(),
    pics: z.array(z.string()),
    has_video: z.boolean(),
    region: zContentRefOut.nullable(),
    winery: zContentRefOut.nullable(),
    wine: zContentRefOut.nullable(),
    experience: zContentRefOut.nullable(),
    published_at: z.iso.datetime({ offset: true }),
    time_label: z.string(),
    likes_count: z.int(),
    liked: z.boolean(),
    comments_count: z.int(),
    views_count: z.int(),
    own: z.boolean(),
    target: z.string(),
    body: z.array(zBodyBlockOut),
    media: z.array(zContentMediaOut),
    wines: z.array(zContentRefOut),
    experiences: z.array(zContentRefOut),
    read_minutes: z.int(),
    comments_label: z.string(),
    can_comment: z.boolean(),
    reply_target: z.string().nullable(),
    more_from_author: zMoreFromAuthorOut.nullable(),
    nearby: z.array(zNearbyOut)
});

/**
 * PublicationSource
 */
export const zPublicationSource = z.enum(PublicationSource);

/**
 * PublicationStatus
 */
export const zPublicationStatus = z.enum(PublicationStatus);

/**
 * PublicationCreatedOut
 */
export const zPublicationCreatedOut = z.object({
    id: z.uuid(),
    status: zPublicationStatus,
    kind: zPublicationKind,
    published_at: z.iso.datetime({ offset: true }).nullable(),
    read_minutes: z.int(),
    toast: z.string()
});

/**
 * PublicationRowOut
 */
export const zPublicationRowOut = z.object({
    id: z.uuid(),
    title: z.string(),
    author_label: z.string(),
    links_label: z.string().nullable(),
    date: z.iso.datetime({ offset: true }).nullable(),
    status: zPublicationStatus,
    status_label: z.string(),
    kind: zPublicationKind,
    kind_label: z.string(),
    source: zPublicationSource,
    can: zPublicationCan
});

/**
 * CntPage[PublicationRowOut]
 */
export const zCntPagePublicationRowOut = z.object({
    items: z.array(zPublicationRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * PublicationViewsIn
 */
export const zPublicationViewsIn = z.object({
    publication_ids: z.array(z.uuid()).min(1).max(50)
});

/**
 * PurchaseBreakdownOut
 */
export const zPurchaseBreakdownOut = z.object({
    opened: z.boolean(),
    paid_lessons: z.int(),
    minutes: z.int(),
    meta: z.string(),
    lessons_separately: zMoney.nullable(),
    course_price: zMoney.nullable(),
    discount: zDiscountOut.nullable(),
    credit: zMoney.nullable(),
    to_pay: zMoney.nullable(),
    savings: zMoney.nullable(),
    savings_pct: z.int().nullable(),
    chips: z.array(z.string()),
    lessons: z.array(zBreakdownLessonOut),
    course_button: z.string().nullable(),
    lesson_offer: zLearnOfferOut.nullable(),
    opened_label: z.string().nullable()
});

/**
 * PushClientOut
 */
export const zPushClientOut = z.object({
    vapid_public_key: z.string().nullish()
});

/**
 * PushProvider
 */
export const zPushProvider = z.enum(PushProvider);

/**
 * AppPushOut
 */
export const zAppPushOut = z.object({
    providers: z.array(zPushProvider),
    web_push_ios_min_version: z.string().nullable()
});

/**
 * DeviceRegisterIn
 *
 * `PUT /devices/current` (FR-ACC-02): платформа совпадает с `X-Client-Platform`.
 */
export const zDeviceRegisterIn = z.object({
    platform: zClientPlatform,
    app_version: z.string().max(20).nullish(),
    os_version: z.string().max(40).nullish(),
    locale: zLanguage,
    push_provider: zPushProvider.nullish(),
    push_token: z.string().max(4096).nullish(),
    push_enabled: z.boolean().optional().default(false),
    timezone: z.string().max(64).nullish()
});

/**
 * PushTilesOut
 *
 * Плитки карточки push-интеграции (17.10.10).
 */
export const zPushTilesOut = z.object({
    active_devices: z.int(),
    sent_24h: z.int(),
    invalid_tokens_24h: z.int(),
    errors_24h: z.int()
});

/**
 * QuizzesAttachIn
 */
export const zQuizzesAttachIn = z.object({
    test_ids: z.array(z.uuid()).min(1).max(100)
});

/**
 * RangeOut
 */
export const zRangeOut = z.object({
    min: z.int(),
    max: z.int(),
    title: z.string(),
    label: z.string()
});

/**
 * OnboardingOut
 */
export const zOnboardingOut = z.object({
    learn: zRangeOut.nullable(),
    taste: zRangeOut.nullable(),
    get: zOnboardingGetOut
});

/**
 * RatingOut
 */
export const zRatingOut = z.object({
    avg: z.number(),
    count: z.int()
});

/**
 * ReactionKind
 */
export const zReactionKind = z.enum(ReactionKind);

/**
 * ReactionsOut
 */
export const zReactionsOut = z.object({
    saved: z.boolean(),
    liked: z.boolean(),
    likes_count: z.int()
});

/**
 * LessonScreenOut
 */
export const zLessonScreenOut = z.object({
    id: z.uuid(),
    title: z.string(),
    course: zLessonCourseRefOut.nullable(),
    number: z.string(),
    index: z.int(),
    count: z.int(),
    duration_min: z.int(),
    is_paid: z.boolean(),
    access: zLessonAccessOut,
    completed: z.boolean(),
    xp: z.int().nullable(),
    cover_url: z.string().nullable(),
    video: zLessonVideoOut,
    body: z.array(zBlockOut).nullish(),
    theses: z.array(z.string()).nullish(),
    tests: z.array(zLessonTestItemOut).nullish(),
    completion_mode: z.enum(['button', 'test']),
    nav: z.array(zLessonNavItemOut),
    next: zNextLessonOut.nullable(),
    last_text: z.string().nullable(),
    reactions: zReactionsOut,
    header: z.string(),
    overline: z.string(),
    completed_label: z.string().nullable(),
    offer: zLearnOfferOut.nullish(),
    course_offer: zLearnOfferOut.nullish(),
    paywall: zPaywallOut.nullish(),
    lesson_set: z.record(z.string(), z.unknown()).nullish(),
    cta: zCtaOut,
    secondary: zLinkOut.nullable()
});

/**
 * ReadIn
 *
 * `{ids: [...]}` — отметить записи (тап по записи, открытие пуша) или `{all: true}` —
 * «Прочитано» (все записи во всех вкладках, BR-SRV-48).
 */
export const zReadIn = z.object({
    ids: z.array(z.uuid()).max(200).nullish(),
    all: z.boolean().nullish()
});

/**
 * ReadOnlyFieldOut
 */
export const zReadOnlyFieldOut = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string()
});

/**
 * ReadOut
 */
export const zReadOut = z.object({
    unread_count: z.int()
});

/**
 * RecipientOut
 */
export const zRecipientOut = z.object({
    kind: z.enum([
        'user',
        'device',
        'member',
        'email'
    ]),
    kind_label: z.string(),
    id: z.uuid().nullish(),
    label: z.string()
});

/**
 * RecognitionClientOut
 */
export const zRecognitionClientOut = z.object({
    enabled: z.boolean()
});

/**
 * RecognitionStatus
 */
export const zRecognitionStatus = z.enum(RecognitionStatus);

/**
 * RecognitionTilesOut
 *
 * Плитки карточки распознавания FastCork (17.11.8).
 */
export const zRecognitionTilesOut = z.object({
    recognitions_24h: z.int(),
    recognitions_30d: z.int(),
    not_recognized_pct: z.number().nullable(),
    matched_pct: z.number().nullable(),
    wrong_wine_pct: z.number().nullable(),
    avg_response_ms: z.int().nullable(),
    errors_24h: z.int(),
    balance_estimate: z.int().nullable()
});

/**
 * RecognitionTranslation
 */
export const zRecognitionTranslation = z.enum(RecognitionTranslation);

/**
 * RecommendationOut
 */
export const zRecommendationOut = z.object({
    lesson_id: z.uuid(),
    number: z.string().nullable(),
    title: z.string(),
    errors: z.int(),
    target: z.string()
});

/**
 * LevelCheckResultOut
 */
export const zLevelCheckResultOut = z.object({
    score: z.int(),
    total: z.int(),
    band: z.enum([
        'low',
        'mid',
        'high'
    ]),
    perfect_ever: z.boolean(),
    recommendations: z.array(zRecommendationOut),
    title: z.string(),
    text: z.string(),
    primary: zLinkOut,
    secondary: zLinkOut
});

/**
 * RecommendationsOut
 */
export const zRecommendationsOut = z.object({
    computed_at: z.iso.datetime({ offset: true }),
    time_label: z.string(),
    title: z.string(),
    subtitle: z.string(),
    items: z.array(zFeedCardOut)
});

/**
 * RefClimate
 */
export const zRefClimate = z.enum(RefClimate);

/**
 * RefLabelOut
 */
export const zRefLabelOut = z.object({
    id: z.uuid(),
    name: z.string().nullable()
});

/**
 * RefOut
 */
export const zRefOut = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * FormatsOut
 */
export const zFormatsOut = z.object({
    winery: zRefOut,
    items: z.array(zFormatOut)
});

/**
 * RefreshIn
 */
export const zRefreshIn = z.object({
    refresh_token: z.string().max(300)
});

/**
 * RefundReason
 *
 * Причина возврата — единый перечень ORD, EXP и INT-PAY (7.3.10 `Refund.reason`,
 * 15.4.2 `PaymentRefund.reason_code`).
 */
export const zRefundReason = z.enum(RefundReason);

/**
 * OrdRefundIn
 */
export const zOrdRefundIn = z.object({
    reason: zRefundReason,
    comment: z.string().max(300).nullish()
});

/**
 * OrdStatusIn
 */
export const zOrdStatusIn = z.object({
    to_status: zOrderStatus,
    refund: zOrdRefundIn.nullish(),
    reason: z.string().max(300).nullish(),
    confirm: z.boolean().optional().default(false)
});

/**
 * RegionBriefOut
 */
export const zRegionBriefOut = z.object({
    id: z.uuid(),
    name: z.string(),
    country: zPathCountryRefOut,
    image_url: z.string().nullable()
});

/**
 * RegionCan
 */
export const zRegionCan = z.object({
    update: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    attach: z.boolean().optional().default(false)
});

/**
 * RegionCourseOut
 */
export const zRegionCourseOut = z.object({
    course: zCourseInfoOut,
    author: zAuthorBriefOut,
    access: zCourseAccessOut,
    progress: zProgressOut,
    offer: zLearnOfferOut.nullable(),
    lessons: z.array(zCourseLessonItemOut),
    cta: zCtaOut,
    subtitle: z.string(),
    reactions: zCourseLikeOut,
    purchase_breakdown: zPurchaseBreakdownOut
});

/**
 * RegionNodeOut
 */
export const zRegionNodeOut = z.object({
    id: z.uuid(),
    name: z.string(),
    state: z.enum([
        'collected',
        'started',
        'new'
    ]),
    stamp: z.enum(['bronze', 'gold']).nullable(),
    has_new: z.boolean(),
    lessons_count: z.int(),
    xp_left: z.int().nullable(),
    label: z.string(),
    target: z.string()
});

/**
 * CountryScreenOut
 */
export const zCountryScreenOut = z.object({
    country: zPathCountryOut,
    facts: z.array(zFactItemOut),
    facts_label: z.string(),
    facts_updated: z.boolean(),
    quiz: zPathCountryQuizOut.nullable(),
    quiz_updated: z.boolean(),
    xp_total: z.int().nullable(),
    regions: z.array(zRegionNodeOut),
    regions_label: z.string()
});

/**
 * RegionOptionOut
 */
export const zRegionOptionOut = z.object({
    id: z.uuid(),
    name: z.string(),
    country: zCountryRefOut
});

/**
 * RegionStatus
 */
export const zRegionStatus = z.enum(RegionStatus);

/**
 * RegionSubscriptionsOut
 */
export const zRegionSubscriptionsOut = z.object({
    wines: z.boolean(),
    tastings: z.boolean(),
    excursions: z.boolean()
});

/**
 * RegisterIn
 *
 * Шаг «О вас» регистрации по почте (BR-ACC-11, пример 4.8.2.1).
 */
export const zRegisterIn = z.object({
    verification_token: z.string().max(2000),
    full_name: z.string().max(100),
    birth_date: z.iso.date(),
    phone: zPhoneIn,
    country_code: z.string().regex(/^[A-Za-z]{2}$/).nullish(),
    city_name: z.string().max(80),
    city_suggestion_id: z.uuid().nullish(),
    password: z.string().max(500),
    avatar_file_id: z.uuid().nullish(),
    consents: z.array(zConsentItemIn).max(3).optional()
});

/**
 * RegistrationMethod
 */
export const zRegistrationMethod = z.enum(RegistrationMethod);

/**
 * RelatedIn
 */
export const zRelatedIn = z.object({
    wine_ids: z.array(z.uuid()).max(50).optional(),
    set_ids: z.array(z.uuid()).max(50).optional(),
    drop_ids: z.array(z.uuid()).max(50).optional(),
    tasting_ids: z.array(z.uuid()).max(50).optional()
});

/**
 * RelatedItem
 *
 * Связанная запись (4.3.18 `related`). С `id` — проверяется модулем-владельцем записи.
 */
export const zRelatedItem = z.object({
    type: z.enum([
        'order',
        'booking',
        'event',
        'trip',
        'course',
        'lesson',
        'refund',
        'other'
    ]),
    id: z.uuid().nullish(),
    label: z.string().min(1).max(200)
});

/**
 * RelatedItemOut
 */
export const zRelatedItemOut = z.object({
    object_type: z.enum([
        'wine',
        'set',
        'drop',
        'tasting'
    ]),
    object_id: z.uuid(),
    title: z.string(),
    status_label: z.string().nullable()
});

/**
 * RelatedOut
 */
export const zRelatedOut = z.object({
    wines: z.array(zRelatedItemOut),
    sets: z.array(zRelatedItemOut),
    drops: z.array(zRelatedItemOut),
    tastings: z.array(zRelatedItemOut)
});

/**
 * ReplyIn
 */
export const zReplyIn = z.object({
    text: z.string().max(600)
});

/**
 * RequiredIfOut
 */
export const zRequiredIfOut = z.object({
    field: z.string(),
    value: z.string()
});

/**
 * FieldOut
 *
 * Поле формы настроек (таблицы «Настройки в админке» глав INT).
 */
export const zFieldOut = z.object({
    name: z.string(),
    type: zFieldType,
    label: z.string(),
    required: z.boolean(),
    required_if: zRequiredIfOut.nullish(),
    secret: z.boolean(),
    readonly: z.boolean().optional().default(false),
    default: z.unknown().optional(),
    options: z.array(zFieldOptionOut).optional(),
    min: z.number().nullish(),
    max: z.number().nullish(),
    max_length: z.int().nullish(),
    pattern: z.string().nullish(),
    hint: z.string().nullish()
});

/**
 * ActionOut
 */
export const zActionOut = z.object({
    key: z.string(),
    label: z.string(),
    available: z.boolean(),
    reason: z.string().nullish(),
    fields: z.array(zFieldOut).optional()
});

/**
 * RetranslateIn
 */
export const zRetranslateIn = z.object({
    entity_type: z.string().min(1).max(40).regex(/^[a-z_]+$/),
    entity_id: z.uuid(),
    field_paths: z.array(z.string().min(1).max(120)).min(1).max(50)
});

/**
 * ReviewAuthorOut
 */
export const zReviewAuthorOut = z.object({
    display_name: z.string(),
    avatar_url: z.string().nullable()
});

/**
 * ReviewFormatOut
 */
export const zReviewFormatOut = z.object({
    excursion_id: z.uuid().nullable(),
    title: z.string()
});

/**
 * ReviewIn
 */
export const zReviewIn = z.object({
    rating: z.int().nullish(),
    excursion_id: z.uuid().nullish(),
    text: z.string().max(10000).optional().default('')
});

/**
 * ReviewNoteIn
 */
export const zReviewNoteIn = z.object({
    note: z.string().min(1).max(600)
});

/**
 * ReviewNoteOut
 */
export const zReviewNoteOut = z.object({
    note: z.string(),
    by_name: z.string().nullable(),
    label: z.string()
});

/**
 * ReviewOut
 */
export const zReviewOut = z.object({
    id: z.uuid(),
    winery_id: z.uuid(),
    rating: z.int(),
    excursion_id: z.uuid().nullable(),
    format_title: z.string(),
    text: z.string(),
    author: zReviewAuthorOut,
    created_at: z.iso.datetime({ offset: true }),
    edited_at: z.iso.datetime({ offset: true }).nullable(),
    is_mine: z.boolean()
});

/**
 * MyReviewOut
 */
export const zMyReviewOut = z.object({
    review: zReviewOut.nullable(),
    formats: z.array(zReviewFormatOut),
    default_format: zReviewFormatOut,
    can_review: z.boolean()
});

/**
 * ReviewSavedOut
 */
export const zReviewSavedOut = z.object({
    id: z.uuid(),
    winery_id: z.uuid(),
    rating: z.int(),
    excursion_id: z.uuid().nullable(),
    format_title: z.string(),
    text: z.string(),
    author: zReviewAuthorOut,
    created_at: z.iso.datetime({ offset: true }),
    edited_at: z.iso.datetime({ offset: true }).nullable(),
    is_mine: z.boolean(),
    winery_rating: zRatingOut
});

/**
 * RevisionState
 */
export const zRevisionState = z.enum(RevisionState);

/**
 * RevisionOut
 */
export const zRevisionOut = z.object({
    id: z.uuid(),
    state: zRevisionState,
    state_label: z.string(),
    changed_fields: z.array(z.string()),
    changed_labels: z.array(z.string()),
    submitted_at: z.iso.datetime({ offset: true }).nullable(),
    decided_at: z.iso.datetime({ offset: true }).nullable(),
    review_note: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * RewardItemOut
 */
export const zRewardItemOut = z.object({
    id: z.uuid(),
    title: z.string(),
    description: z.string().nullable(),
    kind: z.enum(['reward', 'goal']),
    kind_label: z.string(),
    cost: z.int(),
    cost_label: z.string(),
    icon_url: z.string().nullable(),
    affordable: z.boolean(),
    missing: z.int(),
    missing_label: z.string().nullable(),
    button_label: z.string()
});

/**
 * RewardKind
 */
export const zRewardKind = z.enum(RewardKind);

/**
 * ExchangeCardOut
 */
export const zExchangeCardOut = z.object({
    id: z.uuid(),
    number: z.string(),
    user: zGamUserRefOut,
    type_label: z.string(),
    reward: zExchangeRewardOut,
    executor_type: zExecutorType,
    executor: z.string(),
    when_label: z.string(),
    persons: z.int(),
    cost: z.int(),
    amount_label: z.string(),
    status: z.enum([
        'requested',
        'paid',
        'declined'
    ]),
    status_label: z.string(),
    requested_at: z.iso.datetime({ offset: true }),
    due_date: z.iso.date(),
    overdue: z.boolean(),
    paid_at: z.iso.datetime({ offset: true }).nullable(),
    declined_at: z.iso.datetime({ offset: true }).nullable(),
    order: zExchangeOrderOut.nullable(),
    can: zExchangeCan,
    overline: z.string(),
    title: z.string(),
    subtitle: z.string(),
    reward_description: z.string().nullable(),
    reward_kind: zRewardKind,
    reward_kind_label: z.string(),
    payment_label: z.string(),
    bonus_balance: z.int().nullable(),
    balance_label: z.string().nullable(),
    requested_label: z.string(),
    due_label: z.string(),
    given_text: z.string().nullable(),
    team_note: z.string().nullable(),
    decline_reason: zGamDeclineReason.nullable(),
    decline_reason_label: z.string().nullable(),
    decline_comment: z.string().nullable(),
    executor_winery_id: z.uuid().nullable(),
    executor_expert_id: z.uuid().nullable(),
    steps: z.array(zExchangeStepOut),
    note: z.string(),
    rules: z.array(z.string()),
    reward_panel: zExchangeRewardPanelOut,
    user_exchanges_count: z.int(),
    user_exchanges_label: z.string(),
    order_panel: z.string().nullable(),
    history: z.array(zExchangeHistoryOut)
});

/**
 * RewardOut
 */
export const zRewardOut = z.object({
    xp: z.int(),
    total_xp: z.int(),
    rank: z.string(),
    to_next: z.int().nullable()
});

/**
 * RewardStatus
 */
export const zRewardStatus = z.enum(RewardStatus);

/**
 * RewardsOut
 */
export const zRewardsOut = z.object({
    items: z.array(zRewardItemOut)
});

/**
 * RoomKind
 */
export const zRoomKind = z.enum(RoomKind);

/**
 * ApproveIn
 */
export const zApproveIn = z.object({
    room_type: zRoomKind,
    agreed_amount: zMoney.nullish(),
    agreed_date_from: z.iso.date().nullish(),
    agreed_date_to: z.iso.date().nullish()
});

/**
 * RoomApp
 */
export const zRoomApp = z.object({
    type: zRoomKind,
    label: z.string(),
    description: z.string(),
    supplement: zMoney.nullable(),
    available_with_children: z.boolean()
});

/**
 * RoomType
 */
export const zRoomType = z.enum(RoomType);

/**
 * RouteProgressOut
 */
export const zRouteProgressOut = z.object({
    done: z.int(),
    steps: z.int(),
    pct: z.int(),
    bar_pct: z.int(),
    label: z.string()
});

/**
 * RowCan
 */
export const zRowCan = z.object({
    update: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * RuleCan
 */
export const zRuleCan = z.object({
    update: z.boolean(),
    enable: z.boolean(),
    disable: z.boolean(),
    delete: z.boolean()
});

/**
 * SalesChannel
 */
export const zSalesChannel = z.enum(SalesChannel);

/**
 * SavedCountsOut
 */
export const zSavedCountsOut = z.object({
    wine: z.int(),
    set: z.int(),
    experience: z.int(),
    winery: z.int(),
    lesson: z.int(),
    total: z.int()
});

/**
 * SavedIn
 */
export const zSavedIn = z.object({
    target_type: z.enum([
        'wine',
        'set',
        'drop',
        'tasting',
        'excursion',
        'trip',
        'winery',
        'lesson'
    ]),
    target_id: z.uuid()
});

/**
 * SavedItemOut
 */
export const zSavedItemOut = z.object({
    target_type: z.string(),
    target_id: z.uuid(),
    saved_at: z.iso.datetime({ offset: true }),
    target: z.string(),
    card: z.record(z.string(), z.unknown())
});

/**
 * SavedLessonOut
 */
export const zSavedLessonOut = z.object({
    lesson_id: z.uuid(),
    title: z.string(),
    duration_min: z.int(),
    region: z.string().nullable(),
    cover_url: z.string().nullable(),
    saved_at: z.iso.datetime({ offset: true }),
    target: z.string()
});

/**
 * SavedLessonPageOut
 */
export const zSavedLessonPageOut = z.object({
    items: z.array(zSavedLessonOut),
    next_cursor: z.string().nullable()
});

/**
 * SavedOut
 */
export const zSavedOut = z.object({
    tab: z.enum([
        'wine',
        'set',
        'experience',
        'winery',
        'lesson'
    ]),
    counts: zSavedCountsOut,
    items: z.array(zSavedItemOut),
    next_cursor: z.string().nullable(),
    empty: z.record(z.string(), z.string()).nullable()
});

/**
 * SavedStateOut
 */
export const zSavedStateOut = z.object({
    saved: z.boolean(),
    toast: z.string()
});

/**
 * ScaleOut
 */
export const zScaleOut = z.object({
    done: z.int(),
    all: z.int(),
    label: z.string()
});

/**
 * ScheduleMode
 */
export const zScheduleMode = z.enum(ScheduleMode);

/**
 * ExcursionCard
 */
export const zExcursionCard = z.object({
    id: z.uuid(),
    title: z.string(),
    overline: z.string(),
    winery: zRefOut,
    region: zRefOut.nullable(),
    duration_min: z.int().nullable(),
    duration_label: z.string().nullable(),
    price: zMoney,
    price_label: z.string(),
    cover_url: z.string().nullable(),
    schedule_mode: zScheduleMode,
    next_slot_at: z.iso.datetime({ offset: true }).nullable(),
    attended: z.boolean().optional().default(false),
    status_chip: z.string().nullish()
});

/**
 * CursorPage[ExcursionCard]
 */
export const zCursorPageExcursionCard = z.object({
    items: z.array(zExcursionCard),
    next_cursor: z.string().nullable()
});

/**
 * ExcursionDetail
 */
export const zExcursionDetail = z.object({
    id: z.uuid(),
    title: z.string(),
    overline: z.string(),
    winery: zRefOut,
    region: zRefOut.nullable(),
    duration_min: z.int().nullable(),
    duration_label: z.string().nullable(),
    price: zMoney,
    price_label: z.string(),
    cover_url: z.string().nullable(),
    schedule_mode: zScheduleMode,
    next_slot_at: z.iso.datetime({ offset: true }).nullable(),
    attended: z.boolean().optional().default(false),
    status_chip: z.string().nullish(),
    published: z.boolean(),
    description: z.string().nullable(),
    seats_per_slot: z.int(),
    group_label: z.string(),
    hours_label: z.string().nullable(),
    visit_status_label: z.string(),
    details: z.array(zAppPair),
    steps: z.array(zAppStep),
    route: z.array(zAppPair),
    media: z.array(zAppMedia),
    cancel_policy_text: z.string(),
    has_slots: z.boolean(),
    other_formats: z.array(zRefOut),
    me: zMeOut.nullable()
});

/**
 * ScheduledPriceOut
 */
export const zScheduledPriceOut = z.object({
    id: z.uuid(),
    new_price: zMoney,
    new_price_label: z.string(),
    effective_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * ScopeOut
 */
export const zScopeOut = z.object({
    winery_ids: z.array(z.uuid())
});

/**
 * SearchCountsOut
 */
export const zSearchCountsOut = z.object({
    all: z.int(),
    regions: z.int(),
    wines: z.int(),
    sets: z.int(),
    wineries: z.int(),
    experiences: z.int(),
    lessons: z.int(),
    people: z.int()
});

/**
 * SecretStateOut
 *
 * BR-INT-07: значение секрета не отдаётся никогда — только признак и 4 последних символа.
 */
export const zSecretStateOut = z.object({
    is_set: z.boolean(),
    last4: z.string().nullish()
});

/**
 * SectionCompletedOut
 */
export const zSectionCompletedOut = z.object({
    section_id: z.uuid(),
    number: z.int(),
    title: z.string(),
    achievement_title: z.string().nullable()
});

/**
 * SectionCountOut
 */
export const zSectionCountOut = z.object({
    section: zNotificationSection,
    label: z.string(),
    count: z.int()
});

/**
 * SectionItemOut
 */
export const zSectionItemOut = z.object({
    type: z.string(),
    id: z.uuid(),
    title: z.string(),
    overline: z.string().nullish(),
    subtitle: z.string().nullish(),
    meta: z.string().nullish(),
    state: z.string().nullish(),
    image_url: z.string().nullish(),
    price: zMoney.nullish(),
    price_label: z.string().nullish(),
    target: z.string()
});

/**
 * PageSectionOut
 */
export const zPageSectionOut = z.object({
    key: z.string(),
    number: z.string(),
    title: z.string(),
    count: z.int(),
    items: z.array(zSectionItemOut)
});

/**
 * SectionLinkOut
 */
export const zSectionLinkOut = z.object({
    section: z.string(),
    filters: z.record(z.string(), z.string())
});

/**
 * ActivityItemOut
 */
export const zActivityItemOut = z.object({
    label: z.string(),
    value: z.string(),
    link: zSectionLinkOut.nullable()
});

/**
 * ActivityGroupOut
 */
export const zActivityGroupOut = z.object({
    key: z.string(),
    title: z.string(),
    items: z.array(zActivityItemOut)
});

/**
 * OwnedGroupOut
 */
export const zOwnedGroupOut = z.object({
    section: z.string(),
    title: z.string(),
    count: z.int(),
    items: z.array(zOwnedItemOut),
    link: zSectionLinkOut.nullable()
});

/**
 * OwnedSummaryOut
 */
export const zOwnedSummaryOut = z.object({
    groups: z.array(zOwnedGroupOut),
    total: z.int()
});

/**
 * SeenSection
 */
export const zSeenSection = z.enum(SeenSection);

/**
 * SensoryParam
 */
export const zSensoryParam = z.enum(SensoryParam);

/**
 * ServeTemp
 */
export const zServeTemp = z.enum(ServeTemp);

/**
 * SetApp
 */
export const zSetApp = z.object({
    id: z.uuid(),
    title: z.string(),
    price: zMoney
});

/**
 * SetAuthorType
 */
export const zSetAuthorType = z.enum(SetAuthorType);

/**
 * SetCan
 */
export const zSetCan = z.object({
    update: z.boolean().optional().default(false),
    edit_items: z.boolean().optional().default(false),
    edit_media: z.boolean().optional().default(false),
    submit: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    return: z.boolean().optional().default(false),
    reject: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    awaiting_decision: z.boolean().optional().default(false)
});

/**
 * SetFormat
 */
export const zSetFormat = z.enum(SetFormat);

/**
 * SetLinksOut
 */
export const zSetLinksOut = z.object({
    winery: zRefOut.nullable(),
    expert: zRefOut.nullable(),
    course: zRefOut.nullable(),
    lesson: zRefOut.nullable()
});

/**
 * SetPortion
 */
export const zSetPortion = z.enum(SetPortion);

/**
 * SetItemIn
 */
export const zSetItemIn = z.object({
    wine_id: z.uuid(),
    position: z.int().gte(1).lte(99).nullish(),
    portion: zSetPortion.optional().default('bottle'),
    portion_price: zMoneyIn.nullish()
});

/**
 * SetItemsIn
 */
export const zSetItemsIn = z.object({
    items: z.array(zSetItemIn).max(30)
});

/**
 * SetRefOut
 */
export const zSetRefOut = z.object({
    id: z.uuid(),
    sku: z.string(),
    name: z.string(),
    format: z.string(),
    published: z.boolean()
});

/**
 * SetRowOut
 */
export const zSetRowOut = z.object({
    id: z.uuid(),
    sku: z.string(),
    name: z.string(),
    author_label: z.string(),
    items_count: z.int(),
    items_label: z.string(),
    regions_label: z.string(),
    price: zMoney,
    price_label: z.string(),
    available: z.int().nullable(),
    available_label: z.string(),
    format: zSetFormat,
    format_label: z.string(),
    status: zCatalogStatus,
    display_status: zCatalogStatus,
    display_status_label: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * CatalogPageOut[SetRowOut]
 */
export const zCatalogPageOutSetRowOut = z.object({
    items: z.array(zSetRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * SetSalesOut
 */
export const zSetSalesOut = z.object({
    orders_count: z.int(),
    revenue: zMoney,
    revenue_label: z.string(),
    sold_count: z.int(),
    price: zMoney,
    available: z.int().nullable()
});

/**
 * SetWineOut
 */
export const zSetWineOut = z.object({
    id: z.uuid(),
    title: z.string(),
    name: z.string(),
    vintage_label: z.string(),
    winery: zRefOut.nullable(),
    region: zRefOut.nullable(),
    status: zCatalogStatus,
    status_label: z.string(),
    price: zMoney,
    volume_ml: z.int()
});

/**
 * SetItemOut
 */
export const zSetItemOut = z.object({
    id: z.uuid(),
    position: z.int(),
    position_label: z.string(),
    wine: zSetWineOut,
    portion: zSetPortion,
    portion_label: z.string(),
    portion_price: zMoney,
    portion_price_label: z.string(),
    volume_ml: z.int(),
    hidden_from_user: z.boolean()
});

/**
 * ShareTarget
 */
export const zShareTarget = z.enum(ShareTarget);

/**
 * AppPassportOut
 */
export const zAppPassportOut = z.object({
    share_targets: z.array(zShareTarget)
});

/**
 * ShareTargetOut
 */
export const zShareTargetOut = z.object({
    key: z.string(),
    title: z.string(),
    subtitle: z.string()
});

/**
 * ShippingCarrier
 */
export const zShippingCarrier = z.enum(ShippingCarrier);

/**
 * ShippingCostToBuyer
 */
export const zShippingCostToBuyer = z.enum(ShippingCostToBuyer);

/**
 * AdminShippingOut
 */
export const zAdminShippingOut = z.object({
    carrier: zShippingCarrier,
    destination_scope: zDestinationScope,
    pickup_enabled: z.boolean(),
    age_check_on_delivery: z.boolean(),
    cost_to_buyer: zShippingCostToBuyer
});

/**
 * SkipReason
 */
export const zSkipReason = z.enum(SkipReason);

/**
 * DispatchCardOut
 */
export const zDispatchCardOut = z.object({
    id: z.uuid(),
    created_at: z.iso.datetime({ offset: true }),
    scheduled_for: z.iso.datetime({ offset: true }),
    processed_at: z.iso.datetime({ offset: true }).nullable(),
    template_code: z.string(),
    template_name: z.string(),
    section: zNotificationSection.nullable(),
    recipient: zRecipientOut,
    status: zDispatchStatus,
    status_label: z.string(),
    skip_reason: zSkipReason.nullable(),
    skip_reason_label: z.string().nullable(),
    channels: z.array(z.string()),
    rendered_title: z.string(),
    rendered_body: z.string(),
    locale: z.string(),
    params: z.record(z.string(), z.unknown()),
    variant: z.string().nullable(),
    deeplink: z.string().nullable(),
    subject_type: z.string().nullable(),
    subject_id: z.string().nullable(),
    condition: z.string().nullable(),
    source_event_id: z.uuid().nullable(),
    feed_id: z.uuid().nullable(),
    feed_read_at: z.iso.datetime({ offset: true }).nullable(),
    deliveries: z.array(zDeliveryOut)
});

/**
 * DispatchRowOut
 */
export const zDispatchRowOut = z.object({
    id: z.uuid(),
    created_at: z.iso.datetime({ offset: true }),
    scheduled_for: z.iso.datetime({ offset: true }),
    processed_at: z.iso.datetime({ offset: true }).nullable(),
    template_code: z.string(),
    template_name: z.string(),
    section: zNotificationSection.nullable(),
    recipient: zRecipientOut,
    status: zDispatchStatus,
    status_label: z.string(),
    skip_reason: zSkipReason.nullable(),
    skip_reason_label: z.string().nullable(),
    channels: z.array(z.string()),
    rendered_title: z.string(),
    rendered_body: z.string()
});

/**
 * DispatchesPageOut
 */
export const zDispatchesPageOut = z.object({
    items: z.array(zDispatchRowOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * SlaOut
 */
export const zSlaOut = z.object({
    started_at: z.iso.datetime({ offset: true }),
    due_at: z.iso.datetime({ offset: true }).nullable(),
    paused_since: z.iso.datetime({ offset: true }).nullable(),
    first_response_at: z.iso.datetime({ offset: true }).nullable(),
    overdue: z.boolean()
});

/**
 * SlaUnit
 */
export const zSlaUnit = z.enum(SlaUnit);

/**
 * SlotDayOut
 */
export const zSlotDayOut = z.object({
    date: z.iso.date(),
    slots: z.array(zExcursionSlotOut)
});

/**
 * SlotIn
 */
export const zSlotIn = z.object({
    weekday: z.int().gte(1).lte(7),
    start: z.iso.time(),
    end: z.iso.time(),
    seats: z.int().gte(1),
    excursion_id: z.uuid().nullish()
});

/**
 * SlotOut
 */
export const zSlotOut = z.object({
    weekday: z.int(),
    start: z.iso.time(),
    end: z.iso.time(),
    seats: z.int(),
    excursion_id: z.uuid().nullable()
});

/**
 * SlotsOut
 */
export const zSlotsOut = z.object({
    timezone: z.string(),
    days: z.array(zSlotDayOut)
});

/**
 * SocialCompleteIn
 *
 * Дозаполнение после соцвхода (4.6.4): поля шага «О вас» без пароля.
 */
export const zSocialCompleteIn = z.object({
    full_name: z.string().max(100),
    birth_date: z.iso.date(),
    phone: zPhoneIn,
    country_code: z.string().regex(/^[A-Za-z]{2}$/).nullish(),
    city_name: z.string().max(80),
    city_suggestion_id: z.uuid().nullish(),
    avatar_file_id: z.uuid().nullish(),
    email_verification_token: z.string().max(2000).nullish(),
    consents: z.array(zConsentItemIn).max(3).optional()
});

/**
 * SocialLoginIn
 *
 * Результат нативного SDK провайдера (4.8.2): ID-токен (Google, Apple) или код авторизации
 * (VK, Яндекс, Google/Apple web); у Яндекса без `code_verifier` — токен LoginSDK.
 */
export const zSocialLoginIn = z.object({
    credential: z.string().min(1).max(8192),
    code_verifier: z.string().max(128).nullish(),
    redirect_uri: z.string().max(500).nullish(),
    nonce: z.string().max(256).nullish(),
    device_id: z.string().max(255).nullish(),
    apple_user: zAppleUserIn.nullish()
});

/**
 * SocialOut
 */
export const zSocialOut = z.object({
    kind: z.enum([
        'telegram',
        'vk',
        'video',
        'site'
    ]),
    label: z.string(),
    url: z.string()
});

/**
 * Soil
 */
export const zSoil = z.enum(Soil);

/**
 * SoonChipOut
 */
export const zSoonChipOut = z.object({
    code: z.string(),
    name: z.string()
});

/**
 * ContinentOut
 */
export const zContinentOut = z.object({
    code: z.string(),
    name: z.string(),
    count: z.int(),
    countries: z.array(zCountryCardOut),
    soon: z.array(zSoonChipOut),
    empty_title: z.string().nullable(),
    empty_text: z.string().nullable()
});

/**
 * PathOut
 */
export const zPathOut = z.object({
    intro: zPathIntroOut.nullable(),
    level_check: zPathLevelCheckOut.nullable(),
    collected: z.int(),
    regions_total: z.int(),
    collected_label: z.string(),
    default_continent: z.string(),
    continents: z.array(zContinentOut)
});

/**
 * SpecCategoryKind
 */
export const zSpecCategoryKind = z.enum(SpecCategoryKind);

/**
 * SpecStatus
 */
export const zSpecStatus = z.enum(SpecStatus);

/**
 * StampOut
 */
export const zStampOut = z.object({
    level: z.enum(['bronze', 'gold']).nullable(),
    image_url: z.string().nullable()
});

/**
 * StampRegionOut
 */
export const zStampRegionOut = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * StampRowOut
 */
export const zStampRowOut = z.object({
    id: z.uuid(),
    user: zGamUserRefOut,
    region: zStampRegionOut,
    level: z.enum(['bronze', 'gold']),
    level_label: z.string(),
    basis: z.string(),
    xp: z.int(),
    xp_label: z.string(),
    awarded_at: z.iso.datetime({ offset: true }),
    when_label: z.string()
});

/**
 * StampSlotOut
 */
export const zStampSlotOut = z.object({
    region_id: z.uuid(),
    region_name: z.string(),
    level: z.enum(['bronze', 'gold']),
    image_url: z.string().nullable(),
    awarded_at: z.iso.datetime({ offset: true }),
    label: z.string(),
    explain: z.string()
});

/**
 * PassportOut
 */
export const zPassportOut = z.object({
    guest: z.boolean(),
    owner_name: z.string().nullable(),
    owner_label: z.string(),
    issued_at: z.iso.date(),
    tier: zPassportTierOut.nullable(),
    next_tier: zNextTierOut.nullable(),
    need: z.array(zNeedOut),
    xp: z.int(),
    accuracy_pct: z.number(),
    accuracy_points: z.number(),
    tastings_count: z.int(),
    stamps_count: z.int(),
    specializations_count: z.int(),
    cells: zPassportCellsOut,
    stamp_slots: z.array(z.union([zStampSlotOut, z.null()])),
    stamps: z.array(zGamStampOut),
    stamps_text: z.string(),
    stamps_empty_text: z.string(),
    mrz: z.array(z.string()),
    level: zPassportLevelOut.nullable(),
    ladder_text: z.string(),
    ladder: z.array(zLadderTierOut),
    guest_banner: z.string().nullable(),
    share_targets: z.array(zShareTargetOut)
});

/**
 * StampsOut
 */
export const zStampsOut = z.object({
    items: z.array(zGamStampOut),
    gold: z.int(),
    bronze: z.int(),
    empty_text: z.string()
});

/**
 * StampsPageOut
 */
export const zStampsPageOut = z.object({
    items: z.array(zStampRowOut),
    total: z.int(),
    can: zListCan
});

/**
 * StarShareOut
 */
export const zStarShareOut = z.object({
    stars: z.int(),
    count: z.int(),
    pct: z.int()
});

/**
 * ReviewSummaryOut
 */
export const zReviewSummaryOut = z.object({
    avg: z.number(),
    count: z.int(),
    distribution: z.array(zStarShareOut)
});

/**
 * StatsLevelOut
 */
export const zStatsLevelOut = z.object({
    level_id: z.uuid(),
    name: z.string(),
    users: z.int()
});

/**
 * StatsStampsOut
 */
export const zStatsStampsOut = z.object({
    bronze: z.int(),
    gold: z.int()
});

/**
 * StatsTierOut
 */
export const zStatsTierOut = z.object({
    tier_id: z.uuid(),
    rank: z.int(),
    title: z.string(),
    users: z.int()
});

/**
 * GamStatsOut
 */
export const zGamStatsOut = z.object({
    users_total: z.int(),
    users_tier2_plus: z.int(),
    users_club: z.int(),
    xp_accrued: z.int(),
    stamps: zStatsStampsOut,
    by_tier: z.array(zStatsTierOut),
    by_level: z.array(zStatsLevelOut)
});

/**
 * StatusActorKind
 */
export const zStatusActorKind = z.enum(StatusActorKind);

/**
 * StayRoomOut
 */
export const zStayRoomOut = z.object({
    name: z.string().nullable(),
    type: z.string(),
    type_label: z.string(),
    beds: z.int(),
    price_per_night: zMoney
});

/**
 * StayOut
 */
export const zStayOut = z.object({
    rooms: z.array(zStayRoomOut)
});

/**
 * StockIn
 */
export const zStockIn = z.object({
    stock: z.int().gte(0)
});

/**
 * StoreCode
 */
export const zStoreCode = z.enum(StoreCode);

/**
 * BillingIntentOut
 */
export const zBillingIntentOut = z.object({
    intent_id: z.uuid(),
    order_id: z.uuid(),
    store: zStoreCode,
    store_product_id: z.string(),
    account_token: z.string()
});

/**
 * BillingProductsOut
 */
export const zBillingProductsOut = z.object({
    store: zStoreCode,
    items: z.array(zBillingProductOut)
});

/**
 * BillingProductsSeenIn
 */
export const zBillingProductsSeenIn = z.object({
    store: zStoreCode,
    items: z.array(zBillingSeenItemIn).max(100)
});

/**
 * BillingPurchaseIn
 */
export const zBillingPurchaseIn = z.object({
    store: zStoreCode,
    intent_id: z.uuid().nullish(),
    app_store: zBillingAppStorePurchaseIn.nullish(),
    google_play: zBillingGooglePlayPurchaseIn.nullish(),
    rustore: zBillingRuStorePurchaseIn.nullish()
});

/**
 * BillingSyncIn
 */
export const zBillingSyncIn = z.object({
    store: zStoreCode,
    purchases: z.array(zBillingSyncPurchaseIn).max(100)
});

/**
 * StoreProductIn
 */
export const zStoreProductIn = z.object({
    product_id: z.string().min(2).max(100).regex(/^[a-z0-9][a-z0-9._]{1,99}$/),
    active: z.boolean().optional().default(true)
});

/**
 * StoreProductOut
 */
export const zStoreProductOut = z.object({
    product_id: z.string(),
    active: z.boolean()
});

/**
 * StoreProductsOut
 */
export const zStoreProductsOut = z.object({
    available: z.boolean(),
    stores: z.array(z.string()),
    items: z.record(z.string(), zStoreProductOut),
    hint: z.string().nullable()
});

/**
 * StoreTilesOut
 *
 * Плитки карточки стора (15.12.2).
 */
export const zStoreTilesOut = z.object({
    products_active: z.int(),
    unacknowledged: z.int(),
    last_notification_at: z.iso.datetime({ offset: true }).nullable(),
    notifications_verified_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * IntegrationRow
 */
export const zIntegrationRow = z.object({
    key: z.string(),
    port: zIntegrationPort,
    group: z.string(),
    group_label: z.string(),
    provider: z.string(),
    title: z.string(),
    mode: zIntegrationMode,
    mode_label: z.string(),
    enabled: z.boolean(),
    is_active: z.boolean().nullable(),
    status: zIntegrationStatus,
    status_label: z.string(),
    available: z.boolean(),
    last_check_at: z.iso.datetime({ offset: true }).nullable(),
    last_check_result: zCheckResultOut.nullable(),
    last_success_at: z.iso.datetime({ offset: true }).nullable(),
    errors_24h: z.int(),
    open_alerts: z.int(),
    payments: zPaymentTilesOut.nullish(),
    store: zStoreTilesOut.nullish(),
    can: zIntegrationCan
});

/**
 * IntegrationsPage
 */
export const zIntegrationsPage = z.object({
    items: z.array(zIntegrationRow),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * StoryBubbleOut
 */
export const zStoryBubbleOut = z.object({
    id: z.uuid(),
    label: z.string(),
    image_url: z.string().nullable()
});

/**
 * AuthorPageOut
 */
export const zAuthorPageOut = z.object({
    member_id: z.uuid(),
    kind: z.enum(['expert', 'maker']),
    header: z.string(),
    name: z.string(),
    role_label: z.string(),
    subtitle: z.string().nullable(),
    photos: z.array(z.string()),
    bio: z.string().nullable(),
    socials: z.array(zSocialOut),
    meta: z.string().nullable(),
    subscribers_count: z.int(),
    subscribers_label: z.string(),
    following: z.boolean(),
    can_follow: z.boolean(),
    stories: z.array(zStoryBubbleOut),
    sections: z.array(zPageSectionOut),
    wineries: z.array(zSectionItemOut),
    publications: z.array(zPublicationCardOut),
    publications_total: z.int(),
    share_target: z.string()
});

/**
 * StoryCan
 */
export const zStoryCan = z.object({
    hide: z.boolean().optional().default(false),
    unhide: z.boolean().optional().default(false)
});

/**
 * StoryItemKind
 */
export const zStoryItemKind = z.enum(StoryItemKind);

/**
 * StoryLinkTarget
 */
export const zStoryLinkTarget = z.enum(StoryLinkTarget);

/**
 * LinkOptionOut
 */
export const zLinkOptionOut = z.object({
    target_type: zStoryLinkTarget,
    target_id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullable(),
    own: z.boolean()
});

/**
 * StoryItemIn
 */
export const zStoryItemIn = z.object({
    kind: zStoryItemKind,
    text: z.string().max(200).nullish(),
    color: z.string().nullish(),
    emoji: z.string().max(8).nullish(),
    link_target_type: zStoryLinkTarget.nullish(),
    link_target_id: z.uuid().nullish(),
    link_url: z.string().max(500).nullish(),
    x: z.number().optional().default(0.5),
    y: z.number().optional().default(0.45)
});

/**
 * StoryLinkOut
 */
export const zStoryLinkOut = z.object({
    target_type: zStoryLinkTarget,
    target_id: z.uuid().nullable(),
    url: z.string().nullable(),
    label: z.string(),
    target: z.string().nullable()
});

/**
 * StoryItemOut
 */
export const zStoryItemOut = z.object({
    id: z.uuid(),
    kind: zStoryItemKind,
    text: z.string().nullable(),
    color: z.string().nullable(),
    emoji: z.string().nullable(),
    link: zStoryLinkOut.nullable(),
    x: z.number(),
    y: z.number(),
    z: z.int()
});

/**
 * StoryOut
 */
export const zStoryOut = z.object({
    id: z.uuid(),
    title: z.string(),
    image_url: z.string().nullable(),
    items: z.array(zStoryItemOut),
    published_at: z.iso.datetime({ offset: true }),
    expires_at: z.iso.datetime({ offset: true }),
    frame_seconds: z.int(),
    seen: z.boolean(),
    left_label: z.string()
});

/**
 * AuthorStoriesOut
 */
export const zAuthorStoriesOut = z.object({
    author: zContentAuthorOut,
    profile_target: z.string().nullable(),
    stories: z.array(zStoryOut),
    empty_text: z.string().nullable()
});

/**
 * StoryPanelsOut
 */
export const zStoryPanelsOut = z.object({
    views: z.int(),
    views_note: z.string(),
    link_taps: z.int(),
    link_taps_note: z.string(),
    author_member_id: z.uuid(),
    author_name: z.string(),
    link: zContentRefOut.nullable(),
    link_note: z.string().nullable()
});

/**
 * StoryRingOut
 */
export const zStoryRingOut = z.object({
    author_member_id: z.uuid(),
    name: z.string(),
    photo_url: z.string().nullable(),
    initial: z.string(),
    role_label: z.string(),
    all_seen: z.boolean(),
    latest_published_at: z.iso.datetime({ offset: true }),
    target: z.string()
});

/**
 * StoryStatus
 */
export const zStoryStatus = z.enum(StoryStatus);

/**
 * StoryCreatedOut
 */
export const zStoryCreatedOut = z.object({
    id: z.uuid(),
    status: zStoryStatus,
    expires_at: z.iso.datetime({ offset: true }),
    title: z.string(),
    toast: z.string()
});

/**
 * StoryTtl
 */
export const zStoryTtl = z.enum(StoryTtl);

/**
 * StoryAdminOut
 */
export const zStoryAdminOut = z.object({
    id: z.uuid(),
    overline: z.string(),
    title: z.string(),
    status: zStoryStatus,
    status_label: z.string(),
    subtitle: z.string(),
    image_url: z.string().nullable(),
    items: z.array(zStoryItemOut),
    frames: z.int(),
    frame_seconds: z.int(),
    duration_label: z.string(),
    total_label: z.string(),
    note: z.string(),
    author_label: z.string(),
    link_label: z.string().nullable(),
    ttl: zStoryTtl,
    ttl_label: z.string(),
    published_at: z.iso.datetime({ offset: true }),
    expires_at: z.iso.datetime({ offset: true }),
    expires_label: z.string(),
    views_count: z.int(),
    link_taps_count: z.int(),
    deleted_by_author: z.boolean(),
    panels: zStoryPanelsOut,
    history: z.array(zContentHistoryOut),
    can: zStoryCan,
    toast: z.string().nullish()
});

/**
 * StoryRowOut
 */
export const zStoryRowOut = z.object({
    id: z.uuid(),
    title: z.string(),
    duration_label: z.string(),
    author_label: z.string(),
    link_label: z.string().nullable(),
    ttl: zStoryTtl,
    ttl_label: z.string(),
    published_at: z.iso.datetime({ offset: true }),
    views_count: z.int(),
    status: zStoryStatus,
    status_label: z.string(),
    deleted_by_author: z.boolean(),
    can: zStoryCan
});

/**
 * CntPage[StoryRowOut]
 */
export const zCntPageStoryRowOut = z.object({
    items: z.array(zStoryRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * StoryViewsIn
 */
export const zStoryViewsIn = z.object({
    story_ids: z.array(z.uuid()).min(1).max(50)
});

/**
 * StudioCommentOut
 */
export const zStudioCommentOut = z.object({
    id: z.uuid(),
    author_name: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    time_label: z.string(),
    text: z.string(),
    reply: zCommentReplyOut.nullable()
});

/**
 * StudioCommentsPublicationOut
 */
export const zStudioCommentsPublicationOut = z.object({
    id: z.uuid(),
    kind: zPublicationKind,
    kind_label: z.string(),
    title: z.string(),
    meta: z.string()
});

/**
 * StudioCommentsOut
 */
export const zStudioCommentsOut = z.object({
    publication: zStudioCommentsPublicationOut,
    items: z.array(zStudioCommentOut),
    next_cursor: z.string().nullable(),
    empty_text: z.string().nullable()
});

/**
 * StudioOut
 */
export const zStudioOut = z.object({
    title: z.string(),
    name: z.string(),
    role_label: z.string(),
    subtitle: z.string(),
    photo_url: z.string().nullable(),
    initial: z.string(),
    subscribers: z.int(),
    publications: z.int(),
    views: z.int(),
    likes: z.int(),
    subscribers_label: z.string(),
    publications_label: z.string(),
    views_label: z.string(),
    likes_label: z.string()
});

/**
 * StudioPublicationIn
 */
export const zStudioPublicationIn = z.object({
    title: z.string().max(120),
    body: z.record(z.string(), z.unknown()),
    media_ids: z.array(z.uuid()).max(10).optional(),
    region_id: z.uuid().nullish(),
    wine_ids: z.array(z.uuid()).max(20).optional(),
    experiences: z.array(zExperienceTagIn).max(20).optional(),
    winery_id: z.uuid().nullish()
});

/**
 * StudioPublicationOut
 */
export const zStudioPublicationOut = z.object({
    card: zPublicationCardOut,
    status: zPublicationStatus,
    draft_note: z.string().nullable(),
    tags: z.array(zContentRefOut),
    views_label: z.string(),
    likes_label: z.string(),
    comments_label: z.string(),
    comments_target: z.string()
});

/**
 * StudioPublicationsOut
 */
export const zStudioPublicationsOut = z.object({
    items: z.array(zStudioPublicationOut),
    next_cursor: z.string().nullable(),
    empty_text: z.string().nullable()
});

/**
 * StudioStoryIn
 */
export const zStudioStoryIn = z.object({
    image_media_id: z.uuid().nullish(),
    ttl: zStoryTtl.optional().default('h24'),
    items: z.array(zStoryItemIn).max(30).optional(),
    winery_id: z.uuid().nullish()
});

/**
 * StudioStoryOut
 */
export const zStudioStoryOut = z.object({
    id: z.uuid(),
    title: z.string(),
    image_url: z.string().nullable(),
    items: z.array(zStoryItemOut),
    published_at: z.iso.datetime({ offset: true }),
    expires_at: z.iso.datetime({ offset: true }),
    frame_seconds: z.int(),
    seen: z.boolean(),
    left_label: z.string(),
    status: zStoryStatus,
    views_count: z.int(),
    views_label: z.string()
});

/**
 * StudyCardOut
 */
export const zStudyCardOut = z.object({
    course_id: z.uuid(),
    title: z.string(),
    xp: z.int().nullable(),
    meta: z.string(),
    done: z.int(),
    lessons_total: z.int(),
    opened: z.boolean(),
    target: z.string()
});

/**
 * SubscriptionLogRowOut
 */
export const zSubscriptionLogRowOut = z.object({
    id: z.uuid(),
    user_id: z.uuid().nullable(),
    user_name: z.string(),
    author_member_id: z.uuid(),
    author_name: z.string(),
    author_role: z.string(),
    author_role_label: z.string(),
    notifications: z.boolean(),
    notifications_label: z.string(),
    at: z.iso.datetime({ offset: true }),
    status: z.string(),
    status_label: z.string()
});

/**
 * CntPage[SubscriptionLogRowOut]
 */
export const zCntPageSubscriptionLogRowOut = z.object({
    items: z.array(zSubscriptionLogRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * SummaryBonusesOut
 */
export const zSummaryBonusesOut = z.object({
    earned: z.int(),
    spent: z.int(),
    balance: z.int(),
    label: z.string()
});

/**
 * SummaryDiscountsOut
 */
export const zSummaryDiscountsOut = z.object({
    learning_pct: z.number(),
    wine_pct: z.number(),
    label: z.string()
});

/**
 * SummaryFirstRegionOut
 */
export const zSummaryFirstRegionOut = z.object({
    status: z.string(),
    pct: z.number().nullable(),
    used_at: z.iso.datetime({ offset: true }).nullable(),
    label: z.string()
});

/**
 * SummaryLevelOut
 */
export const zSummaryLevelOut = z.object({
    id: z.uuid(),
    name: z.string().nullable(),
    min_xp: z.int()
});

/**
 * SummaryLinksOut
 */
export const zSummaryLinksOut = z.object({
    xplog: zGamSectionLinkOut,
    stamplog: zGamSectionLinkOut
});

/**
 * SummaryNeedOut
 */
export const zSummaryNeedOut = z.object({
    k: z.string(),
    v: z.number(),
    all: z.number(),
    done: z.boolean(),
    label: z.string()
});

/**
 * SummaryRowOut
 */
export const zSummaryRowOut = z.object({
    label: z.string(),
    value: z.string()
});

/**
 * ApplicationOut
 */
export const zApplicationOut = z.object({
    id: z.uuid(),
    status: zExpertApplicationStatus,
    status_label: z.string(),
    overline: z.string(),
    title: z.string(),
    text: z.string(),
    submitted_at: z.iso.datetime({ offset: true }),
    submitted_label: z.string(),
    decided_at: z.iso.datetime({ offset: true }).nullable(),
    comment: z.string().nullable(),
    steps: z.array(zContentStepOut),
    summary: z.array(zSummaryRowOut),
    actions: zApplicationActionsOut,
    form: z.record(z.string(), z.unknown()),
    toast: z.string().nullish()
});

/**
 * ApplicationStateOut
 */
export const zApplicationStateOut = z.object({
    application: zApplicationOut.nullable(),
    gate: zContentGateOut
});

/**
 * SummaryStampsOut
 */
export const zSummaryStampsOut = z.object({
    bronze: z.int(),
    gold: z.int()
});

/**
 * SummaryTierOut
 */
export const zSummaryTierOut = z.object({
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    title: z.string(),
    label: z.string().nullish()
});

/**
 * SupportChannel
 */
export const zSupportChannel = z.enum(SupportChannel);

/**
 * ChannelOut
 */
export const zChannelOut = z.object({
    code: zSupportChannel,
    label: z.string()
});

/**
 * SupportHoursOut
 */
export const zSupportHoursOut = z.object({
    from: z.string(),
    to: z.string(),
    timezone: z.string()
});

/**
 * AppSupportOut
 */
export const zAppSupportOut = z.object({
    email: z.string(),
    hours: zSupportHoursOut,
    hours_text: z.string()
});

/**
 * SupportPriorityCode
 */
export const zSupportPriorityCode = z.enum(SupportPriorityCode);

/**
 * SupportTopicCode
 */
export const zSupportTopicCode = z.enum(SupportTopicCode);

/**
 * SupportTopicAppOut
 */
export const zSupportTopicAppOut = z.object({
    code: zSupportTopicCode,
    name: z.string(),
    needs_ref: z.boolean()
});

/**
 * SupportTopicsOut
 */
export const zSupportTopicsOut = z.object({
    items: z.array(zSupportTopicAppOut)
});

/**
 * SuppressionCan
 */
export const zSuppressionCan = z.object({
    delete: z.boolean().optional().default(false)
});

/**
 * SuppressionReason
 */
export const zSuppressionReason = z.enum(SuppressionReason);

/**
 * SuppressionScope
 */
export const zSuppressionScope = z.enum(SuppressionScope);

/**
 * SuppressionCreateIn
 */
export const zSuppressionCreateIn = z.object({
    email: z.string().max(320),
    scope: zSuppressionScope,
    note: z.string().max(300).nullish()
});

/**
 * SuppressionOut
 */
export const zSuppressionOut = z.object({
    id: z.uuid(),
    email: z.string(),
    scope: zSuppressionScope,
    scope_label: z.string(),
    reason: zSuppressionReason,
    reason_label: z.string(),
    source_message_id: z.uuid().nullable(),
    note: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    expires_at: z.iso.datetime({ offset: true }).nullable(),
    can: zSuppressionCan
});

/**
 * Page[SuppressionOut]
 */
export const zPageSuppressionOut = z.object({
    items: z.array(zSuppressionOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * TagOptionOut
 */
export const zTagOptionOut = z.object({
    id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullable(),
    type: z.string()
});

/**
 * TargetOut
 */
export const zTargetOut = z.object({
    type: zBookingTarget,
    id: z.uuid(),
    title: z.string(),
    section: z.string()
});

/**
 * AttendanceRow
 */
export const zAttendanceRow = z.object({
    id: z.uuid(),
    booking_number: z.string(),
    target: zTargetOut,
    host: zRefOut.nullable(),
    winery: zRefOut.nullable(),
    region: zRefOut.nullable(),
    kind_label: z.string().nullable(),
    format_label: z.string().nullable(),
    purpose_label: z.string().nullable(),
    user: zRefOut,
    people: z.int(),
    attended_at: z.iso.datetime({ offset: true })
});

/**
 * BookingAdmin
 */
export const zBookingAdmin = z.object({
    id: z.uuid(),
    number: z.string(),
    overline: z.string(),
    title: z.string(),
    subtitle: z.string(),
    status: zBookingStatus,
    status_label: z.string(),
    booking_type: zBookingType,
    type_label: z.string(),
    target: zTargetOut,
    user: zRefOut,
    organizer_label: z.string(),
    when_label: z.string(),
    slot_starts_at: z.iso.datetime({ offset: true }).nullable(),
    slot_ends_at: z.iso.datetime({ offset: true }).nullable(),
    departure: zDepartureOut.nullable(),
    flexible_dates: z.boolean(),
    agreed_date_from: z.iso.date().nullable(),
    agreed_date_to: z.iso.date().nullable(),
    adults: z.int(),
    sober_guests: z.int(),
    children: z.array(zChildAgeGroup),
    room_type: zRoomKind.nullable(),
    rooms_count: z.int().nullable(),
    note: z.string().nullable(),
    holder_name: z.string(),
    place_label: z.string(),
    guests_label: z.string(),
    amount: zMoney,
    calc_amount: zMoney,
    agreed_amount: zMoney.nullable(),
    amount_label: z.string(),
    payment_label: z.string(),
    entry_label: z.string(),
    cancel_policy_text: z.string().nullable(),
    queue_label: z.string().nullable(),
    reason_label: z.string().nullable(),
    close_comment: z.string().nullable(),
    hold_until: z.iso.datetime({ offset: true }).nullable(),
    paid_at: z.iso.datetime({ offset: true }).nullable(),
    refund_amount: zMoney.nullable(),
    calc_title: z.string(),
    calc_lines: z.array(zLineOut),
    calc_total: zLineOut,
    calc_note: z.string(),
    event_lifecycle_label: z.string().nullable(),
    event_meta: z.string(),
    order: zOrderRefOut.nullable(),
    kit_order: zOrderRefOut.nullable(),
    links: z.record(z.string(), zExpLinkOut),
    attendance_count: z.int(),
    history: z.array(zHistoryOut),
    attend_label: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    can: zBookingAdminCan
});

/**
 * BookingRow
 */
export const zBookingRow = z.object({
    id: z.uuid(),
    number: z.string(),
    user: zRefOut,
    booking_type: zBookingType,
    type_label: z.string(),
    target: zTargetOut,
    organizer_label: z.string(),
    when_label: z.string(),
    starts_at: z.iso.datetime({ offset: true }).nullable(),
    people: z.int(),
    amount: zMoney.nullable(),
    amount_label: z.string(),
    status: zBookingStatus,
    status_label: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    can: zBookingAdminCan
});

/**
 * Page[AttendanceRow]
 */
export const zPageAttendanceRow = z.object({
    items: z.array(zAttendanceRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * Page[BookingRow]
 */
export const zPageBookingRow = z.object({
    items: z.array(zBookingRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TaskOut
 */
export const zTaskOut = z.object({
    kind: z.string(),
    group: z.enum([
        'study',
        'taste',
        'gold',
        'extra'
    ]),
    ref_type: z.string(),
    ref_id: z.uuid().nullable(),
    title: z.string(),
    subtitle: z.string().nullable(),
    xp: z.int().nullable(),
    done: z.boolean(),
    is_new: z.boolean(),
    when: z.string().nullable(),
    target: z.string().nullable()
});

/**
 * ExtraGroupOut
 */
export const zExtraGroupOut = z.object({
    key: z.enum([
        'learning',
        'tastings',
        'visits',
        'wines'
    ]),
    title: z.string(),
    note: z.string().nullable(),
    tasks: z.array(zTaskOut)
});

/**
 * ExtraOut
 */
export const zExtraOut = z.object({
    title: z.string(),
    text: z.string(),
    xp_left: z.int().nullable(),
    groups: z.array(zExtraGroupOut)
});

/**
 * GoldGroupOut
 */
export const zGoldGroupOut = z.object({
    key: z.enum(['tours', 'trips']),
    title: z.string(),
    done: z.int(),
    all: z.int(),
    tasks: z.array(zTaskOut)
});

/**
 * GoldOut
 */
export const zGoldOut = z.object({
    title: z.string(),
    text: z.string(),
    xp: z.int().nullable(),
    groups: z.array(zGoldGroupOut),
    empty_title: z.string().nullable(),
    empty_text: z.string().nullable()
});

/**
 * TasteChar
 */
export const zTasteChar = z.enum(TasteChar);

/**
 * TasteIn
 */
export const zTasteIn = z.object({
    sweetness: z.int(),
    acidity: z.int(),
    tannins: z.int(),
    body: z.int(),
    fruit: z.int(),
    oak: z.int(),
    freshness: z.int(),
    aroma: z.int()
});

/**
 * TasteOut
 */
export const zTasteOut = z.object({
    sweetness: z.int(),
    acidity: z.int(),
    tannins: z.int(),
    body: z.int(),
    fruit: z.int(),
    oak: z.int(),
    freshness: z.int(),
    aroma: z.int()
});

/**
 * TastePatchIn
 */
export const zTastePatchIn = z.object({
    sweetness: z.int().nullish(),
    acidity: z.int().nullish(),
    tannins: z.int().nullish(),
    body: z.int().nullish(),
    fruit: z.int().nullish(),
    oak: z.int().nullish(),
    freshness: z.int().nullish(),
    aroma: z.int().nullish()
});

/**
 * TasteScaleOut
 */
export const zTasteScaleOut = z.object({
    axis: z.string(),
    label: z.string(),
    value: z.int(),
    value_label: z.string()
});

/**
 * TasteStepOut
 */
export const zTasteStepOut = z.object({
    done: z.boolean(),
    n: z.int()
});

/**
 * TasteValues
 */
export const zTasteValues = z.object({
    sweetness: z.int(),
    acidity: z.int(),
    tannins: z.int(),
    body: z.int(),
    fruit: z.int(),
    oak: z.int(),
    freshness: z.int(),
    aroma: z.int()
});

/**
 * TastedWineOut
 */
export const zTastedWineOut = z.object({
    title: z.string(),
    rating: z.int().nullable(),
    accuracy_label: z.string().nullable()
});

/**
 * TasteCardOut
 */
export const zTasteCardOut = z.object({
    title: z.string(),
    text: z.string(),
    meta: z.string(),
    xp: z.int().nullable(),
    done: z.boolean(),
    target: z.string(),
    sheet_title: z.string().nullable(),
    tasted: z.array(zTastedWineOut)
});

/**
 * BronzeOut
 */
export const zBronzeOut = z.object({
    title: z.string(),
    text: z.string(),
    xp: z.int().nullable(),
    study: zStudyCardOut,
    taste: zTasteCardOut,
    tasks: z.array(zTaskOut)
});

/**
 * TastingFormat
 */
export const zTastingFormat = z.enum(TastingFormat);

/**
 * TastingKind
 */
export const zTastingKind = z.enum(TastingKind);

/**
 * TastingCard
 */
export const zTastingCard = z.object({
    id: z.uuid(),
    title: z.string(),
    format: zTastingFormat,
    kind: zTastingKind,
    is_blind: z.boolean(),
    starts_at: z.iso.datetime({ offset: true }),
    timezone: z.string(),
    duration_min: z.int(),
    overline: z.string(),
    meta: z.string(),
    price: zMoney.nullable(),
    price_label: z.string(),
    seats_free: z.int(),
    seats_max: z.int(),
    seats_label: z.string(),
    is_full: z.boolean(),
    lifecycle: zEventLifecycle,
    region: zRefOut.nullable(),
    host: zHostOut.nullable(),
    cover_url: z.string().nullable(),
    attended: z.boolean().optional().default(false),
    status_chip: z.string().nullish()
});

/**
 * CursorPage[TastingCard]
 */
export const zCursorPageTastingCard = z.object({
    items: z.array(zTastingCard),
    next_cursor: z.string().nullable()
});

/**
 * SimilarOut
 */
export const zSimilarOut = z.object({
    items: z.array(zTastingCard)
});

/**
 * TastingRefOut
 */
export const zTastingRefOut = z.object({
    id: z.uuid(),
    title: z.string(),
    starts_at: z.iso.datetime({ offset: true }).nullable(),
    is_blind: z.boolean()
});

/**
 * TeamMemberSource
 */
export const zTeamMemberSource = z.enum(TeamMemberSource);

/**
 * TeamMemberStatus
 */
export const zTeamMemberStatus = z.enum(TeamMemberStatus);

/**
 * TeamRole
 */
export const zTeamRole = z.enum(TeamRole);

/**
 * AdminMemberOut
 */
export const zAdminMemberOut = z.object({
    id: z.uuid(),
    role: zTeamRole,
    role_label: z.string(),
    status: zTeamMemberStatus,
    phone: z.string().nullable(),
    admin_locale: zLanguage,
    registered_at: z.iso.datetime({ offset: true }),
    last_login_at: z.iso.datetime({ offset: true }).nullable()
});

/**
 * AdminMeOut
 */
export const zAdminMeOut = z.object({
    user: zAdminUserOut,
    member: zAdminMemberOut,
    nav: z.array(zNavItemOut),
    scope: zScopeOut
});

/**
 * MemberCreateIn
 */
export const zMemberCreateIn = z.object({
    full_name: z.string().min(2).max(100),
    role: zTeamRole.optional().default('expert'),
    email: z.string().max(254),
    phone: z.string().max(30).nullish(),
    winery_ids: z.array(z.uuid()).optional(),
    region_ids: z.array(z.uuid()).optional()
});

/**
 * MemberPatchIn
 *
 * `{full_name?, role?, email?, phone?, winery_ids?, region_ids?}`. `phone: null` очищает
 * телефон; имя, роль и почта обязательны — `null` недопустим.
 */
export const zMemberPatchIn = z.object({
    full_name: z.string().min(2).max(100).nullish(),
    role: zTeamRole.nullish(),
    email: z.string().max(254).nullish(),
    phone: z.string().max(30).nullish(),
    winery_ids: z.array(z.uuid()).nullish(),
    region_ids: z.array(z.uuid()).nullish()
});

/**
 * MemberRow
 */
export const zMemberRow = z.object({
    id: z.uuid(),
    user_id: z.uuid(),
    full_name: z.string().nullable(),
    role: zTeamRole,
    role_label: z.string(),
    email: z.string(),
    linked_objects: z.string(),
    app_tier: z.string().nullable(),
    last_login_at: z.iso.datetime({ offset: true }).nullable(),
    registered_at: z.iso.datetime({ offset: true }),
    status: zTeamMemberStatus,
    status_label: z.string(),
    source: zTeamMemberSource,
    can: zMemberCan
});

/**
 * MemberCreatedOut
 */
export const zMemberCreatedOut = z.object({
    member: zMemberRow,
    credentials: zCredentialsOut
});

/**
 * Page[MemberRow]
 */
export const zPageMemberRow = z.object({
    items: z.array(zMemberRow),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * TemplateCan
 */
export const zTemplateCan = z.object({
    update: z.boolean().optional().default(false),
    enable: z.boolean().optional().default(false),
    disable: z.boolean().optional().default(false),
    reset: z.boolean().optional().default(false)
});

/**
 * TemplateHistoryOut
 */
export const zTemplateHistoryOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    action_label: z.string(),
    summary: z.string()
});

/**
 * TemplateRowOut
 */
export const zTemplateRowOut = z.object({
    code: z.string(),
    section: zNotificationSection,
    section_label: z.string(),
    name: z.string(),
    channels: zNotificationChannels,
    channels_label: z.string(),
    trigger_event: z.string(),
    trigger_label: z.string(),
    body: z.string(),
    audience: zNotificationAudience,
    audience_label: z.string(),
    preference_category: zPreferenceCategory,
    category_label: z.string(),
    inbox_kind: zInboxKind,
    is_enabled: z.boolean(),
    customized: z.boolean(),
    managed: z.boolean(),
    sent_30d: z.int(),
    can: zTemplateCan
});

/**
 * TemplateStatsOut
 */
export const zTemplateStatsOut = z.object({
    sent_30d: z.int(),
    skipped_30d: z.int(),
    scheduled: z.int()
});

/**
 * TemplatesCountersOut
 */
export const zTemplatesCountersOut = z.object({
    total: z.int(),
    push: z.int(),
    email: z.int(),
    by_section: z.array(zSectionCountOut)
});

/**
 * TemplatesPageOut
 */
export const zTemplatesPageOut = z.object({
    items: z.array(zTemplateRowOut),
    total: z.int(),
    counters: zTemplatesCountersOut,
    footer: z.string(),
    can: zListCan.optional()
});

/**
 * TestCan
 */
export const zTestCan = z.object({
    update: z.boolean().optional().default(false),
    submit: z.boolean().optional().default(false),
    submit_edits: z.boolean().optional().default(false),
    waiting: z.boolean().optional().default(false),
    decide: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    edit_price: z.boolean().optional().default(false),
    edit_store_products: z.boolean().optional().default(false),
    edit_questions: z.boolean().optional().default(false)
});

/**
 * TestKind
 */
export const zTestKind = z.enum(TestKind);

/**
 * AttemptStartOut
 */
export const zAttemptStartOut = z.object({
    attempt_id: z.uuid(),
    test_id: z.uuid(),
    kind: zTestKind,
    title: z.string(),
    header: z.string(),
    questions: z.array(zAttemptQuestionOut),
    total: z.int(),
    xp: z.int().nullable(),
    xp_received: z.boolean()
});

/**
 * LibraryItemOut
 */
export const zLibraryItemOut = z.object({
    id: z.uuid(),
    type: z.enum(['lesson', 'test']),
    title: z.string(),
    status: zContentStatus,
    status_label: z.string(),
    author: z.string(),
    duration_min: z.int().nullish(),
    is_paid: z.boolean().nullish(),
    price: zMoney.nullish(),
    kind: zTestKind.nullish(),
    kind_label: z.string().nullish(),
    questions: z.int().nullish(),
    used_in: z.int(),
    used_in_label: z.string()
});

/**
 * LibraryOut
 */
export const zLibraryOut = z.object({
    items: z.array(zLibraryItemOut)
});

/**
 * TestPassOut
 */
export const zTestPassOut = z.object({
    passed: z.int(),
    total: z.int(),
    label: z.string()
});

/**
 * CourseStatsOut
 */
export const zCourseStatsOut = z.object({
    purchases: z.int(),
    revoked: z.int(),
    started: z.int(),
    completed: z.int(),
    test_pass: zTestPassOut,
    likes: z.int()
});

/**
 * LessonStatsOut
 */
export const zLessonStatsOut = z.object({
    sold: z.int(),
    sales_sum: zMoney,
    completions: z.int(),
    test_pass: zTestPassOut,
    likes: z.int(),
    courses: z.array(zLearnRefOut)
});

/**
 * TestRowOut
 */
export const zTestRowOut = z.object({
    id: z.uuid(),
    title: z.string(),
    author_label: z.string(),
    region_label: z.string(),
    country_id: z.uuid().nullable(),
    questions_count: z.int(),
    links_label: z.string(),
    kind: zTestKind,
    kind_label: z.string(),
    status: zContentStatus,
    display_status: zContentStatus,
    display_status_label: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * LearnPage[TestRowOut]
 */
export const zLearnPageTestRowOut = z.object({
    items: z.array(zTestRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * TicketArchiveIn
 */
export const zTicketArchiveIn = z.object({
    reason: z.string().min(1).max(600),
    comment: z.string().max(600).nullish()
});

/**
 * TicketAttachmentOut
 */
export const zTicketAttachmentOut = z.object({
    id: z.uuid(),
    url: z.string().nullable(),
    content_type: z.string().nullable()
});

/**
 * TicketCanOut
 */
export const zTicketCanOut = z.object({
    add_message: z.boolean(),
    reopen: z.boolean(),
    new_ticket: z.boolean()
});

/**
 * TicketCountsOut
 */
export const zTicketCountsOut = z.object({
    new: z.int(),
    work: z.int(),
    done: z.int()
});

/**
 * TicketMessageIn
 */
export const zTicketMessageIn = z.object({
    text: z.string().max(2000)
});

/**
 * TicketMessageOut
 */
export const zTicketMessageOut = z.object({
    id: z.uuid(),
    kind: z.enum(['initial', 'addition']),
    author: z.literal('user'),
    text: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    attachments: z.array(zTicketAttachmentOut)
});

/**
 * TicketPatchIn
 *
 * `{topic_code?, priority_code?, related?}`; `related: null` — убрать связи.
 */
export const zTicketPatchIn = z.object({
    topic_code: zSupportTopicCode.nullish(),
    priority_code: zSupportPriorityCode.nullish(),
    related: z.array(zRelatedItem).max(20).nullish()
});

/**
 * TicketReplyIn
 */
export const zTicketReplyIn = z.object({
    text: z.string().min(1).max(900),
    next_status: z.enum([
        'in_progress',
        'waiting_user',
        'resolved'
    ])
});

/**
 * TicketReplyOut
 */
export const zTicketReplyOut = z.object({
    id: z.uuid(),
    text: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    author_name: z.string()
});

/**
 * TicketStatus
 */
export const zTicketStatus = z.enum(TicketStatus);

/**
 * HistoryEntryOut
 */
export const zHistoryEntryOut = z.object({
    at: z.iso.datetime({ offset: true }),
    kind: z.enum(['status', 'message']),
    from_status: zTicketStatus.nullish(),
    to_status: zTicketStatus.nullish(),
    label: z.string(),
    actor_kind: zStatusActorKind.nullish(),
    actor_name: z.string().nullish(),
    note: z.string().nullish()
});

/**
 * TicketTimelineStepOut
 */
export const zTicketTimelineStepOut = z.object({
    status: z.enum([
        'new',
        'work',
        'done'
    ]),
    at: z.iso.datetime({ offset: true }),
    label: z.string(),
    caption: z.string(),
    current: z.boolean()
});

/**
 * TicketTopicOut
 */
export const zTicketTopicOut = z.object({
    code: zSupportTopicCode,
    name: z.string()
});

/**
 * TicketOut
 *
 * `Ticket` (пример 4.8.2.3): `status` — пользовательский (`new` | `work` | `done`).
 */
export const zTicketOut = z.object({
    number: z.int(),
    number_label: z.string(),
    topic: zTicketTopicOut,
    subject: z.string(),
    status: z.enum([
        'new',
        'work',
        'done'
    ]),
    status_label: z.string(),
    created_at: z.iso.datetime({ offset: true }),
    status_changed_at: z.iso.datetime({ offset: true }),
    reply_email: z.string(),
    ref_text: z.string().nullable(),
    timeline: z.array(zTicketTimelineStepOut),
    messages: z.array(zTicketMessageOut),
    replies: z.array(zTicketReplyOut),
    can: zTicketCanOut
});

/**
 * TicketShortOut
 */
export const zTicketShortOut = z.object({
    number: z.int(),
    number_label: z.string(),
    topic: zTicketTopicOut,
    subject: z.string(),
    preview: z.string(),
    status: z.enum([
        'new',
        'work',
        'done'
    ]),
    status_label: z.string(),
    status_changed_at: z.iso.datetime({ offset: true }),
    created_at: z.iso.datetime({ offset: true })
});

/**
 * TicketListOut
 */
export const zTicketListOut = z.object({
    items: z.array(zTicketShortOut),
    next_cursor: z.string().nullable(),
    counts: zTicketCountsOut,
    total: z.int(),
    total_label: z.string(),
    open_count: z.int(),
    summary_label: z.string()
});

/**
 * TicketTransitionIn
 */
export const zTicketTransitionIn = z.object({
    action: z.enum([
        'resolve',
        'wait_user',
        'reopen',
        'close'
    ])
});

/**
 * TicketUserOut
 */
export const zTicketUserOut = z.object({
    kind: z.enum([
        'user',
        'guest',
        'contact'
    ]),
    id: z.uuid().nullable(),
    full_name: z.string().nullable(),
    email: z.string().nullable(),
    label: z.string()
});

/**
 * TierGateKey
 */
export const zTierGateKey = z.enum(TierGateKey);

/**
 * TierGatePatchIn
 */
export const zTierGatePatchIn = z.object({
    min_tier_id: z.uuid()
});

/**
 * TierOptionOut
 */
export const zTierOptionOut = z.object({
    id: z.uuid(),
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    title: z.string(),
    users_at_least: z.int(),
    users_label: z.string()
});

/**
 * TierOut
 */
export const zTierOut = z.object({
    rank: z.int(),
    roman: z.string(),
    name: z.string(),
    label: z.string()
});

/**
 * TileOut
 */
export const zTileOut = z.object({
    done: z.int(),
    all: z.int(),
    label: z.string()
});

/**
 * TileSource
 *
 * Источник тайлов фолбэка карты (А.4 `maps.fallback_tiles`, BR-GEO-64).
 */
export const zTileSource = z.object({
    url_template: z.string(),
    max_zoom: z.int().gte(1).lte(22),
    attribution: z.string().min(1)
});

/**
 * AdminMapsOut
 *
 * Карты в админке (контур и центр региона, координаты винодельни; BR-GEO-64).
 */
export const zAdminMapsOut = z.object({
    provider: zMapProvider,
    fallback_tiles: z.array(zTileSource),
    tile_error_threshold: z.int(),
    js_api_key: z.string().nullish(),
    map_id: z.string().nullish(),
    geocoder_available: z.boolean().optional().default(false),
    suggest_available: z.boolean().optional().default(false)
});

/**
 * AppMapsOut
 */
export const zAppMapsOut = z.object({
    provider: zMapProvider,
    fallback_tiles: z.array(zTileSource),
    tile_error_threshold: z.int()
});

/**
 * GeoMapsOut
 */
export const zGeoMapsOut = z.object({
    provider: zMapProvider,
    fallback_tiles: z.array(zTileSource),
    tile_error_threshold: z.int()
});

/**
 * GeoConfigOut
 */
export const zGeoConfigOut = z.object({
    default_point: zDefaultPointOut,
    default_country: z.string(),
    maps: zGeoMapsOut,
    popular_queries: z.array(z.string()),
    you_here_radius_km: z.int(),
    position_max_age_min: z.int()
});

/**
 * TilesOut
 */
export const zTilesOut = z.object({
    lessons: zTileOut,
    wines: zTileOut,
    visits: zTileOut,
    extra: zTileOut
});

/**
 * RegionSummaryOut
 */
export const zRegionSummaryOut = z.object({
    region: zRegionBriefOut,
    stamp: zStampOut,
    study: zScaleOut,
    taste: zScaleOut,
    visit: zScaleOut,
    tiles: zTilesOut,
    hint: z.string(),
    cta: zLinkOut
});

/**
 * TilesVisibilityOut
 */
export const zTilesVisibilityOut = z.object({
    lessons: z.boolean(),
    wine: z.boolean()
});

/**
 * TimezoneOut
 */
export const zTimezoneOut = z.object({
    tz: z.string(),
    label: z.string()
});

/**
 * ExpReferenceOut
 */
export const zExpReferenceOut = z.object({
    timezones: z.array(zTimezoneOut),
    default_timezone: z.string(),
    currency: z.string(),
    hosts: z.array(zHostOut),
    wineries: z.array(zRefOut),
    regions: z.array(zRefOut),
    countries: z.array(zRefOut),
    formats: z.array(zOptionOut),
    kinds: z.array(zOptionOut),
    climates: z.array(zOptionOut),
    schedule_modes: z.array(zOptionOut),
    min_notices: z.array(zOptionOut),
    weekdays: z.array(zOptionOut),
    excursion_cancel_policies: z.array(zOptionOut),
    trip_cancel_policies: z.array(zOptionOut),
    room_types: z.array(zOptionOut),
    child_age_groups: z.array(zOptionOut),
    archive_reasons: z.array(zOptionOut),
    cancel_reasons: z.array(zOptionOut),
    decline_reasons: z.array(zOptionOut)
});

/**
 * TokenIn
 *
 * Токен из ссылки «Отписаться от рассылок» (страница отписки, BR-SRV-57).
 */
export const zTokenIn = z.object({
    token: z.string().min(1).max(2000)
});

/**
 * TokensOut
 */
export const zTokensOut = z.object({
    access_token: z.string(),
    access_expires_in: z.int(),
    refresh_token: z.string(),
    refresh_expires_in: z.int()
});

/**
 * AdminLoginOut
 */
export const zAdminLoginOut = z.object({
    tokens: zTokensOut,
    me: zAdminMeOut
});

/**
 * AdminRefreshOut
 */
export const zAdminRefreshOut = z.object({
    tokens: zTokensOut
});

/**
 * AppRefreshOut
 */
export const zAppRefreshOut = z.object({
    tokens: zTokensOut
});

/**
 * TranslationBatchCan
 */
export const zTranslationBatchCan = z.object({
    cancel: z.boolean().optional().default(false)
});

/**
 * TranslationBatchIn
 */
export const zTranslationBatchIn = z.object({
    entity_types: z.array(z.string().min(1).max(40).regex(/^[a-z_]+$/)).min(1).max(60),
    only_missing: z.boolean().optional().default(true)
});

/**
 * TranslationBatchScopeOut
 */
export const zTranslationBatchScopeOut = z.object({
    entity_types: z.array(z.string()),
    only_missing: z.boolean(),
    statuses: z.array(z.string())
});

/**
 * TranslationBatchStatus
 */
export const zTranslationBatchStatus = z.enum(TranslationBatchStatus);

/**
 * TranslationBatchOut
 */
export const zTranslationBatchOut = z.object({
    id: z.uuid(),
    scope: zTranslationBatchScopeOut,
    scope_label: z.string(),
    status: zTranslationBatchStatus,
    status_label: z.string(),
    total_jobs: z.int(),
    done_jobs: z.int(),
    failed_jobs: z.int(),
    total_chars: z.int(),
    progress_label: z.string(),
    started_by: z.uuid(),
    started_by_name: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    can: zTranslationBatchCan
});

/**
 * TranslationEntityTypeOut
 */
export const zTranslationEntityTypeOut = z.object({
    value: z.string(),
    label: z.string()
});

/**
 * TranslationBatchesPage
 */
export const zTranslationBatchesPage = z.object({
    items: z.array(zTranslationBatchOut),
    total: z.int(),
    can: zListCan.optional(),
    entity_types: z.array(zTranslationEntityTypeOut)
});

/**
 * TranslationEstimateOut
 */
export const zTranslationEstimateOut = z.object({
    fields: z.int(),
    chars: z.int(),
    label: z.string()
});

/**
 * TranslationJobCan
 */
export const zTranslationJobCan = z.object({
    retry: z.boolean().optional().default(false)
});

/**
 * TranslationJobStatus
 */
export const zTranslationJobStatus = z.enum(TranslationJobStatus);

/**
 * RetranslateJobOut
 */
export const zRetranslateJobOut = z.object({
    id: z.uuid(),
    field_path: z.string(),
    status: zTranslationJobStatus,
    status_label: z.string()
});

/**
 * RetranslateOut
 */
export const zRetranslateOut = z.object({
    jobs: z.array(zRetranslateJobOut)
});

/**
 * TranslationMode
 */
export const zTranslationMode = z.enum(TranslationMode);

/**
 * TranslationJobOut
 */
export const zTranslationJobOut = z.object({
    id: z.uuid(),
    entity_type: z.string(),
    entity_type_label: z.string(),
    entity_id: z.uuid(),
    entity_title: z.string().nullable(),
    field_path: z.string(),
    source_lang: zContentLanguage,
    target_lang: zContentLanguage,
    direction_label: z.string(),
    mode: zTranslationMode,
    status: zTranslationJobStatus,
    status_label: z.string(),
    attempts: z.int(),
    chars: z.int(),
    batch_id: z.uuid().nullable(),
    error_code: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    finished_at: z.iso.datetime({ offset: true }).nullable(),
    can: zTranslationJobCan
});

/**
 * TranslationJobsPage
 */
export const zTranslationJobsPage = z.object({
    items: z.array(zTranslationJobOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * TranslationStatus
 */
export const zTranslationStatus = z.enum(TranslationStatus);

/**
 * LocalizedText
 *
 * Текст на языках инсталляции (COM-09).
 *
 * RU: ``{"ru": "..."}``; World: ``{"en", "es", "source_lang", "translation_status"}``.
 * Полноту по языкам рынка проверяет `require_languages` (MarketConfig, модули при записи).
 * При сериализации (ответы админки, JSONB) объект содержит ровно ключи текущего рынка —
 * `localized_keys()`; схема OpenAPI описывает все поля как необязательные.
 */
export const zLocalizedText = z.object({
    ru: z.string().nullish(),
    en: z.string().nullish(),
    es: z.string().nullish(),
    source_lang: zLanguage.nullish(),
    translation_status: zTranslationStatus.nullish()
});

/**
 * AdminBrandOut
 */
export const zAdminBrandOut = z.object({
    name: z.string(),
    bonus_name: zLocalizedText,
    logo_url: z.string().nullish()
});

/**
 * AdminCatalogOut
 */
export const zAdminCatalogOut = z.object({
    delivery_options: z.array(zDeliveryOption),
    sales_channels: z.array(zSalesChannel),
    channel_hint: zLocalizedText
});

/**
 * AdminCityOut
 */
export const zAdminCityOut = z.object({
    name: zLocalizedText,
    lat: z.number(),
    lng: z.number()
});

/**
 * AdminGeoOut
 */
export const zAdminGeoOut = z.object({
    default_country: z.string(),
    default_city: zAdminCityOut
});

/**
 * AuthorProfileIn
 *
 * «Заполнить профиль» (4.7.4). Статус меняется только «Опубликовать» / «Скрыть».
 */
export const zAuthorProfileIn = z.object({
    display_name: z.string().min(2).max(100),
    work_region: zLocalizedText.nullish(),
    experience: zLocalizedText.nullish(),
    social_telegram: z.string().nullish(),
    social_vk: z.string().nullish(),
    social_video: z.string().nullish(),
    social_site: z.string().nullish(),
    photo_file_id: z.uuid().nullish(),
    photo_url: z.string().nullish(),
    gallery_file_ids: z.array(z.uuid()).max(10).optional(),
    specialties: z.array(zLocalizedText).min(1).max(6),
    bio: zLocalizedText.nullish()
});

/**
 * CampaignCardOut
 */
export const zCampaignCardOut = z.object({
    id: z.uuid(),
    channel: zCampaignChannel,
    channel_label: z.string(),
    title: z.string(),
    text: z.string(),
    image: zCampaignImageOut.nullable(),
    link: zCampaignLinkOut.nullable(),
    mode: zCampaignMode,
    mode_label: z.string(),
    schedule_label: z.string(),
    send_at: z.iso.datetime({ offset: true }).nullable(),
    send_at_timezone: z.string().nullable(),
    weekdays: z.array(zCampaignWeekday),
    local_time: z.string().nullable(),
    status: zCampaignStatus,
    status_label: z.string(),
    runs_count: z.int(),
    last_run_recipients: z.int().nullable(),
    sent_label: z.string(),
    next_run_at: z.iso.datetime({ offset: true }).nullable(),
    row_action: z.enum([
        'schedule',
        'launch',
        'stop',
        'resume',
        'cancel'
    ]).nullable(),
    row_action_label: z.string().nullable(),
    audience_label: z.string(),
    segment: zCampaignSegmentOut.nullable(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
    can: zCampaignCan,
    texts_title: zLocalizedText,
    texts_text: zLocalizedText,
    image_file_id: z.uuid().nullable(),
    link_type: zCampaignLinkType,
    link_id: z.uuid().nullable(),
    preview: z.string(),
    read_only: z.boolean(),
    runs: z.array(zCampaignRunReportOut),
    history: z.array(zCampaignHistoryOut),
    toast: z.string().nullish()
});

/**
 * CampaignIn
 *
 * Тело модалки «Новое уведомление» (13.7.3.3) + `intent`: `draft` — «Черновик»,
 * `schedule` — «Запланировать» (разовая), `launch` — «Запустить» (регулярная).
 */
export const zCampaignIn = z.object({
    intent: z.enum([
        'draft',
        'schedule',
        'launch'
    ]).optional().default('draft'),
    channel: zCampaignChannel.optional().default('push'),
    title: zLocalizedText,
    text: zLocalizedText,
    image_file_id: z.uuid().nullish(),
    link_type: zCampaignLinkType.optional().default('none'),
    link_id: z.uuid().nullish(),
    mode: zCampaignMode.optional().default('once'),
    send_at: z.iso.datetime({ offset: true }).nullish(),
    send_at_timezone: z.string().max(64).nullish(),
    weekdays: z.array(zCampaignWeekday).max(7).optional(),
    local_time: z.string().max(5).nullish(),
    segment: z.string().max(64).nullish(),
    segment_params: z.record(z.string(), z.string()).optional()
});

/**
 * CampaignPatchIn
 *
 * Частичное тело: переданные поля меняются, `intent` — переход (необязателен). `null`
 * допустим для картинки, объекта, времени, пояса, сегмента; канал, тексты, «Что открыть»,
 * периодичность, дни недели и параметры сегмента очистить нельзя (422).
 */
export const zCampaignPatchIn = z.object({
    intent: z.enum([
        'draft',
        'schedule',
        'launch'
    ]).nullish(),
    channel: zCampaignChannel.nullish(),
    title: zLocalizedText.nullish(),
    text: zLocalizedText.nullish(),
    image_file_id: z.uuid().nullish(),
    link_type: zCampaignLinkType.nullish(),
    link_id: z.uuid().nullish(),
    mode: zCampaignMode.nullish(),
    send_at: z.iso.datetime({ offset: true }).nullish(),
    send_at_timezone: z.string().max(64).nullish(),
    weekdays: z.array(zCampaignWeekday).max(7).nullish(),
    local_time: z.string().max(5).nullish(),
    segment: z.string().max(64).nullish(),
    segment_params: z.record(z.string(), z.string()).nullish()
});

/**
 * CatalogMediaOut
 */
export const zCatalogMediaOut = z.object({
    id: z.uuid(),
    media_type: zMediaType,
    placement: zMediaPlacement,
    role_label: z.string(),
    file_id: z.uuid().nullable(),
    source_url: z.string().nullable(),
    url: z.string().nullable(),
    thumb_url: z.string().nullable(),
    caption: zLocalizedText.nullable(),
    position: z.int()
});

/**
 * CountryFactIn
 */
export const zCountryFactIn = z.object({
    title: zLocalizedText.nullish(),
    text: zLocalizedText.nullish()
});

/**
 * CountryCreateIn
 */
export const zCountryCreateIn = z.object({
    code: z.string().length(2),
    sort_order: z.int().gte(1).nullish(),
    illustration_file_id: z.uuid().nullish(),
    picture_file_id: z.uuid().nullish(),
    facts: z.array(zCountryFactIn).max(50).optional(),
    quiz_ids: z.array(z.uuid()).max(100).optional(),
    note: z.string().max(300).nullish()
});

/**
 * CountryQuizOut
 */
export const zCountryQuizOut = z.object({
    test_id: z.uuid(),
    title: zLocalizedText.nullable(),
    source: z.enum(['auto', 'manual'])
});

/**
 * CountryRef
 */
export const zCountryRef = z.object({
    id: z.uuid(),
    code: z.string(),
    name: zLocalizedText.nullable()
});

/**
 * CountryRegionOut
 */
export const zCountryRegionOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    status: zRegionStatus,
    status_label: z.string(),
    counts: z.record(z.string(), z.int())
});

/**
 * CountryRowOut
 */
export const zCountryRowOut = z.object({
    id: z.uuid(),
    code: z.string(),
    name: zLocalizedText.nullable(),
    regions_count: z.int(),
    sort_order: z.int(),
    sort_order_label: z.string(),
    can: zCountryCan
});

/**
 * CoursePatchIn
 */
export const zCoursePatchIn = z.object({
    title: zLocalizedText.nullish(),
    region_id: z.uuid().nullish(),
    duration_min: z.int().gte(1).lte(6000).nullish(),
    is_paid: z.boolean().nullish(),
    price: zLearnPriceIn.nullish(),
    description: zLocalizedText.nullish(),
    theses: z.array(zLocalizedText).nullish(),
    related: zRelatedIn.nullish(),
    store_products: z.record(z.string(), zStoreProductIn.nullable()).nullish()
});

/**
 * DropPatchIn
 */
export const zDropPatchIn = z.object({
    name: zLocalizedText.nullish(),
    target_type: zDropTargetType.nullish(),
    target_id: z.uuid().nullish(),
    batch_qty: z.int().nullish(),
    price: zMoneyIn.nullish(),
    limit_per_user: z.int().gte(1).nullish(),
    delivery_options: z.array(zDeliveryOption).nullish(),
    early_access: z.boolean().nullish(),
    early_start_at: z.iso.datetime({ offset: true }).nullish(),
    start_at: z.iso.datetime({ offset: true }).nullish(),
    timezone: z.string().nullish(),
    description: zLocalizedText.nullish(),
    conditions: zLocalizedText.nullish()
});

/**
 * EventMediaIn
 *
 * Фото — файл (`exp_image`, JPG/PNG, COM-13) или ссылка; видео — только ссылкой.
 * Первый файл — обложка (BR-EXP-09).
 */
export const zEventMediaIn = z.object({
    id: z.uuid().nullish(),
    file_id: z.uuid().nullish(),
    source_url: z.string().max(1000).nullish(),
    kind: zMediaKind.optional().default('photo'),
    placement: zMediaPlacement.nullish(),
    caption: zLocalizedText.nullish()
});

/**
 * EventMediaOut
 */
export const zEventMediaOut = z.object({
    id: z.uuid(),
    kind: zMediaKind,
    placement: zMediaPlacement,
    file_id: z.uuid().nullable(),
    source_url: z.string().nullable(),
    url: z.string().nullable(),
    thumb_url: z.string().nullable(),
    caption: zLocalizedText.nullable()
});

/**
 * EventTimezoneOut
 */
export const zEventTimezoneOut = z.object({
    tz: z.string(),
    label: zLocalizedText.nullable()
});

/**
 * AdminExpOut
 */
export const zAdminExpOut = z.object({
    event_timezones: z.array(zEventTimezoneOut)
});

/**
 * ExcursionPatchIn
 */
export const zExcursionPatchIn = z.object({
    title: zLocalizedText.nullish(),
    winery_id: z.uuid().nullish(),
    duration_min: z.int().gte(1).lte(1440).nullish(),
    seats_per_slot: z.int().gte(1).lte(100000).nullish(),
    price: zMoney.nullish(),
    cancel_policy: zExcursionCancelPolicy.nullish(),
    schedule_mode: zScheduleMode.nullish(),
    starts_at: z.iso.datetime({ offset: true }).nullish(),
    timezone: z.string().max(64).nullish(),
    min_notice: zMinNotice.nullish(),
    season_until: z.iso.date().nullish()
});

/**
 * ExcursionRow
 */
export const zExcursionRow = z.object({
    id: z.uuid(),
    title: zLocalizedText,
    title_text: z.string(),
    winery: zRefOut.nullable(),
    duration_min: z.int().nullable(),
    duration_label: z.string(),
    price: zMoney,
    price_label: z.string(),
    schedule_mode: zScheduleMode,
    week_slots_count: z.int(),
    slots_label: z.string(),
    moderation_status: zModerationStatus,
    status_label: z.string(),
    is_live: z.boolean(),
    archived: z.boolean(),
    can: zRowCan
});

/**
 * FactIn
 */
export const zFactIn = z.object({
    title: zLocalizedText,
    text: zLocalizedText
});

/**
 * FactPatchIn
 */
export const zFactPatchIn = z.object({
    title: zLocalizedText.nullish(),
    text: zLocalizedText.nullish()
});

/**
 * GrapeOut
 */
export const zGrapeOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable()
});

/**
 * IdName
 *
 * Ссылка на объект для админки: название — LocalizedText целиком (COM-09).
 */
export const zIdName = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable()
});

/**
 * LearnMediaOut
 */
export const zLearnMediaOut = z.object({
    id: z.uuid(),
    placement: zLearnMediaPlacement,
    role_label: z.string(),
    file_id: z.uuid().nullable(),
    source_url: z.string().nullable(),
    url: z.string().nullable(),
    caption: zLocalizedText.nullable()
});

/**
 * LessonPatchIn
 */
export const zLessonPatchIn = z.object({
    title: zLocalizedText.nullish(),
    region_id: z.uuid().nullish(),
    duration_min: z.int().gte(1).lte(600).nullish(),
    is_paid: z.boolean().nullish(),
    price: zLearnPriceIn.nullish(),
    body: zLocalizedText.nullish(),
    theses: z.array(zLocalizedText).nullish(),
    course_ids: z.array(z.uuid()).max(50).nullish(),
    related: zRelatedIn.nullish(),
    store_products: z.record(z.string(), zStoreProductIn.nullable()).nullish()
});

/**
 * MapZoneChoiceIn
 *
 * OQ-GEO-01: существующая зона страны (`id`) или новое название (`new_name`).
 */
export const zMapZoneChoiceIn = z.object({
    id: z.uuid().nullish(),
    new_name: zLocalizedText.nullish()
});

/**
 * MapZoneOut
 */
export const zMapZoneOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    regions_count: z.int()
});

/**
 * MapZoneListOut
 */
export const zMapZoneListOut = z.object({
    items: z.array(zMapZoneOut)
});

/**
 * MediaItemIn
 */
export const zMediaItemIn = z.object({
    file_id: z.uuid().nullish(),
    source_url: z.string().max(1000).nullish(),
    caption: zLocalizedText.nullish()
});

/**
 * CourseCreateIn
 */
export const zCourseCreateIn = z.object({
    title: zLocalizedText,
    region_id: z.uuid().nullish(),
    is_main: z.boolean().optional().default(false),
    duration_min: z.int().gte(1).lte(6000).nullish(),
    is_paid: z.boolean().optional().default(false),
    price: zLearnPriceIn.nullish(),
    description: zLocalizedText,
    theses: z.array(zLocalizedText).optional(),
    media: z.array(zMediaItemIn).max(10).optional(),
    related: zRelatedIn.nullish(),
    store_products: z.record(z.string(), zStoreProductIn.nullable()).nullish()
});

/**
 * LearnMediaIn
 */
export const zLearnMediaIn = z.object({
    items: z.array(zMediaItemIn).max(10)
});

/**
 * LessonCreateIn
 */
export const zLessonCreateIn = z.object({
    title: zLocalizedText,
    region_id: z.uuid().nullish(),
    duration_min: z.int().gte(1).lte(600),
    is_paid: z.boolean().optional().default(false),
    price: zLearnPriceIn.nullish(),
    body: zLocalizedText,
    theses: z.array(zLocalizedText).optional(),
    course_ids: z.array(z.uuid()).max(50).optional(),
    related: zRelatedIn.nullish(),
    media: z.array(zMediaItemIn).max(10).optional(),
    store_products: z.record(z.string(), zStoreProductIn.nullable()).nullish()
});

/**
 * MediaListOut
 */
export const zMediaListOut = z.object({
    items: z.array(zCatalogMediaOut)
});

/**
 * MediaOut
 */
export const zMediaOut = z.object({
    id: z.uuid(),
    slot: zMediaSlot,
    role: zMediaRole.nullable(),
    file_id: z.uuid().nullable(),
    url: z.string().nullable(),
    preview_url: z.string().nullable(),
    content_type: z.string().nullable(),
    width: z.int().nullable(),
    height: z.int().nullable(),
    caption: zLocalizedText.nullable(),
    position: z.int()
});

/**
 * NotesIn
 */
export const zNotesIn = z.object({
    items: z.array(zLocalizedText).max(30)
});

/**
 * OnboardingItemCreateIn
 */
export const zOnboardingItemCreateIn = z.object({
    title: zLocalizedText,
    caption: zLocalizedText,
    icon_file_id: z.uuid()
});

/**
 * OnboardingItemPatchIn
 */
export const zOnboardingItemPatchIn = z.object({
    title: zLocalizedText.nullish(),
    caption: zLocalizedText.nullish(),
    icon_file_id: z.uuid().nullish(),
    position: z.int().gte(1).nullish()
});

/**
 * OnboardingScreenPatchIn
 *
 * `{title?, subtitle?, background_file_id?}`: `subtitle: null` / `background_file_id: null`
 * — удалить; заголовок обязателен.
 */
export const zOnboardingScreenPatchIn = z.object({
    title: zLocalizedText.nullish(),
    subtitle: zLocalizedText.nullish(),
    background_file_id: z.uuid().nullish()
});

/**
 * OnboardingScreenRow
 */
export const zOnboardingScreenRow = z.object({
    id: z.uuid(),
    screen_key: z.string(),
    kind: zOnboardingScreenKind,
    name: z.string(),
    position: z.int(),
    title: zLocalizedText,
    subtitle: zLocalizedText.nullable(),
    illustrations: z.string(),
    items_count: z.int(),
    status: zOnboardingScreenStatus,
    status_label: z.string(),
    fixed: zOnboardingScreenFixed.nullable(),
    step: zOnboardingStepOut,
    can: zOnboardingScreenCan
});

/**
 * OnboardingMoveOut
 */
export const zOnboardingMoveOut = z.object({
    items: z.array(zOnboardingScreenRow)
});

/**
 * OriginCreateIn
 */
export const zOriginCreateIn = z.object({
    code: z.string().min(1).max(20),
    level: zOriginLevel,
    full_name: zLocalizedText,
    country_id: z.uuid(),
    comment: z.string().max(300).nullish()
});

/**
 * OriginOut
 */
export const zOriginOut = z.object({
    id: z.uuid(),
    code: z.string(),
    full_name: zLocalizedText.nullable(),
    country: zCountryRef,
    level: zOriginLevel,
    level_label: z.string(),
    comment: z.string().nullable(),
    can: zOriginCan
});

/**
 * OriginPatchIn
 */
export const zOriginPatchIn = z.object({
    code: z.string().nullish(),
    level: zOriginLevel.nullish(),
    full_name: zLocalizedText.nullish(),
    country_id: z.uuid().nullish(),
    comment: z.string().max(300).nullish()
});

/**
 * Page[CountryRowOut]
 */
export const zPageCountryRowOut = z.object({
    items: z.array(zCountryRowOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * Page[ExcursionRow]
 */
export const zPageExcursionRow = z.object({
    items: z.array(zExcursionRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * Page[OnboardingScreenRow]
 */
export const zPageOnboardingScreenRow = z.object({
    items: z.array(zOnboardingScreenRow),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * Page[OriginOut]
 */
export const zPageOriginOut = z.object({
    items: z.array(zOriginOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * PairIn
 */
export const zPairIn = z.object({
    label: zLocalizedText,
    value: zLocalizedText
});

/**
 * PairOut
 */
export const zPairOut = z.object({
    label: zLocalizedText,
    value: zLocalizedText
});

/**
 * PairingsIn
 */
export const zPairingsIn = z.object({
    categories: z.array(zPairingCategory).optional(),
    custom: zLocalizedText.nullish()
});

/**
 * PriorityOut
 */
export const zPriorityOut = z.object({
    code: zSupportPriorityCode,
    name: zLocalizedText,
    label: z.string(),
    sla_value: z.int(),
    sla_unit: zSlaUnit,
    sla_label: z.string(),
    hint: zLocalizedText.nullable(),
    hint_label: z.string().nullable(),
    position: z.int()
});

/**
 * ProgramSectionIn
 */
export const zProgramSectionIn = z.object({
    id: z.uuid().nullish(),
    title: zLocalizedText.nullish(),
    is_root: z.boolean().optional().default(false),
    order: z.int().gte(0).nullish(),
    result_text: zLocalizedText.nullish(),
    achievement_title: zLocalizedText.nullish(),
    lessons: z.array(zProgramLessonIn).max(200).optional()
});

/**
 * ProgramIn
 */
export const zProgramIn = z.object({
    sections: z.array(zProgramSectionIn).max(50)
});

/**
 * ProgramSectionOut
 */
export const zProgramSectionOut = z.object({
    id: z.uuid(),
    title: zLocalizedText.nullable(),
    is_root: z.boolean(),
    position: z.int(),
    result_text: zLocalizedText.nullable(),
    achievement_title: zLocalizedText.nullable(),
    lessons: z.array(zProgramLessonOut)
});

/**
 * CourseOut
 */
export const zCourseOut = z.object({
    id: z.uuid(),
    kind: zCourseKind,
    kind_label: z.string(),
    main_label: z.string().nullable(),
    title: zLocalizedText.nullable(),
    region: zLearnRefOut.nullable(),
    author: zAuthorOut,
    duration_min: z.int().nullable(),
    duration_total: z.int(),
    lessons_count: z.int(),
    tests_count: z.int(),
    is_paid: z.boolean(),
    price: zMoney.nullable(),
    price_label: z.string(),
    store_products: zStoreProductsOut,
    description: zLocalizedText.nullable(),
    theses: z.array(zLocalizedText),
    media: z.array(zLearnMediaOut),
    related: zRelatedOut,
    sections: z.array(zProgramSectionOut),
    moderation_note: z.string().nullable(),
    status: zContentStatus,
    status_label: z.string(),
    display_status: zContentStatus,
    display_status_label: z.string(),
    published_revision: zLearnRevisionOut.nullable(),
    pending_revision: zLearnRevisionOut.nullable(),
    has_unsubmitted_changes: z.boolean(),
    edits_hint: z.string().nullable(),
    first_published_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: zLearnArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    usages: z.array(zLearnUsageItemOut),
    can: zCourseCan,
    message: z.string().nullish(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * PublicationAdminOut
 */
export const zPublicationAdminOut = z.object({
    id: z.uuid(),
    overline: z.string(),
    subtitle: z.string(),
    title: zLocalizedText,
    title_text: z.string(),
    status: zPublicationStatus,
    status_label: z.string(),
    kind: zPublicationKind,
    kind_label: z.string(),
    source: zPublicationSource,
    author_kind: zAuthorKind,
    author_member_id: z.uuid().nullable(),
    winery: zContentRefOut.nullable(),
    region: zContentRefOut.nullable(),
    wines: z.array(zContentRefOut),
    experiences: z.array(zContentRefOut),
    reading_label: z.string(),
    lead: zLocalizedText.nullable(),
    body: z.record(z.string(), z.unknown()),
    body_markup: zLocalizedText,
    media: z.array(zContentMediaOut),
    facts: zPublicationFactsOut,
    likes_count: z.int(),
    views_count: z.int(),
    comments_count: z.int(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    first_published_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: zContentArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    history: z.array(zContentHistoryOut),
    can: zPublicationCan,
    toast: z.string().nullish()
});

/**
 * PublicationCreateIn
 */
export const zPublicationCreateIn = z.object({
    title: zLocalizedText,
    body: zLocalizedText,
    winery_id: z.uuid().nullish(),
    region_id: z.uuid().nullish(),
    wine_ids: z.array(z.uuid()).max(20).optional(),
    experiences: z.array(zExperienceTagIn).max(20).optional(),
    media_ids: z.array(z.uuid()).max(10).optional()
});

/**
 * PublicationPatchIn
 */
export const zPublicationPatchIn = z.object({
    title: zLocalizedText.nullish(),
    body: zLocalizedText.nullish(),
    winery_id: z.uuid().nullish(),
    region_id: z.uuid().nullish(),
    wine_ids: z.array(z.uuid()).nullish(),
    experiences: z.array(zExperienceTagIn).nullish(),
    media_ids: z.array(z.uuid()).nullish()
});

/**
 * RegionDictionaryItemOut
 *
 * Регион в справочнике для форм (винодельня, вино, урок, событие…).
 */
export const zRegionDictionaryItemOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    label: z.string(),
    country: zCountryRef,
    status: zRegionStatus,
    status_label: z.string()
});

/**
 * RegionDictionaryOut
 */
export const zRegionDictionaryOut = z.object({
    items: z.array(zRegionDictionaryItemOut)
});

/**
 * RegionImagesOut
 */
export const zRegionImagesOut = z.object({
    illustration: zMediaOut.nullable(),
    stamp_bronze: zMediaOut.nullable(),
    stamp_gold: zMediaOut.nullable(),
    specialization: zMediaOut.nullable()
});

/**
 * RewardCreateIn
 */
export const zRewardCreateIn = z.object({
    title: zLocalizedText,
    icon_file_id: z.uuid().nullish(),
    cost_bonuses: z.int().gte(1).lte(1000),
    kind: zRewardKind,
    description: zLocalizedText.nullish(),
    executor_type: zExecutorType,
    status: zRewardStatus.optional().default('active'),
    sort: z.int().gte(0).lte(10000).optional().default(0)
});

/**
 * RewardPatchIn
 */
export const zRewardPatchIn = z.object({
    title: zLocalizedText.nullish(),
    icon_file_id: z.uuid().nullish(),
    cost_bonuses: z.int().gte(1).lte(1000).nullish(),
    kind: zRewardKind.nullish(),
    description: zLocalizedText.nullish(),
    executor_type: zExecutorType.nullish(),
    status: zRewardStatus.nullish(),
    sort: z.int().gte(0).lte(10000).nullish()
});

/**
 * RewardRowOut
 */
export const zRewardRowOut = z.object({
    id: z.uuid(),
    title: zLocalizedText,
    title_label: z.string(),
    cost_bonuses: z.int(),
    cost_label: z.string(),
    kind: zRewardKind,
    kind_label: z.string(),
    description: zLocalizedText.nullable(),
    description_label: z.string().nullable(),
    executor_type: zExecutorType,
    executor_label: z.string(),
    icon_file_id: z.uuid().nullable(),
    icon_url: z.string().nullable(),
    status: zRewardStatus,
    status_label: z.string(),
    sort: z.int(),
    exchanges_count: z.int(),
    can: zGamRowCan
});

/**
 * RewardsPageOut
 */
export const zRewardsPageOut = z.object({
    items: z.array(zRewardRowOut),
    total: z.int(),
    can: zListCan
});

/**
 * RoomIn
 */
export const zRoomIn = z.object({
    name: zLocalizedText.nullish(),
    type: zRoomType.optional().default('standard'),
    beds: z.int().gte(0).lte(100).optional().default(0),
    price_per_night: zMoney.nullish()
});

/**
 * RoomOut
 */
export const zRoomOut = z.object({
    name: zLocalizedText.nullable(),
    type: zRoomType,
    type_label: z.string(),
    beds: z.int(),
    price_per_night: zMoney.nullable(),
    shown_in_app: z.boolean()
});

/**
 * SetPatchIn
 */
export const zSetPatchIn = z.object({
    name: zLocalizedText.nullish(),
    format: zSetFormat.nullish(),
    blind_tasting_id: z.uuid().nullish(),
    sales_channels: z.array(zSalesChannel).nullish(),
    delivery_options: z.array(zDeliveryOption).nullish(),
    price: zMoneyIn.nullish(),
    stock: z.int().gte(0).nullish(),
    description: zLocalizedText.nullish(),
    link_winery_id: z.uuid().nullish(),
    link_expert_id: z.uuid().nullish(),
    link_course_id: z.uuid().nullish(),
    link_lesson_id: z.uuid().nullish()
});

/**
 * StepIn
 */
export const zStepIn = z.object({
    title: zLocalizedText,
    text: zLocalizedText
});

/**
 * ContentIn
 *
 * Описание, детали, шаги, медиа (у экскурсии — ещё «Как добраться»).
 */
export const zContentIn = z.object({
    description: zLocalizedText.nullish(),
    details: z.array(zPairIn).nullish(),
    steps: z.array(zStepIn).nullish(),
    route: z.array(zPairIn).nullish(),
    media: z.array(zEventMediaIn).nullish()
});

/**
 * StepOut
 */
export const zStepOut = z.object({
    title: zLocalizedText,
    text: zLocalizedText
});

/**
 * SystemCountryOut
 */
export const zSystemCountryOut = z.object({
    code: z.string(),
    name: zLocalizedText.nullable(),
    in_catalog: z.boolean()
});

/**
 * SystemCountryListOut
 */
export const zSystemCountryListOut = z.object({
    items: z.array(zSystemCountryOut)
});

/**
 * TastingPatchIn
 */
export const zTastingPatchIn = z.object({
    title: zLocalizedText.nullish(),
    format: zTastingFormat.nullish(),
    kind: zTastingKind.nullish(),
    starts_at: z.iso.datetime({ offset: true }).nullish(),
    timezone: z.string().max(64).nullish(),
    duration_min: z.int().gte(1).lte(1440).nullish(),
    seats_max: z.int().gte(1).lte(100000).nullish(),
    region_id: z.uuid().nullish(),
    host_id: z.uuid().nullish(),
    conference_url: z.string().max(500).nullish(),
    address: z.string().max(300).nullish(),
    venue_winery_id: z.uuid().nullish(),
    is_paid: z.boolean().nullish(),
    price: zMoney.nullish()
});

/**
 * TastingRow
 */
export const zTastingRow = z.object({
    id: z.uuid(),
    title: zLocalizedText,
    title_text: z.string(),
    format: zTastingFormat,
    format_label: z.string(),
    kind: zTastingKind,
    kind_label: z.string(),
    host: zRefOut.nullable(),
    place_label: z.string(),
    region: zRefOut.nullable(),
    starts_at: z.iso.datetime({ offset: true }),
    timezone: z.string(),
    date_label: z.string(),
    price: zMoney.nullable(),
    price_label: z.string(),
    seats_taken: z.int(),
    seats_max: z.int(),
    seats_label: z.string(),
    moderation_status: zModerationStatus,
    lifecycle: zEventLifecycle,
    status_label: z.string(),
    is_live: z.boolean(),
    archived: z.boolean(),
    can: zRowCan
});

/**
 * Page[TastingRow]
 */
export const zPageTastingRow = z.object({
    items: z.array(zTastingRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TastingWineIn
 */
export const zTastingWineIn = z.object({
    wine_id: z.uuid(),
    ref_sweetness: z.int().gte(1).lte(5).nullish(),
    ref_acidity: z.int().gte(1).lte(5).nullish(),
    ref_body: z.int().gte(1).lte(5).nullish(),
    ref_tannin: z.int().gte(1).lte(5).nullish(),
    ref_aroma: z.int().gte(1).lte(5).nullish(),
    ref_finish: z.int().gte(1).lte(5).nullish(),
    ref_grape: z.string().max(120).nullish(),
    ref_country_id: z.uuid().nullish(),
    ref_region_id: z.uuid().nullish(),
    ref_climate: zRefClimate.nullish(),
    expert_word: zLocalizedText.nullish(),
    kit_position: z.int().gte(1).lte(20).nullish()
});

/**
 * TastingCreateIn
 */
export const zTastingCreateIn = z.object({
    title: zLocalizedText,
    format: zTastingFormat,
    kind: zTastingKind,
    starts_at: z.iso.datetime({ offset: true }),
    timezone: z.string().max(64).nullish(),
    duration_min: z.int().gte(1).lte(1440),
    seats_max: z.int().gte(1).lte(100000),
    region_id: z.uuid().nullish(),
    host_id: z.uuid().nullish(),
    conference_url: z.string().max(500).nullish(),
    address: z.string().max(300).nullish(),
    venue_winery_id: z.uuid().nullish(),
    is_paid: z.boolean().optional().default(false),
    price: zMoney.nullish(),
    description: zLocalizedText.nullish(),
    details: z.array(zPairIn).nullish(),
    steps: z.array(zStepIn).nullish(),
    wines: z.array(zTastingWineIn).optional(),
    set_ids: z.array(z.uuid()).optional(),
    media: z.array(zEventMediaIn).optional()
});

/**
 * TastingWinesIn
 */
export const zTastingWinesIn = z.object({
    wines: z.array(zTastingWineIn).max(20).optional(),
    set_ids: z.array(z.uuid()).max(20).optional()
});

/**
 * TemplateCardOut
 */
export const zTemplateCardOut = z.object({
    code: z.string(),
    section: zNotificationSection,
    section_label: z.string(),
    name: z.string(),
    channels: zNotificationChannels,
    channels_label: z.string(),
    trigger_event: z.string(),
    trigger_label: z.string(),
    body: z.string(),
    audience: zNotificationAudience,
    audience_label: z.string(),
    preference_category: zPreferenceCategory,
    category_label: z.string(),
    inbox_kind: zInboxKind,
    is_enabled: z.boolean(),
    customized: z.boolean(),
    managed: z.boolean(),
    sent_30d: z.int(),
    can: zTemplateCan,
    texts_name: zLocalizedText,
    texts_body: zLocalizedText,
    variants: z.record(z.string(), zLocalizedText),
    placeholders: z.array(z.string()),
    email_layout: z.string().nullable(),
    deeplink: z.string().nullable(),
    feed_label: z.string().nullable(),
    customized_at: z.iso.datetime({ offset: true }).nullable(),
    stats: zTemplateStatsOut,
    history: z.array(zTemplateHistoryOut)
});

/**
 * TemplatePatchIn
 *
 * Правка текстов системного уведомления (момент отправки, каналы и получатели — релизом).
 * Тексты — на всех языках рынка; плейсхолдеры — только из текста релиза.
 */
export const zTemplatePatchIn = z.object({
    name: zLocalizedText.nullish(),
    body: zLocalizedText.nullish(),
    variants: z.record(z.string(), zLocalizedText).nullish()
});

/**
 * TestCreateIn
 */
export const zTestCreateIn = z.object({
    title: zLocalizedText,
    kind: zTestKind,
    country_id: z.uuid().nullish(),
    media: z.array(zMediaItemIn).max(10).optional()
});

/**
 * TestOptionIn
 */
export const zTestOptionIn = z.object({
    id: z.uuid().nullish(),
    text: zLocalizedText,
    is_correct: z.boolean().optional().default(false)
});

/**
 * QuestionIn
 */
export const zQuestionIn = z.object({
    id: z.uuid().nullish(),
    text: zLocalizedText,
    answer_type: zAnswerType.optional().default('single'),
    topic: zLocalizedText.nullish(),
    review_lesson_id: z.uuid().nullish(),
    options: z.array(zTestOptionIn).max(10)
});

/**
 * TestOptionOut
 */
export const zTestOptionOut = z.object({
    id: z.uuid(),
    position: z.int(),
    letter: z.string(),
    text: zLocalizedText.nullable(),
    is_correct: z.boolean()
});

/**
 * QuestionOut
 */
export const zQuestionOut = z.object({
    id: z.uuid(),
    position: z.int(),
    text: zLocalizedText.nullable(),
    answer_type: zAnswerType,
    answer_type_label: z.string(),
    topic: zLocalizedText.nullable(),
    review_lesson: zLearnRefOut.nullable(),
    options: z.array(zTestOptionOut),
    has_correct: z.boolean()
});

/**
 * QuestionsSavedOut
 */
export const zQuestionsSavedOut = z.object({
    questions: z.array(zQuestionOut),
    without_correct: z.int(),
    message: z.string()
});

/**
 * TestPatchIn
 */
export const zTestPatchIn = z.object({
    title: zLocalizedText.nullish(),
    country_id: z.uuid().nullish()
});

/**
 * TierCreateIn
 */
export const zTierCreateIn = z.object({
    title: zLocalizedText,
    image_file_id: z.uuid().nullish(),
    xp_min: z.int().gte(0),
    accuracy_min: z.union([
        z.number().gte(0).lte(100),
        z.string()
    ]),
    unlocks: zLocalizedText.nullish()
});

/**
 * TierPatchIn
 */
export const zTierPatchIn = z.object({
    title: zLocalizedText.nullish(),
    image_file_id: z.uuid().nullish(),
    xp_min: z.int().gte(0).nullish(),
    accuracy_min: z.union([
        z.number().gte(0).lte(100),
        z.string()
    ]).nullish(),
    unlocks: zLocalizedText.nullish()
});

/**
 * TierRowOut
 */
export const zTierRowOut = z.object({
    id: z.uuid(),
    rank: z.int(),
    roman: z.string(),
    name: zLocalizedText,
    title: zLocalizedText,
    title_label: z.string(),
    image_file_id: z.uuid().nullable(),
    image_url: z.string().nullable(),
    xp_min: z.int(),
    accuracy_min: z.number(),
    unlocks: zLocalizedText.nullable(),
    unlocks_label: z.string().nullable(),
    gates: z.array(zGateTitleOut),
    functions_label: z.string(),
    users_count: z.int(),
    is_first: z.boolean(),
    can: zGamRowCan
});

/**
 * TiersPageOut
 */
export const zTiersPageOut = z.object({
    items: z.array(zTierRowOut),
    total: z.int(),
    can: zListCan
});

/**
 * TopicOut
 */
export const zTopicOut = z.object({
    code: zSupportTopicCode,
    admin_name: zLocalizedText,
    app_name: zLocalizedText.nullable(),
    label: z.string(),
    needs_ref: z.boolean(),
    position: z.int()
});

/**
 * TranslationTilesOut
 *
 * Плитки карточки переводчика (17.12.6).
 */
export const zTranslationTilesOut = z.object({
    chars_30d: z.int(),
    queued: z.int(),
    failed: z.int(),
    untranslated_fields: z.int().nullable()
});

/**
 * TripCalcOut
 */
export const zTripCalcOut = z.object({
    adults_sum: zMoney,
    children: z.array(zChildCalcOut),
    single_supplement: zMoney,
    total: zMoney
});

/**
 * BookingOut
 */
export const zBookingOut = z.object({
    id: z.uuid(),
    number: z.string(),
    target_type: zBookingTarget,
    target_id: z.uuid(),
    booking_type: zBookingType,
    title: z.string(),
    winery: zRefOut.nullish(),
    slot_starts_at: z.iso.datetime({ offset: true }).nullable(),
    slot_ends_at: z.iso.datetime({ offset: true }).nullable(),
    timezone: z.string(),
    when_label: z.string(),
    adults: z.int(),
    sober_guests: z.int(),
    holder_name: z.string(),
    amount: zMoney,
    amount_label: z.string(),
    status: zBookingStatus,
    status_label: z.string(),
    hold_until: z.iso.datetime({ offset: true }).nullable(),
    waitlist_position: z.int().nullable(),
    cancel_policy_text: z.string(),
    paid_at: z.iso.datetime({ offset: true }).nullable(),
    refund_amount: zMoney.nullable(),
    order: zOrderBrief.nullable(),
    departure: zDepartureRef.nullish(),
    flexible_dates: z.boolean().optional().default(false),
    agreed_date_from: z.iso.date().nullish(),
    agreed_date_to: z.iso.date().nullish(),
    children: z.array(zChildAgeGroup).optional(),
    room_type: zRoomKind.nullish(),
    rooms_count: z.int().nullish(),
    note: z.string().nullish(),
    calc: zTripCalcOut.nullish(),
    amount_is_final: z.boolean().optional().default(true),
    decline_reason: z.string().nullish(),
    close_comment: z.string().nullish(),
    warnings: z.array(z.string()).optional(),
    can: zBookingCan
});

/**
 * BookingCreatedOut
 */
export const zBookingCreatedOut = z.object({
    booking: zBookingOut,
    order: zOrderBrief.nullable()
});

/**
 * TripCancelPolicy
 */
export const zTripCancelPolicy = z.enum(TripCancelPolicy);

/**
 * TripCard
 */
export const zTripCard = z.object({
    id: z.uuid(),
    title: z.string(),
    overline: z.string(),
    meta: z.string(),
    region: zRefOut.nullable(),
    duration_days: z.int(),
    price: zMoney,
    price_label: z.string(),
    cover_url: z.string().nullable(),
    group_size: z.int(),
    lifecycle: zEventLifecycle,
    nearest_departure: zDepartureApp.nullable(),
    attended: z.boolean().optional().default(false),
    status_chip: z.string().nullish()
});

/**
 * CursorPage[TripCard]
 */
export const zCursorPageTripCard = z.object({
    items: z.array(zTripCard),
    next_cursor: z.string().nullable()
});

/**
 * TripContentIn
 */
export const zTripContentIn = z.object({
    about: zLocalizedText.nullish(),
    program: z.array(zStepIn).nullish(),
    included: z.array(zPairIn).nullish(),
    how_to_book: z.array(zStepIn).nullish(),
    media: z.array(zEventMediaIn).nullish()
});

/**
 * TripDetail
 */
export const zTripDetail = z.object({
    id: z.uuid(),
    title: z.string(),
    overline: z.string(),
    meta: z.string(),
    region: zRefOut.nullable(),
    duration_days: z.int(),
    price: zMoney,
    price_label: z.string(),
    cover_url: z.string().nullable(),
    group_size: z.int(),
    lifecycle: zEventLifecycle,
    nearest_departure: zDepartureApp.nullable(),
    attended: z.boolean().optional().default(false),
    status_chip: z.string().nullish(),
    published: z.boolean(),
    about: z.string().nullable(),
    host: zHostOut.nullable(),
    wineries: z.array(zRefOut),
    hotel: z.string(),
    stay: z.array(zAppPair),
    rooms: z.array(zRoomApp),
    kids_label: z.string(),
    program: z.array(zAppStep),
    included: z.array(zAppPair),
    how_to_book: z.array(zAppStep),
    departures: z.array(zDepartureApp),
    media: z.array(zAppMedia),
    cancel_policy_text: z.string(),
    seats_label: z.string(),
    me: zMeOut.nullable()
});

/**
 * TripPatchIn
 */
export const zTripPatchIn = z.object({
    title: zLocalizedText.nullish(),
    region_id: z.uuid().nullish(),
    duration_days: z.int().gte(1).lte(60).nullish(),
    group_size: z.int().gte(1).lte(10000).nullish(),
    price: zMoney.nullish(),
    cancel_policy: zTripCancelPolicy.nullish(),
    host_id: z.uuid().nullish(),
    winery_ids: z.array(z.uuid()).max(20).nullish(),
    about: zLocalizedText.nullish()
});

/**
 * TripRequestIn
 */
export const zTripRequestIn = z.object({
    departure_id: z.uuid().nullish(),
    adults: z.int().gte(1).lte(100).optional().default(2),
    children: z.array(zChildAgeGroup).max(4).optional(),
    room_type: zRoomKind.optional().default('double'),
    note: z.string().max(1000).nullish()
});

/**
 * TripRequestsOut
 *
 * Заявки на поездку для «Моих заказов» (ORD 7.6.6, «Используемые API»).
 */
export const zTripRequestsOut = z.object({
    items: z.array(zBookingOut)
});

/**
 * TripRoomIn
 */
export const zTripRoomIn = z.object({
    type: zRoomKind,
    enabled: z.boolean().optional().default(true),
    count_per_departure: z.int().gte(1).lte(1000).nullish(),
    single_supplement_pct: z.int().gte(0).lte(100).nullish()
});

/**
 * StayIn
 */
export const zStayIn = z.object({
    hotel: zLocalizedText,
    stay: z.array(zPairIn).optional(),
    rooms: z.array(zTripRoomIn),
    kids_pct_under_6: z.int().gte(0),
    kids_pct_7_12: z.int().gte(0),
    kids_pct_13_17: z.int().gte(0)
});

/**
 * TripCreateIn
 */
export const zTripCreateIn = z.object({
    title: zLocalizedText,
    region_id: z.uuid(),
    duration_days: z.int().gte(1).lte(60),
    group_size: z.int().gte(1).lte(10000),
    price: zMoney,
    cancel_policy: zTripCancelPolicy.optional().default('free_14d_then_50'),
    host_id: z.uuid().nullish(),
    winery_ids: z.array(z.uuid()).max(20).optional(),
    about: zLocalizedText.nullish(),
    hotel: zLocalizedText,
    rooms: z.array(zTripRoomIn).nullish(),
    kids_pct_under_6: z.int().gte(0).optional().default(0),
    kids_pct_7_12: z.int().gte(0).optional().default(50),
    kids_pct_13_17: z.int().gte(0).optional().default(70),
    departures: z.array(zDepartureIn).max(100).optional(),
    stay: z.array(zPairIn).nullish(),
    program: z.array(zStepIn).nullish(),
    included: z.array(zPairIn).nullish(),
    how_to_book: z.array(zStepIn).nullish(),
    media: z.array(zEventMediaIn).optional()
});

/**
 * TripRoomOut
 */
export const zTripRoomOut = z.object({
    type: zRoomKind,
    type_label: z.string(),
    description: z.string(),
    capacity: z.int(),
    enabled: z.boolean(),
    count_per_departure: z.int().nullable(),
    single_supplement_pct: z.int().nullable(),
    label: z.string()
});

/**
 * TripAdmin
 */
export const zTripAdmin = z.object({
    id: z.uuid(),
    overline: z.string(),
    subtitle: z.string(),
    title: zLocalizedText,
    region: zRefOut.nullable(),
    duration_days: z.int(),
    duration_label: z.string(),
    group_size: z.int(),
    price: zMoney,
    price_label: z.string(),
    cancel_policy: zTripCancelPolicy,
    cancel_policy_label: z.string(),
    host: zHostOut.nullable(),
    wineries: z.array(zRefOut),
    about: zLocalizedText.nullable(),
    hotel: zLocalizedText,
    stay: z.array(zPairOut),
    rooms: z.array(zTripRoomOut),
    kids_pct_under_6: z.int(),
    kids_pct_7_12: z.int(),
    kids_pct_13_17: z.int(),
    kids_label: z.string(),
    departures: z.array(zDepartureOut),
    when_label: z.string(),
    program: z.array(zStepOut),
    included: z.array(zPairOut),
    how_to_book: z.array(zStepOut),
    media: z.array(zEventMediaOut),
    lifecycle: zEventLifecycle,
    lifecycle_label: z.string(),
    pending_requests: z.int(),
    summary: z.array(zExpTileOut),
    cancelled_at: z.iso.datetime({ offset: true }).nullable(),
    cancel_reason: z.string().nullable(),
    moderation: zModerationOut,
    archived_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: zEventArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
    can: zCardCan
});

/**
 * TripRow
 */
export const zTripRow = z.object({
    id: z.uuid(),
    title: zLocalizedText,
    title_text: z.string(),
    region: zRefOut.nullable(),
    duration_days: z.int(),
    duration_label: z.string(),
    when_label: z.string(),
    wineries_label: z.string(),
    host: zRefOut.nullable(),
    group_label: z.string(),
    price: zMoney,
    price_label: z.string(),
    lifecycle: zEventLifecycle,
    moderation_status: zModerationStatus,
    status_label: z.string(),
    is_live: z.boolean(),
    archived: z.boolean(),
    can: zRowCan
});

/**
 * Page[TripRow]
 */
export const zPageTripRow = z.object({
    items: z.array(zTripRow),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TripSeason
 */
export const zTripSeason = z.enum(TripSeason);

/**
 * TrySetOut
 */
export const zTrySetOut = z.object({
    id: z.uuid(),
    name: z.string(),
    over_label: z.string(),
    author_label: z.string(),
    price: zMoney,
    price_label: z.string()
});

/**
 * TryWineryOut
 */
export const zTryWineryOut = z.object({
    id: z.uuid(),
    name: z.string(),
    subtitle: z.string().nullable(),
    visit_from: zMoney.nullable()
});

/**
 * TstAppliedRowOut
 */
export const zTstAppliedRowOut = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string()
});

/**
 * TstAromaOut
 */
export const zTstAromaOut = z.object({
    up: z.int(),
    down: z.int(),
    total: z.int(),
    caption: z.string(),
    up_pct: z.int(),
    down_pct: z.int(),
    key: zAromaGroup,
    label: z.string()
});

/**
 * TstBadgeOut
 */
export const zTstBadgeOut = z.object({
    label: z.string(),
    tone: z.enum([
        'positive',
        'neutral',
        'gold',
        'negative'
    ])
});

/**
 * TstBlindEventOut
 */
export const zTstBlindEventOut = z.object({
    id: z.uuid(),
    title: z.string(),
    format: z.enum(['online', 'offline'])
});

/**
 * TstBlindIdentificationIn
 */
export const zTstBlindIdentificationIn = z.object({
    grape: z.string().min(1).max(120),
    country_id: z.uuid(),
    region_id: z.uuid().nullish(),
    climate: zBlindClimate.nullish()
});

/**
 * TstBlindAnswerIn
 */
export const zTstBlindAnswerIn = z.object({
    sensory: z.record(z.string(), z.int()),
    identification: zTstBlindIdentificationIn
});

/**
 * TstCalibrationArchiveIn
 */
export const zTstCalibrationArchiveIn = z.object({
    reason: zCalibrationArchiveReason,
    comment: z.string().max(300).nullish()
});

/**
 * TstCalibrationCan
 */
export const zTstCalibrationCan = z.object({
    update: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    change_expert: z.boolean().optional().default(false)
});

/**
 * TstCalibrationRowCan
 */
export const zTstCalibrationRowCan = z.object({
    update: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * TstCharOut
 */
export const zTstCharOut = z.object({
    up: z.int(),
    down: z.int(),
    total: z.int(),
    caption: z.string(),
    up_pct: z.int(),
    down_pct: z.int(),
    key: zTasteChar,
    label: z.string()
});

/**
 * TstCompareOut
 *
 * Строка «Вы и эксперт»: ваша отметка, эталон, точность параметра и чип.
 */
export const zTstCompareOut = z.object({
    key: zSensoryParam,
    label: z.string(),
    low: z.string(),
    high: z.string(),
    you: z.int(),
    expert: z.int(),
    accuracy: z.int(),
    caption: z.string(),
    chip_label: z.string(),
    tone: z.enum([
        'positive',
        'negative',
        'neutral'
    ])
});

/**
 * TstConfirmNameIn
 *
 * Подтверждение удаления вводом точного названия (как `ConfirmNameIn` CAT и GEO).
 */
export const zTstConfirmNameIn = z.object({
    confirm_name: z.string().min(1).max(300)
});

/**
 * TstDiaryHistoryOut
 */
export const zTstDiaryHistoryOut = z.object({
    occurred_at: z.iso.datetime({ offset: true }),
    text: z.string()
});

/**
 * TstDiaryWineBriefOut
 */
export const zTstDiaryWineBriefOut = z.object({
    id: z.uuid(),
    status: z.enum(['rated', 'unrated']),
    name: z.string().nullable(),
    title: z.string(),
    tastings_count: z.int()
});

/**
 * TstEvolutionOut
 */
export const zTstEvolutionOut = z.object({
    up: z.array(z.string()),
    down: z.array(z.string()),
    text: z.string()
});

/**
 * TstExpertOptionOut
 */
export const zTstExpertOptionOut = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * TstExpertOut
 */
export const zTstExpertOut = z.object({
    id: z.uuid(),
    name: z.string(),
    photo_url: z.string().nullable()
});

/**
 * TstFieldRowOut
 */
export const zTstFieldRowOut = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string()
});

/**
 * TstFileOut
 */
export const zTstFileOut = z.object({
    id: z.uuid(),
    url: z.string().nullable()
});

/**
 * TstHistoryItemOut
 */
export const zTstHistoryItemOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    action_label: z.string(),
    summary: z.string()
});

/**
 * TstIdentificationRowOut
 */
export const zTstIdentificationRowOut = z.object({
    key: zIdentificationField,
    label: z.string(),
    you: z.string(),
    right: z.string(),
    ok: z.boolean(),
    caption: z.string()
});

/**
 * TstImageOut
 */
export const zTstImageOut = z.object({
    id: z.uuid(),
    url: z.string().nullable(),
    name: z.string().nullable()
});

/**
 * TstLegendOut
 */
export const zTstLegendOut = z.object({
    kind: z.enum(['calibration', 'blind']),
    label: z.string()
});

/**
 * TstLevelIn
 */
export const zTstLevelIn = z.object({
    name: zLocalizedText,
    image_id: z.uuid().nullish(),
    min_points: z.union([
        z.number().gte(0),
        z.string()
    ]),
    min_successful_blind: z.int().gte(0).lte(1000).nullish(),
    unlocks_text: zLocalizedText.nullish()
});

/**
 * TstLevelOut
 */
export const zTstLevelOut = z.object({
    id: z.uuid(),
    name: z.string(),
    min_points: z.number(),
    min_points_label: z.string(),
    min_successful_blind: z.int(),
    image_url: z.string().nullable(),
    unlocks_text: z.string().nullable(),
    reached: z.boolean(),
    current: z.boolean(),
    status_label: z.string(),
    blind_label: z.string()
});

/**
 * TstLevelPatchIn
 */
export const zTstLevelPatchIn = z.object({
    name: zLocalizedText.nullish(),
    image_id: z.uuid().nullish(),
    min_points: z.union([
        z.number().gte(0),
        z.string()
    ]).nullish(),
    min_successful_blind: z.int().gte(0).lte(1000).nullish(),
    unlocks_text: zLocalizedText.nullish()
});

/**
 * TstLinkOut
 *
 * Переход в раздел админки или на экран приложения.
 */
export const zTstLinkOut = z.object({
    section: z.string(),
    id: z.uuid().nullish(),
    filters: z.record(z.string(), z.string()).optional()
});

/**
 * TstCalibrationLinksOut
 */
export const zTstCalibrationLinksOut = z.object({
    wine: zTstLinkOut,
    expert: zTstLinkOut,
    region: zTstLinkOut.nullable()
});

/**
 * TstDiaryPanelsOut
 */
export const zTstDiaryPanelsOut = z.object({
    entries_count: z.int(),
    entries_rated_label: z.string(),
    entries_link: zTstLinkOut,
    avg_score: z.number().nullable(),
    avg_label: z.string(),
    wine: zTstLinkOut.nullable(),
    wine_title: z.string().nullable(),
    user: zTstLinkOut,
    user_name: z.string()
});

/**
 * TstMyCalibrationOut
 */
export const zTstMyCalibrationOut = z.object({
    accuracy: z.int(),
    points: z.number()
});

/**
 * TstMyResultOut
 */
export const zTstMyResultOut = z.object({
    id: z.uuid(),
    kind: zAccuracyKind,
    title: z.string(),
    meta: z.string(),
    accuracy: z.int(),
    accuracy_label: z.string(),
    highlighted: z.boolean(),
    points: z.number(),
    points_label: z.string(),
    completed_at: z.iso.datetime({ offset: true }),
    wines: z.int(),
    target: z.string()
});

/**
 * TstMyResultsSummaryOut
 */
export const zTstMyResultsSummaryOut = z.object({
    count: z.int(),
    avg_accuracy: z.int().nullable(),
    points: z.number(),
    count_label: z.string(),
    avg_label: z.string(),
    points_label: z.string()
});

/**
 * TstMyResultsOut
 */
export const zTstMyResultsOut = z.object({
    summary: zTstMyResultsSummaryOut,
    items: z.array(zTstMyResultOut),
    next_cursor: z.string().nullable()
});

/**
 * TstNamedRef
 */
export const zTstNamedRef = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * TstAccuracyLogRowOut
 */
export const zTstAccuracyLogRowOut = z.object({
    id: z.uuid(),
    user: zTstNamedRef,
    points: z.number(),
    points_label: z.string(),
    kind: zAccuracyKind,
    kind_label: z.string(),
    for_label: z.string(),
    link: zTstLinkOut.nullable(),
    accuracy: z.int(),
    accuracy_label: z.string(),
    successful: z.boolean(),
    completed_at: z.iso.datetime({ offset: true })
});

/**
 * TstCalibrationRowOut
 */
export const zTstCalibrationRowOut = z.object({
    id: z.uuid(),
    number: z.int(),
    wine: zTstNamedRef,
    expert: zTstNamedRef,
    region: zTstNamedRef.nullable(),
    reference_label: z.string(),
    passed_count: z.int(),
    avg_accuracy: z.int().nullable(),
    avg_label: z.string(),
    status: zCalibrationStatus,
    status_label: z.string(),
    can: zTstCalibrationRowCan
});

/**
 * TstDiaryLogRowOut
 */
export const zTstDiaryLogRowOut = z.object({
    id: z.uuid(),
    kind: z.enum(['tasting', 'unrated']),
    user: zTstNamedRef,
    wine_id: z.uuid().nullable(),
    diary_wine_id: z.uuid(),
    wine_label: z.string(),
    score: z.number().nullable(),
    score_label: z.string(),
    taste_label: z.string(),
    aromas_label: z.string(),
    note: z.string().nullable(),
    recognized: z.boolean(),
    recognition_label: z.string(),
    occurred_at: z.iso.datetime({ offset: true })
});

/**
 * TstNextLevelOut
 */
export const zTstNextLevelOut = z.object({
    id: z.uuid(),
    name: z.string(),
    min_points: z.number(),
    min_successful_blind: z.int(),
    need_points: z.number(),
    need_successful_blind: z.int(),
    progress: z.number(),
    until_label: z.string(),
    need_blind_label: z.string().nullable()
});

/**
 * TstOptionOut
 */
export const zTstOptionOut = z.object({
    value: z.string(),
    label: z.string()
});

/**
 * TstBlindFieldOut
 */
export const zTstBlindFieldOut = z.object({
    key: zIdentificationField,
    label: z.string(),
    options: z.array(zTstOptionOut)
});

/**
 * TstPage[TstAccuracyLogRowOut]
 */
export const zTstPageTstAccuracyLogRowOut = z.object({
    items: z.array(zTstAccuracyLogRowOut),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TstPage[TstCalibrationRowOut]
 */
export const zTstPageTstCalibrationRowOut = z.object({
    items: z.array(zTstCalibrationRowOut),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TstPage[TstDiaryLogRowOut]
 */
export const zTstPageTstDiaryLogRowOut = z.object({
    items: z.array(zTstDiaryLogRowOut),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TstParamOut
 *
 * Параметр сенсорики и подписи полюсов шкалы 1–5 (10.3.1).
 */
export const zTstParamOut = z.object({
    key: zSensoryParam,
    label: z.string(),
    low: z.string(),
    high: z.string()
});

/**
 * TstBlindCurrentOut
 */
export const zTstBlindCurrentOut = z.object({
    position: z.int(),
    cover_number: z.int(),
    title: z.string(),
    params: z.array(zTstParamOut),
    identification: z.array(zTstBlindFieldOut)
});

/**
 * TstBlindFormOut
 */
export const zTstBlindFormOut = z.object({
    state: z.enum([
        'need_booking',
        'waiting',
        'open',
        'in_progress',
        'completed',
        'closed'
    ]),
    event: zTstBlindEventOut,
    opens_at: z.iso.datetime({ offset: true }).nullable(),
    closes_at: z.iso.datetime({ offset: true }).nullable(),
    wines_total: z.int(),
    wines_done: z.int(),
    current: zTstBlindCurrentOut.nullable()
});

/**
 * TstParamStatOut
 */
export const zTstParamStatOut = z.object({
    key: zSensoryParam,
    label: z.string(),
    accuracy: z.int(),
    accuracy_label: z.string(),
    shift: z.number(),
    caption: z.string()
});

/**
 * TstParamValueOut
 */
export const zTstParamValueOut = z.object({
    key: zSensoryParam,
    label: z.string(),
    low: z.string(),
    high: z.string(),
    value: z.number().nullable()
});

/**
 * TstProfileSummaryOut
 *
 * Экран итога записи (10.6.4): «Выводы».
 */
export const zTstProfileSummaryOut = z.object({
    tastings_count: z.int(),
    wines_count: z.int(),
    regions_count: z.int(),
    liked_top: z.array(z.string()),
    liked_text: z.string(),
    based_on: z.string()
});

/**
 * TstPurchasedWineOut
 */
export const zTstPurchasedWineOut = z.object({
    wine_id: z.uuid(),
    name: z.string(),
    winery: z.string().nullable(),
    region: z.string().nullable(),
    photo_url: z.string().nullable(),
    in_diary: z.boolean(),
    diary_wine_id: z.uuid().nullable()
});

/**
 * CursorPage[TstPurchasedWineOut]
 */
export const zCursorPageTstPurchasedWineOut = z.object({
    items: z.array(zTstPurchasedWineOut),
    next_cursor: z.string().nullable()
});

/**
 * TstRecentOut
 */
export const zTstRecentOut = z.object({
    kind: zAccuracyKind,
    accuracy: z.int(),
    points: z.number(),
    completed_at: z.iso.datetime({ offset: true })
});

/**
 * TstRecognitionBlockOut
 */
export const zTstRecognitionBlockOut = z.object({
    id: z.uuid(),
    caption: z.string(),
    status: z.string(),
    applied_caption: z.string(),
    applied: z.array(zTstAppliedRowOut),
    response: z.array(zTstAppliedRowOut),
    photo_url: z.string().nullable(),
    footer: z.string()
});

/**
 * TstRecognitionFeedbackIn
 */
export const zTstRecognitionFeedbackIn = z.object({
    verdict: z.enum(['accepted', 'wrong_wine'])
});

/**
 * TstReferenceIn
 *
 * Оценка эксперта по шести параметрам, целые 1–5; танины — только для красных, оранжевых и
 * креплёных вин (BR-TST-20).
 */
export const zTstReferenceIn = z.object({
    sweetness: z.int().gte(1).lte(5).nullish(),
    acidity: z.int().gte(1).lte(5).nullish(),
    body: z.int().gte(1).lte(5).nullish(),
    tannin: z.int().gte(1).lte(5).nullish(),
    aroma_intensity: z.int().gte(1).lte(5).nullish(),
    finish: z.int().gte(1).lte(5).nullish()
});

/**
 * TstBlindWineIn
 */
export const zTstBlindWineIn = z.object({
    position: z.int().gte(1).lte(20),
    cover_number: z.int().gte(1).lte(20).nullish(),
    wine_id: z.uuid(),
    reference: zTstReferenceIn,
    ref_grape: z.string().max(120).nullish(),
    ref_country_id: z.uuid().nullish(),
    ref_region_id: z.uuid().nullish(),
    ref_climate: zBlindClimate.nullish(),
    expert_word: zLocalizedText.nullish()
});

/**
 * TstBlindWinesIn
 */
export const zTstBlindWinesIn = z.object({
    items: z.array(zTstBlindWineIn).max(20)
});

/**
 * TstCalibrationCreateIn
 */
export const zTstCalibrationCreateIn = z.object({
    wine_id: z.uuid(),
    expert_id: z.uuid().nullish(),
    reference: zTstReferenceIn,
    expert_word: zLocalizedText.nullish()
});

/**
 * TstCalibrationPatchIn
 */
export const zTstCalibrationPatchIn = z.object({
    wine_id: z.uuid().nullish(),
    expert_id: z.uuid().nullish(),
    reference: zTstReferenceIn.nullish(),
    expert_word: zLocalizedText.nullish(),
    status: z.enum(['draft', 'published']).nullish(),
    service_comment: z.string().max(300).nullish()
});

/**
 * TstCalibrationReferenceIn
 *
 * Модалка «Эталон эксперта» (BR-TST-50).
 */
export const zTstCalibrationReferenceIn = z.object({
    wine_id: z.uuid(),
    reference: zTstReferenceIn
});

/**
 * TstRegionOptionOut
 */
export const zTstRegionOptionOut = z.object({
    id: z.uuid(),
    name: z.string(),
    country_id: z.uuid()
});

/**
 * TstRowCan
 */
export const zTstRowCan = z.object({
    update: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * TstLevelRowOut
 */
export const zTstLevelRowOut = z.object({
    id: z.uuid(),
    name: zLocalizedText,
    name_label: z.string(),
    image: zTstImageOut.nullable(),
    min_points: z.number(),
    min_points_label: z.string(),
    min_successful_blind: z.int().nullable(),
    min_successful_label: z.string(),
    unlocks_text: zLocalizedText.nullable(),
    unlocks_label: z.string().nullable(),
    users_count: z.int(),
    is_base: z.boolean(),
    can: zTstRowCan,
    delete_hint: z.string().nullable()
});

/**
 * TstPage[TstLevelRowOut]
 */
export const zTstPageTstLevelRowOut = z.object({
    items: z.array(zTstLevelRowOut),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TstScorePointOut
 */
export const zTstScorePointOut = z.object({
    tasted_at: z.iso.datetime({ offset: true }),
    score: z.number(),
    label: z.string()
});

/**
 * TstSkillOut
 */
export const zTstSkillOut = z.object({
    key: z.string(),
    label: z.string(),
    source: z.enum(['sensory', 'identification']),
    value: z.int()
});

/**
 * TstSpecIn
 */
export const zTstSpecIn = z.object({
    name: zLocalizedText,
    image_id: z.uuid().nullish(),
    category_kind: zSpecCategoryKind,
    category_value: z.string().min(1).max(40),
    condition_text: zLocalizedText.nullish(),
    status: zSpecStatus.optional().default('active')
});

/**
 * TstSpecItemOut
 */
export const zTstSpecItemOut = z.object({
    id: z.uuid(),
    name: z.string(),
    category_kind: zSpecCategoryKind,
    category_label: z.string(),
    image_url: z.string().nullable(),
    ok: z.int(),
    need: z.int(),
    window: z.int(),
    avg_accuracy: z.int().nullable(),
    awarded: z.boolean(),
    awarded_at: z.iso.datetime({ offset: true }).nullable(),
    value_label: z.string(),
    caption: z.string(),
    progress: z.number(),
    tier_note: z.string().nullable()
});

/**
 * TstSpecListOut
 */
export const zTstSpecListOut = z.object({
    items: z.array(zTstSpecItemOut)
});

/**
 * TstSpecLogRowOut
 */
export const zTstSpecLogRowOut = z.object({
    id: z.uuid(),
    user: zTstNamedRef,
    specialization: zTstNamedRef,
    category_kind: zSpecCategoryKind,
    category_label: z.string(),
    ok_count: z.int(),
    window_count: z.int(),
    avg_accuracy: z.int(),
    result_label: z.string(),
    result_link: zTstLinkOut,
    awarded_at: z.iso.datetime({ offset: true })
});

/**
 * TstPage[TstSpecLogRowOut]
 */
export const zTstPageTstSpecLogRowOut = z.object({
    items: z.array(zTstSpecLogRowOut),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TstSpecPatchIn
 */
export const zTstSpecPatchIn = z.object({
    name: zLocalizedText.nullish(),
    image_id: z.uuid().nullish(),
    category_kind: zSpecCategoryKind.nullish(),
    category_value: z.string().min(1).max(40).nullish(),
    condition_text: zLocalizedText.nullish(),
    status: zSpecStatus.nullish()
});

/**
 * TstSpecRowOut
 */
export const zTstSpecRowOut = z.object({
    id: z.uuid(),
    name: zLocalizedText,
    name_label: z.string(),
    image: zTstImageOut.nullable(),
    category_kind: zSpecCategoryKind,
    category_label: z.string(),
    category_value: z.string(),
    category_value_label: z.string(),
    condition_text: zLocalizedText.nullable(),
    condition_label: z.string().nullable(),
    status: zSpecStatus,
    status_label: z.string(),
    awarded_count: z.int(),
    in_progress_count: z.int(),
    can: zTstRowCan,
    delete_hint: z.string().nullable()
});

/**
 * TstPage[TstSpecRowOut]
 */
export const zTstPageTstSpecRowOut = z.object({
    items: z.array(zTstSpecRowOut),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * TstSpecTopOut
 */
export const zTstSpecTopOut = z.object({
    id: z.uuid(),
    name: z.string(),
    image_url: z.string().nullable(),
    ok: z.int(),
    need: z.int(),
    avg_accuracy: z.int().nullable(),
    awarded: z.boolean()
});

/**
 * TstAccuracySummaryOut
 */
export const zTstAccuracySummaryOut = z.object({
    points_total: z.number(),
    points_label: z.string(),
    progress_label: z.string(),
    successful_blind: z.int(),
    level: zTstLevelOut.nullable(),
    next_level: zTstNextLevelOut.nullable(),
    max_level_text: z.string().nullable(),
    current_accuracy: z.int().nullable(),
    current_label: z.string().nullable(),
    delta: z.int().nullable(),
    delta_label: z.string().nullable(),
    recent: z.array(zTstRecentOut),
    legend: z.array(zTstLegendOut),
    skills: z.array(zTstSkillOut),
    specializations_top: z.array(zTstSpecTopOut),
    levels: z.array(zTstLevelOut),
    levels_title: z.string()
});

/**
 * TstSpecWindowRowOut
 */
export const zTstSpecWindowRowOut = z.object({
    id: z.uuid(),
    wine: z.string(),
    completed_at: z.iso.datetime({ offset: true }),
    date_label: z.string(),
    kind: zAccuracyKind,
    kind_label: z.string(),
    country: z.string().nullable(),
    accuracy: z.int(),
    accuracy_label: z.string(),
    ok: z.boolean()
});

/**
 * TstSpecDetailOut
 */
export const zTstSpecDetailOut = z.object({
    id: z.uuid(),
    name: z.string(),
    category_kind: zSpecCategoryKind,
    category_label: z.string(),
    image_url: z.string().nullable(),
    ok: z.int(),
    need: z.int(),
    window: z.int(),
    avg_accuracy: z.int().nullable(),
    awarded: z.boolean(),
    awarded_at: z.iso.datetime({ offset: true }).nullable(),
    value_label: z.string(),
    caption: z.string(),
    progress: z.number(),
    tier_note: z.string().nullable(),
    condition_text: z.string().nullable(),
    condition_hint: z.string(),
    window_caption: z.string(),
    window_results: z.array(zTstSpecWindowRowOut),
    current_label: z.string().nullable(),
    find_label: z.string(),
    target: z.string()
});

/**
 * TstSummaryChangedOut
 */
export const zTstSummaryChangedOut = z.object({
    points_total: z.number(),
    level: z.string().nullable(),
    level_up: z.boolean()
});

/**
 * TstSummaryResultOut
 */
export const zTstSummaryResultOut = z.object({
    id: z.uuid(),
    kind: zAccuracyKind,
    title: z.string(),
    meta: z.string(),
    accuracy: z.int(),
    accuracy_label: z.string(),
    points_label: z.string(),
    completed_at: z.iso.datetime({ offset: true })
});

/**
 * TstSummaryRowOut
 */
export const zTstSummaryRowOut = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string()
});

/**
 * TstTasteCurrentOut
 */
export const zTstTasteCurrentOut = z.object({
    caption: z.string(),
    badge: z.string(),
    params: z.array(zTstParamValueOut),
    favorite_label: z.string().nullable()
});

/**
 * TstTasteReferenceOut
 *
 * Справочники форм разделов TST (API-03).
 */
export const zTstTasteReferenceOut = z.object({
    experts: z.array(zTstExpertOptionOut),
    params: z.array(zTstParamOut),
    wine_types: z.array(zTstOptionOut),
    calibration_statuses: z.array(zTstOptionOut),
    archive_reasons: z.array(zTstOptionOut),
    categories: z.array(zTstOptionOut),
    spec_statuses: z.array(zTstOptionOut),
    countries: z.array(zTstNamedRef),
    regions: z.array(zTstRegionOptionOut),
    climates: z.array(zTstOptionOut)
});

/**
 * TstTasteRowOut
 */
export const zTstTasteRowOut = z.object({
    caption: z.string(),
    badge: z.string(),
    params: z.array(zTstParamValueOut)
});

/**
 * TstDiaryTasteOut
 */
export const zTstDiaryTasteOut = z.object({
    weight_label: z.string(),
    entry: zTstTasteRowOut,
    current: zTstTasteCurrentOut.nullable(),
    footer: z.string()
});

/**
 * TstDiaryEntryCardOut
 */
export const zTstDiaryEntryCardOut = z.object({
    id: z.uuid(),
    kind: z.enum(['tasting', 'unrated']),
    title: z.string(),
    badge: zTstBadgeOut,
    subtitle: z.string(),
    source: zDiarySource,
    fields: z.array(zTstFieldRowOut),
    recognition: zTstRecognitionBlockOut.nullable(),
    taste: zTstDiaryTasteOut,
    history: z.array(zTstDiaryHistoryOut),
    panels: zTstDiaryPanelsOut
});

/**
 * TstTastingBriefOut
 */
export const zTstTastingBriefOut = z.object({
    id: z.uuid(),
    score: z.number(),
    tasted_at: z.iso.datetime({ offset: true })
});

/**
 * TstDiarySavedOut
 */
export const zTstDiarySavedOut = z.object({
    diary_wine: zTstDiaryWineBriefOut,
    tasting: zTstTastingBriefOut.nullable(),
    created: z.boolean(),
    xp_awarded: z.int().nullable(),
    xp_total: z.int().nullable(),
    profile_summary: zTstProfileSummaryOut
});

/**
 * TstTastingOut
 */
export const zTstTastingOut = z.object({
    id: z.uuid(),
    tasted_at: z.iso.datetime({ offset: true }),
    score: z.number(),
    score_label: z.string(),
    liked: z.boolean(),
    votes: z.record(z.string(), z.int()),
    aromas_liked: z.array(zAromaGroup),
    aromas_disliked: z.array(zAromaGroup),
    note: z.string().nullable()
});

/**
 * TstUserTasteSummaryOut
 */
export const zTstUserTasteSummaryOut = z.object({
    user_id: z.uuid(),
    rows: z.array(zTstSummaryRowOut),
    results: z.array(zTstSummaryResultOut),
    all_results: zTstLinkOut
});

/**
 * TstVotesIn
 *
 * «Нравится» (1) / «не нравится» (−1) / пропуск (0) по шести характеристикам (BR-TST-05).
 */
export const zTstVotesIn = z.object({
    sweetness: z.union([
        z.literal(-1),
        z.literal(0),
        z.literal(1)
    ]).optional().default(0),
    acidity: z.union([
        z.literal(-1),
        z.literal(0),
        z.literal(1)
    ]).optional().default(0),
    body: z.union([
        z.literal(-1),
        z.literal(0),
        z.literal(1)
    ]).optional().default(0),
    tannin: z.union([
        z.literal(-1),
        z.literal(0),
        z.literal(1)
    ]).optional().default(0),
    aroma: z.union([
        z.literal(-1),
        z.literal(0),
        z.literal(1)
    ]).optional().default(0),
    finish: z.union([
        z.literal(-1),
        z.literal(0),
        z.literal(1)
    ]).optional().default(0)
});

/**
 * TstTastingIn
 */
export const zTstTastingIn = z.object({
    score: z.union([
        z.number().gte(1).lte(5),
        z.string()
    ]),
    liked: z.boolean().optional().default(true),
    votes: zTstVotesIn.optional(),
    aromas_liked: z.array(zAromaGroup).max(11).optional(),
    aromas_disliked: z.array(zAromaGroup).max(11).optional(),
    note: z.string().max(1000).nullish()
});

/**
 * UnpublishIn
 */
export const zUnpublishIn = z.object({
    comment: z.string().max(600).nullish()
});

/**
 * UnpublishRegionIn
 */
export const zUnpublishRegionIn = z.object({
    reason: zHideReason,
    comment: z.string().max(300).nullish()
});

/**
 * UnreadCountOut
 */
export const zUnreadCountOut = z.object({
    count: z.int()
});

/**
 * UnsubscribeOut
 */
export const zUnsubscribeOut = z.object({
    status: z.enum(['unsubscribed', 'subscribed'])
});

/**
 * UpdatedByOut
 */
export const zUpdatedByOut = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * TierGateOut
 */
export const zTierGateOut = z.object({
    key: z.string(),
    title: z.string(),
    description: z.string(),
    owner_module: z.string(),
    min_tier: zGamGateTierOut.nullable(),
    users_allowed: z.int(),
    users_allowed_label: z.string(),
    updated_at: z.iso.datetime({ offset: true }).nullable(),
    updated_by: zUpdatedByOut.nullable(),
    configured: z.boolean(),
    toast: z.string().nullish()
});

/**
 * TierGatesOut
 */
export const zTierGatesOut = z.object({
    items: z.array(zTierGateOut),
    tiers: z.array(zTierOptionOut)
});

/**
 * UpdatesSeenIn
 */
export const zUpdatesSeenIn = z.object({
    country_code: z.string().length(2),
    section: zSeenSection
});

/**
 * UploadCreateIn
 */
export const zUploadCreateIn = z.object({
    purpose: z.string().max(40),
    content_type: z.string().max(100),
    size: z.int().gt(0)
});

/**
 * UploadTicketOut
 */
export const zUploadTicketOut = z.object({
    upload_id: z.uuid(),
    url: z.string(),
    method: z.string(),
    headers: z.record(z.string(), z.string()),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * UsageCountryOut
 */
export const zUsageCountryOut = z.object({
    country_id: z.uuid(),
    code: z.string(),
    title: z.string(),
    label: z.string()
});

/**
 * UsageCourseOut
 */
export const zUsageCourseOut = z.object({
    course_id: z.uuid(),
    title: z.string(),
    section: z.string(),
    label: z.string()
});

/**
 * UsageItemOut
 */
export const zUsageItemOut = z.object({
    kind: z.string(),
    label: z.string(),
    count: z.int(),
    detail: z.string().nullable()
});

/**
 * UsageLessonOut
 */
export const zUsageLessonOut = z.object({
    lesson_id: z.uuid(),
    title: z.string(),
    is_required: z.boolean(),
    label: z.string()
});

/**
 * TestUsageOut
 */
export const zTestUsageOut = z.object({
    lessons: z.array(zUsageLessonOut),
    courses: z.array(zUsageCourseOut),
    countries: z.array(zUsageCountryOut),
    level_check: z.string().nullable()
});

/**
 * TestOut
 */
export const zTestOut = z.object({
    id: z.uuid(),
    title: zLocalizedText.nullable(),
    kind: zTestKind,
    kind_label: z.string(),
    country: zLearnRefOut.nullable(),
    author: zAuthorOut,
    sample_size: z.int().nullable(),
    questions_count: z.int(),
    questions: z.array(zQuestionOut),
    without_correct: z.int(),
    topics: z.array(z.string()),
    media: z.array(zLearnMediaOut),
    usage: zTestUsageOut,
    moderation_note: z.string().nullable(),
    status: zContentStatus,
    status_label: z.string(),
    display_status: zContentStatus,
    display_status_label: z.string(),
    published_revision: zLearnRevisionOut.nullable(),
    pending_revision: zLearnRevisionOut.nullable(),
    has_unsubmitted_changes: z.boolean(),
    edits_hint: z.string().nullable(),
    first_published_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: zLearnArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    usages: z.array(zLearnUsageItemOut),
    can: zTestCan,
    message: z.string().nullish(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * UsageOut
 */
export const zUsageOut = z.object({
    items: z.array(zUsageItemOut),
    deletable: z.boolean()
});

/**
 * UserAccountOut
 */
export const zUserAccountOut = z.object({
    registration_method: zRegistrationMethod,
    registration_method_label: z.string(),
    email_verified_at: z.iso.datetime({ offset: true }).nullable(),
    identities: z.array(zLinkedIdentityOut),
    first_login_at: z.iso.datetime({ offset: true }).nullable(),
    last_activity_at: z.iso.datetime({ offset: true }).nullable(),
    devices: zDevicesSummaryOut,
    payment_methods: z.array(z.string()).nullable()
});

/**
 * UserActivityOut
 */
export const zUserActivityOut = z.object({
    groups: z.array(zActivityGroupOut)
});

/**
 * UserBlockIn
 */
export const zUserBlockIn = z.object({
    reason: z.string().max(300).nullish()
});

/**
 * UserCardCan
 */
export const zUserCardCan = z.object({
    update: z.boolean().optional().default(false),
    update_country: z.boolean().optional().default(false),
    update_locale: z.boolean().optional().default(false),
    block: z.boolean().optional().default(false),
    unblock: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * UserDeleteIn
 */
export const zUserDeleteIn = z.object({
    confirm_email: z.string().max(254),
    source: z.enum(['support', 'web']),
    ticket_number: z.int().gte(1).lte(2147483647).nullish()
});

/**
 * UserHistoryItem
 */
export const zUserHistoryItem = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    source_module: zActivityModule,
    event_type: z.string(),
    title: z.string(),
    details: z.string().nullable(),
    object_ref: z.record(z.string(), z.unknown()).nullable()
});

/**
 * CursorPage[UserHistoryItem]
 */
export const zCursorPageUserHistoryItem = z.object({
    items: z.array(zUserHistoryItem),
    next_cursor: z.string().nullable()
});

/**
 * UserMeLabels
 *
 * Подписи строк «Настроек» (4.6.12) на языке запроса.
 */
export const zUserMeLabels = z.object({
    profile: z.string(),
    email: z.string(),
    password: z.string(),
    location: z.string(),
    language: z.string()
});

/**
 * OAuthUserOut
 */
export const zOAuthUserOut = z.object({
    id: z.uuid(),
    email: z.string().nullable(),
    email_verified: z.boolean(),
    full_name: z.string().nullable(),
    initial: z.string().nullable(),
    birth_date: z.iso.date().nullable(),
    phone: zPhoneOut.nullable(),
    phone_display: z.string().nullable(),
    country_code: z.string().nullable(),
    city_name: z.string().nullable(),
    city_suggestion_id: z.uuid().nullable(),
    avatar_url: z.string().nullable(),
    avatar_thumb_url: z.string().nullable(),
    locale: zLanguage,
    currency: zCurrency,
    profile_complete: z.boolean(),
    missing: z.array(z.string()),
    has_password: z.boolean(),
    password_changed_at: z.iso.datetime({ offset: true }).nullable(),
    registration_method: z.string(),
    roles: z.array(z.string()),
    expert_mode: z.boolean(),
    expert_badge: z.boolean(),
    created_at: z.iso.datetime({ offset: true }),
    labels: zUserMeLabels,
    name: z.string().nullable()
});

/**
 * OAuthResultOut
 *
 * Результат входа через провайдера (17.18.1): `complete`, `handoff`, `poll`.
 *
 * `status = completed` и `session = null` — вход завершён в другом окне (BR-INT-SVC-12):
 * сессию заберёт приложение `poll`.
 */
export const zOAuthResultOut = z.object({
    status: zOAuthStateStatus,
    session: zOAuthSessionOut.nullable(),
    user: zOAuthUserOut.nullable(),
    is_new: z.boolean().nullable(),
    profile_completion_required: z.boolean().nullable(),
    missing: z.array(z.string()),
    linked_by_email: z.boolean().nullable(),
    prefill: zPrefillOut.nullable(),
    guest_merge: zGuestMergeOut.nullable(),
    return_route: z.string().nullable(),
    error_code: z.string().nullable()
});

/**
 * UserMeOut
 *
 * `UserMe` (пример 4.8.2.1) + поля для экранов профиля и настроек.
 */
export const zUserMeOut = z.object({
    id: z.uuid(),
    email: z.string().nullable(),
    email_verified: z.boolean(),
    full_name: z.string().nullable(),
    initial: z.string().nullable(),
    birth_date: z.iso.date().nullable(),
    phone: zPhoneOut.nullable(),
    phone_display: z.string().nullable(),
    country_code: z.string().nullable(),
    city_name: z.string().nullable(),
    city_suggestion_id: z.uuid().nullable(),
    avatar_url: z.string().nullable(),
    avatar_thumb_url: z.string().nullable(),
    locale: zLanguage,
    currency: zCurrency,
    profile_complete: z.boolean(),
    missing: z.array(z.string()),
    has_password: z.boolean(),
    password_changed_at: z.iso.datetime({ offset: true }).nullable(),
    registration_method: z.string(),
    roles: z.array(z.string()),
    expert_mode: z.boolean(),
    expert_badge: z.boolean(),
    created_at: z.iso.datetime({ offset: true }),
    labels: zUserMeLabels
});

/**
 * AuthOut
 */
export const zAuthOut = z.object({
    tokens: zTokensOut,
    user: zUserMeOut,
    guest_merge: zGuestMergeOut
});

/**
 * SocialAuthOut
 */
export const zSocialAuthOut = z.object({
    tokens: zTokensOut,
    user: zUserMeOut,
    guest_merge: zGuestMergeOut,
    profile_completion_required: z.boolean(),
    missing: z.array(z.string()),
    is_new: z.boolean(),
    linked_by_email: z.boolean(),
    prefill: zPrefillOut.nullable()
});

/**
 * SocialCompleteOut
 */
export const zSocialCompleteOut = z.object({
    user: zUserMeOut,
    account_switched: z.boolean(),
    tokens: zTokensOut.nullable()
});

/**
 * UserPatchIn
 *
 * `{full_name?, phone?, country_code?, city_name?, locale?}`. Отсутствующее поле не меняется;
 * `null` недопустим — поля обязательны для полного профиля (BR-ACC-12).
 */
export const zUserPatchIn = z.object({
    full_name: z.string().max(100).nullish(),
    phone: zPhoneIn.nullish(),
    country_code: z.string().regex(/^[A-Z]{2}$/).nullish(),
    city_name: z.string().max(80).nullish(),
    locale: zLanguage.nullish()
});

/**
 * UserRowCan
 */
export const zUserRowCan = z.object({
    view: z.boolean().optional().default(false),
    update: z.boolean().optional().default(false),
    block: z.boolean().optional().default(false),
    unblock: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false)
});

/**
 * UserStatus
 */
export const zUserStatus = z.enum(UserStatus);

/**
 * UserRow
 *
 * Строка реестра. Показатели модулей ORD/EXP/GAM/TST — null, пока модуль не подключён.
 */
export const zUserRow = z.object({
    id: z.uuid(),
    full_name: z.string().nullable(),
    email: z.string(),
    phone: zPhoneOut.nullable(),
    phone_display: z.string().nullable(),
    birth_date: z.iso.date().nullable(),
    country_code: z.string().nullable(),
    country_name: z.string().nullable(),
    city_name: z.string().nullable(),
    orders_count: z.int().nullable(),
    bookings_count: z.int().nullable(),
    orders_sum: zMoney.nullable(),
    passport_tier: z.string().nullable(),
    xp: z.int().nullable(),
    accuracy: z.number().nullable(),
    bonus_balance: z.int().nullable(),
    last_activity_at: z.iso.datetime({ offset: true }).nullable(),
    registered_at: z.iso.datetime({ offset: true }),
    status: zUserStatus,
    status_label: z.string(),
    team_member_id: z.uuid().nullable(),
    can: zUserRowCan
});

/**
 * Page[UserRow]
 */
export const zPageUserRow = z.object({
    items: z.array(zUserRow),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * UserSummaryOut
 */
export const zUserSummaryOut = z.object({
    user_id: z.uuid(),
    xp: z.int(),
    xp_max: z.int(),
    level: zSummaryLevelOut.nullable(),
    level_label: z.string().nullable(),
    held: z.boolean(),
    discounts: zSummaryDiscountsOut,
    first_region: zSummaryFirstRegionOut,
    bonuses: zSummaryBonusesOut,
    tier: zSummaryTierOut.nullable(),
    next_tier: zSummaryTierOut.nullable(),
    need: z.array(zSummaryNeedOut),
    accuracy_pct: z.number(),
    stamps: zSummaryStampsOut,
    stamps_label: z.string(),
    regions_in_work: z.int(),
    perfect_results: z.int(),
    links: zSummaryLinksOut
});

/**
 * VerificationOut
 */
export const zVerificationOut = z.object({
    verification_token: z.string(),
    expires_in: z.int()
});

/**
 * VideoAssetCan
 */
export const zVideoAssetCan = z.object({
    delete: z.boolean().optional().default(false),
    playback: z.boolean().optional().default(false)
});

/**
 * VideoClientOut
 */
export const zVideoClientOut = z.object({
    playback_rates: z.array(z.number())
});

/**
 * ClientIntegrationsOut
 *
 * Публичные параметры включённых интеграций (BR-INT-08): не секреты. Пока интеграция
 * выключена, не настроена или её адаптер ещё не подключён — значения `null`.
 */
export const zClientIntegrationsOut = z.object({
    auth_providers: z.array(zAuthProviderCode),
    google_client_ids: zGoogleClientIdsOut,
    vk_client_id: z.string().nullish(),
    yandex_client_id: z.string().nullish(),
    apple_services_id: z.string().nullish(),
    maps: zMapsClientOut,
    push: zPushClientOut,
    video: zVideoClientOut,
    recognition: zRecognitionClientOut,
    geo: zGeoClientOut,
    payments: zPaymentsClientOut
});

/**
 * VideoOut
 */
export const zVideoOut = z.object({
    available: z.boolean(),
    video_asset_id: z.uuid().nullable(),
    status: z.string().nullable(),
    status_label: z.string(),
    duration_sec: z.int().nullable(),
    poster_url: z.string().nullable()
});

/**
 * LessonOut
 */
export const zLessonOut = z.object({
    id: z.uuid(),
    title: zLocalizedText.nullable(),
    region: zLearnRefOut.nullable(),
    author: zAuthorOut,
    duration_min: z.int(),
    is_paid: z.boolean(),
    price: zMoney.nullable(),
    price_label: z.string(),
    in_intro: z.boolean(),
    store_products: zStoreProductsOut,
    body: zLocalizedText.nullable(),
    theses: z.array(zLocalizedText),
    media: z.array(zLearnMediaOut),
    related: zRelatedOut,
    tests: z.array(zLessonTestOut),
    test_rules_label: z.string(),
    courses: z.array(zLessonCourseOut),
    courses_hint: z.string().nullable(),
    video: zVideoOut,
    moderation_note: z.string().nullable(),
    status: zContentStatus,
    status_label: z.string(),
    display_status: zContentStatus,
    display_status_label: z.string(),
    published_revision: zLearnRevisionOut.nullable(),
    pending_revision: zLearnRevisionOut.nullable(),
    has_unsubmitted_changes: z.boolean(),
    edits_hint: z.string().nullable(),
    first_published_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: zLearnArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    usages: z.array(zLearnUsageItemOut),
    can: zLessonCan,
    message: z.string().nullish(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * VideoOwnerType
 */
export const zVideoOwnerType = z.enum(VideoOwnerType);

/**
 * VideoImportIn
 */
export const zVideoImportIn = z.object({
    owner_type: zVideoOwnerType,
    owner_id: z.uuid(),
    url: z.string().min(9).max(2000)
});

/**
 * VideoPreviewOut
 *
 * Предпросмотр в админке — та же подписанная ссылка, что в приложении (17.13.3).
 */
export const zVideoPreviewOut = z.object({
    video_asset_id: z.uuid(),
    hls_url: z.string(),
    expires_at: z.iso.datetime({ offset: true }),
    duration_sec: z.int().nullable(),
    thumbnail_url: z.string().nullable()
});

/**
 * VideoProgressIn
 */
export const zVideoProgressIn = z.object({
    video_asset_id: z.uuid(),
    position_sec: z.int().gte(0).lte(86400),
    watched_delta_sec: z.int().gte(0).lte(3600)
});

/**
 * VideoProgressOut
 */
export const zVideoProgressOut = z.object({
    position_sec: z.int(),
    completed: z.boolean()
});

/**
 * VideoProvider
 */
export const zVideoProvider = z.enum(VideoProvider);

/**
 * AdminVideoOut
 */
export const zAdminVideoOut = z.object({
    provider: zVideoProvider,
    max_upload_gb: z.int(),
    playback_rates: z.array(z.number())
});

/**
 * VideoSource
 */
export const zVideoSource = z.enum(VideoSource);

/**
 * VideoStatus
 */
export const zVideoStatus = z.enum(VideoStatus);

/**
 * VideoAssetOut
 */
export const zVideoAssetOut = z.object({
    id: z.uuid(),
    owner_type: zVideoOwnerType,
    owner_id: z.uuid(),
    status: zVideoStatus,
    status_label: z.string(),
    duration_sec: z.int().nullable(),
    duration_label: z.string().nullable(),
    max_resolution: z.string().nullable(),
    aspect_ratio: z.string().nullable(),
    poster_url: z.string().nullable(),
    error_message: z.string().nullable(),
    ready_at: z.iso.datetime({ offset: true }).nullable(),
    source: zVideoSource,
    source_file_name: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    replaced_by_id: z.uuid().nullable(),
    can: zVideoAssetCan
});

/**
 * VideoImportOut
 */
export const zVideoImportOut = z.object({
    video_id: z.uuid(),
    status: zVideoStatus,
    status_label: z.string()
});

/**
 * VideoTilesOut
 *
 * Плитки карточки видеохостинга (17.13.8).
 */
export const zVideoTilesOut = z.object({
    ready: z.int(),
    processing: z.int(),
    errored: z.int(),
    total_hours: z.number(),
    views_30d: z.int()
});

/**
 * VideoUploadIn
 */
export const zVideoUploadIn = z.object({
    owner_type: zVideoOwnerType,
    owner_id: z.uuid(),
    file_name: z.string().min(1).max(255),
    file_size: z.int().gt(0),
    mime: z.string().max(100)
});

/**
 * VideoUploadOut
 */
export const zVideoUploadOut = z.object({
    video_id: z.uuid(),
    upload_url: z.string(),
    protocol: z.string(),
    headers: z.record(z.string(), z.string()).nullish(),
    expires_at: z.iso.datetime({ offset: true })
});

/**
 * VisitFormatOut
 */
export const zVisitFormatOut = z.object({
    excursion_id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullable(),
    price: zMoney.nullable(),
    cover_url: z.string().nullable()
});

/**
 * VisitMode
 */
export const zVisitMode = z.enum(VisitMode);

/**
 * GuestsIn
 */
export const zGuestsIn = z.object({
    visit_mode: zVisitMode,
    group_capacity: z.int().gte(1).lte(1000).nullish(),
    open_hours: z.array(zOpenHoursIn).max(7).optional(),
    slots: z.array(zSlotIn).max(140).optional(),
    stay_enabled: z.boolean().optional().default(false),
    rooms: z.array(zRoomIn).max(50).optional(),
    booking_phone: z.string().max(30).nullish()
});

/**
 * GuestsOut
 */
export const zGuestsOut = z.object({
    visit_mode: zVisitMode,
    group_capacity: z.int().nullable(),
    open_hours: z.array(zOpenHoursOut),
    slots: z.array(zSlotOut),
    stay_enabled: z.boolean(),
    rooms: z.array(zRoomOut),
    booking_phone: z.string().nullable(),
    summary: zGuestsSummaryOut
});

/**
 * RegionWineryOut
 */
export const zRegionWineryOut = z.object({
    id: z.uuid(),
    name: z.string(),
    distance_km: z.int(),
    visit_mode: zVisitMode,
    teaser: z.string().nullable(),
    cover_url: z.string().nullable()
});

/**
 * VisitStepOut
 */
export const zVisitStepOut = z.object({
    done: z.boolean(),
    n: z.int(),
    has_options: z.boolean()
});

/**
 * RegionProgressOut
 */
export const zRegionProgressOut = z.object({
    region_id: z.uuid(),
    name: z.string(),
    country_code: z.string(),
    status: z.enum([
        'done',
        'exploring',
        'first_wine',
        'wish',
        'closed'
    ]),
    status_text: z.string(),
    pct: z.int(),
    study: zGamStepOut,
    taste: zTasteStepOut,
    visit: zVisitStepOut,
    stamp_level: z.enum(['bronze', 'gold']).nullable(),
    stamp_image_url: z.string().nullable(),
    xp_got: z.int(),
    xp_total: z.int(),
    target: z.string()
});

/**
 * RegionsOut
 */
export const zRegionsOut = z.object({
    items: z.array(zRegionProgressOut)
});

/**
 * WatchIn
 */
export const zWatchIn = z.object({
    scope: z.enum([
        'bottles',
        'sets',
        'drops'
    ]),
    region_id: z.uuid().nullish()
});

/**
 * WatchOut
 */
export const zWatchOut = z.object({
    scope: z.enum([
        'bottles',
        'sets',
        'drops'
    ]),
    region_id: z.uuid().nullable()
});

/**
 * WatchListOut
 */
export const zWatchListOut = z.object({
    items: z.array(zWatchOut)
});

/**
 * Weekday
 */
export const zWeekday = z.enum(Weekday);

/**
 * WeekDayOut
 */
export const zWeekDayOut = z.object({
    weekday: zWeekday,
    weekday_label: z.string(),
    open: z.boolean(),
    slots_label: z.string(),
    count_label: z.string()
});

/**
 * WeekSlotIn
 */
export const zWeekSlotIn = z.object({
    weekday: zWeekday,
    start_time: z.iso.time(),
    end_time: z.iso.time(),
    seats: z.int().gte(1).lte(100000).nullish()
});

/**
 * ExcursionCreateIn
 */
export const zExcursionCreateIn = z.object({
    title: zLocalizedText,
    winery_id: z.uuid(),
    duration_min: z.int().gte(1).lte(1440).nullish(),
    seats_per_slot: z.int().gte(1).lte(100000),
    price: zMoney,
    cancel_policy: zExcursionCancelPolicy.optional().default('free_48h'),
    schedule_mode: zScheduleMode.optional().default('once'),
    starts_at: z.iso.datetime({ offset: true }).nullish(),
    timezone: z.string().max(64).nullish(),
    min_notice: zMinNotice.nullish(),
    season_until: z.iso.date().nullish(),
    week_slots: z.array(zWeekSlotIn).max(100).optional(),
    description: zLocalizedText.nullish(),
    details: z.array(zPairIn).nullish(),
    steps: z.array(zStepIn).nullish(),
    route: z.array(zPairIn).nullish(),
    media: z.array(zEventMediaIn).optional()
});

/**
 * WeekSlotOut
 */
export const zWeekSlotOut = z.object({
    weekday: zWeekday,
    weekday_label: z.string(),
    start_time: z.iso.time(),
    end_time: z.iso.time(),
    seats: z.int().nullable()
});

/**
 * ExcursionAdmin
 */
export const zExcursionAdmin = z.object({
    id: z.uuid(),
    overline: z.string(),
    subtitle: z.string(),
    title: zLocalizedText,
    winery: zRefOut.nullable(),
    duration_min: z.int().nullable(),
    duration_label: z.string(),
    seats_per_slot: z.int(),
    price: zMoney,
    price_label: z.string(),
    cancel_policy: zExcursionCancelPolicy,
    cancel_policy_label: z.string(),
    schedule_mode: zScheduleMode,
    schedule_mode_label: z.string(),
    starts_at: z.iso.datetime({ offset: true }).nullable(),
    timezone: z.string(),
    date_label: z.string().nullable(),
    min_notice: zMinNotice.nullable(),
    min_notice_label: z.string().nullable(),
    season_until: z.iso.date().nullable(),
    week_slots: z.array(zWeekSlotOut),
    week_days: z.array(zWeekDayOut),
    seats_week_total: z.int(),
    booked_people: z.int(),
    description: zLocalizedText.nullable(),
    details: z.array(zPairOut),
    steps: z.array(zStepOut),
    route: z.array(zPairOut),
    route_defaults: z.array(zPairOut),
    media: z.array(zEventMediaOut),
    reviews: z.array(zExcursionReviewOut),
    rating_avg: z.number().nullable(),
    reviews_count: z.int(),
    summary: z.array(zExpTileOut),
    cancelled_at: z.iso.datetime({ offset: true }).nullable(),
    cancel_reason: z.string().nullable(),
    moderation: zModerationOut,
    archived_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: zEventArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
    can: zCardCan
});

/**
 * WeekSlotsIn
 */
export const zWeekSlotsIn = z.object({
    slots: z.array(zWeekSlotIn).max(100)
});

/**
 * WhereToTryOut
 */
export const zWhereToTryOut = z.object({
    sets: z.array(zTrySetOut),
    winery: zTryWineryOut.nullable()
});

/**
 * WinOut
 */
export const zWinOut = z.object({
    title: z.string(),
    subtitle: z.string(),
    go: z.string(),
    go_label: z.string()
});

/**
 * CompleteOut
 */
export const zCompleteOut = z.object({
    lesson_id: z.uuid(),
    completed_at: z.iso.datetime({ offset: true }),
    already_completed: z.boolean(),
    course_progress: zCourseProgressBriefOut.nullable(),
    section_completed: zSectionCompletedOut.nullable(),
    course_completed: z.boolean(),
    reward: zRewardOut.nullable(),
    win: zWinOut.nullable(),
    guest_first_lesson: z.boolean()
});

/**
 * LessonCompletedOut
 */
export const zLessonCompletedOut = z.object({
    lesson_id: z.uuid(),
    course_progress: zCourseProgressBriefOut.nullable(),
    section_completed: zSectionCompletedOut.nullable(),
    win: zWinOut.nullable()
});

/**
 * FinishOut
 */
export const zFinishOut = z.object({
    score: z.int(),
    total: z.int(),
    passed: z.boolean(),
    perfect: z.boolean(),
    first_perfect: z.boolean(),
    xp_awarded: z.int().nullable(),
    xp_already_received: z.boolean(),
    band: z.enum([
        'low',
        'mid',
        'high'
    ]).nullish(),
    title: z.string(),
    text: z.string().nullable(),
    xp_label: z.string().nullable(),
    recommendations: z.array(zRecommendationOut).nullish(),
    lesson_completed: zLessonCompletedOut.nullish(),
    primary: zLinkOut.nullable(),
    secondary: zLinkOut.nullable()
});

/**
 * WineCan
 */
export const zWineCan = z.object({
    update: z.boolean().optional().default(false),
    edit_location: z.boolean().optional().default(false),
    change_price: z.boolean().optional().default(false),
    edit_stock: z.boolean().optional().default(false),
    edit_media: z.boolean().optional().default(false),
    submit: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    return: z.boolean().optional().default(false),
    reject: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    attach: z.boolean().optional().default(false),
    awaiting_decision: z.boolean().optional().default(false),
    view_reviews: z.boolean().optional().default(false)
});

/**
 * WineColor
 */
export const zWineColor = z.enum(WineColor);

/**
 * WineDiscountOut
 */
export const zWineDiscountOut = z.object({
    pct: z.number(),
    applies_on_market: z.boolean()
});

/**
 * DiscountsOut
 */
export const zDiscountsOut = z.object({
    learning: zLearningDiscountOut,
    wine: zWineDiscountOut,
    first_region: zFirstRegionOut
});

/**
 * GamMeOut
 */
export const zGamMeOut = z.object({
    xp: z.int(),
    xp_max: z.int(),
    levels: z.array(zLevelOut),
    selected_level_id: z.uuid().nullable(),
    level: zMeLevelOut.nullable(),
    level_by_xp: zHeldLevelOut.nullable(),
    next_level: zNextLevelOut.nullable(),
    levels_subtitle: z.string(),
    discounts: zDiscountsOut,
    next_perk: zNextPerkOut,
    club: zClubOut,
    club_goal: zClubGoalOut.nullable(),
    club_goal_empty_text: z.string(),
    tier: zMeTierOut.nullable(),
    next_tier: zNextTierOut.nullable(),
    accuracy_pct: z.number(),
    gates: z.array(zGateStateOut),
    intro_completed: z.boolean(),
    intro_lessons_total: z.int(),
    intro_lessons_done: z.int(),
    coins_onboarding_seen: z.boolean(),
    level_bar: zLevelBarOut,
    exchange_rules: z.array(z.string()),
    club_level_id: z.uuid().nullable()
});

/**
 * WineKind
 */
export const zWineKind = z.enum(WineKind);

/**
 * WinePartnerStatus
 */
export const zWinePartnerStatus = z.enum(WinePartnerStatus);

/**
 * OrdWinePartnerIn
 */
export const zOrdWinePartnerIn = z.object({
    name: z.string().min(1).max(80),
    sells: z.string().min(1).max(300),
    receives: z.string().max(500).nullish(),
    reward: z.string().max(120).nullish(),
    status: zWinePartnerStatus
});

/**
 * OrdWinePartnerOut
 */
export const zOrdWinePartnerOut = z.object({
    id: z.uuid(),
    name: z.string(),
    sells: z.string(),
    link: z.string().nullable(),
    link_hint: z.string(),
    receives: z.string().nullable(),
    reward: z.string().nullable(),
    status: zWinePartnerStatus,
    status_pill: zOrdPill,
    orders_count: z.int(),
    can: zOrdWinePartnerCan
});

/**
 * OrdPage[OrdWinePartnerOut]
 */
export const zOrdPageOrdWinePartnerOut = z.object({
    items: z.array(zOrdWinePartnerOut),
    total: z.int(),
    can: zListCan.optional(),
    notice: z.string().nullish()
});

/**
 * OrdWinePartnerPatchIn
 */
export const zOrdWinePartnerPatchIn = z.object({
    name: z.string().min(1).max(80).nullish(),
    sells: z.string().min(1).max(300).nullish(),
    receives: z.string().max(500).nullish(),
    reward: z.string().max(120).nullish(),
    status: zWinePartnerStatus.nullish()
});

/**
 * WineRefApp
 */
export const zWineRefApp = z.object({
    id: z.uuid(),
    title: z.string()
});

/**
 * TastingWineApp
 *
 * Вино события. Вслепую до раскрытия — только номер (BR-EXP-22).
 */
export const zTastingWineApp = z.object({
    position: z.int(),
    label: z.string(),
    kit_position: z.int().nullish(),
    revealed: z.boolean().optional().default(true),
    wine: zWineRefApp.nullish(),
    grape: z.string().nullish(),
    country: z.string().nullish(),
    region: z.string().nullish(),
    expert_word: z.string().nullish()
});

/**
 * TastingDetail
 */
export const zTastingDetail = z.object({
    id: z.uuid(),
    title: z.string(),
    format: zTastingFormat,
    kind: zTastingKind,
    is_blind: z.boolean(),
    starts_at: z.iso.datetime({ offset: true }),
    timezone: z.string(),
    duration_min: z.int(),
    overline: z.string(),
    meta: z.string(),
    price: zMoney.nullable(),
    price_label: z.string(),
    seats_free: z.int(),
    seats_max: z.int(),
    seats_label: z.string(),
    is_full: z.boolean(),
    lifecycle: zEventLifecycle,
    region: zRefOut.nullable(),
    host: zHostOut.nullable(),
    cover_url: z.string().nullable(),
    attended: z.boolean().optional().default(false),
    status_chip: z.string().nullish(),
    published: z.boolean(),
    description: z.string().nullable(),
    caption: z.string(),
    price_unit_label: z.string().nullable(),
    access_label: z.string().nullable(),
    cancel_policy_text: z.string(),
    details: z.array(zAppPair),
    steps: z.array(zAppStep),
    media: z.array(zAppMedia),
    wines: z.array(zTastingWineApp),
    sets: z.array(zSetApp),
    kit: zKitOut.nullable(),
    venue: zRefOut.nullable(),
    address: z.string().nullable(),
    one_seat: z.boolean(),
    one_seat_label: z.string().nullable(),
    me: zMeOut.nullable()
});

/**
 * WineRefOut
 */
export const zWineRefOut = z.object({
    id: z.uuid(),
    title: z.string(),
    kind: z.string(),
    published: z.boolean()
});

/**
 * TastingWineOut
 */
export const zTastingWineOut = z.object({
    position: z.int(),
    wine: zWineRefOut.nullable(),
    ref_sweetness: z.int().nullable(),
    ref_acidity: z.int().nullable(),
    ref_body: z.int().nullable(),
    ref_tannin: z.int().nullable(),
    ref_aroma: z.int().nullable(),
    ref_finish: z.int().nullable(),
    ref_grape: z.string().nullable(),
    ref_country: zRefOut.nullable(),
    ref_region: zRefOut.nullable(),
    ref_climate: zRefClimate.nullable(),
    ref_climate_label: z.string().nullable(),
    expert_word: zLocalizedText.nullable(),
    kit_position: z.int().nullable(),
    reference_filled: z.boolean(),
    reference_label: z.string()
});

/**
 * TastingAdmin
 */
export const zTastingAdmin = z.object({
    id: z.uuid(),
    overline: z.string(),
    subtitle: z.string(),
    title: zLocalizedText,
    format: zTastingFormat,
    format_label: z.string(),
    kind: zTastingKind,
    kind_label: z.string(),
    starts_at: z.iso.datetime({ offset: true }),
    timezone: z.string(),
    date_label: z.string(),
    duration_min: z.int(),
    seats_max: z.int(),
    seats_taken: z.int(),
    seats_label: z.string(),
    waitlist_count: z.int(),
    waitlist_label: z.string().nullable(),
    region: zRefOut.nullable(),
    host: zHostOut.nullable(),
    conference_url: z.string().nullable(),
    address: z.string().nullable(),
    venue_winery: zRefOut.nullable(),
    place_label: z.string(),
    is_paid: z.boolean(),
    price: zMoney.nullable(),
    price_label: z.string(),
    access_label: z.string(),
    cancel_policy_text: z.string().nullable(),
    lifecycle: zEventLifecycle,
    lifecycle_label: z.string(),
    cancelled_at: z.iso.datetime({ offset: true }).nullable(),
    cancel_reason: z.string().nullable(),
    description: zLocalizedText.nullable(),
    details: z.array(zPairOut),
    steps: z.array(zStepOut),
    media: z.array(zEventMediaOut),
    wines: z.array(zTastingWineOut),
    sets: z.array(zSetRefOut),
    reference_filled: z.int(),
    reference_total: z.int(),
    reference_label: z.string().nullable(),
    summary: z.array(zExpTileOut),
    moderation: zModerationOut,
    archived_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: zEventArchiveReason.nullable(),
    archive_comment: z.string().nullable(),
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true }),
    can: zCardCan
});

/**
 * WineRowOut
 */
export const zWineRowOut = z.object({
    id: z.uuid(),
    sku: z.string(),
    name: z.string(),
    vintage_label: z.string(),
    type_label: z.string(),
    region: zRefOut.nullable(),
    winery: zRefOut.nullable(),
    price: zMoney,
    price_label: z.string(),
    available: z.int(),
    channels_label: z.string(),
    status: zCatalogStatus,
    display_status: zCatalogStatus,
    display_status_label: z.string(),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * CatalogPageOut[WineRowOut]
 */
export const zCatalogPageOutWineRowOut = z.object({
    items: z.array(zWineRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * WineSalesMode
 */
export const zWineSalesMode = z.enum(WineSalesMode);

/**
 * AdminCommerceOut
 */
export const zAdminCommerceOut = z.object({
    wine_sales_mode: zWineSalesMode,
    partner_code: z.string().nullable()
});

/**
 * AdminConfigOut
 */
export const zAdminConfigOut = z.object({
    market_code: z.string(),
    brand: zAdminBrandOut,
    languages: z.array(zLanguage),
    default_language: zLanguage,
    currency: zCurrency,
    timezone: z.string(),
    admin_timezones: z.array(z.string()),
    platforms: z.array(zClientPlatform),
    age_min: z.int(),
    commerce: zAdminCommerceOut,
    shipping: zAdminShippingOut,
    maps: zAdminMapsOut,
    geo: zAdminGeoOut,
    auth: zAdminAuthOut,
    payments: zAdminPaymentsOut,
    video: zAdminVideoOut,
    content: zAdminContentOut,
    catalog: zAdminCatalogOut,
    exp: zAdminExpOut,
    gam: zAdminGamOut,
    account: zAdminAccountOut,
    support: zAdminSupportOut,
    platform_launched_at: z.iso.date().nullable(),
    legal_documents_base_url: z.string(),
    web_base_url: z.string()
});

/**
 * AppCommerceOut
 */
export const zAppCommerceOut = z.object({
    wine_sales_mode: zWineSalesMode,
    partner_name: z.string().nullable()
});

/**
 * AppFeaturesOut
 *
 * ARCH-11 `features`: параметры, от которых строится интерфейс клиента.
 */
export const zAppFeaturesOut = z.object({
    wine_sales_mode: zWineSalesMode,
    shipping_carrier: zShippingCarrier,
    pickup_enabled: z.boolean(),
    recognition_enabled: z.boolean(),
    content_translation: z.boolean(),
    pwa_enabled: z.boolean(),
    learning_discount_channels: z.array(zClientPlatform),
    wine_discount_applies: z.boolean(),
    payment_methods: z.array(zPaymentMethod),
    digital_billing: z.record(z.string(), zDigitalBillingChannel)
});

/**
 * WineSalesOut
 */
export const zWineSalesOut = z.object({
    days: z.int(),
    published: z.boolean(),
    bottles: z.int(),
    orders_count: z.int(),
    revenue: zMoney,
    revenue_label: z.string(),
    avg_check: zMoney.nullable(),
    avg_check_label: z.string().nullable(),
    by_channel: z.array(zChannelSalesOut),
    price: zMoney,
    available: z.int()
});

/**
 * WineStyle
 */
export const zWineStyle = z.enum(WineStyle);

/**
 * WineSugar
 */
export const zWineSugar = z.enum(WineSugar);

/**
 * WinePatchIn
 *
 * Частичный WineCreate. Цена — `POST …/price-changes`, остаток — `PATCH …/stock`, медиа —
 * `/admin/catalog/wine/{id}/media` (здесь не принимаются: `unknown_field`).
 */
export const zWinePatchIn = z.object({
    name: z.string().nullish(),
    vintage: z.union([
        z.string(),
        z.int()
    ]).nullish(),
    winery_id: z.uuid().nullish(),
    region_id: z.uuid().nullish(),
    origin_classification_id: z.uuid().nullish(),
    kind: zWineKind.nullish(),
    sugar: zWineSugar.nullish(),
    color: zWineColor.nullish(),
    grapes: z.array(zGrapeShareIn).nullish(),
    abv: z.union([
        z.string(),
        z.number()
    ]).nullish(),
    aging: zLocalizedText.nullish(),
    serve_temp: zServeTemp.nullish(),
    decanting: zDecanting.nullish(),
    aging_potential: zAgingPotential.nullish(),
    pairing_categories: z.array(zPairingCategory).nullish(),
    pairing_custom: zLocalizedText.nullish(),
    sales_channels: z.array(zSalesChannel).nullish(),
    volume: zBottleVolume.nullish(),
    delivery_options: z.array(zDeliveryOption).nullish(),
    taste: zTastePatchIn.nullish(),
    notes: z.array(zLocalizedText).nullish(),
    description: zLocalizedText.nullish()
});

/**
 * WineType
 *
 * Тип вина — значения общие с CAT (`WineKind`).
 */
export const zWineType = z.enum(WineType);

/**
 * TstDiaryRowOut
 *
 * Строка дневника (10.6.2): вино без оценки или с оценкой.
 */
export const zTstDiaryRowOut = z.object({
    id: z.uuid(),
    status: z.enum(['rated', 'unrated']),
    wine_id: z.uuid().nullable(),
    title: z.string(),
    name: z.string().nullable(),
    vintage: z.string().nullable(),
    grape: z.string().nullable(),
    type: zWineType,
    type_label: z.string(),
    region: zTstNamedRef.nullable(),
    region_text: z.string().nullable(),
    overline: z.string().nullable(),
    meta: z.string(),
    photo_url: z.string().nullable(),
    illustration: z.enum(['red', 'white']),
    last_score: z.number().nullable(),
    score_label: z.string().nullable(),
    last_tasted_at: z.iso.datetime({ offset: true }).nullable(),
    saved_at: z.iso.datetime({ offset: true }),
    tastings_count: z.int(),
    history: z.array(zTstScorePointOut),
    history_label: z.string().nullable(),
    likes: z.string().nullable(),
    dislikes: z.string().nullable(),
    aromas: z.string().nullable(),
    action_label: z.string().nullable(),
    status_label: z.string().nullable(),
    target: z.string()
});

/**
 * TstDiaryTypeOut
 */
export const zTstDiaryTypeOut = z.object({
    value: zWineType,
    label: z.string()
});

/**
 * TstDiarySummaryOut
 */
export const zTstDiarySummaryOut = z.object({
    wines: z.int(),
    tastings: z.int(),
    regions: z.int(),
    types: z.array(zTstDiaryTypeOut),
    label: z.string()
});

/**
 * TstDiaryListOut
 */
export const zTstDiaryListOut = z.object({
    summary: zTstDiarySummaryOut,
    unrated: z.array(zTstDiaryRowOut),
    items: z.array(zTstDiaryRowOut),
    next_cursor: z.string().nullable()
});

/**
 * TstDiaryWineCreateIn
 */
export const zTstDiaryWineCreateIn = z.object({
    rated: z.boolean(),
    wine_id: z.uuid().nullish(),
    recognition_id: z.uuid().nullish(),
    label_photo_id: z.uuid().nullish(),
    name: z.string().max(200).nullish(),
    vintage: z.string().max(20).nullish(),
    grape: z.string().max(120).nullish(),
    region_id: z.uuid().nullish(),
    region_text: z.string().max(120).nullish(),
    type: zWineType.nullish(),
    tasting: zTstTastingIn.nullish()
});

/**
 * TstDiaryWinePatchIn
 *
 * «Сохранить изменения»: поля вина и последней дегустации (BR-TST-01).
 */
export const zTstDiaryWinePatchIn = z.object({
    name: z.string().max(200).nullish(),
    vintage: z.string().max(20).nullish(),
    grape: z.string().max(120).nullish(),
    region_id: z.uuid().nullish(),
    region_text: z.string().max(120).nullish(),
    type: zWineType.nullish(),
    label_photo_id: z.uuid().nullish(),
    score: z.union([
        z.number().gte(1).lte(5),
        z.string()
    ]).nullish(),
    liked: z.boolean().nullish(),
    votes: zTstVotesIn.nullish(),
    aromas_liked: z.array(zAromaGroup).max(11).nullish(),
    aromas_disliked: z.array(zAromaGroup).max(11).nullish(),
    note: z.string().max(1000).nullish()
});

/**
 * TstRecognitionApplyOut
 *
 * Что «Оценить» подставляет в форму (BR-TST-43).
 */
export const zTstRecognitionApplyOut = z.object({
    name: z.string().nullable(),
    grape: z.string().nullable(),
    type: zWineType.nullable(),
    region: zTstNamedRef.nullable(),
    region_text: z.string().nullable(),
    region_hint: z.string().nullable(),
    wine_id: z.uuid().nullable(),
    banner: z.string(),
    text: z.string()
});

/**
 * TstRecognitionResultOut
 */
export const zTstRecognitionResultOut = z.object({
    name: z.string().nullable(),
    producer: z.string().nullable(),
    vintage: z.int().nullable(),
    appellation: z.string().nullable(),
    country: z.string().nullable(),
    region_id: z.uuid().nullable(),
    type: zWineType.nullable(),
    type_label: z.string().nullable(),
    grapes: z.array(z.string()),
    abv: z.number().nullable(),
    aroma_taste: z.string().nullable(),
    food: z.string().nullable(),
    serve: z.string().nullable(),
    decant: z.string().nullable()
});

/**
 * TstTypeRefOut
 */
export const zTstTypeRefOut = z.object({
    code: zWineType,
    name: z.string()
});

/**
 * TstRecognitionCardOut
 *
 * Данные карточки вина каталога для листа «Распознано по фото» (D12).
 */
export const zTstRecognitionCardOut = z.object({
    name: z.string(),
    vintage: z.string().nullable(),
    winery: z.string().nullable(),
    winery_about: z.string().nullable(),
    region: zTstNamedRef.nullable(),
    type: zTstTypeRefOut.nullable(),
    grapes: z.array(z.string()),
    abv: z.number().nullable(),
    serve: z.string().nullable(),
    decant: z.string().nullable(),
    aroma: z.string().nullable(),
    taste: z.string().nullable(),
    food: z.string().nullable(),
    photo_url: z.string().nullable()
});

/**
 * TstRecognitionMatchOut
 */
export const zTstRecognitionMatchOut = z.object({
    wine_id: z.uuid(),
    score: z.number(),
    card: zTstRecognitionCardOut
});

/**
 * TstRecognitionOut
 */
export const zTstRecognitionOut = z.object({
    id: z.uuid(),
    status: zRecognitionStatus,
    lang: z.string(),
    translation_status: zRecognitionTranslation,
    translation_note: z.string().nullable(),
    match: zTstRecognitionMatchOut.nullable(),
    result: zTstRecognitionResultOut.nullable(),
    apply: zTstRecognitionApplyOut.nullable(),
    message: z.string().nullable()
});

/**
 * TstWineOptionOut
 */
export const zTstWineOptionOut = z.object({
    id: z.uuid(),
    title: z.string(),
    region: zTstNamedRef.nullable(),
    type: zWineType.nullable(),
    type_label: z.string().nullable(),
    tannin_applicable: z.boolean()
});

/**
 * TstWineOptionsOut
 */
export const zTstWineOptionsOut = z.object({
    items: z.array(zTstWineOptionOut)
});

/**
 * TstWineOut
 *
 * Карточка вина каталога для экранов TST: фото, «<винодельня> · <регион>», сорта.
 */
export const zTstWineOut = z.object({
    id: z.uuid(),
    name: z.string(),
    vintage: z.string().nullable(),
    title: z.string(),
    photo_url: z.string().nullable(),
    winery: zTstNamedRef.nullable(),
    region: zTstNamedRef.nullable(),
    place: z.string().nullable(),
    grapes: z.string().nullable(),
    type: zWineType.nullable(),
    type_label: z.string().nullable(),
    published: z.boolean()
});

/**
 * TstBlindRevealOut
 */
export const zTstBlindRevealOut = z.object({
    position: z.int(),
    cover_number: z.int(),
    title: z.string(),
    wine: zTstWineOut.nullable(),
    accuracy: z.int(),
    accuracy_label: z.string(),
    sensory: z.int(),
    identification: z.int(),
    stats_label: z.string(),
    points: z.number(),
    points_label: z.string(),
    why_points: z.string(),
    param_accuracy: z.record(z.string(), z.int()),
    identification_match: z.record(z.string(), z.boolean()),
    identification_rows: z.array(zTstIdentificationRowOut),
    params: z.array(zTstCompareOut),
    reference: z.record(z.string(), z.int().nullable()),
    expert_word: z.string().nullable(),
    expert: zTstExpertOut.nullable(),
    submitted_at: z.iso.datetime({ offset: true }),
    is_last: z.boolean(),
    next_position: z.int().nullable()
});

/**
 * TstBlindWineAdminOut
 */
export const zTstBlindWineAdminOut = z.object({
    position: z.int(),
    cover_number: z.int(),
    wine: zTstWineOut.nullable(),
    reference: z.record(z.string(), z.int().nullable()),
    params: z.array(zTstParamValueOut),
    ref_grape: z.string().nullable(),
    ref_country: zTstNamedRef.nullable(),
    ref_region: zTstNamedRef.nullable(),
    ref_climate: zBlindClimate.nullable(),
    ref_climate_label: z.string().nullable(),
    identification_label: z.string(),
    expert_word: zLocalizedText.nullable(),
    reference_filled: z.boolean(),
    reference_label: z.string(),
    answers: z.int(),
    locked: z.boolean()
});

/**
 * TstBlindWineResultOut
 */
export const zTstBlindWineResultOut = z.object({
    position: z.int(),
    cover_number: z.int(),
    title: z.string(),
    wine: zTstWineOut.nullable(),
    accuracy: z.int(),
    accuracy_label: z.string(),
    sensory: z.int(),
    identification: z.int(),
    stats_label: z.string(),
    points: z.number(),
    points_label: z.string(),
    why_points: z.string(),
    param_accuracy: z.record(z.string(), z.int()),
    identification_match: z.record(z.string(), z.boolean()),
    identification_rows: z.array(zTstIdentificationRowOut),
    params: z.array(zTstCompareOut),
    reference: z.record(z.string(), z.int().nullable()),
    expert_word: z.string().nullable(),
    expert: zTstExpertOut.nullable(),
    submitted_at: z.iso.datetime({ offset: true })
});

/**
 * TstBlindResultOut
 */
export const zTstBlindResultOut = z.object({
    event: zTstBlindEventOut,
    wines: z.array(zTstBlindWineResultOut),
    accuracy: z.int().nullable(),
    points_total: z.number(),
    points_label: z.string(),
    wines_done: z.int(),
    wines_total: z.int(),
    completed: z.boolean(),
    rated_label: z.string(),
    sum_caption: z.string()
});

/**
 * TstBlindWinesOut
 */
export const zTstBlindWinesOut = z.object({
    tasting_id: z.uuid(),
    title: z.string(),
    format: z.enum(['online', 'offline']),
    items: z.array(zTstBlindWineAdminOut),
    summary_label: z.string(),
    started: z.boolean(),
    can_update: z.boolean()
});

/**
 * TstCalibrationAdminOut
 */
export const zTstCalibrationAdminOut = z.object({
    id: z.uuid(),
    wine: zTstWineOut.nullable(),
    wine_id: z.uuid(),
    expert: zTstNamedRef,
    region: zTstNamedRef.nullable(),
    region_manual: z.boolean(),
    reference: z.record(z.string(), z.int().nullable()),
    reference_filled: z.boolean(),
    reference_label: z.string(),
    params: z.array(zTstParamValueOut),
    expert_word: zLocalizedText.nullable(),
    status: zCalibrationStatus,
    status_label: z.string(),
    archive_reason: zCalibrationArchiveReason.nullable(),
    archive_reason_label: z.string().nullable(),
    archive_comment: z.string().nullable(),
    service_comment: z.string().nullable(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    passed_count: z.int(),
    avg_accuracy: z.int().nullable(),
    avg_label: z.string(),
    param_stats: z.array(zTstParamStatOut),
    param_stats_empty: z.string().nullable(),
    history: z.array(zTstHistoryItemOut),
    links: zTstCalibrationLinksOut,
    can: zTstCalibrationCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * TstCalibrationFormOut
 */
export const zTstCalibrationFormOut = z.object({
    id: z.uuid(),
    wine: zTstWineOut,
    expert: zTstExpertOut,
    params: z.array(zTstParamOut),
    params_count: z.int(),
    how_text: z.string(),
    passed: z.boolean(),
    passed_label: z.string().nullable(),
    result_accuracy: z.int().nullable()
});

/**
 * TstCalibrationItemOut
 */
export const zTstCalibrationItemOut = z.object({
    id: z.uuid(),
    wine: zTstWineOut,
    expert: zTstExpertOut,
    region_id: z.uuid(),
    overline: z.string(),
    passed: z.boolean(),
    my_result: zTstMyCalibrationOut.nullable(),
    value_label: z.string(),
    caption: z.string(),
    target: z.string()
});

/**
 * TstCalibrationListOut
 */
export const zTstCalibrationListOut = z.object({
    items: z.array(zTstCalibrationItemOut)
});

/**
 * TstCalibrationResultOut
 */
export const zTstCalibrationResultOut = z.object({
    calibration_id: z.uuid(),
    wine: zTstWineOut,
    accuracy: z.int(),
    accuracy_label: z.string(),
    points: z.number(),
    points_label: z.string(),
    param_accuracy: z.record(z.string(), z.int()),
    answers: z.record(z.string(), z.int()),
    reference: z.record(z.string(), z.int()),
    params: z.array(zTstCompareOut),
    expert: zTstExpertOut,
    expert_word: z.string().nullable(),
    photo_url: z.string().nullable(),
    submitted_at: z.iso.datetime({ offset: true }),
    summary_changed: zTstSummaryChangedOut
});

/**
 * TstDiaryWineDetailOut
 *
 * Запись для режимов `rate` / `view` (10.6.3).
 */
export const zTstDiaryWineDetailOut = z.object({
    id: z.uuid(),
    status: z.enum(['rated', 'unrated']),
    wine_id: z.uuid().nullable(),
    recognition_id: z.uuid().nullable(),
    name: z.string().nullable(),
    title: z.string(),
    vintage: z.string().nullable(),
    grape: z.string().nullable(),
    region: zTstNamedRef.nullable(),
    region_text: z.string().nullable(),
    region_hint: z.string(),
    type: zWineType,
    type_label: z.string(),
    tannin_applicable: z.boolean(),
    label_photo: zTstFileOut.nullable(),
    source: zDiarySource,
    saved_at: z.iso.datetime({ offset: true }),
    last_tasting: zTstTastingOut.nullable(),
    history: z.array(zTstScorePointOut),
    wine: zTstWineOut.nullable()
});

/**
 * WineryCan
 */
export const zWineryCan = z.object({
    update: z.boolean().optional().default(false),
    edit_guests: z.boolean().optional().default(false),
    submit: z.boolean().optional().default(false),
    publish: z.boolean().optional().default(false),
    return: z.boolean().optional().default(false),
    reject: z.boolean().optional().default(false),
    unpublish: z.boolean().optional().default(false),
    archive: z.boolean().optional().default(false),
    restore: z.boolean().optional().default(false),
    delete: z.boolean().optional().default(false),
    attach: z.boolean().optional().default(false),
    awaiting_decision: z.boolean().optional().default(false)
});

/**
 * WineryOptionOut
 */
export const zWineryOptionOut = z.object({
    id: z.uuid(),
    name: z.string(),
    region_id: z.uuid()
});

/**
 * ReferenceOut
 */
export const zReferenceOut = z.object({
    grapes: z.array(zGrapeOptionOut),
    pairings: z.array(zOptionOut),
    colors: z.array(zOptionOut),
    serve_temps: z.array(zOptionOut),
    decantings: z.array(zOptionOut),
    potentials: z.array(zOptionOut),
    volumes: z.array(zOptionOut),
    channels: z.array(zOptionOut),
    delivery_options: z.array(zOptionOut),
    origin_classifications: z.array(zOriginOptionOut),
    early_access_tier: zTierOut.nullable(),
    kinds: z.array(zOptionOut),
    sugars: z.array(zOptionOut),
    taste_axes: z.array(zOptionOut),
    archive_reasons: z.array(zOptionOut),
    portions: z.array(zOptionOut),
    set_formats: z.array(zOptionOut),
    media_types: z.array(zOptionOut),
    media_placements: z.array(zOptionOut),
    timezones: z.array(z.string()),
    default_timezone: z.string(),
    currency: zCurrency,
    channel_hint: z.string(),
    wineries: z.array(zWineryOptionOut),
    regions: z.array(zRegionOptionOut),
    experts: z.array(zExpertOptionOut),
    tastings: z.array(zTastingRefOut),
    courses: z.array(zCourseOptionOut),
    lessons: z.array(zLessonOptionOut)
});

/**
 * WineryPatchIn
 */
export const zWineryPatchIn = z.object({
    name: zLocalizedText.nullish(),
    region_id: z.uuid().nullish(),
    visit_mode: zVisitMode.nullish(),
    group_capacity: z.int().gte(1).lte(1000).nullish(),
    address: zLocalizedText.nullish(),
    location: zGeoPointIn.nullish(),
    site_url: z.string().max(500).nullish(),
    description: zLocalizedText.nullish(),
    road: zLocalizedText.nullish(),
    transfer: zLocalizedText.nullish(),
    founded_year: z.int().nullish(),
    vineyard_area_ha: z.union([
        z.number(),
        z.string()
    ]).nullish(),
    grape_ids: z.array(z.uuid()).nullish(),
    booking_phone: z.string().max(30).nullish(),
    stay_enabled: z.boolean().nullish()
});

/**
 * WineryPinOut
 */
export const zWineryPinOut = z.object({
    id: z.uuid(),
    name: z.string(),
    location: zGeoPointOut,
    distance_km: z.int(),
    visit_mode: zVisitMode,
    rating: zRatingOut.nullable(),
    visit_from: zMoney.nullable(),
    teaser: z.string().nullable()
});

/**
 * RegionPointsOut
 */
export const zRegionPointsOut = z.object({
    region: zPointsRegionOut,
    wineries: z.array(zWineryPinOut),
    tastings: z.array(z.record(z.string(), z.unknown())),
    excursions: z.array(z.record(z.string(), z.unknown())),
    bounds: z.record(z.string(), z.record(z.string(), z.number())).nullable()
});

/**
 * WineryRatingOut
 */
export const zWineryRatingOut = z.object({
    avg: z.number(),
    count: z.int()
});

/**
 * WineryRegionOut
 */
export const zWineryRegionOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    status: zRegionStatus
});

/**
 * WineryStatus
 */
export const zWineryStatus = z.enum(WineryStatus);

/**
 * WorldProgressOut
 */
export const zWorldProgressOut = z.object({
    region_id: z.uuid(),
    name: z.string(),
    status: z.string(),
    status_text: z.string(),
    pct: z.int(),
    target: z.string()
});

/**
 * WorldTileOut
 */
export const zWorldTileOut = z.object({
    key: z.string(),
    value: z.int(),
    label: z.string(),
    caption: z.string(),
    target: z.string()
});

/**
 * WorldTilesOut
 */
export const zWorldTilesOut = z.object({
    wines: z.int(),
    tastings: z.int(),
    regions_done: z.int(),
    regions_exploring: z.int(),
    visits: z.int(),
    saved_wineries: z.int(),
    lessons: z.int(),
    intro_lessons: z.int(),
    subscriptions: z.int(),
    experiences: z.int()
});

/**
 * WorldOut
 */
export const zWorldOut = z.object({
    regions_in_work: z.int(),
    regions_total: z.int(),
    header: z.string(),
    tiles: zWorldTilesOut,
    tiles_view: z.array(zWorldTileOut),
    progress: z.array(zWorldProgressOut)
});

/**
 * WorldTitleOut
 */
export const zWorldTitleOut = z.object({
    overline: z.string(),
    title: z.string()
});

/**
 * FeedOut
 */
export const zFeedOut = z.object({
    stories: z.array(zStoryRingOut).nullable(),
    recommendations: zRecommendationsOut.nullable(),
    world_title: zWorldTitleOut.nullable(),
    items: z.array(zFeedCardOut),
    next_cursor: z.string().nullable()
});

/**
 * XpEventType
 */
export const zXpEventType = z.enum(XpEventType);

/**
 * XpLedgerRowOut
 */
export const zXpLedgerRowOut = z.object({
    id: z.uuid(),
    user: zGamUserRefOut,
    amount: z.int(),
    amount_label: z.string(),
    kind: z.enum(['accrual', 'reversal']),
    title: z.string(),
    object: zLedgerObjectOut,
    event_type: z.string(),
    event_label: z.string(),
    section: z.string(),
    section_label: z.string(),
    occurred_at: z.iso.datetime({ offset: true }),
    when_label: z.string()
});

/**
 * XpLedgerPageOut
 */
export const zXpLedgerPageOut = z.object({
    items: z.array(zXpLedgerRowOut),
    total: z.int(),
    can: zListCan
});

/**
 * XpLevelCreateIn
 */
export const zXpLevelCreateIn = z.object({
    name: zLocalizedText,
    subtitle: zLocalizedText.nullish(),
    image_file_id: z.uuid().nullish(),
    min_xp: z.int().gte(0),
    perks: zLocalizedText.nullish(),
    learning_discount_pct: z.union([
        z.number().gte(0).lte(50),
        z.string()
    ]).nullish(),
    wine_discount_pct: z.union([
        z.number().gte(0).lte(50),
        z.string()
    ]).nullish(),
    one_time_reward_type: zOneTimeRewardType.optional().default('none'),
    one_time_reward_value: z.int().nullish(),
    one_time_reward_text: zLocalizedText.nullish()
});

/**
 * XpLevelOut
 */
export const zXpLevelOut = z.object({
    id: z.uuid(),
    name: zLocalizedText,
    name_label: z.string(),
    subtitle: zLocalizedText.nullable(),
    image_file_id: z.uuid().nullable(),
    image_url: z.string().nullable(),
    min_xp: z.int(),
    perks: zLocalizedText.nullable(),
    perks_list: z.array(z.string()),
    learning_discount_pct: z.number().nullable(),
    wine_discount_pct: z.number().nullable(),
    learning_label: z.string(),
    wine_label: z.string(),
    one_time_reward_type: zOneTimeRewardType,
    one_time_reward_type_label: z.string(),
    one_time_reward_value: z.int().nullable(),
    one_time_reward_text: zLocalizedText.nullable(),
    one_time_reward_label: z.string(),
    users_count: z.int(),
    is_start: z.boolean(),
    can: zGamRowCan
});

/**
 * XpLevelPatchIn
 */
export const zXpLevelPatchIn = z.object({
    name: zLocalizedText.nullish(),
    subtitle: zLocalizedText.nullish(),
    image_file_id: z.uuid().nullish(),
    min_xp: z.int().gte(0).nullish(),
    perks: zLocalizedText.nullish(),
    learning_discount_pct: z.union([
        z.number().gte(0).lte(50),
        z.string()
    ]).nullish(),
    wine_discount_pct: z.union([
        z.number().gte(0).lte(50),
        z.string()
    ]).nullish(),
    one_time_reward_type: zOneTimeRewardType.nullish(),
    one_time_reward_value: z.int().nullish(),
    one_time_reward_text: zLocalizedText.nullish()
});

/**
 * XpLevelsPageOut
 */
export const zXpLevelsPageOut = z.object({
    items: z.array(zXpLevelOut),
    total: z.int(),
    intro_lessons_total: z.int(),
    can: zListCan
});

/**
 * XpPairOut
 */
export const zXpPairOut = z.object({
    got: z.int(),
    total: z.int(),
    label: z.string()
});

/**
 * RouteOut
 */
export const zRouteOut = z.object({
    region: zRegionBriefOut,
    stamp: zStampOut,
    progress: zRouteProgressOut,
    xp: zXpPairOut.nullable(),
    main_course: zStudyCardOut,
    bronze: zBronzeOut,
    gold: zGoldOut,
    extra: zExtraOut,
    collected: zCollectedOut.nullable(),
    buttons: z.array(zLinkOut)
});

/**
 * XpRuleStatus
 */
export const zXpRuleStatus = z.enum(XpRuleStatus);

/**
 * XpRuleCardOut
 */
export const zXpRuleCardOut = z.object({
    id: z.uuid(),
    event_type: zXpEventType,
    event_label: z.string(),
    title: z.string(),
    xp: z.int(),
    repeat_text: z.string(),
    conditions: z.string(),
    unique_key_text: z.string(),
    status: zXpRuleStatus,
    status_label: z.string(),
    section: z.string(),
    section_label: z.string(),
    version: z.int(),
    activated_at: z.iso.datetime({ offset: true }).nullable(),
    disabled_at: z.iso.datetime({ offset: true }).nullable(),
    ledger_count: z.int(),
    can: zRuleCan,
    ledger_link: zGamSectionLinkOut,
    history: z.array(zGamRuleHistoryEntryOut)
});

/**
 * XpRuleCreateIn
 */
export const zXpRuleCreateIn = z.object({
    event_type: zXpEventType,
    title: z.string().min(1).max(120),
    xp: z.int().gte(1).lte(10000),
    conditions: z.string().min(1).max(300),
    status: zXpRuleStatus.optional().default('draft'),
    confirm_replace: z.boolean().optional().default(false)
});

/**
 * XpRuleOut
 */
export const zXpRuleOut = z.object({
    id: z.uuid(),
    event_type: zXpEventType,
    event_label: z.string(),
    title: z.string(),
    xp: z.int(),
    repeat_text: z.string(),
    conditions: z.string(),
    unique_key_text: z.string(),
    status: zXpRuleStatus,
    status_label: z.string(),
    section: z.string(),
    section_label: z.string(),
    version: z.int(),
    activated_at: z.iso.datetime({ offset: true }).nullable(),
    disabled_at: z.iso.datetime({ offset: true }).nullable(),
    ledger_count: z.int(),
    can: zRuleCan
});

/**
 * XpRulePatchIn
 */
export const zXpRulePatchIn = z.object({
    title: z.string().min(1).max(120).nullish(),
    xp: z.int().gte(1).lte(10000).nullish(),
    conditions: z.string().min(1).max(300).nullish(),
    status: zXpRuleStatus.nullish(),
    confirm_replace: z.boolean().optional().default(false)
});

/**
 * XpRulesPageOut
 */
export const zXpRulesPageOut = z.object({
    items: z.array(zXpRuleOut),
    total: z.int(),
    active_count: z.int(),
    can: zListCan
});

/**
 * XpTileOut
 */
export const zXpTileOut = z.object({
    event_type: z.string(),
    xp: z.int(),
    xp_label: z.string(),
    group: z.enum([
        'study',
        'diary',
        'experiences',
        'stamps'
    ]),
    title: z.string(),
    hint: z.string()
});

/**
 * XpGroupOut
 */
export const zXpGroupOut = z.object({
    key: z.string(),
    title: z.string(),
    items: z.array(zXpTileOut)
});

/**
 * GamConfigOut
 */
export const zGamConfigOut = z.object({
    xp_rules: z.array(zXpTileOut),
    xp_groups: z.array(zXpGroupOut),
    levels: z.array(zLevelOut),
    tiers: z.array(zGamTierOut),
    gates: z.array(zGateInfoOut),
    club: zClubConfigOut.nullable(),
    intro_lessons_total: z.int(),
    tiles: zTilesVisibilityOut,
    onboarding: zOnboardingOut,
    exchange_rules: z.array(z.string()),
    bonus_name: z.string(),
    brand_name: z.string()
});

/**
 * ZoneBrief
 */
export const zZoneBrief = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * PersonOut
 */
export const zPersonOut = z.object({
    user_id: z.uuid(),
    member_id: z.uuid().nullish(),
    name: z.string().nullish(),
    role: z.string(),
    avatar_url: z.string().nullish(),
    winery: zZoneBrief.nullish()
});

/**
 * ZoneInfoOut
 */
export const zZoneInfoOut = z.object({
    id: z.uuid(),
    name: z.string(),
    subtitle: z.string().nullable(),
    center: zGeoPointOut,
    regions_count: z.int(),
    country: zCountryBrief
});

/**
 * ZonePinOut
 */
export const zZonePinOut = z.object({
    id: z.uuid(),
    name: z.string(),
    subtitle: z.string().nullable(),
    center: zGeoPointOut,
    regions_count: z.int(),
    wineries_count: z.int(),
    min_distance_km: z.int(),
    max_distance_km: z.int(),
    cover_url: z.string().nullable(),
    nearest_region_id: z.uuid()
});

/**
 * ZoneType
 */
export const zZoneType = z.enum(ZoneType);

/**
 * RegionPatchIn
 */
export const zRegionPatchIn = z.object({
    name: zLocalizedText.nullish(),
    country_id: z.uuid().nullish(),
    map_zone: zMapZoneChoiceIn.nullish(),
    center: zGeoPointIn.nullish(),
    description: zLocalizedText.nullish(),
    origin_id: z.uuid().nullish(),
    zone_type: zZoneType.nullish(),
    area_ha: z.int().nullish(),
    climate: zClimate.nullish(),
    soils: z.array(zSoil).nullish(),
    grape_ids: z.array(z.uuid()).nullish(),
    styles: z.array(zWineStyle).nullish(),
    harvest: zLocalizedText.nullish(),
    season: zTripSeason.nullish(),
    sort_order: z.int().nullish()
});

/**
 * ZonesOut
 */
export const zZonesOut = z.object({
    country: zCountryBrief,
    items: z.array(zZonePinOut),
    regions_count: z.int(),
    min_distance_km: z.int().nullable(),
    max_distance_km: z.int().nullable(),
    show_you_here: z.boolean(),
    you_here: zGeoPointOut.nullable(),
    bounds: z.record(z.string(), z.record(z.string(), z.number())).nullable()
});

/**
 * HistoryItemOut
 */
export const zAppIntegrationsSchemasHistoryItemOut = z.object({
    id: z.uuid(),
    occurred_at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    action_label: z.string(),
    summary: z.string()
});

/**
 * IntegrationCard
 */
export const zIntegrationCard = z.object({
    key: z.string(),
    port: zIntegrationPort,
    group: z.string(),
    group_label: z.string(),
    provider: z.string(),
    title: z.string(),
    mode: zIntegrationMode,
    mode_label: z.string(),
    enabled: z.boolean(),
    is_active: z.boolean().nullable(),
    status: zIntegrationStatus,
    status_label: z.string(),
    available: z.boolean(),
    availability_note: z.string().nullable(),
    settings: z.record(z.string(), z.unknown()),
    secrets: z.record(z.string(), zSecretStateOut),
    fields: z.array(zFieldOut),
    read_only: z.record(z.string(), z.string()),
    read_only_fields: z.array(zReadOnlyFieldOut),
    last_check_at: z.iso.datetime({ offset: true }).nullable(),
    last_check_result: zCheckResultOut.nullable(),
    last_success_at: z.iso.datetime({ offset: true }).nullable(),
    last_error_at: z.iso.datetime({ offset: true }).nullable(),
    last_error: z.string().nullable(),
    metrics: zMetricsOut,
    recent_errors: z.array(zLogBriefOut),
    alerts: z.array(zAlertOut),
    warnings: z.array(z.string()),
    actions: z.array(zActionOut),
    degradation: z.string(),
    history: z.array(zAppIntegrationsSchemasHistoryItemOut),
    mail: zMailTilesOut.nullish(),
    push: zPushTilesOut.nullish(),
    payments: zPaymentTilesOut.nullish(),
    store: zStoreTilesOut.nullish(),
    recognition: zRecognitionTilesOut.nullish(),
    translation: zTranslationTilesOut.nullish(),
    video: zVideoTilesOut.nullish(),
    partner: zIntPartnerCardOut.nullish(),
    shipping: zIntShippingCardOut.nullish(),
    reference: zIntReferenceCardOut.nullish(),
    can: zIntegrationCan
});

/**
 * IntegrationSaveOut
 */
export const zIntegrationSaveOut = z.object({
    key: z.string(),
    port: zIntegrationPort,
    group: z.string(),
    group_label: z.string(),
    provider: z.string(),
    title: z.string(),
    mode: zIntegrationMode,
    mode_label: z.string(),
    enabled: z.boolean(),
    is_active: z.boolean().nullable(),
    status: zIntegrationStatus,
    status_label: z.string(),
    available: z.boolean(),
    availability_note: z.string().nullable(),
    settings: z.record(z.string(), z.unknown()),
    secrets: z.record(z.string(), zSecretStateOut),
    fields: z.array(zFieldOut),
    read_only: z.record(z.string(), z.string()),
    read_only_fields: z.array(zReadOnlyFieldOut),
    last_check_at: z.iso.datetime({ offset: true }).nullable(),
    last_check_result: zCheckResultOut.nullable(),
    last_success_at: z.iso.datetime({ offset: true }).nullable(),
    last_error_at: z.iso.datetime({ offset: true }).nullable(),
    last_error: z.string().nullable(),
    metrics: zMetricsOut,
    recent_errors: z.array(zLogBriefOut),
    alerts: z.array(zAlertOut),
    warnings: z.array(z.string()),
    actions: z.array(zActionOut),
    degradation: z.string(),
    history: z.array(zAppIntegrationsSchemasHistoryItemOut),
    mail: zMailTilesOut.nullish(),
    push: zPushTilesOut.nullish(),
    payments: zPaymentTilesOut.nullish(),
    store: zStoreTilesOut.nullish(),
    recognition: zRecognitionTilesOut.nullish(),
    translation: zTranslationTilesOut.nullish(),
    video: zVideoTilesOut.nullish(),
    partner: zIntPartnerCardOut.nullish(),
    shipping: zIntShippingCardOut.nullish(),
    reference: zIntReferenceCardOut.nullish(),
    can: zIntegrationCan,
    check_result: zCheckResultOut.nullable()
});

/**
 * CityOut
 */
export const zAppMarketSchemasCityOut = z.object({
    name: z.string(),
    lat: z.number(),
    lng: z.number()
});

/**
 * AppMarketOut
 *
 * ARCH-11 `market`: базовые параметры инсталляции.
 */
export const zAppMarketSchemasAppMarketOut = z.object({
    code: z.string(),
    default_country: z.string(),
    default_city: zAppMarketSchemasCityOut,
    phone_code: z.string(),
    platforms: z.array(zClientPlatform),
    timezone: z.string()
});

/**
 * AppConfigOut
 */
export const zAppConfigOut = z.object({
    market_code: z.string(),
    brand: zAppBrandOut,
    locale: zAppLocaleOut,
    currency: zCurrency,
    auth_providers: z.array(zAuthProviderCode),
    age_min: z.int(),
    commerce: zAppCommerceOut,
    account: zAppAccountOut,
    support: zAppSupportOut,
    market_info: zAppMarketInfoOut,
    market: zAppMarketSchemasAppMarketOut,
    features: zAppFeaturesOut,
    integrations: zClientIntegrationsOut,
    min_client_version: z.record(z.string(), z.string()),
    maps: zAppMapsOut,
    geo: zAppGeoOut,
    video: zAppVideoOut,
    search: zAppSearchOut,
    links: zAppLinksOut,
    push: zAppPushOut,
    passport: zAppPassportOut
});

/**
 * CityOut
 */
export const zAppModulesAccSchemasAppCityOut = z.object({
    id: z.uuid(),
    name: z.string(),
    is_default: z.boolean()
});

/**
 * CitiesOut
 */
export const zCitiesOut = z.object({
    country_code: z.string(),
    items: z.array(zAppModulesAccSchemasAppCityOut)
});

/**
 * ConsentOut
 */
export const zAppModulesAccSchemasAppConsentOut = z.object({
    kind: zConsentKind,
    document_version_id: z.uuid().nullable(),
    document_version_label: z.string().nullable(),
    accepted_at: z.iso.datetime({ offset: true })
});

/**
 * ConsentsOut
 */
export const zConsentsOut = z.object({
    items: z.array(zAppModulesAccSchemasAppConsentOut)
});

/**
 * TicketCreateIn
 */
export const zAppModulesAccSchemasAppTicketCreateIn = z.object({
    topic_code: zSupportTopicCode,
    subject: z.string().max(120),
    details: z.string().max(4000),
    ref_text: z.string().max(40).nullish(),
    attachment_file_ids: z.array(z.uuid()).max(3).optional(),
    reply_email: z.string().nullish()
});

/**
 * StatusOut
 */
export const zAppModulesAccSchemasCareStatusOut = z.object({
    code: zTicketStatus,
    label: z.string(),
    description: z.string()
});

/**
 * CareDictionariesOut
 */
export const zCareDictionariesOut = z.object({
    topics: z.array(zTopicOut),
    priorities: z.array(zPriorityOut),
    statuses: z.array(zAppModulesAccSchemasCareStatusOut),
    channels: z.array(zChannelOut),
    priorities_note: z.string(),
    statuses_note: z.string()
});

/**
 * TicketCreateIn
 *
 * «Новое обращение»: найденный пользователь (`user_id`) или почта + имя (`contact_*`).
 */
export const zAppModulesAccSchemasCareTicketCreateIn = z.object({
    user_id: z.uuid().nullish(),
    contact_email: z.string().max(254).nullish(),
    contact_name: z.string().max(100).nullish(),
    channel: zSupportChannel,
    topic_code: zSupportTopicCode,
    priority_code: zSupportPriorityCode,
    subject: z.string().min(4).max(120),
    first_message: z.string().min(1).max(900),
    related: z.array(zRelatedItem).max(20).nullish()
});

/**
 * FileRefOut
 */
export const zAppModulesAccSchemasCommonFileRefOut = z.object({
    file_id: z.uuid(),
    url: z.string().nullable(),
    content_type: z.string().nullish(),
    width: z.int().nullish(),
    height: z.int().nullish()
});

/**
 * AuthorProfileOut
 *
 * Профиль автора. `id = null` — профиль ещё не сохранён («Заполнить профиль»).
 */
export const zAuthorProfileOut = z.object({
    id: z.uuid().nullable(),
    member_id: z.uuid(),
    display_name: z.string().nullable(),
    work_region: zLocalizedText.nullable(),
    experience: zLocalizedText.nullable(),
    social_telegram: z.string().nullable(),
    social_vk: z.string().nullable(),
    social_video: z.string().nullable(),
    social_site: z.string().nullable(),
    socials_count: z.int(),
    photo: zAppModulesAccSchemasCommonFileRefOut.nullable(),
    photo_url: z.string().nullable(),
    gallery: z.array(zAppModulesAccSchemasCommonFileRefOut),
    specialties: z.array(zLocalizedText),
    bio: zLocalizedText.nullable(),
    status: zAuthorProfileStatus,
    status_label: z.string(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    updated_at: z.iso.datetime({ offset: true }).nullable(),
    can: zAuthorProfileCan
});

/**
 * OnboardingItemOut
 */
export const zOnboardingItemOut = z.object({
    id: z.uuid(),
    screen_id: z.uuid(),
    code: z.string(),
    position: z.int(),
    title: zLocalizedText,
    caption: zLocalizedText,
    icon: zAppModulesAccSchemasCommonFileRefOut.nullable(),
    can: zOnboardingItemCan
});

/**
 * OnboardingScreenOut
 */
export const zOnboardingScreenOut = z.object({
    id: z.uuid(),
    screen_key: z.string(),
    kind: zOnboardingScreenKind,
    name: z.string(),
    fixed: zOnboardingScreenFixed.nullable(),
    position: z.int(),
    title: zLocalizedText,
    subtitle: zLocalizedText.nullable(),
    background: zAppModulesAccSchemasCommonFileRefOut.nullable(),
    status: zOnboardingScreenStatus,
    status_label: z.string(),
    step: zOnboardingStepOut,
    items: z.array(zOnboardingItemOut),
    can: zOnboardingScreenCan
});

/**
 * NamedRef
 */
export const zAppModulesAccSchemasCommonNamedRef = z.object({
    id: z.uuid(),
    full_name: z.string()
});

/**
 * CareRow
 */
export const zCareRow = z.object({
    id: z.uuid(),
    number: z.int(),
    number_label: z.string(),
    user: zTicketUserOut,
    priority: zCodeLabel,
    topic: zCodeLabel,
    subject: z.string(),
    channel: zSupportChannel,
    channel_label: z.string(),
    assignee: zAppModulesAccSchemasCommonNamedRef.nullable(),
    assignee_label: z.string(),
    wait_seconds: z.int(),
    wait_label: z.string(),
    status: zTicketStatus,
    status_label: z.string(),
    archived: z.boolean(),
    sla_due_at: z.iso.datetime({ offset: true }),
    created_at: z.iso.datetime({ offset: true }),
    can: zCareRowCan
});

/**
 * CareTicket
 */
export const zCareTicket = z.object({
    id: z.uuid(),
    number: z.int(),
    number_label: z.string(),
    status: zTicketStatus,
    status_label: z.string(),
    archived: z.boolean(),
    archived_at: z.iso.datetime({ offset: true }).nullable(),
    archive_reason: z.string().nullable(),
    archive_comment: z.string().nullable(),
    user: zTicketUserOut,
    contact_name: z.string().nullable(),
    reply_email: z.string(),
    channel: zSupportChannel,
    channel_label: z.string(),
    topic: zCodeLabel,
    priority: zCodeLabel,
    subject: z.string(),
    ref_text: z.string().nullable(),
    related: z.array(zRelatedItem),
    assignee: zAppModulesAccSchemasCommonNamedRef.nullable(),
    created_at: z.iso.datetime({ offset: true }),
    wait_seconds: z.int(),
    wait_label: z.string(),
    sla: zSlaOut,
    resolution: z.string().nullable(),
    resolved_at: z.iso.datetime({ offset: true }).nullable(),
    closed_at: z.iso.datetime({ offset: true }).nullable(),
    messages: z.array(zMessageOut),
    history: z.array(zHistoryEntryOut),
    can: zCareTicketCan
});

/**
 * ExpertApplicationOut
 *
 * Анкета заявки эксперта (CNT 12.3.8) в карточке участника.
 */
export const zExpertApplicationOut = z.object({
    id: z.uuid(),
    status: z.string(),
    status_label: z.string(),
    submitted_at: z.iso.datetime({ offset: true }),
    decided_at: z.iso.datetime({ offset: true }).nullable(),
    decided_by: zAppModulesAccSchemasCommonNamedRef.nullable(),
    full_name: z.string(),
    city: z.string(),
    applicant_role: zCodeLabel,
    experience: zCodeLabel,
    specializations: z.array(zCodeLabel),
    qualifications: z.array(zCodeLabel),
    diploma_document_id: z.uuid().nullable(),
    links: z.string().nullable(),
    formats: z.array(zCodeLabel),
    about: zLocalizedText.nullable(),
    author_terms_version_id: z.uuid().nullable(),
    author_terms_accepted_at: z.iso.datetime({ offset: true }).nullable(),
    passport_snapshot: z.string().nullable(),
    decision_comment: z.string().nullable()
});

/**
 * MemberCard
 */
export const zMemberCard = z.object({
    id: z.uuid(),
    user_id: z.uuid(),
    full_name: z.string().nullable(),
    role: zTeamRole,
    role_label: z.string(),
    status: zTeamMemberStatus,
    status_label: z.string(),
    source: zTeamMemberSource,
    email: z.string(),
    phone: z.string().nullable(),
    registered_at: z.iso.datetime({ offset: true }),
    registered_by: zAppModulesAccSchemasCommonNamedRef.nullable(),
    last_login_at: z.iso.datetime({ offset: true }).nullable(),
    app_enabled: z.boolean(),
    user_status: zUserStatus,
    winery_ids: z.array(z.uuid()),
    wineries: z.array(zRefLabelOut),
    region_ids: z.array(z.uuid()),
    regions: z.array(zRefLabelOut),
    linked_objects: z.string(),
    app_tier: z.string().nullable(),
    decision_comment: z.string().nullable(),
    expert_application: zExpertApplicationOut.nullable(),
    author_profile: zAuthorProfileOut.nullable(),
    history: z.array(zAuditHistoryItemOut),
    can: zMemberCan
});

/**
 * Page[CareRow]
 */
export const zPageCareRow = z.object({
    items: z.array(zCareRow),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * ConsentOut
 */
export const zAppModulesAccSchemasUsersConsentOut = z.object({
    accepted_at: z.iso.datetime({ offset: true }),
    version_label: z.string().nullable(),
    document_version_id: z.uuid().nullable()
});

/**
 * UserConsentsOut
 */
export const zUserConsentsOut = z.object({
    personal_data: zAppModulesAccSchemasUsersConsentOut.nullable(),
    privacy_policy: zAppModulesAccSchemasUsersConsentOut.nullable(),
    age_confirmation: zAppModulesAccSchemasUsersConsentOut.nullable()
});

/**
 * UserCard
 */
export const zUserCard = z.object({
    id: z.uuid(),
    full_name: z.string().nullable(),
    email: z.string(),
    status: zUserStatus,
    status_label: z.string(),
    phone: zPhoneOut.nullable(),
    phone_display: z.string().nullable(),
    birth_date: z.iso.date().nullable(),
    avatar_url: z.string().nullable(),
    country_code: z.string().nullable(),
    country_name: z.string().nullable(),
    currency: zCurrency,
    city_name: z.string().nullable(),
    city_suggestion_id: z.uuid().nullable(),
    locale: zLanguage,
    locale_label: z.string(),
    goals: z.array(zOnboardingGoalOut),
    consents: zUserConsentsOut,
    account: zUserAccountOut,
    notifications: zNotificationsOut,
    passport_tier: z.string().nullable(),
    team_member_id: z.uuid().nullable(),
    block_reason: z.string().nullable(),
    blocked_at: z.iso.datetime({ offset: true }).nullable(),
    blocked_by: zAppModulesAccSchemasCommonNamedRef.nullable(),
    profile_completed_at: z.iso.datetime({ offset: true }).nullable(),
    registered_at: z.iso.datetime({ offset: true }),
    can: zUserCardCan
});

/**
 * ArchiveReason
 */
export const zAppModulesCatModelsArchiveReason = z.enum(AppModulesCatModelsArchiveReason);

/**
 * DropAdmin
 */
export const zDropAdmin = z.object({
    id: z.uuid(),
    sku: z.string(),
    name: zLocalizedText.nullable(),
    title: z.string(),
    target: zDropTargetOut,
    winery: zRefOut.nullable(),
    region: zRefOut.nullable(),
    batch_qty: z.int(),
    sold_qty: z.int(),
    reserved_qty: z.int(),
    remaining: z.int(),
    price: zMoney,
    price_label: z.string(),
    limit_per_user: z.int().nullable(),
    effective_limit: z.int(),
    limit_label: z.string(),
    delivery_options: z.array(zDeliveryOption),
    delivery_labels: z.array(z.string()),
    early_access: z.boolean(),
    early_access_label: z.string(),
    early_access_tier: zTierOut.nullable(),
    early_start_at: z.iso.datetime({ offset: true }).nullable(),
    start_at: z.iso.datetime({ offset: true }).nullable(),
    timezone: z.string(),
    sales_state: zDropSalesState,
    sales_state_label: z.string(),
    started: z.boolean(),
    description: zLocalizedText.nullable(),
    conditions: zLocalizedText.nullable(),
    ended_at: z.iso.datetime({ offset: true }).nullable(),
    media: z.array(zCatalogMediaOut),
    cover_url: z.string().nullable(),
    reminders_count: z.int(),
    orders_count: z.int(),
    status: zCatalogStatus,
    status_label: z.string(),
    display_status: zCatalogStatus,
    display_status_label: z.string(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    review: zReviewNoteOut.nullable(),
    archive_reason: zAppModulesCatModelsArchiveReason.nullable(),
    archive_reason_label: z.string().nullable(),
    archive_comment: z.string().nullable(),
    revision: zRevisionOut.nullable(),
    revision_preview: z.lazy((): any => zDropAdmin).nullish(),
    can: zDropCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * SetAdmin
 */
export const zSetAdmin = z.object({
    id: z.uuid(),
    sku: z.string(),
    name: zLocalizedText.nullable(),
    title: z.string(),
    format: zSetFormat,
    format_label: z.string(),
    blind_tasting: zTastingRefOut.nullable(),
    blind_warning: z.string().nullable(),
    author_type: zSetAuthorType,
    kind_label: z.string(),
    author_label: z.string(),
    composer_label: z.string(),
    author_winery: zRefOut.nullable(),
    author_expert: zRefOut.nullable(),
    items: z.array(zSetItemOut),
    items_count: z.int(),
    items_cost: zMoney,
    items_cost_label: z.string(),
    markup_pct: z.int().nullable(),
    markup_label: z.string().nullable(),
    total_volume_ml: z.int(),
    total_volume_label: z.string(),
    summary_label: z.string(),
    countries: z.array(zCountryRefOut),
    regions: z.array(zRefOut),
    price: zMoney,
    price_label: z.string(),
    price_placeholder: zMoney.nullable(),
    stock: z.int().nullable(),
    stock_reserved: z.int(),
    available: z.int().nullable(),
    sales_channels: z.array(zSalesChannel),
    sales_channel_labels: z.array(z.string()),
    delivery_options: z.array(zDeliveryOption),
    delivery_labels: z.array(z.string()),
    description: zLocalizedText.nullable(),
    links: zSetLinksOut,
    media: z.array(zCatalogMediaOut),
    cover_url: z.string().nullable(),
    seller_label: z.string().nullable(),
    status: zCatalogStatus,
    status_label: z.string(),
    display_status: zCatalogStatus,
    display_status_label: z.string(),
    sold_count: z.int(),
    likes_count: z.int(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    review: zReviewNoteOut.nullable(),
    archive_reason: zAppModulesCatModelsArchiveReason.nullable(),
    archive_reason_label: z.string().nullable(),
    archive_comment: z.string().nullable(),
    revision: zRevisionOut.nullable(),
    revision_preview: z.lazy((): any => zSetAdmin).nullish(),
    can: zSetCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * ArchiveIn
 */
export const zAppModulesCatSchemasAdminArchiveIn = z.object({
    reason: zAppModulesCatModelsArchiveReason,
    comment: z.string().max(300).nullish()
});

/**
 * ConfirmNameIn
 */
export const zAppModulesCatSchemasAdminConfirmNameIn = z.object({
    confirm_name: z.string().max(300)
});

/**
 * HistoryItemOut
 */
export const zAppModulesCatSchemasAdminHistoryItemOut = z.object({
    id: z.uuid(),
    at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    text: z.string()
});

/**
 * HistoryPageOut
 */
export const zAppModulesCatSchemasAdminHistoryPageOut = z.object({
    items: z.array(zAppModulesCatSchemasAdminHistoryItemOut),
    total: z.int()
});

/**
 * LinkItemOut
 */
export const zAppModulesCatSchemasAdminLinkItemOut = z.object({
    kind: z.string(),
    id: z.uuid(),
    title: z.string(),
    detail: z.string(),
    tag: z.string()
});

/**
 * MediaIn
 */
export const zAppModulesCatSchemasAdminMediaIn = z.object({
    media_type: zMediaType.optional().default('photo'),
    placement: zMediaPlacement.nullish(),
    file_id: z.uuid().nullish(),
    source_url: z.string().max(1000).nullish(),
    caption: zLocalizedText.nullish()
});

/**
 * DropCreateIn
 */
export const zDropCreateIn = z.object({
    name: zLocalizedText,
    target_type: zDropTargetType,
    target_id: z.uuid(),
    batch_qty: z.int().gte(1),
    price: zMoneyIn,
    limit_per_user: z.int().gte(1).nullish(),
    delivery_options: z.array(zDeliveryOption).optional(),
    early_access: z.boolean().optional().default(false),
    early_start_at: z.iso.datetime({ offset: true }).nullish(),
    start_at: z.iso.datetime({ offset: true }).nullish(),
    timezone: z.string().max(64).nullish(),
    description: zLocalizedText,
    conditions: zLocalizedText.nullish(),
    media: z.array(zAppModulesCatSchemasAdminMediaIn).max(30).optional()
});

/**
 * SetCreateIn
 */
export const zSetCreateIn = z.object({
    name: zLocalizedText,
    format: zSetFormat.optional().default('open'),
    blind_tasting_id: z.uuid().nullish(),
    author_winery_id: z.uuid().nullish(),
    items: z.array(zSetItemIn).max(30).optional(),
    sales_channels: z.array(zSalesChannel).optional(),
    delivery_options: z.array(zDeliveryOption).optional(),
    price: zMoneyIn.nullish(),
    stock: z.int().gte(0).nullish(),
    description: zLocalizedText,
    media: z.array(zAppModulesCatSchemasAdminMediaIn).max(30).optional(),
    link_winery_id: z.uuid().nullish(),
    link_expert_id: z.uuid().nullish(),
    link_course_id: z.uuid().nullish(),
    link_lesson_id: z.uuid().nullish()
});

/**
 * WineCreateIn
 */
export const zWineCreateIn = z.object({
    name: z.string().max(160),
    vintage: z.union([
        z.string(),
        z.int()
    ]),
    winery_id: z.uuid(),
    region_id: z.uuid(),
    origin_classification_id: z.uuid().nullish(),
    kind: zWineKind,
    sugar: zWineSugar,
    color: zWineColor,
    grapes: z.array(zGrapeShareIn).max(20).optional(),
    abv: z.union([
        z.string(),
        z.number()
    ]),
    aging: zLocalizedText.nullish(),
    serve_temp: zServeTemp,
    decanting: zDecanting,
    aging_potential: zAgingPotential,
    pairing_categories: z.array(zPairingCategory).optional(),
    pairing_custom: zLocalizedText.nullish(),
    sales_channels: z.array(zSalesChannel).optional(),
    price: zMoneyIn,
    volume: zBottleVolume,
    stock: z.int().gte(0),
    delivery_options: z.array(zDeliveryOption).optional(),
    taste: zTasteIn,
    notes: z.array(zLocalizedText).max(30).optional(),
    description: zLocalizedText,
    media: z.array(zAppModulesCatSchemasAdminMediaIn).max(30).optional()
});

/**
 * MediaPatchIn
 */
export const zAppModulesCatSchemasAdminMediaPatchIn = z.object({
    file_id: z.uuid().nullish(),
    source_url: z.string().max(1000).nullish(),
    caption: zLocalizedText.nullish(),
    placement: zMediaPlacement.nullish()
});

/**
 * OriginRefOut
 */
export const zAppModulesCatSchemasAdminOriginRefOut = z.object({
    id: z.uuid(),
    code: z.string(),
    full_name: z.string(),
    label: z.string()
});

/**
 * WineAdmin
 */
export const zWineAdmin = z.object({
    id: z.uuid(),
    sku: z.string(),
    name: z.string(),
    title: z.string(),
    vintage: z.int().nullable(),
    is_non_vintage: z.boolean(),
    vintage_label: z.string(),
    winery: zRefOut.nullable(),
    region: zRefOut.nullable(),
    country: zCountryRefOut.nullable(),
    origin_classification: zAppModulesCatSchemasAdminOriginRefOut.nullable(),
    kind: zWineKind,
    kind_label: z.string(),
    sugar: zWineSugar,
    sugar_label: z.string(),
    type_label: z.string(),
    color: zWineColor,
    color_label: z.string(),
    grapes: z.array(zGrapeShareOut),
    grapes_label: z.string(),
    abv: z.number(),
    aging: zLocalizedText.nullable(),
    serve_temp: zServeTemp,
    serve_temp_label: z.string(),
    decanting: zDecanting,
    decanting_label: z.string(),
    aging_potential: zAgingPotential,
    aging_potential_label: z.string(),
    pairing_categories: z.array(zPairingCategory),
    pairing_labels: z.array(z.string()),
    pairing_custom: zLocalizedText.nullable(),
    taste: zTasteOut,
    taste_scale5: zTasteOut,
    notes: z.array(zLocalizedText),
    description: zLocalizedText.nullable(),
    price: zMoney,
    price_label: z.string(),
    scheduled_price: zScheduledPriceOut.nullable(),
    volume: zBottleVolume,
    volume_label: z.string(),
    stock: z.int(),
    stock_reserved: z.int(),
    available: z.int(),
    sales_channels: z.array(zSalesChannel),
    sales_channel_labels: z.array(z.string()),
    channels_label: z.string(),
    delivery_options: z.array(zDeliveryOption),
    delivery_labels: z.array(z.string()),
    seller_label: z.string().nullable(),
    media: z.array(zCatalogMediaOut),
    cover_url: z.string().nullable(),
    status: zCatalogStatus,
    status_label: z.string(),
    display_status: zCatalogStatus,
    display_status_label: z.string(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    review: zReviewNoteOut.nullable(),
    archive_reason: zAppModulesCatModelsArchiveReason.nullable(),
    archive_reason_label: z.string().nullable(),
    archive_comment: z.string().nullable(),
    likes_count: z.int(),
    author: zRefOut.nullable(),
    revision: zRevisionOut.nullable(),
    revision_preview: z.lazy((): any => zWineAdmin).nullish(),
    can: zWineCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * WineCardOut
 *
 * `GET /admin/wines/{id}` — `{wine, revision?, permissions}` (6.8.3).
 */
export const zWineCardOut = z.object({
    wine: zWineAdmin,
    revision: zRevisionOut.nullable(),
    permissions: zWineCan
});

/**
 * ReviewsOut
 */
export const zAppModulesCatSchemasAdminReviewsOut = z.object({
    diary_entries: z.int().nullable(),
    avg_rating: z.number().nullable(),
    repeat_purchase_pct: z.int().nullable()
});

/**
 * WineLinksOut
 */
export const zWineLinksOut = z.object({
    sales_places: z.array(zAppModulesCatSchemasAdminLinkItemOut),
    learning: z.array(zAppModulesCatSchemasAdminLinkItemOut),
    reviews: zAppModulesCatSchemasAdminReviewsOut.nullable(),
    likes: zLikesOut
});

/**
 * StatusOut
 */
export const zAppModulesCatSchemasAdminStatusOut = z.object({
    status: zCatalogStatus,
    status_label: z.string(),
    display_status: zCatalogStatus,
    display_status_label: z.string(),
    revision_state: zRevisionState.nullable()
});

/**
 * NamedRef
 */
export const zAppModulesCatSchemasAppNamedRef = z.object({
    id: z.uuid(),
    name: z.string()
});

/**
 * CharacteristicOut
 */
export const zCharacteristicOut = z.object({
    key: z.string(),
    label: z.string(),
    value: z.string(),
    link: zAppModulesCatSchemasAppNamedRef.nullish()
});

/**
 * DropListItem
 */
export const zDropListItem = z.object({
    id: z.uuid(),
    name: z.string(),
    overline: z.string(),
    state_for_user: z.enum([
        'live',
        'upcoming',
        'sold'
    ]),
    sales_state: zDropSalesState,
    meta: z.string(),
    winery: zAppModulesCatSchemasAppNamedRef.nullable(),
    region: zAppModulesCatSchemasAppNamedRef.nullable(),
    price: zMoney,
    price_label: z.string(),
    remaining: z.int(),
    right_label: z.string().nullable(),
    start_at: z.iso.datetime({ offset: true }).nullable(),
    early_start_at: z.iso.datetime({ offset: true }).nullable(),
    timezone: z.string(),
    cover_url: z.string().nullable()
});

/**
 * DropPageOut
 */
export const zDropPageOut = z.object({
    items: z.array(zDropListItem),
    next_cursor: z.string().nullable()
});

/**
 * SetItemAppOut
 */
export const zSetItemAppOut = z.object({
    number: z.int(),
    number_label: z.string(),
    title: z.string(),
    portion: zSetPortion,
    portion_label: z.string(),
    volume_ml: z.int(),
    portion_price: zMoney,
    portion_price_label: z.string(),
    item_id: z.uuid().nullable(),
    wine: zAppModulesCatSchemasAppNamedRef.nullable(),
    subtitle: z.string().nullable(),
    cover_url: z.string().nullable(),
    wine_available: z.boolean()
});

/**
 * SetDetailOut
 */
export const zSetDetailOut = z.object({
    id: z.uuid(),
    name: z.string(),
    format: zSetFormat,
    blind_tasting_id: z.uuid().nullable(),
    over_label: z.string(),
    author_type: zSetAuthorType,
    author_label: z.string(),
    author: zAppModulesCatSchemasAppNamedRef.nullable(),
    author_line: z.string(),
    sold_count: z.int(),
    orders_line: z.string(),
    description: z.string().nullable(),
    media: z.array(zMediaAppOut),
    items: z.array(zSetItemAppOut),
    items_count: z.int(),
    items_label: z.string(),
    countries: z.array(zCountryOut),
    regions: z.array(zAppModulesCatSchemasAppNamedRef),
    composition_label: z.string(),
    total_volume_ml: z.int(),
    seller_label: z.string().nullable(),
    ship_incl: z.string().nullable(),
    ship_fact: z.string().nullable(),
    age_check: z.string(),
    price: zMoney,
    price_label: z.string(),
    items_cost: zMoney,
    savings: zMoney.nullable(),
    available: z.int().nullable(),
    max_qty: z.int(),
    in_stock: z.boolean(),
    purchase_action: z.enum([
        'cart',
        'partner',
        'none'
    ]),
    purchase_label: z.string().nullable(),
    link_lesson_id: z.uuid().nullable(),
    link_course_id: z.uuid().nullable(),
    liked: z.boolean(),
    likes_count: z.int(),
    is_saved: z.boolean(),
    bought: z.boolean(),
    xp_kind: z.literal('set').optional().default('set')
});

/**
 * SetListItem
 */
export const zSetListItem = z.object({
    id: z.uuid(),
    name: z.string(),
    format: zSetFormat,
    title: z.string(),
    overline: z.string(),
    meta: z.string(),
    author_type: zSetAuthorType,
    author_label: z.string(),
    kind_label: z.string(),
    over_label: z.string(),
    portion_chip: z.string().nullable(),
    items_count: z.int(),
    regions: z.array(zAppModulesCatSchemasAppNamedRef),
    cover_url: z.string().nullable(),
    price: zMoney,
    price_label: z.string(),
    sold_count: z.int(),
    orders_label: z.string().nullable(),
    available: z.int().nullable(),
    purchase_action: z.enum([
        'cart',
        'partner',
        'none'
    ]),
    action_label: z.string(),
    blind_tasting_id: z.uuid().nullable(),
    liked: z.boolean(),
    likes_count: z.int(),
    xp_kind: z.literal('set').optional().default('set')
});

/**
 * SetItemsListOut
 */
export const zSetItemsListOut = z.object({
    items: z.array(zSetListItem)
});

/**
 * SetPageOut
 */
export const zSetPageOut = z.object({
    items: z.array(zSetListItem),
    next_cursor: z.string().nullable()
});

/**
 * WineDetailOut
 *
 * `WineDetail` (6.8.1, пример).
 */
export const zWineDetailOut = z.object({
    id: z.uuid(),
    name: z.string(),
    vintage: z.int().nullable(),
    is_non_vintage: z.boolean(),
    vintage_label: z.string(),
    type_label: z.string(),
    winery: zAppModulesCatSchemasAppNamedRef.nullable(),
    region: zAppModulesCatSchemasAppNamedRef.nullable(),
    country: zCountryOut.nullable(),
    origin_classification: z.string().nullable(),
    grapes: z.array(zGrapeShareAppOut),
    kind: zWineKind,
    sugar: zWineSugar,
    color: zWineColor,
    abv: z.number(),
    volume: zBottleVolume,
    aging: z.string().nullable(),
    serve_temp: zServeTemp,
    decanting: zDecanting,
    aging_potential: zAgingPotential,
    pairings: z.array(z.string()),
    notes: z.array(z.string()),
    description: z.string().nullable(),
    taste: zTasteValues,
    taste_scale5: zTasteValues,
    media: z.array(zMediaAppOut),
    offer: zOfferOut,
    where_to_try: zWhereToTryOut.nullable(),
    in_sets: z.array(zAppModulesCatSchemasAppNamedRef),
    liked: z.boolean(),
    likes_count: z.int(),
    is_saved: z.boolean(),
    in_diary: z.boolean(),
    bought: z.boolean(),
    xp_kind: z.literal('wine').optional().default('wine'),
    subtitle: z.string(),
    characteristics: z.array(zCharacteristicOut),
    taste_scales: z.array(zTasteScaleOut),
    stock_alert: z.boolean(),
    first_set: zAppModulesCatSchemasAppNamedRef.nullable()
});

/**
 * WineListItem
 *
 * Строка вкладки «Вина», мини-карточка (похожие, регион, винодельня), поиск.
 */
export const zWineListItem = z.object({
    id: z.uuid(),
    name: z.string(),
    vintage: z.int().nullable(),
    is_non_vintage: z.boolean(),
    vintage_label: z.string(),
    overline: z.string(),
    winery: zAppModulesCatSchemasAppNamedRef.nullable(),
    region: zAppModulesCatSchemasAppNamedRef.nullable(),
    type_label: z.string(),
    kind: zWineKind,
    main_grape: z.string().nullable(),
    meta: z.string(),
    in_set: zAppModulesCatSchemasAppNamedRef.nullable(),
    cover_url: z.string().nullable(),
    offer_state: zOfferState,
    offer_label: z.string(),
    badge: z.string().nullable(),
    chips: z.array(z.string()),
    price: zMoney.nullable(),
    price_label: z.string().nullable(),
    purchase_action: z.enum([
        'cart',
        'partner',
        'none'
    ]),
    action_label: z.string(),
    liked: z.boolean(),
    likes_count: z.int(),
    xp_kind: z.literal('wine').optional().default('wine')
});

/**
 * DropDetailOut
 *
 * `DropDetail` (6.8.1, пример).
 */
export const zDropDetailOut = z.object({
    id: z.uuid(),
    name: z.string(),
    target: zDropTargetAppOut,
    winery: zAppModulesCatSchemasAppNamedRef.nullable(),
    region: zAppModulesCatSchemasAppNamedRef.nullable(),
    price: zMoney,
    batch_qty: z.int(),
    remaining: z.int(),
    sales_state: zDropSalesState,
    state_for_user: z.enum([
        'live',
        'upcoming',
        'sold'
    ]),
    early_access: zEarlyAccessOut,
    start_at: z.iso.datetime({ offset: true }).nullable(),
    timezone: z.string(),
    limit_per_user: z.int(),
    user_bought_qty: z.int(),
    reminder_set: z.boolean(),
    conditions: z.string().nullable(),
    description: z.string().nullable(),
    purchase_action: z.enum([
        'cart',
        'partner',
        'none'
    ]),
    price_label: z.string(),
    header: z.string(),
    limit_note: z.string(),
    sold_qty: z.int(),
    notes: z.array(z.string()),
    media: z.array(zMediaAppOut),
    characteristics: z.array(zCharacteristicOut),
    target_items: z.array(zSetItemAppOut),
    same_hands: z.array(zWineListItem),
    max_qty: z.int(),
    purchase_label: z.string().nullable(),
    is_saved: z.boolean()
});

/**
 * LikedItemOut
 */
export const zLikedItemOut = z.object({
    object_type: zLikeObjectType,
    object_id: z.uuid(),
    liked_at: z.iso.datetime({ offset: true }),
    wine: zWineListItem.nullable(),
    set: zSetListItem.nullable()
});

/**
 * LikedPageOut
 */
export const zLikedPageOut = z.object({
    items: z.array(zLikedItemOut),
    next_cursor: z.string().nullable()
});

/**
 * TstPickOut
 */
export const zTstPickOut = z.object({
    kind: z.enum(['fit', 'trial']),
    label: z.string(),
    wine: zWineListItem
});

/**
 * TstTasteProfileOut
 */
export const zTstTasteProfileOut = z.object({
    empty: z.boolean(),
    wines_count: z.int(),
    tastings_count: z.int(),
    regions_count: z.int(),
    liked_phrases: z.array(z.string()),
    disliked_phrases: z.array(z.string()),
    liked_text: z.string(),
    disliked_text: z.string(),
    evolution: zTstEvolutionOut.nullable(),
    diary_caption: z.string(),
    last_wines: z.array(zTstDiaryRowOut),
    chars: z.array(zTstCharOut),
    aromas: z.array(zTstAromaOut),
    axes: z.record(z.string(), z.number()).nullable(),
    picks: z.array(zTstPickOut),
    picks_text: z.string().nullable()
});

/**
 * WineItemsOut
 */
export const zWineItemsOut = z.object({
    items: z.array(zWineListItem)
});

/**
 * WinePageOut
 */
export const zWinePageOut = z.object({
    items: z.array(zWineListItem),
    next_cursor: z.string().nullable(),
    sort: z.enum([
        'fit',
        'price_asc',
        'price_desc'
    ])
});

/**
 * SearchOut
 */
export const zAppModulesCatSchemasAppSearchOut = z.object({
    wines: z.array(zWineListItem),
    sets: z.array(zSetListItem)
});

/**
 * ArchiveReason
 */
export const zAppModulesGeoModelsArchiveReason = z.enum(AppModulesGeoModelsArchiveReason);

/**
 * AppMarketOut
 *
 * Строка реестра и карточка страны приложения.
 *
 * Языки интерфейса, валюта и возраст покупки вина — параметры инсталляции (MarketConfig),
 * одинаковые для всех стран приложения; в записи страны они не хранятся и не правятся.
 */
export const zAppModulesGeoSchemasAdminAppMarketOut = z.object({
    id: z.uuid(),
    country_code: z.string(),
    name: zLocalizedText.nullable(),
    languages: z.array(z.string()),
    languages_label: z.string(),
    currency: z.string(),
    currency_label: z.string(),
    wine_purchase_min_age: z.int(),
    wine_purchase_min_age_label: z.string(),
    phone_code: z.string().nullable(),
    status: zAppMarketStatus,
    status_label: z.string(),
    is_default: z.boolean(),
    sort_order: z.int(),
    note: z.string().nullable(),
    config_note: z.string(),
    can: zAppMarketCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * Page[AppMarketOut]
 */
export const zPageAppMarketOut = z.object({
    items: z.array(zAppModulesGeoSchemasAdminAppMarketOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * ArchiveIn
 */
export const zAppModulesGeoSchemasAdminArchiveIn = z.object({
    reason: zAppModulesGeoModelsArchiveReason,
    comment: z.string().max(300).nullish()
});

/**
 * ConfirmNameIn
 */
export const zAppModulesGeoSchemasAdminConfirmNameIn = z.object({
    confirm_name: z.string().max(300)
});

/**
 * FactOut
 */
export const zAppModulesGeoSchemasAdminFactOut = z.object({
    id: z.uuid(),
    title: zLocalizedText.nullable(),
    text: zLocalizedText.nullable(),
    position: z.int()
});

/**
 * HistoryItemOut
 */
export const zAppModulesGeoSchemasAdminHistoryItemOut = z.object({
    id: z.uuid(),
    at: z.iso.datetime({ offset: true }),
    actor_name: z.string(),
    action: z.string()
});

/**
 * HistoryPageOut
 */
export const zAppModulesGeoSchemasAdminHistoryPageOut = z.object({
    items: z.array(zAppModulesGeoSchemasAdminHistoryItemOut),
    next_cursor: z.string().nullable()
});

/**
 * LinkItemOut
 */
export const zAppModulesGeoSchemasAdminLinkItemOut = z.object({
    entity_type: z.string(),
    entity_id: z.uuid(),
    title: z.string(),
    subtitle: z.string().nullable(),
    source: z.enum(['auto', 'manual']),
    can_detach: z.boolean()
});

/**
 * LinkListOut
 */
export const zLinkListOut = z.object({
    tab: z.string(),
    items: z.array(zAppModulesGeoSchemasAdminLinkItemOut)
});

/**
 * OriginRefOut
 */
export const zAppModulesGeoSchemasAdminOriginRefOut = z.object({
    id: z.uuid(),
    code: z.string(),
    full_name: zLocalizedText.nullable(),
    level: zOriginLevel
});

/**
 * RegionDetailOut
 */
export const zRegionDetailOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    country: zCountryRef,
    map_zone: zIdName.nullable(),
    sort_order: z.int(),
    center: zGeoPointOut,
    contour: zGeoPolygonOut.nullable(),
    contour_points: z.int(),
    description: zLocalizedText.nullable(),
    origin: zAppModulesGeoSchemasAdminOriginRefOut,
    zone_type: zZoneType,
    area_ha: z.int(),
    climate: zClimate,
    soils: z.array(zSoil),
    grape_ids: z.array(z.uuid()),
    grapes: z.array(zGrapeOut),
    styles: z.array(zWineStyle),
    harvest: zLocalizedText.nullable(),
    season: zTripSeason,
    status: zRegionStatus,
    status_label: z.string(),
    hide_reason: zHideReason.nullable(),
    archive_reason: zAppModulesGeoModelsArchiveReason.nullable(),
    status_comment: z.string().nullable(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    media: z.array(zMediaOut),
    images: zRegionImagesOut,
    facts: z.array(zAppModulesGeoSchemasAdminFactOut),
    links_counts: z.record(z.string(), z.int()),
    can: zRegionCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * RegionCountsOut
 */
export const zAppModulesGeoSchemasAdminRegionCountsOut = z.object({
    wineries: z.int(),
    wines: z.int(),
    learning: z.int(),
    experiences: z.int()
});

/**
 * RegionRowOut
 */
export const zAppModulesGeoSchemasAdminRegionRowOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    country: zCountryRef,
    counts: zAppModulesGeoSchemasAdminRegionCountsOut,
    status: zRegionStatus,
    status_label: z.string(),
    sort_order: z.int(),
    can: zRegionCan
});

/**
 * Page[RegionRowOut]
 */
export const zPageRegionRowOut = z.object({
    items: z.array(zAppModulesGeoSchemasAdminRegionRowOut),
    total: z.int(),
    can: zListCan.optional()
});

/**
 * WinemakerOut
 */
export const zAppModulesGeoSchemasAdminWinemakerOut = z.object({
    user_id: z.uuid(),
    name: z.string().nullable()
});

/**
 * WineryDetailOut
 */
export const zWineryDetailOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    region: zWineryRegionOut,
    region_link_source: zLinkSource,
    winemakers: z.array(zAppModulesGeoSchemasAdminWinemakerOut),
    winemakers_label: z.string(),
    visit_mode: zVisitMode,
    group_capacity: z.int().nullable(),
    address: zLocalizedText.nullable(),
    location: zGeoPointOut,
    site_url: z.string().nullable(),
    description: zLocalizedText.nullable(),
    road: zLocalizedText.nullable(),
    transfer: zLocalizedText.nullable(),
    founded_year: z.int(),
    vineyard_area_ha: z.number(),
    grape_ids: z.array(z.uuid()),
    grapes: z.array(zGrapeOut),
    booking_phone: z.string().nullable(),
    stay_enabled: z.boolean(),
    guests: zGuestsOut,
    facts: z.array(zAppModulesGeoSchemasAdminFactOut),
    media: z.array(zMediaOut),
    status: zWineryStatus,
    status_label: z.string(),
    moderation: zModerationNoteOut.nullable(),
    archive_reason: zAppModulesGeoModelsArchiveReason.nullable(),
    status_comment: z.string().nullable(),
    has_published_version: z.boolean(),
    has_unpublished_changes: z.boolean(),
    published_at: z.iso.datetime({ offset: true }).nullable(),
    rating: zWineryRatingOut.nullable(),
    links_counts: z.record(z.string(), z.int()),
    can: zWineryCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * WineryRowOut
 */
export const zAppModulesGeoSchemasAdminWineryRowOut = z.object({
    id: z.uuid(),
    name: zLocalizedText.nullable(),
    region: zWineryRegionOut,
    winemakers: z.array(z.string()),
    winemakers_label: z.string(),
    wines_count: z.int(),
    visit_mode: zVisitMode,
    visit_mode_label: z.string(),
    status: zWineryStatus,
    status_label: z.string(),
    can: zWineryCan
});

/**
 * WineryPageOut
 */
export const zWineryPageOut = z.object({
    items: z.array(zAppModulesGeoSchemasAdminWineryRowOut),
    total: z.int(),
    can: zListCan,
    notice: z.string().nullish()
});

/**
 * FactOut
 */
export const zAppModulesGeoSchemasAppFactOut = z.object({
    label: z.string(),
    value: z.string()
});

/**
 * RegionOverviewOut
 */
export const zRegionOverviewOut = z.object({
    id: z.uuid(),
    name: z.string(),
    country: zCountryBrief,
    map_zone: zZoneBrief.nullable(),
    illustration_url: z.string().nullable(),
    cover_url: z.string().nullable(),
    center: zGeoPointOut,
    distance_km: z.int(),
    description: z.string().nullable(),
    facts: z.array(zAppModulesGeoSchemasAppFactOut),
    wines: z.array(z.record(z.string(), z.unknown())),
    wineries: z.array(zRegionWineryOut),
    experts: z.array(zPersonOut),
    experts_total: z.int(),
    tastings: z.array(z.record(z.string(), z.unknown())),
    excursions: z.array(z.record(z.string(), z.unknown())),
    gallery: z.array(zGalleryItemOut),
    publications: z.array(z.record(z.string(), z.unknown())),
    subscriptions: zRegionSubscriptionsOut
});

/**
 * RegionCountsOut
 */
export const zAppModulesGeoSchemasAppRegionCountsOut = z.object({
    wineries: z.int(),
    lessons: z.int(),
    events: z.int()
});

/**
 * RegionRowOut
 */
export const zAppModulesGeoSchemasAppRegionRowOut = z.object({
    id: z.uuid(),
    name: z.string(),
    country: zCountryBrief,
    map_zone: zZoneBrief.nullable(),
    cover_url: z.string().nullable(),
    center: zGeoPointOut,
    distance_km: z.int(),
    counts: zAppModulesGeoSchemasAppRegionCountsOut,
    early_free: z.boolean(),
    is_done: z.boolean()
});

/**
 * RegionListOut
 */
export const zRegionListOut = z.object({
    items: z.array(zAppModulesGeoSchemasAppRegionRowOut),
    next_cursor: z.string().nullable(),
    total: z.int(),
    done_count: z.int()
});

/**
 * ZoneOut
 */
export const zZoneOut = z.object({
    zone: zZoneInfoOut,
    regions: z.array(zAppModulesGeoSchemasAppRegionRowOut),
    show_you_here: z.boolean(),
    you_here: zGeoPointOut.nullable(),
    bounds: z.record(z.string(), z.record(z.string(), z.number())).nullable()
});

/**
 * ReviewsOut
 */
export const zAppModulesGeoSchemasAppReviewsOut = z.object({
    summary: zReviewSummaryOut.nullable(),
    mine: zReviewOut.nullable(),
    can_review: z.boolean(),
    items: z.array(zReviewOut),
    next_cursor: z.string().nullable()
});

/**
 * WinemakerOut
 */
export const zAppModulesGeoSchemasAppWinemakerOut = z.object({
    user_id: z.uuid(),
    member_id: z.uuid().nullish(),
    name: z.string().nullable(),
    role: z.string(),
    since: z.string().nullable(),
    followers: z.int().nullable(),
    avatar_url: z.string().nullable()
});

/**
 * WineryCardOut
 */
export const zWineryCardOut = z.object({
    id: z.uuid(),
    name: z.string(),
    region: zZoneBrief.nullable(),
    location: zGeoPointOut,
    distance_km: z.int(),
    site_url: z.string().nullable(),
    site_domain: z.string().nullable(),
    visit_mode: zVisitMode,
    visit_mode_label: z.string(),
    visit_from: zMoney.nullable(),
    visit_free: z.boolean(),
    rating: zRatingOut.nullable(),
    photos: z.array(z.string()),
    description: z.string().nullable(),
    facts: z.array(zAppModulesGeoSchemasAppFactOut),
    winemakers: z.array(zAppModulesGeoSchemasAppWinemakerOut),
    wines_count: z.int(),
    wines: z.array(z.record(z.string(), z.unknown())),
    visit_formats: z.array(zVisitFormatOut),
    tastings: z.array(z.record(z.string(), z.unknown())),
    stay: zStayOut.nullable(),
    booking_phone: z.string().nullable(),
    open_hours: z.array(zOpenHoursOut),
    hours_summary: z.string().nullable(),
    how_to_get: zHowToGetOut,
    gallery: z.array(zGalleryItemOut),
    chronicle_count: z.int(),
    is_saved: z.boolean()
});

/**
 * WineryRowOut
 */
export const zAppModulesGeoSchemasAppWineryRowOut = z.object({
    id: z.uuid(),
    name: z.string(),
    region: zZoneBrief.nullable(),
    location: zGeoPointOut,
    distance_km: z.int(),
    cover_url: z.string().nullable(),
    visit_mode: zVisitMode,
    rating: zRatingOut.nullable(),
    visit_from: zMoney.nullable(),
    visit_free: z.boolean(),
    is_visited: z.boolean()
});

/**
 * PopularOut
 */
export const zPopularOut = z.object({
    set: z.record(z.string(), z.unknown()).nullable(),
    tasting: z.record(z.string(), z.unknown()).nullable(),
    winery: zAppModulesGeoSchemasAppWineryRowOut.nullable()
});

/**
 * SearchGroupsOut
 */
export const zSearchGroupsOut = z.object({
    regions: z.array(zAppModulesGeoSchemasAppRegionRowOut),
    wines: z.array(z.record(z.string(), z.unknown())),
    sets: z.array(z.record(z.string(), z.unknown())),
    wineries: z.array(zAppModulesGeoSchemasAppWineryRowOut),
    experiences: z.array(z.record(z.string(), z.unknown())),
    lessons: z.array(z.record(z.string(), z.unknown())),
    people: z.array(z.record(z.string(), z.unknown()))
});

/**
 * WineryListOut
 */
export const zWineryListOut = z.object({
    items: z.array(zAppModulesGeoSchemasAppWineryRowOut),
    next_cursor: z.string().nullable(),
    total: z.int()
});

/**
 * SearchOut
 */
export const zAppModulesGeoSchemasAppSearchOut = z.object({
    query: z.string(),
    counts: zSearchCountsOut,
    groups: zSearchGroupsOut,
    next_cursor: z.string().nullable()
});

/**
 * FileRefOut
 */
export const zAppModulesGeoSchemasCommonFileRefOut = z.object({
    file_id: z.uuid(),
    url: z.string().nullable(),
    preview_url: z.string().nullable(),
    width: z.int().nullable(),
    height: z.int().nullable()
});

/**
 * CountryDetailOut
 */
export const zCountryDetailOut = z.object({
    id: z.uuid(),
    code: z.string(),
    name: zLocalizedText.nullable(),
    sort_order: z.int(),
    sort_order_label: z.string(),
    regions_count: z.int(),
    note: z.string().nullable(),
    illustration: zAppModulesGeoSchemasCommonFileRefOut.nullable(),
    picture: zAppModulesGeoSchemasCommonFileRefOut.nullable(),
    regions: z.array(zCountryRegionOut),
    facts: z.array(zAppModulesGeoSchemasAdminFactOut),
    quizzes: z.array(zCountryQuizOut),
    can: zCountryCan,
    created_at: z.iso.datetime({ offset: true }),
    updated_at: z.iso.datetime({ offset: true })
});

/**
 * MediaIn
 *
 * Медиа (5.8.2): `{file_id, role, caption, slot}`; видео — ссылкой `url`.
 */
export const zAppModulesGeoSchemasCommonMediaIn = z.object({
    slot: zMediaSlot.nullish(),
    role: zMediaRole.nullish(),
    file_id: z.uuid().nullish(),
    url: z.string().max(1000).nullish(),
    caption: zLocalizedText.nullish()
});

/**
 * RegionCreateIn
 */
export const zRegionCreateIn = z.object({
    name: zLocalizedText,
    country_id: z.uuid(),
    map_zone: zMapZoneChoiceIn.nullish(),
    center: zGeoPointIn,
    contour: zGeoPolygonIn.nullish(),
    description: zLocalizedText.nullish(),
    origin_id: z.uuid(),
    zone_type: zZoneType,
    area_ha: z.int().gt(0),
    climate: zClimate,
    soils: z.array(zSoil),
    grape_ids: z.array(z.uuid()).optional(),
    styles: z.array(zWineStyle),
    harvest: zLocalizedText.nullish(),
    season: zTripSeason,
    sort_order: z.int().gte(1).nullish(),
    media: z.array(zAppModulesGeoSchemasCommonMediaIn).max(60).optional()
});

/**
 * WineryCreateIn
 */
export const zWineryCreateIn = z.object({
    name: zLocalizedText,
    region_id: z.uuid(),
    visit_mode: zVisitMode,
    group_capacity: z.int().gte(1).lte(1000).nullish(),
    address: zLocalizedText,
    location: zGeoPointIn,
    site_url: z.string().max(500).nullish(),
    description: zLocalizedText,
    road: zLocalizedText.nullish(),
    transfer: zLocalizedText.nullish(),
    founded_year: z.int(),
    vineyard_area_ha: z.union([
        z.number().gt(0),
        z.string()
    ]),
    grape_ids: z.array(z.uuid()).optional(),
    booking_phone: z.string().max(30).nullish(),
    stay_enabled: z.boolean().optional().default(false),
    media: z.array(zAppModulesGeoSchemasCommonMediaIn).max(60).optional()
});

/**
 * MediaPatchIn
 *
 * «Заменить» (новый `file_id`/`url` на то же место с той же подписью), подпись, позиция,
 * «Сделать обложкой» (`slot = cover`).
 */
export const zAppModulesGeoSchemasCommonMediaPatchIn = z.object({
    file_id: z.uuid().nullish(),
    url: z.string().max(1000).nullish(),
    caption: zLocalizedText.nullish(),
    slot: zMediaSlot.nullish(),
    role: zMediaRole.nullish(),
    position: z.int().nullish()
});

export const zAppMarketConfigGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppMarketConfigGetResponse = zAppConfigOut;

export const zAppCoreUploadsCreateBody = zUploadCreateIn;

export const zAppCoreUploadsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCoreUploadsCreateResponse = zUploadTicketOut;

export const zAppCoreUploadsCreateFilesBody = zUploadCreateIn;

export const zAppCoreUploadsCreateFilesHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCoreUploadsCreateFilesResponse = zUploadTicketOut;

export const zAppCoreUploadsCompleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCoreUploadsCompletePath = z.object({
    upload_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCoreUploadsCompleteResponse = zFileOut;

export const zAppIntClientConfigGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppIntClientConfigGetResponse = zClientIntegrationsOut;

export const zAppAccDeviceUpdateBody = zDevicePatchIn;

export const zAppAccDeviceUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccDeviceUpdateResponse = z.void();

export const zAppAccDeviceRegisterBody = zDeviceRegisterIn;

export const zAppAccDeviceRegisterHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccDeviceRegisterResponse = zDeviceOut;

export const zAppAccOnboardingGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccOnboardingGetResponse = zOnboardingAppOut;

export const zAppAccGuestAgeGateBody = zAgeGateIn;

export const zAppAccGuestAgeGateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccGuestAgeGateResponse = zAgeGateOut;

export const zAppAccGuestConsentsCreateBody = zGuestConsentsIn;

export const zAppAccGuestConsentsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccGuestConsentsCreateResponse = zConsentsOut;

export const zAppAccOnboardingGoalsSaveBody = zOnboardingGoalsIn;

export const zAppAccOnboardingGoalsSaveHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccOnboardingGoalsSaveResponse = z.void();

export const zAppAccGuestLocationUpdateBody = zGuestLocationIn;

export const zAppAccGuestLocationUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccGuestLocationUpdateResponse = zGuestLocationOut;

export const zAppAccAuthCodesSendBody = zCodeRequestIn;

export const zAppAccAuthCodesSendHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthCodesSendResponse = zCodeSentOut;

export const zAppAccAuthCodesVerifyBody = zCodeVerifyIn;

export const zAppAccAuthCodesVerifyHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthCodesVerifyResponse = zVerificationOut;

export const zAppAccAuthRegisterBody = zRegisterIn;

export const zAppAccAuthRegisterHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthRegisterResponse = zAuthOut;

export const zAppAccAuthLoginPasswordBody = zPasswordLoginIn;

export const zAppAccAuthLoginPasswordHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthLoginPasswordResponse = zAuthOut;

export const zAppAccAuthLoginCodeBody = zCodeLoginIn;

export const zAppAccAuthLoginCodeHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthLoginCodeResponse = zAuthOut;

export const zAppAccAuthSocialCompleteBody = zSocialCompleteIn;

export const zAppAccAuthSocialCompleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthSocialCompleteResponse = zSocialCompleteOut;

export const zAppAccAuthSocialLoginBody = zSocialLoginIn;

export const zAppAccAuthSocialLoginHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccAuthSocialLoginPath = z.object({
    provider: zOAuthProvider
});

/**
 * Successful Response
 */
export const zAppAccAuthSocialLoginResponse = zSocialAuthOut;

export const zAppAccAuthOauthHandoffBody = zOAuthHandoffIn;

export const zAppAccAuthOauthHandoffHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthOauthHandoffResponse = zOAuthResultOut;

export const zAppAccAuthOauthPollBody = zOAuthPollIn;

export const zAppAccAuthOauthPollHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthOauthPollResponse = zOAuthResultOut;

export const zAppAccAuthOauthStartBody = zOAuthStartIn;

export const zAppAccAuthOauthStartHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccAuthOauthStartPath = z.object({
    provider: zOAuthProvider
});

/**
 * Successful Response
 */
export const zAppAccAuthOauthStartResponse = zOAuthStartOut;

export const zAppAccAuthOauthCompleteBody = zOAuthCompleteIn;

export const zAppAccAuthOauthCompleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccAuthOauthCompletePath = z.object({
    provider: zOAuthProvider
});

/**
 * Successful Response
 */
export const zAppAccAuthOauthCompleteResponse = zOAuthResultOut;

export const zAppAccAuthPasswordResetBody = zPasswordResetIn;

export const zAppAccAuthPasswordResetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthPasswordResetResponse = zAuthOut;

export const zAppAccAuthRefreshBody = zAppRefreshIn;

export const zAppAccAuthRefreshHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccAuthRefreshResponse = zAppRefreshOut;

export const zAppAccAuthLogoutHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Выход выполнен
 */
export const zAppAccAuthLogoutResponse = z.void();

export const zAppAccMeDeleteBody = zDeleteMeIn;

export const zAppAccMeDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeDeleteResponse = z.void();

export const zAppAccMeGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeGetResponse = zUserMeOut;

export const zAppAccMeUpdateBody = zMePatchIn;

export const zAppAccMeUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeUpdateResponse = zUserMeOut;

export const zAppAccMeAvatarDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeAvatarDeleteResponse = z.void();

export const zAppAccMeAvatarSetBody = zAvatarIn;

export const zAppAccMeAvatarSetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeAvatarSetResponse = zAvatarOut;

export const zAppAccMeLocationUpdateBody = zMeLocationIn;

export const zAppAccMeLocationUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeLocationUpdateResponse = zUserMeOut;

export const zAppAccMeEmailChangeRequestBody = zEmailChangeIn;

export const zAppAccMeEmailChangeRequestHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeEmailChangeRequestResponse = zCodeSentOut;

export const zAppAccMeEmailConfirmBody = zEmailConfirmIn;

export const zAppAccMeEmailConfirmHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeEmailConfirmResponse = zUserMeOut;

export const zAppAccMePasswordChangeBody = zMePasswordIn;

export const zAppAccMePasswordChangeHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMePasswordChangeResponse = z.void();

export const zAppAccMeDeletionCheckHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccMeDeletionCheckResponse = zDeletionCheckOut;

export const zAppAccSettingsNotificationsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccSettingsNotificationsGetResponse = zNotificationSettingsOut;

export const zAppAccSettingsNotificationsSaveBody = zNotificationSettingsIn;

export const zAppAccSettingsNotificationsSaveHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccSettingsNotificationsSaveResponse = zNotificationSettingsOut;

export const zAppAccDictionariesCitiesHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccDictionariesCitiesQuery = z.object({
    country: z.string().regex(/^[A-Za-z]{2}$/).nullish(),
    q: z.string().max(80).nullish()
});

/**
 * Successful Response
 */
export const zAppAccDictionariesCitiesResponse = zCitiesOut;

export const zAppAccDictionariesPhoneCodesHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccDictionariesPhoneCodesResponse = zPhoneCodesOut;

export const zAppAccSupportTopicsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppAccSupportTopicsListResponse = zSupportTopicsOut;

export const zAppAccSupportTicketsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccSupportTicketsListQuery = z.object({
    status: z.enum([
        'new',
        'work',
        'done'
    ]).nullish(),
    summary: z.boolean().optional().default(false),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppAccSupportTicketsListResponse = zTicketListOut;

export const zAppAccSupportTicketsCreateBody = zAppModulesAccSchemasAppTicketCreateIn;

export const zAppAccSupportTicketsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAppAccSupportTicketsCreateResponse = zTicketOut;

export const zAppAccSupportTicketsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccSupportTicketsGetPath = z.object({
    number: z.int()
});

/**
 * Successful Response
 */
export const zAppAccSupportTicketsGetResponse = zTicketOut;

export const zAppAccSupportTicketsAddMessageBody = zTicketMessageIn;

export const zAppAccSupportTicketsAddMessageHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccSupportTicketsAddMessagePath = z.object({
    number: z.int()
});

/**
 * Successful Response
 */
export const zAppAccSupportTicketsAddMessageResponse = zTicketOut;

export const zAppAccSupportTicketsReopenHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppAccSupportTicketsReopenPath = z.object({
    number: z.int()
});

/**
 * Successful Response
 */
export const zAppAccSupportTicketsReopenResponse = zTicketOut;

export const zAppGeoConfigGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGeoConfigGetResponse = zGeoConfigOut;

export const zAppGeoCountriesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGeoCountriesListResponse = zAppCountryListOut;

export const zAppGeoDictionariesCountriesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGeoDictionariesCountriesListResponse = zAppDictionaryCountriesOut;

export const zAppGeoRegionsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoRegionsListQuery = z.object({
    country: z.string().max(10).nullish(),
    status: z.enum(['all', 'done']).optional().default('all'),
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppGeoRegionsListResponse = zRegionListOut;

export const zAppGeoRegionsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoRegionsGetPath = z.object({
    region_id: z.uuid()
});

export const zAppGeoRegionsGetQuery = z.object({
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppGeoRegionsGetResponse = zRegionOverviewOut;

export const zAppGeoRegionsPointsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoRegionsPointsPath = z.object({
    region_id: z.uuid()
});

export const zAppGeoRegionsPointsQuery = z.object({
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppGeoRegionsPointsResponse = zRegionPointsOut;

export const zAppGeoMapZonesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoMapZonesListQuery = z.object({
    country: z.string().max(10).nullish(),
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppGeoMapZonesListResponse = zZonesOut;

export const zAppGeoMapZonesGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoMapZonesGetPath = z.object({
    zone_id: z.uuid()
});

export const zAppGeoMapZonesGetQuery = z.object({
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppGeoMapZonesGetResponse = zZoneOut;

export const zAppGeoWineriesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoWineriesListQuery = z.object({
    region_id: z.uuid().nullish(),
    visit_mode: z.enum(['open', 'by_appointment']).nullish(),
    visited: z.boolean().optional().default(false),
    sort: z.enum([
        'near',
        'rating',
        'cheap',
        'dear'
    ]).optional().default('near'),
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppGeoWineriesListResponse = zWineryListOut;

export const zAppGeoWineriesGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoWineriesGetPath = z.object({
    winery_id: z.uuid()
});

export const zAppGeoWineriesGetQuery = z.object({
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppGeoWineriesGetResponse = zWineryCardOut;

export const zAppGeoReviewsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoReviewsListPath = z.object({
    winery_id: z.uuid()
});

export const zAppGeoReviewsListQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(5)
});

/**
 * Successful Response
 */
export const zAppGeoReviewsListResponse = zAppModulesGeoSchemasAppReviewsOut;

export const zAppGeoReviewsMeGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoReviewsMeGetPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppGeoReviewsMeGetResponse = zMyReviewOut;

export const zAppGeoReviewsMePutBody = zReviewIn;

export const zAppGeoReviewsMePutHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoReviewsMePutPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppGeoReviewsMePutResponse = zReviewSavedOut;

export const zAppGeoSearchHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoSearchQuery = z.object({
    q: z.string().max(200).nullish(),
    group: z.enum([
        'all',
        'regions',
        'wines',
        'sets',
        'wineries',
        'experiences',
        'lessons',
        'people'
    ]).optional().default('all'),
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppGeoSearchResponse = zAppModulesGeoSchemasAppSearchOut;

export const zAppGeoSearchPopularHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoSearchPopularQuery = z.object({
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppGeoSearchPopularResponse = zPopularOut;

export const zAppGeoNotifySubscriptionsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGeoNotifySubscriptionsListResponse = zNotifySubscriptionListOut;

export const zAppGeoNotifySubscriptionsDisableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoNotifySubscriptionsDisablePath = z.object({
    topic: zNotifyTopic
});

export const zAppGeoNotifySubscriptionsDisableQuery = z.object({
    region_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAppGeoNotifySubscriptionsDisableResponse = z.void();

export const zAppGeoNotifySubscriptionsEnableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGeoNotifySubscriptionsEnablePath = z.object({
    topic: zNotifyTopic
});

export const zAppGeoNotifySubscriptionsEnableQuery = z.object({
    region_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAppGeoNotifySubscriptionsEnableResponse = z.void();

export const zAppSrvNotificationsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppSrvNotificationsListQuery = z.object({
    tab: z.enum([
        'all',
        'orders',
        'events',
        'learning'
    ]).optional().default('all'),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppSrvNotificationsListResponse = zFeedPageOut;

export const zAppSrvNotificationsUnreadCountHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppSrvNotificationsUnreadCountResponse = zUnreadCountOut;

export const zAppSrvNotificationsReadBody = zReadIn;

export const zAppSrvNotificationsReadHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppSrvNotificationsReadResponse = zReadOut;

export const zAppSrvNotificationSettingsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppSrvNotificationSettingsGetResponse = zNotificationPreferencesOut;

export const zAppSrvNotificationSettingsUpdateBody = zNotificationSettingsPatchIn;

export const zAppSrvNotificationSettingsUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppSrvNotificationSettingsUpdateResponse = zNotificationPreferencesOut;

export const zAppCatCountsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCatCountsResponse = zCountsOut;

export const zAppCatFacetsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatFacetsQuery = z.object({
    tab: z.enum([
        'bottles',
        'sets',
        'drops'
    ]).optional().default('bottles')
});

/**
 * Successful Response
 */
export const zAppCatFacetsResponse = zFacetsOut;

export const zAppCatSearchHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatSearchQuery = z.object({
    q: z.string().max(200).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCatSearchResponse = zAppModulesCatSchemasAppSearchOut;

export const zAppCatWinesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatWinesListQuery = z.object({
    region_id: z.uuid().nullish(),
    winery_id: z.uuid().nullish(),
    grape_id: z.uuid().nullish(),
    kind: zWineKind.nullish(),
    taste: z.enum([
        'fresh',
        'body',
        'fruit',
        'oak',
        'aroma'
    ]).nullish(),
    sort: z.enum([
        'fit',
        'price_asc',
        'price_desc'
    ]).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCatWinesListResponse = zWinePageOut;

export const zAppCatWinesGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatWinesGetPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCatWinesGetResponse = zWineDetailOut;

export const zAppCatWinesSimilarHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatWinesSimilarPath = z.object({
    wine_id: z.uuid()
});

export const zAppCatWinesSimilarQuery = z.object({
    limit: z.int().gte(1).lte(20).optional().default(6)
});

/**
 * Successful Response
 */
export const zAppCatWinesSimilarResponse = zWineItemsOut;

export const zAppCatStockAlertDisableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatStockAlertDisablePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCatStockAlertDisableResponse = z.void();

export const zAppCatStockAlertEnableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatStockAlertEnablePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCatStockAlertEnableResponse = z.void();

export const zAppCatSetsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatSetsListQuery = z.object({
    region_id: z.uuid().nullish(),
    author_type: zSetAuthorType.nullish(),
    format: zSetFormat.nullish(),
    lesson_id: z.uuid().nullish(),
    course_id: z.uuid().nullish(),
    blind_tasting_id: z.uuid().nullish(),
    tasting_id: z.uuid().nullish(),
    sort: z.enum(['price_asc', 'price_desc']).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCatSetsListResponse = zSetPageOut;

export const zAppCatSetsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatSetsGetPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCatSetsGetResponse = zSetDetailOut;

export const zAppCatSetsSimilarHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatSetsSimilarPath = z.object({
    set_id: z.uuid()
});

export const zAppCatSetsSimilarQuery = z.object({
    limit: z.int().gte(1).lte(20).optional().default(6)
});

/**
 * Successful Response
 */
export const zAppCatSetsSimilarResponse = zSetItemsListOut;

export const zAppCatDropsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatDropsListQuery = z.object({
    state: z.enum([
        'live',
        'upcoming',
        'sold'
    ]).nullish(),
    region_id: z.uuid().nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCatDropsListResponse = zDropPageOut;

export const zAppCatDropsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatDropsGetPath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCatDropsGetResponse = zDropDetailOut;

export const zAppCatDropReminderDisableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatDropReminderDisablePath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCatDropReminderDisableResponse = z.void();

export const zAppCatDropReminderEnableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatDropReminderEnablePath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCatDropReminderEnableResponse = z.void();

export const zAppCatLikesRemoveBody = zLikeIn;

export const zAppCatLikesRemoveHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCatLikesRemoveResponse = zLikeOut;

export const zAppCatLikesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCatLikesListQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCatLikesListResponse = zLikedPageOut;

export const zAppCatLikesAddBody = zLikeIn;

export const zAppCatLikesAddHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCatLikesAddResponse = zLikeOut;

export const zAppCatWatchesDisableBody = zWatchIn;

export const zAppCatWatchesDisableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCatWatchesDisableResponse = z.void();

export const zAppCatWatchesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCatWatchesListResponse = zWatchListOut;

export const zAppCatWatchesEnableBody = zWatchIn;

export const zAppCatWatchesEnableHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCatWatchesEnableResponse = z.void();

export const zAppCatAvailabilityCheckBody = zAvailabilityIn;

export const zAppCatAvailabilityCheckHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCatAvailabilityCheckResponse = zAvailabilityOut;

export const zAppExpTastingsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpTastingsListQuery = z.object({
    format: zTastingFormat.nullish(),
    kind: zTastingKind.nullish(),
    region_id: z.uuid().nullish(),
    wine_type: z.string().max(20).nullish(),
    country_id: z.uuid().nullish(),
    status: z.string().regex(/^(registered|attended)$/).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppExpTastingsListResponse = zCursorPageTastingCard;

export const zAppExpTastingsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpTastingsGetPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpTastingsGetResponse = zTastingDetail;

export const zAppExpTastingsSimilarHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpTastingsSimilarPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpTastingsSimilarResponse = zSimilarOut;

export const zAppExpWaitlistLeaveHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpWaitlistLeavePath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpWaitlistLeaveResponse = z.void();

export const zAppExpWaitlistJoinHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

export const zAppExpWaitlistJoinPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpWaitlistJoinResponse = zBookingOut;

export const zAppExpExcursionsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpExcursionsListQuery = z.object({
    region_id: z.uuid().nullish(),
    winery_id: z.uuid().nullish(),
    status: z.string().regex(/^(registered|attended)$/).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppExpExcursionsListResponse = zCursorPageExcursionCard;

export const zAppExpExcursionsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpExcursionsGetPath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpExcursionsGetResponse = zExcursionDetail;

export const zAppExpExcursionSlotsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpExcursionSlotsPath = z.object({
    excursion_id: z.uuid()
});

export const zAppExpExcursionSlotsQuery = z.object({
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zAppExpExcursionSlotsResponse = zSlotsOut;

export const zAppExpWineryFormatsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpWineryFormatsPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpWineryFormatsResponse = zFormatsOut;

export const zAppExpTripsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpTripsListQuery = z.object({
    region_id: z.uuid().nullish(),
    status: z.string().regex(/^(registered|attended)$/).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppExpTripsListResponse = zCursorPageTripCard;

export const zAppExpTripsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpTripsGetPath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpTripsGetResponse = zTripDetail;

export const zAppExpTripRequestCreateBody = zTripRequestIn;

export const zAppExpTripRequestCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

export const zAppExpTripRequestCreatePath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpTripRequestCreateResponse = zBookingOut;

export const zAppExpTripRequestsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpTripRequestsListQuery = z.object({
    status: z.string().regex(/^(review|agreed|paid)$/).nullish()
});

/**
 * Successful Response
 */
export const zAppExpTripRequestsListResponse = zTripRequestsOut;

export const zAppExpTripRequestsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpTripRequestsGetPath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpTripRequestsGetResponse = zBookingOut;

export const zAppExpEventsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpEventsListQuery = z.object({
    from: z.iso.date().nullish(),
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zAppExpEventsListResponse = zEventsOut;

export const zAppExpBookingsCreateBody = zBookingCreateIn;

export const zAppExpBookingsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAppExpBookingsCreateResponse = zBookingCreatedOut;

export const zAppExpMyHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpMyQuery = z.object({
    kind: zBookingTarget.nullish(),
    status: z.string().regex(/^(registered|attended)$/).nullish()
});

/**
 * Successful Response
 */
export const zAppExpMyResponse = zMyOut;

export const zAppExpBookingsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpBookingsGetPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpBookingsGetResponse = zBookingOut;

export const zAppExpBookingsUpdateBody = zTripRequestIn;

export const zAppExpBookingsUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpBookingsUpdatePath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpBookingsUpdateResponse = zBookingOut;

export const zAppExpBookingsCancelPreviewHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpBookingsCancelPreviewPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpBookingsCancelPreviewResponse = zCancelPreviewOut;

export const zAppExpBookingsCancelHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpBookingsCancelPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpBookingsCancelResponse = zBookingOut;

export const zAppExpBookingsPayHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpBookingsPayPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpBookingsPayResponse = zPayOut;

export const zAppExpBookingsConferenceHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpBookingsConferencePath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppExpBookingsConferenceResponse = zConferenceOut;

export const zAppExpBookingsCalendarHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpBookingsCalendarPath = z.object({
    booking_id: z.uuid()
});

export const zAppExpAlertsDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppExpAlertsDeleteQuery = z.object({
    kind: zBookingTarget,
    region_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAppExpAlertsDeleteResponse = z.void();

export const zAppExpAlertsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppExpAlertsListResponse = zAlertsOut;

export const zAppExpAlertsCreateBody = zAlertIn;

export const zAppExpAlertsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnPathHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppLrnPathResponse = zPathOut;

export const zAppLrnPathUpdatesSeenBody = zUpdatesSeenIn;

export const zAppLrnPathUpdatesSeenHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppLrnPathUpdatesSeenResponse = z.void();

export const zAppLrnPathCountryHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnPathCountryPath = z.object({
    code: z.string()
});

/**
 * Successful Response
 */
export const zAppLrnPathCountryResponse = zCountryScreenOut;

export const zAppLrnPathRegionSummaryHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnPathRegionSummaryPath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnPathRegionSummaryResponse = zRegionSummaryOut;

export const zAppLrnPathRegionRouteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnPathRegionRoutePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnPathRegionRouteResponse = zRouteOut;

export const zAppLrnCoursesIntroHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppLrnCoursesIntroResponse = zIntroCourseOut;

export const zAppLrnCoursesGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnCoursesGetPath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnCoursesGetResponse = zCourseScreenOut;

export const zAppLrnCoursesLikeDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnCoursesLikeDeletePath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnCoursesLikeDeleteResponse = zCourseLikeOut;

export const zAppLrnCoursesLikeSetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnCoursesLikeSetPath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnCoursesLikeSetResponse = zCourseLikeOut;

export const zAppLrnRegionsCourseHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnRegionsCoursePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnRegionsCourseResponse = zRegionCourseOut;

export const zAppLrnLessonsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnLessonsGetPath = z.object({
    lesson_id: z.uuid()
});

export const zAppLrnLessonsGetQuery = z.object({
    course_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAppLrnLessonsGetResponse = zLessonScreenOut;

export const zAppLrnLessonsCompleteBody = zCompleteIn;

export const zAppLrnLessonsCompleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnLessonsCompletePath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnLessonsCompleteResponse = zCompleteOut;

export const zAppLrnLessonsPlaybackHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnLessonsPlaybackPath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnLessonsPlaybackResponse = zPlaybackOut;

export const zAppLrnLessonsVideoProgressBody = zVideoProgressIn;

export const zAppLrnLessonsVideoProgressHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnLessonsVideoProgressPath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnLessonsVideoProgressResponse = zVideoProgressOut;

export const zAppLrnLessonsReactionDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnLessonsReactionDeletePath = z.object({
    lesson_id: z.uuid(),
    kind: zReactionKind
});

/**
 * Successful Response
 */
export const zAppLrnLessonsReactionDeleteResponse = zReactionsOut;

export const zAppLrnLessonsReactionSetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnLessonsReactionSetPath = z.object({
    lesson_id: z.uuid(),
    kind: zReactionKind
});

/**
 * Successful Response
 */
export const zAppLrnLessonsReactionSetResponse = zReactionsOut;

export const zAppLrnTestsAttemptsCreateBody = zAttemptStartIn;

export const zAppLrnTestsAttemptsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnTestsAttemptsCreatePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnTestsAttemptsCreateResponse = zAttemptStartOut;

export const zAppLrnLevelCheckAttemptsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppLrnLevelCheckAttemptsCreateResponse = zAttemptStartOut;

export const zAppLrnLevelCheckGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppLrnLevelCheckGetResponse = zLevelCheckResultOut;

export const zAppLrnTestAttemptsAnswerBody = zAnswerIn;

export const zAppLrnTestAttemptsAnswerHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnTestAttemptsAnswerPath = z.object({
    attempt_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnTestAttemptsAnswerResponse = zAnswerOut;

export const zAppLrnTestAttemptsFinishHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnTestAttemptsFinishPath = z.object({
    attempt_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppLrnTestAttemptsFinishResponse = zFinishOut;

export const zAppLrnOffersHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnOffersQuery = z.object({
    items: z.string().max(2000)
});

/**
 * Successful Response
 */
export const zAppLrnOffersResponse = zLearnOffersOut;

export const zAppLrnEntitlementsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnEntitlementsListQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppLrnEntitlementsListResponse = zEntitlementPageOut;

export const zAppLrnSavedLessonsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppLrnSavedLessonsListQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppLrnSavedLessonsListResponse = zSavedLessonPageOut;

export const zAppTstTasteProfileGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppTstTasteProfileGetResponse = zTstTasteProfileOut;

export const zAppTstDiaryWinesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstDiaryWinesListQuery = z.object({
    type: zWineType.nullish(),
    sort: z.enum([
        'new',
        'high',
        'low'
    ]).optional().default('new'),
    region_id: z.uuid().nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppTstDiaryWinesListResponse = zTstDiaryListOut;

export const zAppTstDiaryWinesCreateBody = zTstDiaryWineCreateIn;

export const zAppTstDiaryWinesCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppTstDiaryWinesCreateResponse = zTstDiarySavedOut;

export const zAppTstDiaryPurchasedWinesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstDiaryPurchasedWinesListQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppTstDiaryPurchasedWinesListResponse = zCursorPageTstPurchasedWineOut;

export const zAppTstDiaryWinesGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstDiaryWinesGetPath = z.object({
    diary_wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstDiaryWinesGetResponse = zTstDiaryWineDetailOut;

export const zAppTstDiaryWinesUpdateBody = zTstDiaryWinePatchIn;

export const zAppTstDiaryWinesUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstDiaryWinesUpdatePath = z.object({
    diary_wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstDiaryWinesUpdateResponse = zTstDiaryWineDetailOut;

export const zAppTstDiaryTastingsCreateBody = zTstTastingIn;

export const zAppTstDiaryTastingsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstDiaryTastingsCreatePath = z.object({
    diary_wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstDiaryTastingsCreateResponse = zTstDiarySavedOut;

export const zAppTstCalibrationsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstCalibrationsListQuery = z.object({
    region_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAppTstCalibrationsListResponse = zTstCalibrationListOut;

export const zAppTstCalibrationsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstCalibrationsGetPath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstCalibrationsGetResponse = zTstCalibrationFormOut;

export const zAppTstCalibrationResultsCreateBody = z.object({
    photo: z.string(),
    answers: z.string()
});

export const zAppTstCalibrationResultsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstCalibrationResultsCreatePath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstCalibrationResultsCreateResponse = zTstCalibrationResultOut;

export const zAppTstCalibrationResultGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstCalibrationResultGetPath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstCalibrationResultGetResponse = zTstCalibrationResultOut;

export const zAppTstBlindFormGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstBlindFormGetPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstBlindFormGetResponse = zTstBlindFormOut;

export const zAppTstBlindAnswersCreateBody = zTstBlindAnswerIn;

export const zAppTstBlindAnswersCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstBlindAnswersCreatePath = z.object({
    tasting_id: z.uuid(),
    position: z.int().gte(1).lte(20)
});

/**
 * Successful Response
 */
export const zAppTstBlindAnswersCreateResponse = zTstBlindRevealOut;

export const zAppTstBlindResultGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstBlindResultGetPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstBlindResultGetResponse = zTstBlindResultOut;

export const zAppTstAccuracySummaryGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppTstAccuracySummaryGetResponse = zTstAccuracySummaryOut;

export const zAppTstAccuracyResultsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstAccuracyResultsListQuery = z.object({
    kind: z.enum([
        'all',
        'calibration',
        'blind'
    ]).optional().default('all'),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppTstAccuracyResultsListResponse = zTstMyResultsOut;

export const zAppTstSpecializationsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstSpecializationsListQuery = z.object({
    awarded: z.boolean().optional().default(false)
});

/**
 * Successful Response
 */
export const zAppTstSpecializationsListResponse = zTstSpecListOut;

export const zAppTstSpecializationsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstSpecializationsGetPath = z.object({
    specialization_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstSpecializationsGetResponse = zTstSpecDetailOut;

export const zAppTstRecognitionLabelsCreateBody = z.object({
    image: z.string()
});

export const zAppTstRecognitionLabelsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppTstRecognitionLabelsCreateResponse = zTstRecognitionOut;

export const zAppTstRecognitionLabelsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstRecognitionLabelsGetPath = z.object({
    recognition_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstRecognitionLabelsGetResponse = zTstRecognitionOut;

export const zAppTstRecognitionFeedbackCreateBody = zTstRecognitionFeedbackIn;

export const zAppTstRecognitionFeedbackCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppTstRecognitionFeedbackCreatePath = z.object({
    recognition_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppTstRecognitionFeedbackCreateResponse = z.void();

export const zAppGamConfigHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamConfigResponse = zGamConfigOut;

export const zAppGamMeHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamMeResponse = zGamMeOut;

export const zAppGamMeUpdateBody = zGamMePatchIn;

export const zAppGamMeUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamMeUpdateResponse = zGamMeOut;

export const zAppGamLedgerHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGamLedgerQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(8)
});

/**
 * Successful Response
 */
export const zAppGamLedgerResponse = zLedgerPageOut;

export const zAppGamRewardsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamRewardsResponse = zRewardsOut;

export const zAppGamExchangesHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGamExchangesQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppGamExchangesResponse = zGamExchangesPageOut;

export const zAppGamExchangesCreateBody = zExchangeCreateIn;

export const zAppGamExchangesCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAppGamExchangesCreateResponse = zExchangeCreatedOut;

export const zAppGamPassportHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamPassportResponse = zPassportOut;

export const zAppGamStampsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamStampsResponse = zStampsOut;

export const zAppGamRegionsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGamRegionsQuery = z.object({
    country: z.string().length(2).nullish()
});

/**
 * Successful Response
 */
export const zAppGamRegionsResponse = zRegionsOut;

export const zAppGamRegionHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGamRegionPath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppGamRegionResponse = zRegionProgressOut;

export const zAppGamWorldHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamWorldResponse = zWorldOut;

export const zAppGamAchievementsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppGamAchievementsResponse = zAchievementsOut;

export const zAppGamHistoryHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppGamHistoryQuery = z.object({
    year: z.int().gte(2000).lte(2100).nullish(),
    month: z.int().gte(1).lte(12).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppGamHistoryResponse = zGamHistoryPageOut;

export const zAppCntFeedHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntFeedQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20),
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppCntFeedResponse = zFeedOut;

export const zAppCntStoriesViewsBody = zStoryViewsIn;

export const zAppCntStoriesViewsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntStoriesViewsResponse = z.void();

export const zAppCntStoryLinkTapHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStoryLinkTapPath = z.object({
    story_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntStoryLinkTapResponse = z.void();

export const zAppCntAuthorStoriesHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntAuthorStoriesPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntAuthorStoriesResponse = zAuthorStoriesOut;

export const zAppCntPeopleHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntPeopleQuery = z.object({
    tab: z.enum(['experts', 'makers']).optional().default('experts'),
    region_id: z.uuid().nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCntPeopleResponse = zPeopleOut;

export const zAppCntExpertPageHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntExpertPagePath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntExpertPageResponse = zAuthorPageOut;

export const zAppCntMakerPageHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntMakerPagePath = z.object({
    member_id: z.uuid()
});

export const zAppCntMakerPageQuery = z.object({
    lat: z.number().gte(-90).lte(90).nullish(),
    lng: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAppCntMakerPageResponse = zAuthorPageOut;

export const zAppCntUnfollowHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntUnfollowPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntUnfollowResponse = zFollowStateOut;

export const zAppCntFollowHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntFollowPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntFollowResponse = zFollowStateOut;

export const zAppCntPublicationsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntPublicationsListQuery = z.object({
    scope: z.enum([
        'people',
        'region',
        'winery',
        'author',
        'wine',
        'experience'
    ]),
    region_id: z.uuid().nullish(),
    winery_id: z.uuid().nullish(),
    author_member_id: z.uuid().nullish(),
    wine_id: z.uuid().nullish(),
    experience_type: z.enum([
        'tasting',
        'excursion',
        'trip'
    ]).nullish(),
    experience_id: z.uuid().nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCntPublicationsListResponse = zPublicationListOut;

export const zAppCntPublicationsViewsBody = zPublicationViewsIn;

export const zAppCntPublicationsViewsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntPublicationsViewsResponse = z.void();

export const zAppCntPublicationGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntPublicationGetPath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntPublicationGetResponse = zPublicationScreenOut;

export const zAppCntPublicationUnlikeHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntPublicationUnlikePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntPublicationUnlikeResponse = zLikeStateOut;

export const zAppCntPublicationLikeHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntPublicationLikePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntPublicationLikeResponse = zLikeStateOut;

export const zAppCntCommentsListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntCommentsListPath = z.object({
    publication_id: z.uuid()
});

export const zAppCntCommentsListQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCntCommentsListResponse = zCommentListOut;

export const zAppCntCommentCreateBody = zPublicationCommentIn;

export const zAppCntCommentCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntCommentCreatePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntCommentCreateResponse = zCommentOut;

export const zAppCntSavedListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntSavedListQuery = z.object({
    type: z.enum([
        'wine',
        'set',
        'experience',
        'winery',
        'lesson'
    ]).optional().default('wine'),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCntSavedListResponse = zSavedOut;

export const zAppCntSavedCreateBody = zSavedIn;

export const zAppCntSavedCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntSavedCreateResponse = zSavedStateOut;

export const zAppCntSavedDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntSavedDeletePath = z.object({
    target_type: z.enum([
        'wine',
        'set',
        'drop',
        'tasting',
        'excursion',
        'trip',
        'winery',
        'lesson'
    ]),
    target_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntSavedDeleteResponse = zSavedStateOut;

export const zAppCntExpertApplicationWithdrawHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntExpertApplicationWithdrawResponse = z.void();

export const zAppCntExpertApplicationGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntExpertApplicationGetResponse = zApplicationStateOut;

export const zAppCntExpertApplicationSubmitBody = zApplicationIn;

export const zAppCntExpertApplicationSubmitHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntExpertApplicationSubmitResponse = zApplicationOut;

export const zAppCntExpertApplicationResubmitBody = zApplicationIn;

export const zAppCntExpertApplicationResubmitHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntExpertApplicationResubmitResponse = zApplicationOut;

export const zAppCntLegalDocumentsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Response App Cnt Legal Documents
 *
 * Successful Response
 */
export const zAppCntLegalDocumentsResponse = z.array(zLegalDocumentOut);

export const zAppCntLegalDocumentsCurrentHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Response App Cnt Legal Documents Current
 *
 * Successful Response
 */
export const zAppCntLegalDocumentsCurrentResponse = z.array(zLegalDocumentOut);

export const zAppCntLegalDocumentsAcceptBody = zLegalAcceptIn;

export const zAppCntLegalDocumentsAcceptHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Response App Cnt Legal Documents Accept
 *
 * Successful Response
 */
export const zAppCntLegalDocumentsAcceptResponse = z.array(zLegalDocumentOut);

export const zAppCntStudioHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntStudioResponse = zStudioOut;

export const zAppCntStudioPublicationsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStudioPublicationsQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCntStudioPublicationsResponse = zStudioPublicationsOut;

export const zAppCntStudioPublicationCreateBody = zStudioPublicationIn;

export const zAppCntStudioPublicationCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntStudioPublicationCreateResponse = zPublicationCreatedOut;

export const zAppCntStudioPublicationDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStudioPublicationDeletePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntStudioPublicationDeleteResponse = z.void();

export const zAppCntStudioCommentsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStudioCommentsPath = z.object({
    publication_id: z.uuid()
});

export const zAppCntStudioCommentsQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(20)
});

/**
 * Successful Response
 */
export const zAppCntStudioCommentsResponse = zStudioCommentsOut;

export const zAppCntStudioCommentReplyBody = zReplyIn;

export const zAppCntStudioCommentReplyHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStudioCommentReplyPath = z.object({
    comment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntStudioCommentReplyResponse = zCommentReplyOut;

export const zAppCntStudioStoriesHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Response App Cnt Studio Stories
 *
 * Successful Response
 */
export const zAppCntStudioStoriesResponse = z.array(zStudioStoryOut);

export const zAppCntStudioStoryCreateBody = zStudioStoryIn;

export const zAppCntStudioStoryCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppCntStudioStoryCreateResponse = zStoryCreatedOut;

export const zAppCntStudioStoryDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStudioStoryDeletePath = z.object({
    story_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppCntStudioStoryDeleteResponse = z.void();

export const zAppCntStudioTagOptionsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStudioTagOptionsQuery = z.object({
    type: z.enum([
        'region',
        'wine',
        'experience'
    ]),
    q: z.string().max(100).nullish(),
    region_id: z.uuid().nullish()
});

/**
 * Response App Cnt Studio Tag Options
 *
 * Successful Response
 */
export const zAppCntStudioTagOptionsResponse = z.array(zTagOptionOut);

export const zAppCntStudioLinkOptionsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppCntStudioLinkOptionsQuery = z.object({
    q: z.string().max(100).nullish()
});

/**
 * Response App Cnt Studio Link Options
 *
 * Successful Response
 */
export const zAppCntStudioLinkOptionsResponse = z.array(zLinkOptionOut);

export const zAppOrdCartGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdCartGetResponse = zOrdCartOut;

export const zAppOrdCartItemDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdCartItemDeletePath = z.object({
    item_key: z.string()
});

/**
 * Successful Response
 */
export const zAppOrdCartItemDeleteResponse = zOrdCartOut;

export const zAppOrdCartItemPutBody = zOrdCartItemIn;

export const zAppOrdCartItemPutHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdCartItemPutPath = z.object({
    item_key: z.string()
});

/**
 * Successful Response
 */
export const zAppOrdCartItemPutResponse = zOrdCartOut;

export const zAppOrdCartMergeBody = zOrdCartMergeIn;

export const zAppOrdCartMergeHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdCartMergeResponse = zOrdCartOut;

export const zAppOrdCheckoutDiscountsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdCheckoutDiscountsResponse = zOrdDiscountsOut;

export const zAppOrdCheckoutQuoteBody = zOrdQuoteIn;

export const zAppOrdCheckoutQuoteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdCheckoutQuoteResponse = zOrdQuoteOut;

export const zAppOrdCheckoutCreateBody = zOrdCheckoutIn;

export const zAppOrdCheckoutCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAppOrdCheckoutCreateResponse = zOrdCheckoutOut;

export const zAppOrdCheckoutGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdCheckoutGetPath = z.object({
    checkout_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdCheckoutGetResponse = zOrdCheckoutStatusOut;

export const zAppOrdCheckoutPaymentCreateBody = zOrdPaymentIn;

export const zAppOrdCheckoutPaymentCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

export const zAppOrdCheckoutPaymentCreatePath = z.object({
    checkout_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdCheckoutPaymentCreateResponse = zOrdAttemptView;

export const zAppOrdCheckoutClientResultBody = zOrdClientResultIn;

export const zAppOrdCheckoutClientResultHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdCheckoutClientResultPath = z.object({
    checkout_id: z.uuid(),
    attempt_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdCheckoutClientResultResponse = zOrdAttemptView;

export const zAppOrdPaymentMethodsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdPaymentMethodsQuery = z.object({
    group: zCartGroup.nullish()
});

/**
 * Successful Response
 */
export const zAppOrdPaymentMethodsResponse = zOrdPaymentMethodsOut;

export const zAppOrdAddressesListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdAddressesListResponse = zOrdAddressesOut;

export const zAppOrdAddressesCreateBody = zOrdAddressIn;

export const zAppOrdAddressesCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdAddressesCreateResponse = zOrdAddressOut;

export const zAppOrdAddressesDeleteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdAddressesDeletePath = z.object({
    address_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdAddressesDeleteResponse = z.void();

export const zAppOrdAddressesUpdateBody = zOrdAddressPatchIn;

export const zAppOrdAddressesUpdateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdAddressesUpdatePath = z.object({
    address_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdAddressesUpdateResponse = zOrdAddressOut;

export const zAppOrdShippingQuoteBody = zOrdShippingQuoteIn;

export const zAppOrdShippingQuoteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdShippingQuoteResponse = zOrdShippingQuoteOut;

export const zAppOrdShippingValidateBody = zOrdAddressValidateIn;

export const zAppOrdShippingValidateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdShippingValidateResponse = zOrdAddressValidateOut;

export const zAppOrdOrdersListHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdOrdersListQuery = z.object({
    group: zOrderGroup.nullish(),
    flow: zFlow.nullish(),
    ref: z.string().max(120).nullish(),
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAppOrdOrdersListResponse = zOrdOrdersPage;

export const zAppOrdOrdersOwnedRefsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppOrdOrdersOwnedRefsResponse = zOrdOwnedRefsOut;

export const zAppOrdOrdersGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdOrdersGetPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdOrdersGetResponse = zOrdOrderDetail;

export const zAppOrdOrdersCancelQuoteHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdOrdersCancelQuotePath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdOrdersCancelQuoteResponse = zOrdCancelQuoteOut;

export const zAppOrdOrdersCancelBody = zOrdCancelIn;

export const zAppOrdOrdersCancelHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdOrdersCancelPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdOrdersCancelResponse = zOrdOrderDetail;

export const zAppOrdOrdersReceiptsHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdOrdersReceiptsPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdOrdersReceiptsResponse = zOrdReceiptsOut;

export const zAppOrdOrdersReceiptResendHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdOrdersReceiptResendPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdOrdersReceiptResendResponse = zOrdAcceptedOut;

export const zAppOrdOrdersTrackingHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdOrdersTrackingPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdOrdersTrackingResponse = zOrdTrackingOut;

export const zAppOrdPartnerHandoffCreateBody = zOrdHandoffIn;

export const zAppOrdPartnerHandoffCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAppOrdPartnerHandoffCreateResponse = zOrdHandoffOut;

export const zAppOrdPartnerHandoffGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppOrdPartnerHandoffGetPath = z.object({
    handoff_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAppOrdPartnerHandoffGetResponse = zOrdHandoffStatusOut;

export const zAppIntBillingProductsGetHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

export const zAppIntBillingProductsGetQuery = z.object({
    items: z.string().max(8000)
});

/**
 * Successful Response
 */
export const zAppIntBillingProductsGetResponse = zBillingProductsOut;

export const zAppIntBillingProductsSeenBody = zBillingProductsSeenIn;

export const zAppIntBillingProductsSeenHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppIntBillingProductsSeenResponse = z.void();

export const zAppIntBillingIntentsCreateBody = zBillingIntentIn;

export const zAppIntBillingIntentsCreateHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string(),
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAppIntBillingIntentsCreateResponse = zBillingIntentOut;

export const zAppIntBillingPurchasesVerifyBody = zBillingPurchaseIn;

export const zAppIntBillingPurchasesVerifyHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppIntBillingPurchasesVerifyResponse = zBillingPurchaseOut;

export const zAppIntBillingSyncBody = zBillingSyncIn;

export const zAppIntBillingSyncHeaders = z.object({
    'X-Device-Id': z.uuid(),
    'X-Client-Platform': z.string(),
    'X-Client-Version': z.string()
});

/**
 * Successful Response
 */
export const zAppIntBillingSyncResponse = zBillingSyncOut;

export const zAppIntEmailUnsubscribePageQuery = z.object({
    token: z.string().max(2000)
});

/**
 * Страница результата
 */
export const zAppIntEmailUnsubscribePageResponse = z.string();

export const zAppIntEmailUnsubscribeOneClickQuery = z.object({
    token: z.string().max(2000)
});

/**
 * Отписка выполнена
 */
export const zAppIntEmailUnsubscribeOneClickResponse = z.string();

export const zAppSrvNotificationsUnsubscribeBody = zTokenIn;

/**
 * Successful Response
 */
export const zAppSrvNotificationsUnsubscribeResponse = zUnsubscribeOut;

export const zAppSrvNotificationsResubscribeBody = zTokenIn;

/**
 * Successful Response
 */
export const zAppSrvNotificationsResubscribeResponse = zUnsubscribeOut;

export const zAppAccAuthOauthAppleFormPostBody = z.object({
    code: z.string().optional(),
    id_token: z.string().optional(),
    state: z.string().optional(),
    user: z.string().optional(),
    error: z.string().optional()
});

/**
 * Successful Response
 */
export const zAdminMarketConfigGetResponse = zAdminConfigOut;

export const zAdminAccAuthLoginBody = zAdminLoginIn;

/**
 * Successful Response
 */
export const zAdminAccAuthLoginResponse = zAdminLoginOut;

export const zAdminAccAuthRefreshBody = zRefreshIn;

/**
 * Successful Response
 */
export const zAdminAccAuthRefreshResponse = zAdminRefreshOut;

/**
 * Successful Response
 */
export const zAdminAccAuthLogoutResponse = z.void();

/**
 * Successful Response
 */
export const zAdminAccMeGetResponse = zAdminMeOut;

export const zAdminAccMeUpdateBody = zAdminMePatchIn;

/**
 * Successful Response
 */
export const zAdminAccMeUpdateResponse = zAdminMeOut;

export const zAdminAccMePasswordChangeBody = zPasswordChangeIn;

/**
 * Successful Response
 */
export const zAdminAccMePasswordChangeResponse = z.void();

/**
 * Successful Response
 */
export const zAdminAccMeAuthorProfileGetResponse = zAuthorProfileOut;

export const zAdminAccMeAuthorProfileSaveBody = zAuthorProfileIn;

/**
 * Successful Response
 */
export const zAdminAccMeAuthorProfileSaveResponse = zAuthorProfileOut;

/**
 * Successful Response
 */
export const zAdminAccMeAuthorProfilePublishResponse = zAuthorProfileOut;

/**
 * Successful Response
 */
export const zAdminAccMeAuthorProfileHideResponse = zAuthorProfileOut;

export const zAdminAccUsersListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[full_name]': z.string().nullish(),
    'filter[full_name][]': z.array(z.string()).nullish(),
    'filter[email]': z.string().nullish(),
    'filter[email][]': z.array(z.string()).nullish(),
    'filter[phone]': z.string().nullish(),
    'filter[phone][]': z.array(z.string()).nullish(),
    'filter[country_code]': z.string().nullish(),
    'filter[country_code][]': z.array(z.string()).nullish(),
    'filter[city_name]': z.string().nullish(),
    'filter[city_name][]': z.array(z.string()).nullish(),
    'filter[birth_date]': z.string().nullish(),
    'filter[birth_date][]': z.array(z.string()).nullish(),
    'filter[registration_method]': z.string().nullish(),
    'filter[registration_method][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminAccUsersListResponse = zPageUserRow;

export const zAdminAccUsersExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[full_name]': z.string().nullish(),
    'filter[full_name][]': z.array(z.string()).nullish(),
    'filter[email]': z.string().nullish(),
    'filter[email][]': z.array(z.string()).nullish(),
    'filter[phone]': z.string().nullish(),
    'filter[phone][]': z.array(z.string()).nullish(),
    'filter[country_code]': z.string().nullish(),
    'filter[country_code][]': z.array(z.string()).nullish(),
    'filter[city_name]': z.string().nullish(),
    'filter[city_name][]': z.array(z.string()).nullish(),
    'filter[birth_date]': z.string().nullish(),
    'filter[birth_date][]': z.array(z.string()).nullish(),
    'filter[registration_method]': z.string().nullish(),
    'filter[registration_method][]': z.array(z.string()).nullish()
});

export const zAdminAccUsersGetPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccUsersGetResponse = zUserCard;

export const zAdminAccUsersUpdateBody = zUserPatchIn;

export const zAdminAccUsersUpdatePath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccUsersUpdateResponse = zUserCard;

export const zAdminAccUsersActivityPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccUsersActivityResponse = zUserActivityOut;

export const zAdminAccUsersHistoryPath = z.object({
    user_id: z.uuid()
});

export const zAdminAccUsersHistoryQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAdminAccUsersHistoryResponse = zCursorPageUserHistoryItem;

export const zAdminAccUsersBlockBody = zUserBlockIn;

export const zAdminAccUsersBlockPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccUsersBlockResponse = zUserCard;

export const zAdminAccUsersUnblockPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccUsersUnblockResponse = zUserCard;

export const zAdminAccUsersDeleteBody = zUserDeleteIn;

export const zAdminAccUsersDeletePath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccUsersDeleteResponse = z.void();

export const zAdminAccTeamListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[role]': z.string().nullish(),
    'filter[role][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminAccTeamListResponse = zPageMemberRow;

export const zAdminAccTeamCreateBody = zMemberCreateIn;

/**
 * Successful Response
 */
export const zAdminAccTeamCreateResponse = zMemberCreatedOut;

export const zAdminAccTeamGetPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamGetResponse = zMemberCard;

export const zAdminAccTeamUpdateBody = zMemberPatchIn;

export const zAdminAccTeamUpdatePath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamUpdateResponse = zMemberCard;

export const zAdminAccTeamResetPasswordPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamResetPasswordResponse = zPasswordResetOut;

export const zAdminAccTeamBlockBody = zMemberBlockIn;

export const zAdminAccTeamBlockPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamBlockResponse = zMemberCard;

export const zAdminAccTeamUnblockPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamUnblockResponse = zMemberCard;

export const zAdminAccTeamApplicationDecisionBody = zApplicationDecisionIn;

export const zAdminAccTeamApplicationDecisionPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamApplicationDecisionResponse = zMemberCard;

export const zAdminAccTeamOwnedSummaryPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamOwnedSummaryResponse = zOwnedSummaryOut;

export const zAdminAccTeamAuthorProfileSaveBody = zAuthorProfileIn;

export const zAdminAccTeamAuthorProfileSavePath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccTeamAuthorProfileSaveResponse = zAuthorProfileOut;

export const zAdminAccCareTicketsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[archived]': z.string().nullish(),
    'filter[archived][]': z.array(z.string()).nullish(),
    'filter[priority]': z.string().nullish(),
    'filter[priority][]': z.array(z.string()).nullish(),
    'filter[topic]': z.string().nullish(),
    'filter[topic][]': z.array(z.string()).nullish(),
    'filter[channel]': z.string().nullish(),
    'filter[channel][]': z.array(z.string()).nullish(),
    'filter[assignee_id]': z.string().nullish(),
    'filter[assignee_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminAccCareTicketsListResponse = zPageCareRow;

export const zAdminAccCareTicketsCreateBody = zAppModulesAccSchemasCareTicketCreateIn;

/**
 * Successful Response
 */
export const zAdminAccCareTicketsCreateResponse = zCareTicket;

/**
 * Successful Response
 */
export const zAdminAccCareDictionariesGetResponse = zCareDictionariesOut;

export const zAdminAccCareTicketsGetPath = z.object({
    ticket_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccCareTicketsGetResponse = zCareTicket;

export const zAdminAccCareTicketsUpdateBody = zTicketPatchIn;

export const zAdminAccCareTicketsUpdatePath = z.object({
    ticket_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccCareTicketsUpdateResponse = zCareTicket;

export const zAdminAccCareTicketsTakePath = z.object({
    ticket_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccCareTicketsTakeResponse = zCareTicket;

export const zAdminAccCareTicketsReplyBody = zTicketReplyIn;

export const zAdminAccCareTicketsReplyPath = z.object({
    ticket_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccCareTicketsReplyResponse = zCareTicket;

export const zAdminAccCareTicketsTransitionBody = zTicketTransitionIn;

export const zAdminAccCareTicketsTransitionPath = z.object({
    ticket_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccCareTicketsTransitionResponse = zCareTicket;

export const zAdminAccCareTicketsArchiveBody = zTicketArchiveIn;

export const zAdminAccCareTicketsArchivePath = z.object({
    ticket_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccCareTicketsArchiveResponse = zCareTicket;

export const zAdminAccOnboardingScreensListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingScreensListResponse = zPageOnboardingScreenRow;

export const zAdminAccOnboardingScreensGetPath = z.object({
    screen_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingScreensGetResponse = zOnboardingScreenOut;

export const zAdminAccOnboardingScreensUpdateBody = zOnboardingScreenPatchIn;

export const zAdminAccOnboardingScreensUpdatePath = z.object({
    screen_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingScreensUpdateResponse = zOnboardingScreenOut;

export const zAdminAccOnboardingScreensMoveBody = zOnboardingMoveIn;

export const zAdminAccOnboardingScreensMovePath = z.object({
    screen_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingScreensMoveResponse = zOnboardingMoveOut;

export const zAdminAccOnboardingScreensVisibilityBody = zOnboardingVisibilityIn;

export const zAdminAccOnboardingScreensVisibilityPath = z.object({
    screen_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingScreensVisibilityResponse = zOnboardingScreenOut;

export const zAdminAccOnboardingItemsCreateBody = zOnboardingItemCreateIn;

export const zAdminAccOnboardingItemsCreatePath = z.object({
    screen_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingItemsCreateResponse = zOnboardingItemOut;

export const zAdminAccOnboardingItemsDeletePath = z.object({
    item_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingItemsDeleteResponse = z.void();

export const zAdminAccOnboardingItemsUpdateBody = zOnboardingItemPatchIn;

export const zAdminAccOnboardingItemsUpdatePath = z.object({
    item_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminAccOnboardingItemsUpdateResponse = zOnboardingItemOut;

export const zAdminCoreUploadsCreateBody = zUploadCreateIn;

/**
 * Successful Response
 */
export const zAdminCoreUploadsCreateResponse = zUploadTicketOut;

export const zAdminCoreUploadsCompletePath = z.object({
    upload_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCoreUploadsCompleteResponse = zFileOut;

export const zAdminIntMailCapacityGetQuery = z.object({
    recipients: z.int().gte(0).lte(10000000).optional().default(0)
});

/**
 * Successful Response
 */
export const zAdminIntMailCapacityGetResponse = zCapacityOut;

export const zAdminIntIntegrationsListQuery = z.object({
    'filter[status]': z.string().nullish(),
    'filter[port]': z.string().nullish(),
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsListResponse = zIntegrationsPage;

export const zAdminIntIntegrationsGetPath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsGetResponse = zIntegrationCard;

export const zAdminIntIntegrationsUpdateBody = zIntegrationUpdateIn;

export const zAdminIntIntegrationsUpdatePath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsUpdateResponse = zIntegrationSaveOut;

export const zAdminIntIntegrationsTestPath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsTestResponse = zCheckResultOut;

export const zAdminIntIntegrationsEnablePath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsEnableResponse = zIntegrationCard;

export const zAdminIntIntegrationsDisableBody = zConfirmIn;

export const zAdminIntIntegrationsDisablePath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsDisableResponse = zIntegrationCard;

export const zAdminIntIntegrationsActivateBody = zConfirmIn;

export const zAdminIntIntegrationsActivatePath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsActivateResponse = zIntegrationCard;

/**
 * Body
 */
export const zAdminIntIntegrationsActionBody = zIntegrationActionIn.nullable();

export const zAdminIntIntegrationsActionPath = z.object({
    key: z.string(),
    action: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntIntegrationsActionResponse = zActionResultOut;

export const zAdminIntAlertsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[integration_key]': z.string().nullish(),
    'filter[integration_key][]': z.array(z.string()).nullish(),
    'filter[severity]': z.string().nullish(),
    'filter[severity][]': z.array(z.string()).nullish(),
    'filter[type]': z.string().nullish(),
    'filter[type][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntAlertsListResponse = zPageAlertOut;

/**
 * Body
 */
export const zAdminIntAlertsResolveBody = zAlertResolveIn.nullable();

export const zAdminIntAlertsResolvePath = z.object({
    alert_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntAlertsResolveResponse = zAlertOut;

export const zAdminIntLogsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(50),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[integration_key]': z.string().nullish(),
    'filter[integration_key][]': z.array(z.string()).nullish(),
    'filter[direction]': z.string().nullish(),
    'filter[direction][]': z.array(z.string()).nullish(),
    'filter[result]': z.string().nullish(),
    'filter[result][]': z.array(z.string()).nullish(),
    'filter[operation]': z.string().nullish(),
    'filter[operation][]': z.array(z.string()).nullish(),
    'filter[environment]': z.string().nullish(),
    'filter[environment][]': z.array(z.string()).nullish(),
    'filter[category]': z.string().nullish(),
    'filter[category][]': z.array(z.string()).nullish(),
    'filter[from]': z.string().nullish(),
    'filter[from][]': z.array(z.string()).nullish(),
    'filter[to]': z.string().nullish(),
    'filter[to][]': z.array(z.string()).nullish(),
    'filter[trace_id]': z.string().nullish(),
    'filter[trace_id][]': z.array(z.string()).nullish(),
    'filter[order_id]': z.string().nullish(),
    'filter[order_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[entity_type]': z.string().nullish(),
    'filter[entity_type][]': z.array(z.string()).nullish(),
    'filter[entity_id]': z.string().nullish(),
    'filter[entity_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntLogsListResponse = zLogsPage;

export const zAdminIntLogsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(50),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[integration_key]': z.string().nullish(),
    'filter[integration_key][]': z.array(z.string()).nullish(),
    'filter[direction]': z.string().nullish(),
    'filter[direction][]': z.array(z.string()).nullish(),
    'filter[result]': z.string().nullish(),
    'filter[result][]': z.array(z.string()).nullish(),
    'filter[operation]': z.string().nullish(),
    'filter[operation][]': z.array(z.string()).nullish(),
    'filter[environment]': z.string().nullish(),
    'filter[environment][]': z.array(z.string()).nullish(),
    'filter[category]': z.string().nullish(),
    'filter[category][]': z.array(z.string()).nullish(),
    'filter[from]': z.string().nullish(),
    'filter[from][]': z.array(z.string()).nullish(),
    'filter[to]': z.string().nullish(),
    'filter[to][]': z.array(z.string()).nullish(),
    'filter[trace_id]': z.string().nullish(),
    'filter[trace_id][]': z.array(z.string()).nullish(),
    'filter[order_id]': z.string().nullish(),
    'filter[order_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[entity_type]': z.string().nullish(),
    'filter[entity_type][]': z.array(z.string()).nullish(),
    'filter[entity_id]': z.string().nullish(),
    'filter[entity_id][]': z.array(z.string()).nullish()
});

export const zAdminIntLogsGetPath = z.object({
    log_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntLogsGetResponse = zLogCard;

export const zAdminIntMailSuppressionsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[reason]': z.string().nullish(),
    'filter[reason][]': z.array(z.string()).nullish(),
    'filter[scope]': z.string().nullish(),
    'filter[scope][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntMailSuppressionsListResponse = zPageSuppressionOut;

export const zAdminIntMailSuppressionsCreateBody = zSuppressionCreateIn;

/**
 * Successful Response
 */
export const zAdminIntMailSuppressionsCreateResponse = zSuppressionOut;

export const zAdminIntMailSuppressionsDeletePath = z.object({
    suppression_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntMailSuppressionsDeleteResponse = z.void();

export const zAdminGeoDictionariesRegionsListQuery = z.object({
    country_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoDictionariesRegionsListResponse = zRegionDictionaryOut;

export const zAdminGeoSystemCountriesListQuery = z.object({
    q: z.string().max(100).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoSystemCountriesListResponse = zSystemCountryListOut;

export const zAdminGeoCountriesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[code]': z.string().nullish(),
    'filter[code][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoCountriesListResponse = zPageCountryRowOut;

export const zAdminGeoCountriesCreateBody = zCountryCreateIn;

/**
 * Successful Response
 */
export const zAdminGeoCountriesCreateResponse = zCountryDetailOut;

export const zAdminGeoCountriesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[code]': z.string().nullish(),
    'filter[code][]': z.array(z.string()).nullish()
});

export const zAdminGeoCountriesDeleteBody = zAppModulesGeoSchemasAdminConfirmNameIn;

export const zAdminGeoCountriesDeletePath = z.object({
    country_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountriesDeleteResponse = z.void();

export const zAdminGeoCountriesGetPath = z.object({
    country_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountriesGetResponse = zCountryDetailOut;

export const zAdminGeoCountriesUpdateBody = zCountryPatchIn;

export const zAdminGeoCountriesUpdatePath = z.object({
    country_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountriesUpdateResponse = zCountryDetailOut;

export const zAdminGeoCountryFactsCreateBody = zFactIn;

export const zAdminGeoCountryFactsCreatePath = z.object({
    country_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountryFactsCreateResponse = zAppModulesGeoSchemasAdminFactOut;

export const zAdminGeoCountryFactsDeletePath = z.object({
    country_id: z.uuid(),
    fact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountryFactsDeleteResponse = z.void();

export const zAdminGeoCountryFactsUpdateBody = zFactPatchIn;

export const zAdminGeoCountryFactsUpdatePath = z.object({
    country_id: z.uuid(),
    fact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountryFactsUpdateResponse = zAppModulesGeoSchemasAdminFactOut;

export const zAdminGeoCountryQuizzesAttachBody = zQuizzesAttachIn;

export const zAdminGeoCountryQuizzesAttachPath = z.object({
    country_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountryQuizzesAttachResponse = z.void();

export const zAdminGeoCountryQuizzesDetachPath = z.object({
    country_id: z.uuid(),
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoCountryQuizzesDetachResponse = z.void();

export const zAdminGeoOriginsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[country_id]': z.string().nullish(),
    'filter[country_id][]': z.array(z.string()).nullish(),
    'filter[level]': z.string().nullish(),
    'filter[level][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoOriginsListResponse = zPageOriginOut;

export const zAdminGeoOriginsCreateBody = zOriginCreateIn;

/**
 * Successful Response
 */
export const zAdminGeoOriginsCreateResponse = zOriginOut;

export const zAdminGeoOriginsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[country_id]': z.string().nullish(),
    'filter[country_id][]': z.array(z.string()).nullish(),
    'filter[level]': z.string().nullish(),
    'filter[level][]': z.array(z.string()).nullish()
});

export const zAdminGeoOriginsUpdateBody = zOriginPatchIn;

export const zAdminGeoOriginsUpdatePath = z.object({
    origin_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoOriginsUpdateResponse = zOriginOut;

export const zAdminGeoMapZonesListQuery = z.object({
    country_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoMapZonesListResponse = zMapZoneListOut;

export const zAdminGeoRegionsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[country_id]': z.string().nullish(),
    'filter[country_id][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[map_zone_id]': z.string().nullish(),
    'filter[map_zone_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsListResponse = zPageRegionRowOut;

export const zAdminGeoRegionsCreateBody = zRegionCreateIn;

/**
 * Successful Response
 */
export const zAdminGeoRegionsCreateResponse = zRegionDetailOut;

export const zAdminGeoRegionsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[country_id]': z.string().nullish(),
    'filter[country_id][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[map_zone_id]': z.string().nullish(),
    'filter[map_zone_id][]': z.array(z.string()).nullish()
});

export const zAdminGeoRegionsDeleteBody = zAppModulesGeoSchemasAdminConfirmNameIn;

export const zAdminGeoRegionsDeletePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsDeleteResponse = z.void();

export const zAdminGeoRegionsGetPath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsGetResponse = zRegionDetailOut;

export const zAdminGeoRegionsUpdateBody = zRegionPatchIn;

export const zAdminGeoRegionsUpdatePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsUpdateResponse = zRegionDetailOut;

export const zAdminGeoRegionsContourSetBody = zContourIn;

export const zAdminGeoRegionsContourSetPath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsContourSetResponse = zRegionDetailOut;

export const zAdminGeoRegionsPublishPath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsPublishResponse = zRegionDetailOut;

export const zAdminGeoRegionsUnpublishBody = zUnpublishRegionIn;

export const zAdminGeoRegionsUnpublishPath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsUnpublishResponse = zRegionDetailOut;

export const zAdminGeoRegionsArchiveBody = zAppModulesGeoSchemasAdminArchiveIn;

export const zAdminGeoRegionsArchivePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsArchiveResponse = zRegionDetailOut;

export const zAdminGeoRegionsRestorePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsRestoreResponse = zRegionDetailOut;

export const zAdminGeoRegionFactsCreateBody = zFactIn;

export const zAdminGeoRegionFactsCreatePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionFactsCreateResponse = zAppModulesGeoSchemasAdminFactOut;

export const zAdminGeoRegionFactsDeletePath = z.object({
    region_id: z.uuid(),
    fact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionFactsDeleteResponse = z.void();

export const zAdminGeoRegionFactsUpdateBody = zFactPatchIn;

export const zAdminGeoRegionFactsUpdatePath = z.object({
    region_id: z.uuid(),
    fact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionFactsUpdateResponse = zAppModulesGeoSchemasAdminFactOut;

export const zAdminGeoRegionMediaCreateBody = zAppModulesGeoSchemasCommonMediaIn;

export const zAdminGeoRegionMediaCreatePath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionMediaCreateResponse = zMediaOut;

export const zAdminGeoRegionMediaDeletePath = z.object({
    region_id: z.uuid(),
    media_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionMediaDeleteResponse = z.void();

export const zAdminGeoRegionMediaUpdateBody = zAppModulesGeoSchemasCommonMediaPatchIn;

export const zAdminGeoRegionMediaUpdatePath = z.object({
    region_id: z.uuid(),
    media_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionMediaUpdateResponse = zMediaOut;

export const zAdminGeoRegionLinksListPath = z.object({
    region_id: z.uuid()
});

export const zAdminGeoRegionLinksListQuery = z.object({
    tab: z.string().max(20)
});

/**
 * Successful Response
 */
export const zAdminGeoRegionLinksListResponse = zLinkListOut;

export const zAdminGeoRegionLinksAttachBody = zLinkAttachIn;

export const zAdminGeoRegionLinksAttachPath = z.object({
    region_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionLinksAttachResponse = zAppModulesGeoSchemasAdminLinkItemOut;

export const zAdminGeoRegionLinksCandidatesPath = z.object({
    region_id: z.uuid()
});

export const zAdminGeoRegionLinksCandidatesQuery = z.object({
    tab: z.string().max(20),
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionLinksCandidatesResponse = zLinkCandidateListOut;

export const zAdminGeoRegionLinksDetachPath = z.object({
    region_id: z.uuid(),
    entity_type: z.string(),
    entity_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoRegionLinksDetachResponse = z.void();

export const zAdminGeoRegionsHistoryPath = z.object({
    region_id: z.uuid()
});

export const zAdminGeoRegionsHistoryQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAdminGeoRegionsHistoryResponse = zAppModulesGeoSchemasAdminHistoryPageOut;

export const zAdminGeoWineriesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[visit_mode]': z.string().nullish(),
    'filter[visit_mode][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesListResponse = zWineryPageOut;

export const zAdminGeoWineriesCreateBody = zWineryCreateIn;

/**
 * Successful Response
 */
export const zAdminGeoWineriesCreateResponse = zWineryDetailOut;

export const zAdminGeoWineriesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[visit_mode]': z.string().nullish(),
    'filter[visit_mode][]': z.array(z.string()).nullish()
});

export const zAdminGeoWineriesDeleteBody = zAppModulesGeoSchemasAdminConfirmNameIn;

export const zAdminGeoWineriesDeletePath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesDeleteResponse = z.void();

export const zAdminGeoWineriesGetPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesGetResponse = zWineryDetailOut;

export const zAdminGeoWineriesUpdateBody = zWineryPatchIn;

export const zAdminGeoWineriesUpdatePath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesUpdateResponse = zWineryDetailOut;

export const zAdminGeoWineriesGuestsUpdateBody = zGuestsIn;

export const zAdminGeoWineriesGuestsUpdatePath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesGuestsUpdateResponse = zWineryDetailOut;

export const zAdminGeoWineriesSubmitPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesSubmitResponse = zWineryDetailOut;

export const zAdminGeoWineriesPublishPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesPublishResponse = zWineryDetailOut;

export const zAdminGeoWineriesReturnBody = zCommentIn;

export const zAdminGeoWineriesReturnPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesReturnResponse = zWineryDetailOut;

export const zAdminGeoWineriesRejectBody = zCommentIn;

export const zAdminGeoWineriesRejectPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesRejectResponse = zWineryDetailOut;

export const zAdminGeoWineriesUnpublishPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesUnpublishResponse = zWineryDetailOut;

export const zAdminGeoWineriesArchiveBody = zAppModulesGeoSchemasAdminArchiveIn;

export const zAdminGeoWineriesArchivePath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesArchiveResponse = zWineryDetailOut;

export const zAdminGeoWineriesRestorePath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesRestoreResponse = zWineryDetailOut;

export const zAdminGeoWineryFactsCreateBody = zFactIn;

export const zAdminGeoWineryFactsCreatePath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryFactsCreateResponse = zAppModulesGeoSchemasAdminFactOut;

export const zAdminGeoWineryFactsDeletePath = z.object({
    winery_id: z.uuid(),
    fact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryFactsDeleteResponse = z.void();

export const zAdminGeoWineryFactsUpdateBody = zFactPatchIn;

export const zAdminGeoWineryFactsUpdatePath = z.object({
    winery_id: z.uuid(),
    fact_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryFactsUpdateResponse = zAppModulesGeoSchemasAdminFactOut;

export const zAdminGeoWineryMediaCreateBody = zAppModulesGeoSchemasCommonMediaIn;

export const zAdminGeoWineryMediaCreatePath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryMediaCreateResponse = zMediaOut;

export const zAdminGeoWineryMediaDeletePath = z.object({
    winery_id: z.uuid(),
    media_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryMediaDeleteResponse = z.void();

export const zAdminGeoWineryMediaUpdateBody = zAppModulesGeoSchemasCommonMediaPatchIn;

export const zAdminGeoWineryMediaUpdatePath = z.object({
    winery_id: z.uuid(),
    media_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryMediaUpdateResponse = zMediaOut;

export const zAdminGeoWineryReviewsListPath = z.object({
    winery_id: z.uuid()
});

export const zAdminGeoWineryReviewsListQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAdminGeoWineryReviewsListResponse = zAdminReviewsOut;

export const zAdminGeoWineryLinksListPath = z.object({
    winery_id: z.uuid()
});

export const zAdminGeoWineryLinksListQuery = z.object({
    tab: z.string().max(20)
});

/**
 * Successful Response
 */
export const zAdminGeoWineryLinksListResponse = zLinkListOut;

export const zAdminGeoWineryLinksAttachBody = zLinkAttachIn;

export const zAdminGeoWineryLinksAttachPath = z.object({
    winery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryLinksAttachResponse = zAppModulesGeoSchemasAdminLinkItemOut;

export const zAdminGeoWineryLinksCandidatesPath = z.object({
    winery_id: z.uuid()
});

export const zAdminGeoWineryLinksCandidatesQuery = z.object({
    tab: z.string().max(20),
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryLinksCandidatesResponse = zLinkCandidateListOut;

export const zAdminGeoWineryLinksDetachPath = z.object({
    winery_id: z.uuid(),
    entity_type: z.string(),
    entity_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoWineryLinksDetachResponse = z.void();

export const zAdminGeoWineriesHistoryPath = z.object({
    winery_id: z.uuid()
});

export const zAdminGeoWineriesHistoryQuery = z.object({
    cursor: z.string().max(1000).nullish(),
    limit: z.int().optional().default(20)
});

/**
 * Successful Response
 */
export const zAdminGeoWineriesHistoryResponse = zAppModulesGeoSchemasAdminHistoryPageOut;

export const zAdminGeoAppMarketsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGeoAppMarketsListResponse = zPageAppMarketOut;

export const zAdminGeoAppMarketsCreateBody = zAppMarketCreateIn;

/**
 * Successful Response
 */
export const zAdminGeoAppMarketsCreateResponse = zAppModulesGeoSchemasAdminAppMarketOut;

export const zAdminGeoAppMarketsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish()
});

export const zAdminGeoAppMarketsGetPath = z.object({
    market_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoAppMarketsGetResponse = zAppModulesGeoSchemasAdminAppMarketOut;

export const zAdminGeoAppMarketsUpdateBody = zAppMarketPatchIn;

export const zAdminGeoAppMarketsUpdatePath = z.object({
    market_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoAppMarketsUpdateResponse = zAppModulesGeoSchemasAdminAppMarketOut;

export const zAdminGeoAppMarketsSetStatusBody = zAppMarketStatusIn;

export const zAdminGeoAppMarketsSetStatusPath = z.object({
    market_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGeoAppMarketsSetStatusResponse = zAppModulesGeoSchemasAdminAppMarketOut;

export const zAdminCatReferenceGetQuery = z.object({
    region_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAdminCatReferenceGetResponse = zReferenceOut;

export const zAdminCatWinePickerQuery = z.object({
    q: z.string().max(100).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zAdminCatWinePickerResponse = zPickerOut;

export const zAdminCatDropTargetsQuery = z.object({
    q: z.string().max(100).nullish(),
    limit: z.int().gte(1).lte(100).optional().default(50)
});

/**
 * Successful Response
 */
export const zAdminCatDropTargetsResponse = zDropTargetsOut;

export const zAdminCatMediaListPath = z.object({
    owner_type: zCatalogType,
    record_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatMediaListResponse = zMediaListOut;

export const zAdminCatMediaCreateBody = zAppModulesCatSchemasAdminMediaIn;

export const zAdminCatMediaCreatePath = z.object({
    owner_type: zCatalogType,
    record_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatMediaCreateResponse = zCatalogMediaOut;

export const zAdminCatMediaDeletePath = z.object({
    owner_type: zCatalogType,
    record_id: z.uuid(),
    media_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatMediaDeleteResponse = z.void();

export const zAdminCatMediaUpdateBody = zAppModulesCatSchemasAdminMediaPatchIn;

export const zAdminCatMediaUpdatePath = z.object({
    owner_type: zCatalogType,
    record_id: z.uuid(),
    media_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatMediaUpdateResponse = zCatalogMediaOut;

export const zAdminCatWinesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[winery_id]': z.string().nullish(),
    'filter[winery_id][]': z.array(z.string()).nullish(),
    'filter[name]': z.string().nullish(),
    'filter[name][]': z.array(z.string()).nullish(),
    'filter[region]': z.string().nullish(),
    'filter[region][]': z.array(z.string()).nullish(),
    'filter[winery]': z.string().nullish(),
    'filter[winery][]': z.array(z.string()).nullish(),
    'filter[sku]': z.string().nullish(),
    'filter[sku][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCatWinesListResponse = zCatalogPageOutWineRowOut;

export const zAdminCatWinesCreateBody = zWineCreateIn;

/**
 * Successful Response
 */
export const zAdminCatWinesCreateResponse = zWineAdmin;

export const zAdminCatWinesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[winery_id]': z.string().nullish(),
    'filter[winery_id][]': z.array(z.string()).nullish(),
    'filter[name]': z.string().nullish(),
    'filter[name][]': z.array(z.string()).nullish(),
    'filter[region]': z.string().nullish(),
    'filter[region][]': z.array(z.string()).nullish(),
    'filter[winery]': z.string().nullish(),
    'filter[winery][]': z.array(z.string()).nullish(),
    'filter[sku]': z.string().nullish(),
    'filter[sku][]': z.array(z.string()).nullish()
});

export const zAdminCatWinesDeleteBody = zAppModulesCatSchemasAdminConfirmNameIn;

export const zAdminCatWinesDeletePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesDeleteResponse = z.void();

export const zAdminCatWinesGetPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesGetResponse = zWineCardOut;

export const zAdminCatWinesUpdateBody = zWinePatchIn;

export const zAdminCatWinesUpdatePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesUpdateResponse = zWineAdmin;

export const zAdminCatWinesGrapesSetBody = zGrapesIn;

export const zAdminCatWinesGrapesSetPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesGrapesSetResponse = zWineAdmin;

export const zAdminCatWinesNotesSetBody = zNotesIn;

export const zAdminCatWinesNotesSetPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesNotesSetResponse = zWineAdmin;

export const zAdminCatWinesPairingsSetBody = zPairingsIn;

export const zAdminCatWinesPairingsSetPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesPairingsSetResponse = zWineAdmin;

export const zAdminCatWinesPriceChangeBody = zPriceChangeIn;

export const zAdminCatWinesPriceChangePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesPriceChangeResponse = zPriceChangeOut;

export const zAdminCatWinesStockUpdateBody = zStockIn;

export const zAdminCatWinesStockUpdatePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesStockUpdateResponse = zWineAdmin;

export const zAdminCatWinesSalesPath = z.object({
    wine_id: z.uuid()
});

export const zAdminCatWinesSalesQuery = z.object({
    days: z.int().gte(1).lte(3650).optional().default(90)
});

/**
 * Successful Response
 */
export const zAdminCatWinesSalesResponse = zWineSalesOut;

export const zAdminCatWinesLinksPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesLinksResponse = zWineLinksOut;

export const zAdminCatWinesAttachBody = zAttachIn;

export const zAdminCatWinesAttachPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesAttachResponse = z.void();

export const zAdminCatSetsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[format]': z.string().nullish(),
    'filter[format][]': z.array(z.string()).nullish(),
    'filter[author_type]': z.string().nullish(),
    'filter[author_type][]': z.array(z.string()).nullish(),
    'filter[name]': z.string().nullish(),
    'filter[name][]': z.array(z.string()).nullish(),
    'filter[sku]': z.string().nullish(),
    'filter[sku][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCatSetsListResponse = zCatalogPageOutSetRowOut;

export const zAdminCatSetsCreateBody = zSetCreateIn;

/**
 * Successful Response
 */
export const zAdminCatSetsCreateResponse = zSetAdmin;

export const zAdminCatSetsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[format]': z.string().nullish(),
    'filter[format][]': z.array(z.string()).nullish(),
    'filter[author_type]': z.string().nullish(),
    'filter[author_type][]': z.array(z.string()).nullish(),
    'filter[name]': z.string().nullish(),
    'filter[name][]': z.array(z.string()).nullish(),
    'filter[sku]': z.string().nullish(),
    'filter[sku][]': z.array(z.string()).nullish()
});

export const zAdminCatSetsDeleteBody = zAppModulesCatSchemasAdminConfirmNameIn;

export const zAdminCatSetsDeletePath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsDeleteResponse = z.void();

export const zAdminCatSetsGetPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsGetResponse = zSetAdmin;

export const zAdminCatSetsUpdateBody = zSetPatchIn;

export const zAdminCatSetsUpdatePath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsUpdateResponse = zSetAdmin;

export const zAdminCatSetsItemsSetBody = zSetItemsIn;

export const zAdminCatSetsItemsSetPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsItemsSetResponse = zSetAdmin;

export const zAdminCatSetsSalesPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsSalesResponse = zSetSalesOut;

export const zAdminCatDropsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[sales_state]': z.string().nullish(),
    'filter[sales_state][]': z.array(z.string()).nullish(),
    'filter[target_type]': z.string().nullish(),
    'filter[target_type][]': z.array(z.string()).nullish(),
    'filter[target_wine_id]': z.string().nullish(),
    'filter[target_wine_id][]': z.array(z.string()).nullish(),
    'filter[target_set_id]': z.string().nullish(),
    'filter[target_set_id][]': z.array(z.string()).nullish(),
    'filter[name]': z.string().nullish(),
    'filter[name][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCatDropsListResponse = zCatalogPageOutDropRowOut;

export const zAdminCatDropsCreateBody = zDropCreateIn;

/**
 * Successful Response
 */
export const zAdminCatDropsCreateResponse = zDropAdmin;

export const zAdminCatDropsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[sales_state]': z.string().nullish(),
    'filter[sales_state][]': z.array(z.string()).nullish(),
    'filter[target_type]': z.string().nullish(),
    'filter[target_type][]': z.array(z.string()).nullish(),
    'filter[target_wine_id]': z.string().nullish(),
    'filter[target_wine_id][]': z.array(z.string()).nullish(),
    'filter[target_set_id]': z.string().nullish(),
    'filter[target_set_id][]': z.array(z.string()).nullish(),
    'filter[name]': z.string().nullish(),
    'filter[name][]': z.array(z.string()).nullish()
});

export const zAdminCatDropsDeleteBody = zAppModulesCatSchemasAdminConfirmNameIn;

export const zAdminCatDropsDeletePath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsDeleteResponse = z.void();

export const zAdminCatDropsGetPath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsGetResponse = zDropAdmin;

export const zAdminCatDropsUpdateBody = zDropPatchIn;

export const zAdminCatDropsUpdatePath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsUpdateResponse = zDropAdmin;

export const zAdminCatWinesSubmitPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesSubmitResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatWinesPublishPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesPublishResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatWinesReturnBody = zReviewNoteIn;

export const zAdminCatWinesReturnPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesReturnResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatWinesRejectBody = zReviewNoteIn;

export const zAdminCatWinesRejectPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesRejectResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatWinesUnpublishPath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesUnpublishResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatWinesArchiveBody = zAppModulesCatSchemasAdminArchiveIn;

export const zAdminCatWinesArchivePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesArchiveResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatWinesRestorePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesRestoreResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatWinesUsagePath = z.object({
    wine_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatWinesUsageResponse = zUsageOut;

export const zAdminCatWinesHistoryPath = z.object({
    wine_id: z.uuid()
});

export const zAdminCatWinesHistoryQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminCatWinesHistoryResponse = zAppModulesCatSchemasAdminHistoryPageOut;

export const zAdminCatSetsSubmitPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsSubmitResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatSetsPublishPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsPublishResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatSetsReturnBody = zReviewNoteIn;

export const zAdminCatSetsReturnPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsReturnResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatSetsRejectBody = zReviewNoteIn;

export const zAdminCatSetsRejectPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsRejectResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatSetsUnpublishPath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsUnpublishResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatSetsArchiveBody = zAppModulesCatSchemasAdminArchiveIn;

export const zAdminCatSetsArchivePath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsArchiveResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatSetsRestorePath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsRestoreResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatSetsUsagePath = z.object({
    set_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatSetsUsageResponse = zUsageOut;

export const zAdminCatSetsHistoryPath = z.object({
    set_id: z.uuid()
});

export const zAdminCatSetsHistoryQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminCatSetsHistoryResponse = zAppModulesCatSchemasAdminHistoryPageOut;

export const zAdminCatDropsSubmitPath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsSubmitResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatDropsPublishPath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsPublishResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatDropsReturnBody = zReviewNoteIn;

export const zAdminCatDropsReturnPath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsReturnResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatDropsRejectBody = zReviewNoteIn;

export const zAdminCatDropsRejectPath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsRejectResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatDropsUnpublishPath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsUnpublishResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatDropsArchiveBody = zAppModulesCatSchemasAdminArchiveIn;

export const zAdminCatDropsArchivePath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsArchiveResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatDropsRestorePath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsRestoreResponse = zAppModulesCatSchemasAdminStatusOut;

export const zAdminCatDropsUsagePath = z.object({
    drop_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCatDropsUsageResponse = zUsageOut;

export const zAdminCatDropsHistoryPath = z.object({
    drop_id: z.uuid()
});

export const zAdminCatDropsHistoryQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminCatDropsHistoryResponse = zAppModulesCatSchemasAdminHistoryPageOut;

/**
 * Successful Response
 */
export const zAdminExpReferenceGetResponse = zExpReferenceOut;

export const zAdminExpTastingsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[format]': z.string().nullish(),
    'filter[format][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[lifecycle]': z.string().nullish(),
    'filter[lifecycle][]': z.array(z.string()).nullish(),
    'filter[moderation_status]': z.string().nullish(),
    'filter[moderation_status][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[host_id]': z.string().nullish(),
    'filter[host_id][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[venue_winery_id]': z.string().nullish(),
    'filter[venue_winery_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsListResponse = zPageTastingRow;

export const zAdminExpTastingsCreateBody = zTastingCreateIn;

/**
 * Successful Response
 */
export const zAdminExpTastingsCreateResponse = zTastingAdmin;

export const zAdminExpTastingsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[format]': z.string().nullish(),
    'filter[format][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[lifecycle]': z.string().nullish(),
    'filter[lifecycle][]': z.array(z.string()).nullish(),
    'filter[moderation_status]': z.string().nullish(),
    'filter[moderation_status][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[host_id]': z.string().nullish(),
    'filter[host_id][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[venue_winery_id]': z.string().nullish(),
    'filter[venue_winery_id][]': z.array(z.string()).nullish()
});

export const zAdminExpTastingsDeletePath = z.object({
    tasting_id: z.uuid()
});

export const zAdminExpTastingsDeleteQuery = z.object({
    confirm_title: z.string().min(1).max(300)
});

/**
 * Successful Response
 */
export const zAdminExpTastingsDeleteResponse = z.void();

export const zAdminExpTastingsGetPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsGetResponse = zTastingAdmin;

export const zAdminExpTastingsUpdateBody = zTastingPatchIn;

export const zAdminExpTastingsUpdatePath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsUpdateResponse = zTastingAdmin;

export const zAdminExpTastingsWinesUpdateBody = zTastingWinesIn;

export const zAdminExpTastingsWinesUpdatePath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsWinesUpdateResponse = zTastingAdmin;

export const zAdminExpTastingsContentUpdateBody = zContentIn;

export const zAdminExpTastingsContentUpdatePath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsContentUpdateResponse = zTastingAdmin;

export const zAdminExpTastingsCancelBody = zCancelEventIn;

export const zAdminExpTastingsCancelPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsCancelResponse = zCancelEventOut;

export const zAdminExpTastingsArchiveBody = zEventArchiveIn;

export const zAdminExpTastingsArchivePath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsArchiveResponse = zTastingAdmin;

export const zAdminExpTastingsRestorePath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTastingsRestoreResponse = zTastingAdmin;

export const zAdminExpExcursionsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[moderation_status]': z.string().nullish(),
    'filter[moderation_status][]': z.array(z.string()).nullish(),
    'filter[winery_id]': z.string().nullish(),
    'filter[winery_id][]': z.array(z.string()).nullish(),
    'filter[schedule_mode]': z.string().nullish(),
    'filter[schedule_mode][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsListResponse = zPageExcursionRow;

export const zAdminExpExcursionsCreateBody = zExcursionCreateIn;

/**
 * Successful Response
 */
export const zAdminExpExcursionsCreateResponse = zExcursionAdmin;

export const zAdminExpExcursionsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[moderation_status]': z.string().nullish(),
    'filter[moderation_status][]': z.array(z.string()).nullish(),
    'filter[winery_id]': z.string().nullish(),
    'filter[winery_id][]': z.array(z.string()).nullish(),
    'filter[schedule_mode]': z.string().nullish(),
    'filter[schedule_mode][]': z.array(z.string()).nullish()
});

export const zAdminExpExcursionsDeletePath = z.object({
    excursion_id: z.uuid()
});

export const zAdminExpExcursionsDeleteQuery = z.object({
    confirm_title: z.string().min(1).max(300)
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsDeleteResponse = z.void();

export const zAdminExpExcursionsGetPath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsGetResponse = zExcursionAdmin;

export const zAdminExpExcursionsUpdateBody = zExcursionPatchIn;

export const zAdminExpExcursionsUpdatePath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsUpdateResponse = zExcursionAdmin;

export const zAdminExpExcursionsWeekSlotsUpdateBody = zWeekSlotsIn;

export const zAdminExpExcursionsWeekSlotsUpdatePath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsWeekSlotsUpdateResponse = zExcursionAdmin;

export const zAdminExpExcursionsContentUpdateBody = zContentIn;

export const zAdminExpExcursionsContentUpdatePath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsContentUpdateResponse = zExcursionAdmin;

export const zAdminExpExcursionsCancelBody = zCancelEventIn;

export const zAdminExpExcursionsCancelPath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsCancelResponse = zCancelEventOut;

export const zAdminExpExcursionsArchiveBody = zEventArchiveIn;

export const zAdminExpExcursionsArchivePath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsArchiveResponse = zExcursionAdmin;

export const zAdminExpExcursionsRestorePath = z.object({
    excursion_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpExcursionsRestoreResponse = zExcursionAdmin;

export const zAdminExpTripsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[lifecycle]': z.string().nullish(),
    'filter[lifecycle][]': z.array(z.string()).nullish(),
    'filter[moderation_status]': z.string().nullish(),
    'filter[moderation_status][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[host_id]': z.string().nullish(),
    'filter[host_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminExpTripsListResponse = zPageTripRow;

export const zAdminExpTripsCreateBody = zTripCreateIn;

/**
 * Successful Response
 */
export const zAdminExpTripsCreateResponse = zTripAdmin;

export const zAdminExpTripsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[lifecycle]': z.string().nullish(),
    'filter[lifecycle][]': z.array(z.string()).nullish(),
    'filter[moderation_status]': z.string().nullish(),
    'filter[moderation_status][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[host_id]': z.string().nullish(),
    'filter[host_id][]': z.array(z.string()).nullish()
});

export const zAdminExpTripsDeletePath = z.object({
    trip_id: z.uuid()
});

export const zAdminExpTripsDeleteQuery = z.object({
    confirm_title: z.string().min(1).max(300)
});

/**
 * Successful Response
 */
export const zAdminExpTripsDeleteResponse = z.void();

export const zAdminExpTripsGetPath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsGetResponse = zTripAdmin;

export const zAdminExpTripsUpdateBody = zTripPatchIn;

export const zAdminExpTripsUpdatePath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsUpdateResponse = zTripAdmin;

export const zAdminExpTripsDeparturesUpdateBody = zDeparturesIn;

export const zAdminExpTripsDeparturesUpdatePath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsDeparturesUpdateResponse = zTripAdmin;

export const zAdminExpTripsStayUpdateBody = zStayIn;

export const zAdminExpTripsStayUpdatePath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsStayUpdateResponse = zTripAdmin;

export const zAdminExpTripsContentUpdateBody = zTripContentIn;

export const zAdminExpTripsContentUpdatePath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsContentUpdateResponse = zTripAdmin;

export const zAdminExpTripsCancelBody = zCancelEventIn;

export const zAdminExpTripsCancelPath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsCancelResponse = zCancelEventOut;

export const zAdminExpTripsArchiveBody = zEventArchiveIn;

export const zAdminExpTripsArchivePath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsArchiveResponse = zTripAdmin;

export const zAdminExpTripsRestorePath = z.object({
    trip_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpTripsRestoreResponse = zTripAdmin;

export const zAdminExpBookingsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[type]': z.string().nullish(),
    'filter[type][]': z.array(z.string()).nullish(),
    'filter[target_type]': z.string().nullish(),
    'filter[target_type][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[target_id]': z.string().nullish(),
    'filter[target_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsListResponse = zPageBookingRow;

export const zAdminExpBookingsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[type]': z.string().nullish(),
    'filter[type][]': z.array(z.string()).nullish(),
    'filter[target_type]': z.string().nullish(),
    'filter[target_type][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[target_id]': z.string().nullish(),
    'filter[target_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

export const zAdminExpBookingsGetPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsGetResponse = zBookingAdmin;

export const zAdminExpBookingsApproveBody = zApproveIn;

export const zAdminExpBookingsApprovePath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsApproveResponse = zBookingAdmin;

export const zAdminExpBookingsDeclineBody = zDeclineIn;

export const zAdminExpBookingsDeclinePath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsDeclineResponse = zBookingAdmin;

export const zAdminExpBookingsCancelBody = zAdminCancelIn;

export const zAdminExpBookingsCancelHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

export const zAdminExpBookingsCancelPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsCancelResponse = zBookingAdmin;

export const zAdminExpBookingsRemindPaymentPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsRemindPaymentResponse = zAcceptedOut;

export const zAdminExpBookingsMarkAttendedPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsMarkAttendedResponse = zBookingAdmin;

export const zAdminExpBookingsMarkNoShowPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsMarkNoShowResponse = zBookingAdmin;

export const zAdminExpBookingsOfferSeatPath = z.object({
    booking_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminExpBookingsOfferSeatResponse = zBookingAdmin;

export const zAdminExpAttendanceListQuery = z.object({
    kind: zBookingTarget,
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[target_id]': z.string().nullish(),
    'filter[target_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[type]': z.string().nullish(),
    'filter[type][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminExpAttendanceListResponse = zPageAttendanceRow;

export const zAdminExpAttendanceExportQuery = z.object({
    kind: zBookingTarget,
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[target_id]': z.string().nullish(),
    'filter[target_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[type]': z.string().nullish(),
    'filter[type][]': z.array(z.string()).nullish()
});

export const zAdminSrvDashboardGetQuery = z.object({
    view: z.enum(['overview', 'dynamics']).nullish(),
    period: z.enum([
        'week',
        'month',
        'year',
        'all',
        'custom'
    ]).nullish(),
    month: z.string().max(7).nullish(),
    year: z.int().gte(2000).lte(2100).nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvDashboardGetResponse = zDashboardOut;

export const zAdminSrvModerationQueueListQuery = z.object({
    tab: z.enum(['waiting', 'returned']).optional().default('waiting'),
    group: z.enum([
        'all',
        'catalog',
        'learning',
        'experiences',
        'people',
        'documents'
    ]).optional().default('all'),
    q: z.string().max(200).nullish(),
    sort: z.string().nullish(),
    page: z.int().optional().default(1),
    page_size: z.int().optional().default(100),
    'filter[title]': z.string().max(200).nullish(),
    'filter[kind]': z.string().max(200).nullish(),
    'filter[author]': z.string().max(200).nullish(),
    'filter[note]': z.string().max(200).nullish(),
    'filter[age]': z.string().max(200).nullish(),
    'filter[status]': z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvModerationQueueListResponse = zModerationQueuePageOut;

/**
 * Successful Response
 */
export const zAdminSrvModerationCountersGetResponse = zModerationCountersOut;

export const zAdminSrvModerationDecisionsListQuery = z.object({
    scope: z.enum(['session', 'all']).optional().default('session'),
    group: z.enum([
        'all',
        'catalog',
        'learning',
        'experiences',
        'people',
        'documents'
    ]).optional().default('all'),
    q: z.string().max(200).nullish(),
    sort: z.string().nullish(),
    page: z.int().optional().default(1),
    page_size: z.int().optional().default(25),
    'filter[title]': z.string().max(200).nullish(),
    'filter[kind]': z.string().max(200).nullish(),
    'filter[decided_by]': z.string().max(200).nullish(),
    'filter[status]': z.string().max(200).nullish(),
    'filter[comment]': z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvModerationDecisionsListResponse = zModerationDecisionsPageOut;

export const zAdminSrvModerationGetPath = z.object({
    entity_type: z.string(),
    entity_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvModerationGetResponse = zModerationStateOut;

/**
 * Body
 */
export const zAdminSrvModerationSubmitBody = zModerationActionIn.nullable();

export const zAdminSrvModerationSubmitPath = z.object({
    entity_type: z.string(),
    entity_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvModerationSubmitResponse = zModerationStateOut;

export const zAdminSrvModerationDecideBody = zModerationDecisionIn;

export const zAdminSrvModerationDecidePath = z.object({
    entity_type: z.string(),
    entity_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvModerationDecideResponse = zModerationStateOut;

/**
 * Body
 */
export const zAdminSrvModerationUnpublishBody = zModerationActionIn.nullable();

export const zAdminSrvModerationUnpublishPath = z.object({
    entity_type: z.string(),
    entity_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvModerationUnpublishResponse = zModerationStateOut;

export const zAdminSrvAuditListQuery = z.object({
    category: zAuditCategory.nullish(),
    actor_id: z.uuid().nullish(),
    entity_type: z.string().max(64).nullish(),
    entity_id: z.uuid().nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    q: z.string().max(200).nullish(),
    sort: z.enum(['occurred_at', '-occurred_at']).nullish(),
    page: z.int().optional().default(1),
    page_size: z.int().optional().default(25),
    'filter[actor]': z.string().max(200).nullish(),
    'filter[role]': z.string().max(200).nullish(),
    'filter[entity]': z.string().max(200).nullish(),
    'filter[action]': z.string().max(200).nullish(),
    'filter[summary]': z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvAuditListResponse = zAuditPageOut;

export const zAdminSrvAuditExportBody = zAuditExportIn;

/**
 * Successful Response
 */
export const zAdminSrvAuditExportResponse = zAuditExportOut;

export const zAdminSrvAuditExportsGetPath = z.object({
    export_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvAuditExportsGetResponse = zAuditExportOut;

export const zAdminSrvAuditGetPath = z.object({
    entry_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvAuditGetResponse = zAuditCardOut;

export const zAdminSrvNotificationTemplatesListQuery = z.object({
    channel: z.string().regex(/^(push|email)$/).nullish(),
    section: zNotificationSection.nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationTemplatesListResponse = zTemplatesPageOut;

export const zAdminSrvNotificationDispatchesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[template_code]': z.string().nullish(),
    'filter[template_code][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[device_id]': z.string().nullish(),
    'filter[device_id][]': z.array(z.string()).nullish(),
    'filter[team_member_id]': z.string().nullish(),
    'filter[team_member_id][]': z.array(z.string()).nullish(),
    'filter[channel]': z.string().nullish(),
    'filter[channel][]': z.array(z.string()).nullish(),
    'filter[from]': z.string().nullish(),
    'filter[from][]': z.array(z.string()).nullish(),
    'filter[to]': z.string().nullish(),
    'filter[to][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationDispatchesListResponse = zDispatchesPageOut;

export const zAdminSrvNotificationDispatchesGetPath = z.object({
    dispatch_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationDispatchesGetResponse = zDispatchCardOut;

export const zAdminSrvNotificationTemplatesGetPath = z.object({
    code: z.string()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationTemplatesGetResponse = zTemplateCardOut;

export const zAdminSrvNotificationTemplatesUpdateBody = zTemplatePatchIn;

export const zAdminSrvNotificationTemplatesUpdatePath = z.object({
    code: z.string()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationTemplatesUpdateResponse = zTemplateCardOut;

export const zAdminSrvNotificationTemplatesEnablePath = z.object({
    code: z.string()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationTemplatesEnableResponse = zTemplateCardOut;

export const zAdminSrvNotificationTemplatesDisablePath = z.object({
    code: z.string()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationTemplatesDisableResponse = zTemplateCardOut;

export const zAdminSrvNotificationTemplatesResetPath = z.object({
    code: z.string()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationTemplatesResetResponse = zTemplateCardOut;

export const zAdminSrvNotificationCampaignsListQuery = z.object({
    channel: zCampaignChannel.nullish(),
    status: zCampaignStatus.nullish(),
    page: z.int().gte(1).optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsListResponse = zCampaignsPageOut;

export const zAdminSrvNotificationCampaignsCreateBody = zCampaignIn;

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsCreateResponse = zCampaignCardOut;

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsFormResponse = zCampaignFormOut;

export const zAdminSrvNotificationCampaignsLinkOptionsQuery = z.object({
    link_type: zCampaignLinkType,
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsLinkOptionsResponse = zCampaignLinkOptionsOut;

export const zAdminSrvNotificationCampaignsGetPath = z.object({
    campaign_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsGetResponse = zCampaignCardOut;

export const zAdminSrvNotificationCampaignsUpdateBody = zCampaignPatchIn;

export const zAdminSrvNotificationCampaignsUpdatePath = z.object({
    campaign_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsUpdateResponse = zCampaignCardOut;

export const zAdminSrvNotificationCampaignsRunGetPath = z.object({
    campaign_id: z.uuid(),
    run_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsRunGetResponse = zCampaignRunReportOut;

export const zAdminSrvNotificationCampaignsActionPath = z.object({
    campaign_id: z.uuid(),
    action: z.enum([
        'schedule',
        'launch',
        'stop',
        'resume',
        'cancel'
    ])
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationCampaignsActionResponse = zCampaignCardOut;

export const zAdminSrvNotificationAudienceEstimateQuery = z.object({
    channel: zCampaignChannel,
    segment: z.string().max(64).nullish(),
    feature: z.string().max(40).nullish(),
    author_member_id: z.string().max(40).nullish(),
    winery_id: z.string().max(40).nullish(),
    wine_id: z.string().max(40).nullish(),
    course_id: z.string().max(40).nullish(),
    event_type: z.string().max(20).nullish(),
    event_id: z.string().max(40).nullish()
});

/**
 * Successful Response
 */
export const zAdminSrvNotificationAudienceEstimateResponse = zCampaignAudienceEstimateOut;

export const zAdminLrnLibraryQuery = z.object({
    type: z.enum(['lesson', 'test']),
    q: z.string().max(100).nullish(),
    kind: zTestKind.nullish()
});

/**
 * Successful Response
 */
export const zAdminLrnLibraryResponse = zLibraryOut;

export const zAdminLrnLogListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[test]': z.string().nullish(),
    'filter[test][]': z.array(z.string()).nullish(),
    'filter[course_id]': z.string().nullish(),
    'filter[course_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[lesson_id]': z.string().nullish(),
    'filter[lesson_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminLrnLogListResponse = zLearnPageLearnLogRowOut;

export const zAdminLrnLogExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[test]': z.string().nullish(),
    'filter[test][]': z.array(z.string()).nullish(),
    'filter[course_id]': z.string().nullish(),
    'filter[course_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[lesson_id]': z.string().nullish(),
    'filter[lesson_id][]': z.array(z.string()).nullish()
});

export const zAdminLrnCoursesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[is_paid]': z.string().nullish(),
    'filter[is_paid][]': z.array(z.string()).nullish(),
    'filter[title]': z.string().nullish(),
    'filter[title][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesListResponse = zLearnPageCourseRowOut;

export const zAdminLrnCoursesCreateBody = zCourseCreateIn;

/**
 * Successful Response
 */
export const zAdminLrnCoursesCreateResponse = zCourseOut;

export const zAdminLrnCoursesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[is_paid]': z.string().nullish(),
    'filter[is_paid][]': z.array(z.string()).nullish(),
    'filter[title]': z.string().nullish(),
    'filter[title][]': z.array(z.string()).nullish()
});

export const zAdminLrnCoursesDeleteBody = zDeleteIn;

export const zAdminLrnCoursesDeletePath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesDeleteResponse = z.void();

export const zAdminLrnCoursesGetPath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesGetResponse = zCourseOut;

export const zAdminLrnCoursesUpdateBody = zCoursePatchIn;

export const zAdminLrnCoursesUpdatePath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesUpdateResponse = zCourseOut;

export const zAdminLrnCoursesProgramSaveBody = zProgramIn;

export const zAdminLrnCoursesProgramSavePath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesProgramSaveResponse = zProgramSavedOut;

export const zAdminLrnCoursesMediaSaveBody = zLearnMediaIn;

export const zAdminLrnCoursesMediaSavePath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesMediaSaveResponse = zCourseOut;

export const zAdminLrnCoursesStatsPath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesStatsResponse = zCourseStatsOut;

export const zAdminLrnLessonsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[is_paid]': z.string().nullish(),
    'filter[is_paid][]': z.array(z.string()).nullish(),
    'filter[course_id]': z.string().nullish(),
    'filter[course_id][]': z.array(z.string()).nullish(),
    'filter[title]': z.string().nullish(),
    'filter[title][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsListResponse = zLearnPageLessonRowOut;

export const zAdminLrnLessonsCreateBody = zLessonCreateIn;

/**
 * Successful Response
 */
export const zAdminLrnLessonsCreateResponse = zLessonOut;

export const zAdminLrnLessonsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[is_paid]': z.string().nullish(),
    'filter[is_paid][]': z.array(z.string()).nullish(),
    'filter[course_id]': z.string().nullish(),
    'filter[course_id][]': z.array(z.string()).nullish(),
    'filter[title]': z.string().nullish(),
    'filter[title][]': z.array(z.string()).nullish()
});

export const zAdminLrnLessonsDeleteBody = zDeleteIn;

export const zAdminLrnLessonsDeletePath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsDeleteResponse = z.void();

export const zAdminLrnLessonsGetPath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsGetResponse = zLessonOut;

export const zAdminLrnLessonsUpdateBody = zLessonPatchIn;

export const zAdminLrnLessonsUpdatePath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsUpdateResponse = zLessonOut;

/**
 * Body
 */
export const zAdminLrnLessonsTestsSaveBody = z.array(zLessonTestIn).max(20);

export const zAdminLrnLessonsTestsSavePath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsTestsSaveResponse = zLessonTestsOut;

export const zAdminLrnLessonsMediaSaveBody = zLearnMediaIn;

export const zAdminLrnLessonsMediaSavePath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsMediaSaveResponse = zLessonOut;

export const zAdminLrnLessonsStatsPath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsStatsResponse = zLessonStatsOut;

export const zAdminLrnTestsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[country_id]': z.string().nullish(),
    'filter[country_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[title]': z.string().nullish(),
    'filter[title][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsListResponse = zLearnPageTestRowOut;

export const zAdminLrnTestsCreateBody = zTestCreateIn;

/**
 * Successful Response
 */
export const zAdminLrnTestsCreateResponse = zTestOut;

export const zAdminLrnTestsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[country_id]': z.string().nullish(),
    'filter[country_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[title]': z.string().nullish(),
    'filter[title][]': z.array(z.string()).nullish()
});

export const zAdminLrnTestsDeleteBody = zDeleteIn;

export const zAdminLrnTestsDeletePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsDeleteResponse = z.void();

export const zAdminLrnTestsGetPath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsGetResponse = zTestOut;

export const zAdminLrnTestsUpdateBody = zTestPatchIn;

export const zAdminLrnTestsUpdatePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsUpdateResponse = zTestOut;

/**
 * Body
 */
export const zAdminLrnTestsQuestionsSaveBody = z.array(zQuestionIn).max(200);

export const zAdminLrnTestsQuestionsSavePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsQuestionsSaveResponse = zQuestionsSavedOut;

export const zAdminLrnTestsMediaSaveBody = zLearnMediaIn;

export const zAdminLrnTestsMediaSavePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsMediaSaveResponse = zTestOut;

export const zAdminLrnTestsUsagePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsUsageResponse = zTestUsageOut;

export const zAdminLrnCoursesSubmitPath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesSubmitResponse = zLearnStatusOut;

export const zAdminLrnCoursesDecisionBody = zDecisionIn;

export const zAdminLrnCoursesDecisionPath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesDecisionResponse = zLearnStatusOut;

/**
 * Body
 */
export const zAdminLrnCoursesUnpublishBody = zUnpublishIn.nullable();

export const zAdminLrnCoursesUnpublishPath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesUnpublishResponse = zLearnStatusOut;

export const zAdminLrnCoursesArchiveBody = zLearnArchiveIn;

export const zAdminLrnCoursesArchivePath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesArchiveResponse = zLearnStatusOut;

export const zAdminLrnCoursesRestorePath = z.object({
    course_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesRestoreResponse = zLearnStatusOut;

export const zAdminLrnCoursesHistoryPath = z.object({
    course_id: z.uuid()
});

export const zAdminLrnCoursesHistoryQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminLrnCoursesHistoryResponse = zLearnHistoryPageOut;

export const zAdminLrnLessonsSubmitPath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsSubmitResponse = zLearnStatusOut;

export const zAdminLrnLessonsDecisionBody = zDecisionIn;

export const zAdminLrnLessonsDecisionPath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsDecisionResponse = zLearnStatusOut;

/**
 * Body
 */
export const zAdminLrnLessonsUnpublishBody = zUnpublishIn.nullable();

export const zAdminLrnLessonsUnpublishPath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsUnpublishResponse = zLearnStatusOut;

export const zAdminLrnLessonsArchiveBody = zLearnArchiveIn;

export const zAdminLrnLessonsArchivePath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsArchiveResponse = zLearnStatusOut;

export const zAdminLrnLessonsRestorePath = z.object({
    lesson_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsRestoreResponse = zLearnStatusOut;

export const zAdminLrnLessonsHistoryPath = z.object({
    lesson_id: z.uuid()
});

export const zAdminLrnLessonsHistoryQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminLrnLessonsHistoryResponse = zLearnHistoryPageOut;

export const zAdminLrnTestsSubmitPath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsSubmitResponse = zLearnStatusOut;

export const zAdminLrnTestsDecisionBody = zDecisionIn;

export const zAdminLrnTestsDecisionPath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsDecisionResponse = zLearnStatusOut;

/**
 * Body
 */
export const zAdminLrnTestsUnpublishBody = zUnpublishIn.nullable();

export const zAdminLrnTestsUnpublishPath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsUnpublishResponse = zLearnStatusOut;

export const zAdminLrnTestsArchiveBody = zLearnArchiveIn;

export const zAdminLrnTestsArchivePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsArchiveResponse = zLearnStatusOut;

export const zAdminLrnTestsRestorePath = z.object({
    test_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminLrnTestsRestoreResponse = zLearnStatusOut;

export const zAdminLrnTestsHistoryPath = z.object({
    test_id: z.uuid()
});

export const zAdminLrnTestsHistoryQuery = z.object({
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(100).optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminLrnTestsHistoryResponse = zLearnHistoryPageOut;

/**
 * Successful Response
 */
export const zAdminTstReferenceGetResponse = zTstTasteReferenceOut;

export const zAdminTstWinesSearchQuery = z.object({
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminTstWinesSearchResponse = zTstWineOptionsOut;

export const zAdminTstCalibrationsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[expert_id]': z.string().nullish(),
    'filter[expert_id][]': z.array(z.string()).nullish(),
    'filter[wine_id]': z.string().nullish(),
    'filter[wine_id][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminTstCalibrationsListResponse = zTstPageTstCalibrationRowOut;

export const zAdminTstCalibrationsCreateBody = zTstCalibrationCreateIn;

/**
 * Successful Response
 */
export const zAdminTstCalibrationsCreateResponse = zTstCalibrationAdminOut;

export const zAdminTstCalibrationsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[expert_id]': z.string().nullish(),
    'filter[expert_id][]': z.array(z.string()).nullish(),
    'filter[wine_id]': z.string().nullish(),
    'filter[wine_id][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish()
});

export const zAdminTstCalibrationsDeleteBody = zTstConfirmNameIn;

export const zAdminTstCalibrationsDeletePath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstCalibrationsDeleteResponse = z.void();

export const zAdminTstCalibrationsGetPath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstCalibrationsGetResponse = zTstCalibrationAdminOut;

export const zAdminTstCalibrationsUpdateBody = zTstCalibrationPatchIn;

export const zAdminTstCalibrationsUpdatePath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstCalibrationsUpdateResponse = zTstCalibrationAdminOut;

export const zAdminTstCalibrationsReferencePutBody = zTstCalibrationReferenceIn;

export const zAdminTstCalibrationsReferencePutPath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstCalibrationsReferencePutResponse = zTstCalibrationAdminOut;

export const zAdminTstCalibrationsArchiveBody = zTstCalibrationArchiveIn;

export const zAdminTstCalibrationsArchivePath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstCalibrationsArchiveResponse = zTstCalibrationAdminOut;

export const zAdminTstCalibrationsRestorePath = z.object({
    calibration_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstCalibrationsRestoreResponse = zTstCalibrationAdminOut;

export const zAdminTstBlindWinesGetPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstBlindWinesGetResponse = zTstBlindWinesOut;

export const zAdminTstBlindWinesPutBody = zTstBlindWinesIn;

export const zAdminTstBlindWinesPutPath = z.object({
    tasting_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstBlindWinesPutResponse = zTstBlindWinesOut;

export const zAdminTstAccuracyLevelsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminTstAccuracyLevelsListResponse = zTstPageTstLevelRowOut;

export const zAdminTstAccuracyLevelsCreateBody = zTstLevelIn;

/**
 * Successful Response
 */
export const zAdminTstAccuracyLevelsCreateResponse = zTstLevelRowOut;

export const zAdminTstAccuracyLevelsDeletePath = z.object({
    level_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstAccuracyLevelsDeleteResponse = z.void();

export const zAdminTstAccuracyLevelsUpdateBody = zTstLevelPatchIn;

export const zAdminTstAccuracyLevelsUpdatePath = z.object({
    level_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstAccuracyLevelsUpdateResponse = zTstLevelRowOut;

export const zAdminTstSpecializationsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[category_kind]': z.string().nullish(),
    'filter[category_kind][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminTstSpecializationsListResponse = zTstPageTstSpecRowOut;

export const zAdminTstSpecializationsCreateBody = zTstSpecIn;

/**
 * Successful Response
 */
export const zAdminTstSpecializationsCreateResponse = zTstSpecRowOut;

export const zAdminTstSpecializationsDeletePath = z.object({
    specialization_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstSpecializationsDeleteResponse = z.void();

export const zAdminTstSpecializationsUpdateBody = zTstSpecPatchIn;

export const zAdminTstSpecializationsUpdatePath = z.object({
    specialization_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstSpecializationsUpdateResponse = zTstSpecRowOut;

export const zAdminTstAccuracyResultsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminTstAccuracyResultsListResponse = zTstPageTstAccuracyLogRowOut;

export const zAdminTstAccuracyResultsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

export const zAdminTstUserSpecializationsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[category_kind]': z.string().nullish(),
    'filter[category_kind][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[specialization_id]': z.string().nullish(),
    'filter[specialization_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminTstUserSpecializationsListResponse = zTstPageTstSpecLogRowOut;

export const zAdminTstUserSpecializationsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[category_kind]': z.string().nullish(),
    'filter[category_kind][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[specialization_id]': z.string().nullish(),
    'filter[specialization_id][]': z.array(z.string()).nullish()
});

export const zAdminTstDiaryEntriesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[recognized]': z.string().nullish(),
    'filter[recognized][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminTstDiaryEntriesListResponse = zTstPageTstDiaryLogRowOut;

export const zAdminTstDiaryEntriesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[recognized]': z.string().nullish(),
    'filter[recognized][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish()
});

export const zAdminTstDiaryEntriesGetPath = z.object({
    entry_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstDiaryEntriesGetResponse = zTstDiaryEntryCardOut;

export const zAdminTstUserTasteSummaryGetPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminTstUserTasteSummaryGetResponse = zTstUserTasteSummaryOut;

export const zAdminGamXpRulesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[section]': z.string().nullish(),
    'filter[section][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[event_type]': z.string().nullish(),
    'filter[event_type][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGamXpRulesListResponse = zXpRulesPageOut;

export const zAdminGamXpRulesCreateBody = zXpRuleCreateIn;

/**
 * Successful Response
 */
export const zAdminGamXpRulesCreateResponse = zXpRuleOut;

export const zAdminGamXpRulesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[section]': z.string().nullish(),
    'filter[section][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[event_type]': z.string().nullish(),
    'filter[event_type][]': z.array(z.string()).nullish()
});

export const zAdminGamXpRulesDeletePath = z.object({
    rule_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamXpRulesDeleteResponse = z.void();

export const zAdminGamXpRulesGetPath = z.object({
    rule_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamXpRulesGetResponse = zXpRuleCardOut;

export const zAdminGamXpRulesUpdateBody = zXpRulePatchIn;

export const zAdminGamXpRulesUpdatePath = z.object({
    rule_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamXpRulesUpdateResponse = zXpRuleOut;

export const zAdminGamXpLedgerListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[section]': z.string().nullish(),
    'filter[section][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[event_type]': z.string().nullish(),
    'filter[event_type][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[from]': z.string().nullish(),
    'filter[from][]': z.array(z.string()).nullish(),
    'filter[to]': z.string().nullish(),
    'filter[to][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGamXpLedgerListResponse = zXpLedgerPageOut;

export const zAdminGamXpLedgerExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[section]': z.string().nullish(),
    'filter[section][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[event_type]': z.string().nullish(),
    'filter[event_type][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[from]': z.string().nullish(),
    'filter[from][]': z.array(z.string()).nullish(),
    'filter[to]': z.string().nullish(),
    'filter[to][]': z.array(z.string()).nullish()
});

export const zAdminGamStampsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[level]': z.string().nullish(),
    'filter[level][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGamStampsListResponse = zStampsPageOut;

export const zAdminGamStampsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[level]': z.string().nullish(),
    'filter[level][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish()
});

export const zAdminGamLevelsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(100),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminGamLevelsListResponse = zXpLevelsPageOut;

export const zAdminGamLevelsCreateBody = zXpLevelCreateIn;

/**
 * Successful Response
 */
export const zAdminGamLevelsCreateResponse = zXpLevelOut;

export const zAdminGamLevelsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(100),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish()
});

export const zAdminGamLevelsDeletePath = z.object({
    level_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamLevelsDeleteResponse = z.void();

export const zAdminGamLevelsUpdateBody = zXpLevelPatchIn;

export const zAdminGamLevelsUpdatePath = z.object({
    level_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamLevelsUpdateResponse = zXpLevelOut;

export const zAdminGamRewardsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[executor_type]': z.string().nullish(),
    'filter[executor_type][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGamRewardsListResponse = zRewardsPageOut;

export const zAdminGamRewardsCreateBody = zRewardCreateIn;

/**
 * Successful Response
 */
export const zAdminGamRewardsCreateResponse = zRewardRowOut;

export const zAdminGamRewardsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[executor_type]': z.string().nullish(),
    'filter[executor_type][]': z.array(z.string()).nullish()
});

export const zAdminGamRewardsDeletePath = z.object({
    reward_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamRewardsDeleteResponse = z.void();

export const zAdminGamRewardsUpdateBody = zRewardPatchIn;

export const zAdminGamRewardsUpdatePath = z.object({
    reward_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamRewardsUpdateResponse = zRewardRowOut;

/**
 * Successful Response
 */
export const zAdminGamTiersListResponse = zTiersPageOut;

export const zAdminGamTiersCreateBody = zTierCreateIn;

/**
 * Successful Response
 */
export const zAdminGamTiersCreateResponse = zTierRowOut;

/**
 * Successful Response
 */
export const zAdminGamTierGatesListResponse = zTierGatesOut;

export const zAdminGamTierGatesUpdateBody = zTierGatePatchIn;

export const zAdminGamTierGatesUpdatePath = z.object({
    key: zTierGateKey
});

/**
 * Successful Response
 */
export const zAdminGamTierGatesUpdateResponse = zTierGateOut;

export const zAdminGamTiersDeletePath = z.object({
    tier_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamTiersDeleteResponse = z.void();

export const zAdminGamTiersUpdateBody = zTierPatchIn;

export const zAdminGamTiersUpdatePath = z.object({
    tier_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamTiersUpdateResponse = zTierRowOut;

export const zAdminGamExchangesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[overdue]': z.string().nullish(),
    'filter[overdue][]': z.array(z.string()).nullish(),
    'filter[reward_id]': z.string().nullish(),
    'filter[reward_id][]': z.array(z.string()).nullish(),
    'filter[executor_type]': z.string().nullish(),
    'filter[executor_type][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminGamExchangesListResponse = zGamAdminExchangesPageOut;

export const zAdminGamExchangesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish(),
    'filter[overdue]': z.string().nullish(),
    'filter[overdue][]': z.array(z.string()).nullish(),
    'filter[reward_id]': z.string().nullish(),
    'filter[reward_id][]': z.array(z.string()).nullish(),
    'filter[executor_type]': z.string().nullish(),
    'filter[executor_type][]': z.array(z.string()).nullish()
});

export const zAdminGamExchangesGetPath = z.object({
    exchange_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamExchangesGetResponse = zExchangeCardOut;

export const zAdminGamExchangesCompleteBody = zExchangeCompleteIn;

export const zAdminGamExchangesCompleteHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

export const zAdminGamExchangesCompletePath = z.object({
    exchange_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamExchangesCompleteResponse = zExchangeCardOut;

export const zAdminGamExchangesDeclineBody = zExchangeDeclineIn;

export const zAdminGamExchangesDeclinePath = z.object({
    exchange_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamExchangesDeclineResponse = zExchangeCardOut;

export const zAdminGamUsersSummaryPath = z.object({
    user_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminGamUsersSummaryResponse = zUserSummaryOut;

export const zAdminGamStatsQuery = z.object({
    from: z.iso.datetime({ offset: true }).nullish(),
    to: z.iso.datetime({ offset: true }).nullish()
});

/**
 * Successful Response
 */
export const zAdminGamStatsResponse = zGamStatsOut;

export const zAdminCntPublicationsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[author_kind]': z.string().nullish(),
    'filter[author_kind][]': z.array(z.string()).nullish(),
    'filter[source]': z.string().nullish(),
    'filter[source][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[winery_id]': z.string().nullish(),
    'filter[winery_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsListResponse = zCntPagePublicationRowOut;

export const zAdminCntPublicationsCreateBody = zPublicationCreateIn;

/**
 * Successful Response
 */
export const zAdminCntPublicationsCreateResponse = zPublicationAdminOut;

export const zAdminCntPublicationsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[kind]': z.string().nullish(),
    'filter[kind][]': z.array(z.string()).nullish(),
    'filter[author_kind]': z.string().nullish(),
    'filter[author_kind][]': z.array(z.string()).nullish(),
    'filter[source]': z.string().nullish(),
    'filter[source][]': z.array(z.string()).nullish(),
    'filter[region_id]': z.string().nullish(),
    'filter[region_id][]': z.array(z.string()).nullish(),
    'filter[winery_id]': z.string().nullish(),
    'filter[winery_id][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish()
});

export const zAdminCntPublicationsOptionsQuery = z.object({
    type: z.enum([
        'region',
        'wine',
        'experience',
        'winery'
    ]),
    q: z.string().max(100).nullish(),
    region_id: z.uuid().nullish()
});

/**
 * Response Admin Cnt Publications Options
 *
 * Successful Response
 */
export const zAdminCntPublicationsOptionsResponse = z.array(zContentOptionOut);

export const zAdminCntPublicationsDeleteBody = zPublicationDeleteIn;

export const zAdminCntPublicationsDeletePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsDeleteResponse = z.void();

export const zAdminCntPublicationsGetPath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsGetResponse = zPublicationAdminOut;

export const zAdminCntPublicationsUpdateBody = zPublicationPatchIn;

export const zAdminCntPublicationsUpdatePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsUpdateResponse = zPublicationAdminOut;

export const zAdminCntPublicationsPublishPath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsPublishResponse = zPublicationAdminOut;

export const zAdminCntPublicationsUnpublishPath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsUnpublishResponse = zPublicationAdminOut;

export const zAdminCntPublicationsArchiveBody = zContentArchiveIn;

export const zAdminCntPublicationsArchivePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsArchiveResponse = zPublicationAdminOut;

export const zAdminCntPublicationsRestorePath = z.object({
    publication_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntPublicationsRestoreResponse = zPublicationAdminOut;

export const zAdminCntStoriesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[ttl]': z.string().nullish(),
    'filter[ttl][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCntStoriesListResponse = zCntPageStoryRowOut;

export const zAdminCntStoriesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[ttl]': z.string().nullish(),
    'filter[ttl][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish()
});

export const zAdminCntStoriesGetPath = z.object({
    story_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntStoriesGetResponse = zStoryAdminOut;

export const zAdminCntStoriesHidePath = z.object({
    story_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntStoriesHideResponse = zStoryAdminOut;

export const zAdminCntStoriesUnhidePath = z.object({
    story_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntStoriesUnhideResponse = zStoryAdminOut;

export const zAdminCntDocumentsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[doc_key]': z.string().nullish(),
    'filter[doc_key][]': z.array(z.string()).nullish(),
    'filter[lang]': z.string().nullish(),
    'filter[lang][]': z.array(z.string()).nullish(),
    'filter[owner_kind]': z.string().nullish(),
    'filter[owner_kind][]': z.array(z.string()).nullish(),
    'filter[visibility]': z.string().nullish(),
    'filter[visibility][]': z.array(z.string()).nullish(),
    'filter[owner_member_id]': z.string().nullish(),
    'filter[owner_member_id][]': z.array(z.string()).nullish(),
    'filter[owner_winery_id]': z.string().nullish(),
    'filter[owner_winery_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsListResponse = zCntPageDocumentRowOut;

export const zAdminCntDocumentsCreateBody = zDocumentCreateIn;

/**
 * Successful Response
 */
export const zAdminCntDocumentsCreateResponse = zDocumentAdminOut;

export const zAdminCntDocumentsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[doc_key]': z.string().nullish(),
    'filter[doc_key][]': z.array(z.string()).nullish(),
    'filter[lang]': z.string().nullish(),
    'filter[lang][]': z.array(z.string()).nullish(),
    'filter[owner_kind]': z.string().nullish(),
    'filter[owner_kind][]': z.array(z.string()).nullish(),
    'filter[visibility]': z.string().nullish(),
    'filter[visibility][]': z.array(z.string()).nullish(),
    'filter[owner_member_id]': z.string().nullish(),
    'filter[owner_member_id][]': z.array(z.string()).nullish(),
    'filter[owner_winery_id]': z.string().nullish(),
    'filter[owner_winery_id][]': z.array(z.string()).nullish()
});

export const zAdminCntDocumentsDeletePath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsDeleteResponse = z.void();

export const zAdminCntDocumentsGetPath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsGetResponse = zDocumentAdminOut;

export const zAdminCntDocumentsUpdateBody = zDocumentPatchIn;

export const zAdminCntDocumentsUpdatePath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsUpdateResponse = zDocumentAdminOut;

export const zAdminCntDocumentsReplaceBody = zDocumentVersionIn;

export const zAdminCntDocumentsReplacePath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsReplaceResponse = zDocumentAdminOut;

export const zAdminCntDocumentsDecideBody = zDocumentDecisionIn;

export const zAdminCntDocumentsDecidePath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsDecideResponse = zDocumentAdminOut;

export const zAdminCntDocumentsArchiveBody = zContentArchiveIn;

export const zAdminCntDocumentsArchivePath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsArchiveResponse = zDocumentAdminOut;

export const zAdminCntDocumentsRestorePath = z.object({
    document_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntDocumentsRestoreResponse = zDocumentAdminOut;

export const zAdminCntExpertApplicationsGetPath = z.object({
    application_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntExpertApplicationsGetResponse = zApplicationAdminOut;

export const zAdminCntExpertApplicationsDecideBody = zExpertApplicationDecisionIn;

export const zAdminCntExpertApplicationsDecidePath = z.object({
    application_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminCntExpertApplicationsDecideResponse = zApplicationDecisionOut;

export const zAdminCntLogsSubscriptionsQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[role]': z.string().nullish(),
    'filter[role][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCntLogsSubscriptionsResponse = zCntPageSubscriptionLogRowOut;

export const zAdminCntLogsSubscriptionsExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[role]': z.string().nullish(),
    'filter[role][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[author_member_id]': z.string().nullish(),
    'filter[author_member_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

export const zAdminCntLogsLikesQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[object_type]': z.string().nullish(),
    'filter[object_type][]': z.array(z.string()).nullish(),
    'filter[object_id]': z.string().nullish(),
    'filter[object_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCntLogsLikesResponse = zCntPageLikeLogRowOut;

export const zAdminCntLogsLikesExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[object_type]': z.string().nullish(),
    'filter[object_type][]': z.array(z.string()).nullish(),
    'filter[object_id]': z.string().nullish(),
    'filter[object_id][]': z.array(z.string()).nullish(),
    'filter[user_id]': z.string().nullish(),
    'filter[user_id][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminCntBrandGetResponse = zBrandLogoOut;

export const zAdminCntBrandLogoReplaceBody = zBrandLogoIn;

/**
 * Successful Response
 */
export const zAdminCntBrandLogoReplaceResponse = zBrandLogoOut;

export const zAdminOrdOrdersListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[group]': z.string().nullish(),
    'filter[group][]': z.array(z.string()).nullish(),
    'filter[payment]': z.string().nullish(),
    'filter[payment][]': z.array(z.string()).nullish(),
    'filter[type]': z.string().nullish(),
    'filter[type][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[flow]': z.string().nullish(),
    'filter[flow][]': z.array(z.string()).nullish(),
    'filter[buyer_id]': z.string().nullish(),
    'filter[buyer_id][]': z.array(z.string()).nullish(),
    'filter[event_id]': z.string().nullish(),
    'filter[event_id][]': z.array(z.string()).nullish(),
    'filter[partner]': z.string().nullish(),
    'filter[partner][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersListResponse = zOrdPageOrdAdminOrderRow;

export const zAdminOrdOrdersExportQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[group]': z.string().nullish(),
    'filter[group][]': z.array(z.string()).nullish(),
    'filter[payment]': z.string().nullish(),
    'filter[payment][]': z.array(z.string()).nullish(),
    'filter[type]': z.string().nullish(),
    'filter[type][]': z.array(z.string()).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[flow]': z.string().nullish(),
    'filter[flow][]': z.array(z.string()).nullish(),
    'filter[buyer_id]': z.string().nullish(),
    'filter[buyer_id][]': z.array(z.string()).nullish(),
    'filter[event_id]': z.string().nullish(),
    'filter[event_id][]': z.array(z.string()).nullish(),
    'filter[partner]': z.string().nullish(),
    'filter[partner][]': z.array(z.string()).nullish()
});

export const zAdminOrdOrdersPartnerCreateBody = zOrdPartnerOrderIn;

export const zAdminOrdOrdersPartnerCreateHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersPartnerCreateResponse = zOrdCardResult;

export const zAdminOrdOrdersGetPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersGetResponse = zOrdAdminOrderCard;

export const zAdminOrdOrdersStatusBody = zOrdStatusIn;

export const zAdminOrdOrdersStatusPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersStatusResponse = zOrdStatusResult;

export const zAdminOrdOrdersCommentBody = zOrdCommentIn;

export const zAdminOrdOrdersCommentPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersCommentResponse = zOrdCardResult;

export const zAdminOrdOrdersPaymentReminderPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersPaymentReminderResponse = zOrdAcceptedOut;

export const zAdminOrdOrdersPaymentsPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersPaymentsResponse = zOrdPaymentsBlockOut;

export const zAdminOrdOrdersPartnerLinkUserBody = zOrdLinkUserIn;

export const zAdminOrdOrdersPartnerLinkUserPath = z.object({
    order_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdOrdersPartnerLinkUserResponse = zOrdCardResult;

export const zAdminOrdWinePartnersListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminOrdWinePartnersListResponse = zOrdPageOrdWinePartnerOut;

export const zAdminOrdWinePartnersCreateBody = zOrdWinePartnerIn;

/**
 * Successful Response
 */
export const zAdminOrdWinePartnersCreateResponse = zOrdWinePartnerOut;

export const zAdminOrdWinePartnersDeletePath = z.object({
    partner_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdWinePartnersDeleteResponse = z.void();

export const zAdminOrdWinePartnersUpdateBody = zOrdWinePartnerPatchIn;

export const zAdminOrdWinePartnersUpdatePath = z.object({
    partner_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdWinePartnersUpdateResponse = zOrdWinePartnerOut;

export const zAdminOrdDeliveriesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[carrier]': z.string().nullish(),
    'filter[carrier][]': z.array(z.string()).nullish(),
    'filter[kit]': z.string().nullish(),
    'filter[kit][]': z.array(z.string()).nullish(),
    'filter[shipment]': z.string().nullish(),
    'filter[shipment][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminOrdDeliveriesListResponse = zOrdPageOrdDeliveryRow;

export const zAdminOrdDeliveriesGetPath = z.object({
    delivery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdDeliveriesGetResponse = zOrdDeliveryCard;

export const zAdminOrdDeliveriesUpdateBody = zOrdDeliveryPatchIn;

export const zAdminOrdDeliveriesUpdatePath = z.object({
    delivery_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdDeliveriesUpdateResponse = zOrdDeliveryCard;

export const zAdminOrdRevenueEntriesQuery = z.object({
    period: z.string().regex(/^(all|month|quarter|half|year|custom)$/).optional().default('all'),
    value: z.string().max(10).nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    group: zCartGroup.nullish(),
    recipient_id: z.string().max(40).nullish(),
    q: z.string().max(200).nullish(),
    payout_status: zPayoutStatus.nullish(),
    page: z.int().optional().default(1),
    page_size: z.int().optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminOrdRevenueEntriesResponse = zOrdPageOrdRevenueRow;

export const zAdminOrdRevenueSummaryQuery = z.object({
    period: z.string().regex(/^(all|month|quarter|half|year|custom)$/).optional().default('all'),
    value: z.string().max(10).nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    group: zCartGroup.nullish(),
    recipient_id: z.string().max(40).nullish(),
    q: z.string().max(200).nullish(),
    payout_status: zPayoutStatus.nullish(),
    page: z.int().optional().default(1),
    page_size: z.int().optional().default(25)
});

/**
 * Successful Response
 */
export const zAdminOrdRevenueSummaryResponse = zOrdRevenueSummaryOut;

export const zAdminOrdRevenueExportQuery = z.object({
    period: z.string().regex(/^(all|month|quarter|half|year|custom)$/).optional().default('all'),
    value: z.string().max(10).nullish(),
    from: z.iso.date().nullish(),
    to: z.iso.date().nullish(),
    group: zCartGroup.nullish(),
    recipient_id: z.string().max(40).nullish(),
    q: z.string().max(200).nullish(),
    payout_status: zPayoutStatus.nullish(),
    page: z.int().optional().default(1),
    page_size: z.int().optional().default(25)
});

export const zAdminOrdRevenuePayoutsCreateBody = zOrdPayoutIn;

/**
 * Successful Response
 */
export const zAdminOrdRevenuePayoutsCreateResponse = zOrdPayoutOut;

export const zAdminOrdRevenueSharesGetPath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdRevenueSharesGetResponse = zOrdSharesOut;

export const zAdminOrdRevenueSharesUpdateBody = zOrdSharesIn;

export const zAdminOrdRevenueSharesUpdatePath = z.object({
    member_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminOrdRevenueSharesUpdateResponse = zOrdSharesOut;

export const zAdminIntBillingStoreProductsCheckQuery = z.object({
    store: zStoreCode,
    product_id: z.string().min(2).max(100).regex(/^[a-z0-9][a-z0-9._]{1,99}$/),
    exclude_item_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAdminIntBillingStoreProductsCheckResponse = zBillingProductCheckOut;

export const zAdminIntGeoGeocodeQuery = z.object({
    q: z.string().min(1).max(300),
    country: z.string().regex(/^[A-Za-z]{2}$/).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntGeoGeocodeResponse = zGeocodeListOut;

export const zAdminIntGeoReverseQuery = z.object({
    lat: z.number(),
    lon: z.number(),
    region_id: z.uuid().nullish()
});

/**
 * Successful Response
 */
export const zAdminIntGeoReverseResponse = zGeocodeReverseOut;

export const zAdminIntGeoSuggestQuery = z.object({
    input: z.string().min(1).max(200),
    session_token: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/),
    near_lat: z.number().gte(-90).lte(90).nullish(),
    near_lon: z.number().gte(-180).lte(180).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntGeoSuggestResponse = zGeocodeSuggestionsOut;

export const zAdminIntGeoSuggestDetailsPath = z.object({
    suggestion_id: z.string().min(1).max(600)
});

export const zAdminIntGeoSuggestDetailsQuery = z.object({
    session_token: z.string().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/)
});

/**
 * Successful Response
 */
export const zAdminIntGeoSuggestDetailsResponse = zGeocodeDetailsOut;

export const zAdminIntTranslationsRetranslateBody = zRetranslateIn;

/**
 * Successful Response
 */
export const zAdminIntTranslationsRetranslateResponse = zRetranslateOut;

export const zAdminIntTranslationsJobsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[entity_type]': z.string().nullish(),
    'filter[entity_type][]': z.array(z.string()).nullish(),
    'filter[entity_id]': z.string().nullish(),
    'filter[entity_id][]': z.array(z.string()).nullish(),
    'filter[batch_id]': z.string().nullish(),
    'filter[batch_id][]': z.array(z.string()).nullish(),
    'filter[batched]': z.string().nullish(),
    'filter[batched][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntTranslationsJobsListResponse = zTranslationJobsPage;

export const zAdminIntTranslationsJobsRetryPath = z.object({
    job_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntTranslationsJobsRetryResponse = zTranslationJobOut;

export const zAdminIntTranslationsBatchesEstimateBody = zTranslationBatchIn;

/**
 * Successful Response
 */
export const zAdminIntTranslationsBatchesEstimateResponse = zTranslationEstimateOut;

export const zAdminIntTranslationsBatchesListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntTranslationsBatchesListResponse = zTranslationBatchesPage;

export const zAdminIntTranslationsBatchesStartBody = zTranslationBatchIn;

/**
 * Successful Response
 */
export const zAdminIntTranslationsBatchesStartResponse = zTranslationBatchOut;

export const zAdminIntTranslationsBatchesCancelPath = z.object({
    batch_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntTranslationsBatchesCancelResponse = zTranslationBatchOut;

export const zAdminIntVideosUploadCreateBody = zVideoUploadIn;

/**
 * Successful Response
 */
export const zAdminIntVideosUploadCreateResponse = zVideoUploadOut;

export const zAdminIntVideosImportBody = zVideoImportIn;

/**
 * Successful Response
 */
export const zAdminIntVideosImportResponse = zVideoImportOut;

export const zAdminIntVideosDeletePath = z.object({
    video_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntVideosDeleteResponse = z.void();

export const zAdminIntVideosGetPath = z.object({
    video_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntVideosGetResponse = zVideoAssetOut;

export const zAdminIntVideosPlaybackPath = z.object({
    video_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntVideosPlaybackResponse = zVideoPreviewOut;

export const zAdminIntPartnerCredentialsCreateBody = zIntPartnerCredentialIn;

export const zAdminIntPartnerCredentialsCreatePath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerCredentialsCreateResponse = zIntPartnerCredentialCreated;

export const zAdminIntPartnerCredentialsRevokePath = z.object({
    key: z.string(),
    key_id: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerCredentialsRevokeResponse = z.void();

export const zAdminIntPartnerSigningRotatePath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerSigningRotateResponse = zIntPartnerSigningKeyCreated;

export const zAdminIntPartnerWebhookTestPath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerWebhookTestResponse = zIntPartnerWebhookTestOut;

export const zAdminIntPartnerWebhookResendPath = z.object({
    key: z.string(),
    event_id: z.uuid()
});

export const zAdminIntPartnerCatalogNotifyAllPath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerCatalogNotifyAllResponse = zIntPartnerNotifyAllOut;

export const zAdminIntPartnerCatalogStateListPath = z.object({
    key: z.string()
});

export const zAdminIntPartnerCatalogStateListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[view]': z.string().nullish(),
    'filter[view][]': z.array(z.string()).nullish(),
    'filter[item_type]': z.string().nullish(),
    'filter[item_type][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerCatalogStateListResponse = zIntPartnerCatalogStatePage;

export const zAdminIntPartnerCatalogStateUpdateBody = zIntPartnerSkuIn;

export const zAdminIntPartnerCatalogStateUpdatePath = z.object({
    key: z.string(),
    item_type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]),
    item_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerCatalogStateUpdateResponse = zIntPartnerCatalogStateRow;

export const zAdminIntPartnerHandoffsListPath = z.object({
    key: z.string()
});

export const zAdminIntPartnerHandoffsListQuery = z.object({
    page: z.int().optional().default(1),
    page_size: z.union([
        z.literal(25),
        z.literal(50),
        z.literal(100)
    ]).optional().default(25),
    sort: z.string().nullish(),
    q: z.string().max(200).nullish(),
    'filter[status]': z.string().nullish(),
    'filter[status][]': z.array(z.string()).nullish(),
    'filter[view]': z.string().nullish(),
    'filter[view][]': z.array(z.string()).nullish(),
    'filter[item_type]': z.string().nullish(),
    'filter[item_type][]': z.array(z.string()).nullish()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerHandoffsListResponse = zIntPartnerHandoffPage;

export const zAdminIntPartnerTestLinkBody = zIntPartnerTestLinkIn;

export const zAdminIntPartnerTestLinkPath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerTestLinkResponse = zIntPartnerTestLinkOut;

export const zAdminIntPartnerSignatureCheckBody = zIntPartnerSignatureCheckIn;

export const zAdminIntPartnerSignatureCheckPath = z.object({
    key: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntPartnerSignatureCheckResponse = zIntPartnerSignatureCheckOut;

export const zAdminIntShipmentsGetPath = z.object({
    shipment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipmentsGetResponse = zIntShipmentOut;

export const zAdminIntShipmentsUpdateBody = zIntShipmentPatchIn;

export const zAdminIntShipmentsUpdatePath = z.object({
    shipment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipmentsUpdateResponse = zIntShipmentOut;

export const zAdminIntShipmentsLabelPath = z.object({
    shipment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipmentsLabelResponse = z.string();

export const zAdminIntShipmentsRetryBody = zIntShipmentRetryIn;

export const zAdminIntShipmentsRetryPath = z.object({
    shipment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipmentsRetryResponse = zIntShipmentAccepted;

export const zAdminIntShipmentsCancelPath = z.object({
    shipment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipmentsCancelResponse = zIntShipmentOut;

export const zAdminIntShipmentsRefreshTrackingPath = z.object({
    shipment_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipmentsRefreshTrackingResponse = zIntShipmentTrackingOut;

export const zAdminIntPickupsCreateBody = zIntPickupIn;

/**
 * Successful Response
 */
export const zAdminIntPickupsCreateResponse = zIntPickupOut;

export const zAdminIntPickupsDeletePath = z.object({
    pickup_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntPickupsDeleteResponse = z.void();

/**
 * Successful Response
 */
export const zAdminIntShipperLocationsListResponse = zIntShipperLocationsPage;

export const zAdminIntShipperLocationsCreateBody = zIntShipperLocationIn;

/**
 * Successful Response
 */
export const zAdminIntShipperLocationsCreateResponse = zIntShipperLocationOut;

export const zAdminIntShipperLocationsDeletePath = z.object({
    location_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipperLocationsDeleteResponse = z.void();

export const zAdminIntShipperLocationsGetPath = z.object({
    location_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipperLocationsGetResponse = zIntShipperLocationOut;

export const zAdminIntShipperLocationsUpdateBody = zIntShipperLocationIn;

export const zAdminIntShipperLocationsUpdatePath = z.object({
    location_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntShipperLocationsUpdateResponse = zIntShipperLocationOut;

/**
 * Successful Response
 */
export const zAdminIntPackagePresetsListResponse = zIntPackagePresetsPage;

export const zAdminIntPackagePresetsCreateBody = zIntPackagePresetIn;

/**
 * Successful Response
 */
export const zAdminIntPackagePresetsCreateResponse = zIntPackagePresetOut;

export const zAdminIntPackagePresetsDeletePath = z.object({
    preset_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntPackagePresetsDeleteResponse = z.void();

export const zAdminIntPackagePresetsUpdateBody = zIntPackagePresetIn;

export const zAdminIntPackagePresetsUpdatePath = z.object({
    preset_id: z.uuid()
});

/**
 * Successful Response
 */
export const zAdminIntPackagePresetsUpdateResponse = zIntPackagePresetOut;

/**
 * Successful Response
 */
export const zAdminIntTaxProfilesListResponse = zIntTaxProfilesPage;

export const zAdminIntTaxProfilesUpdateBody = zIntTaxProfileIn;

export const zAdminIntTaxProfilesUpdatePath = z.object({
    country_code: z.string()
});

/**
 * Successful Response
 */
export const zAdminIntTaxProfilesUpdateResponse = zIntTaxProfileOut;

/**
 * Successful Response
 */
export const zPublicCoreHealthResponse = zHealthOut;

export const zWebhooksIntBillingGooglePlayResponse = z.union([
    z.unknown(),
    z.void()
]);

/**
 * Successful Response
 */
export const zPartnerIntPingResponse = zPartnerApiPingOut;

/**
 * Successful Response
 */
export const zPartnerIntJwksResponse = zPartnerApiJwksOut;

export const zPartnerIntCatalogListQuery = z.object({
    updated_since: z.iso.datetime({ offset: true }).nullish(),
    type: z.enum([
        'wine',
        'set',
        'drop',
        'blind_kit'
    ]).nullish(),
    cursor: z.string().max(500).nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100)
});

/**
 * Successful Response
 */
export const zPartnerIntCatalogListResponse = zPartnerApiCatalogPage;

export const zPartnerIntCatalogAvailabilityBody = zPartnerApiAvailabilityIn;

export const zPartnerIntCatalogAvailabilityHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zPartnerIntCatalogAvailabilityResponse = zPartnerApiAvailabilityOut;

export const zPartnerIntCatalogGetPath = z.object({
    item_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPartnerIntCatalogGetResponse = zPartnerApiCatalogItem;

export const zPartnerIntRefsVerifyBody = zPartnerApiVerifyIn;

export const zPartnerIntRefsVerifyHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

/**
 * Successful Response
 */
export const zPartnerIntRefsVerifyResponse = zPartnerApiVerifyOut;

export const zPartnerIntHoldsReleasePath = z.object({
    hold_id: z.uuid()
});

/**
 * Successful Response
 */
export const zPartnerIntHoldsReleaseResponse = z.void();

export const zPartnerIntOrdersListQuery = z.object({
    updated_from: z.iso.datetime({ offset: true }),
    updated_to: z.iso.datetime({ offset: true }),
    status: z.enum([
        'awaiting_payment',
        'paid',
        'assembling',
        'handed_to_carrier',
        'in_delivery',
        'delivered',
        'cancelled',
        'refunded'
    ]).nullish(),
    cursor: z.string().max(500).nullish(),
    limit: z.int().gte(1).lte(500).optional().default(100)
});

/**
 * Successful Response
 */
export const zPartnerIntOrdersListResponse = zPartnerApiOrdersPage;

export const zPartnerIntOrdersCreateBody = zPartnerApiOrderIn;

export const zPartnerIntOrdersCreateHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

/**
 * OK
 */
export const zPartnerIntOrdersCreateResponse = zPartnerApiOrderCreated;

export const zPartnerIntOrdersGetPath = z.object({
    partner_order_number: z.string().min(1).max(64)
});

/**
 * Successful Response
 */
export const zPartnerIntOrdersGetResponse = zPartnerApiOrderOut;

export const zPartnerIntOrdersUpdateBody = zPartnerApiOrderPatchIn;

export const zPartnerIntOrdersUpdateHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

export const zPartnerIntOrdersUpdatePath = z.object({
    partner_order_number: z.string().min(1).max(64)
});

/**
 * Successful Response
 */
export const zPartnerIntOrdersUpdateResponse = zPartnerApiOrderOut;

export const zPartnerIntOrdersEventBody = zPartnerApiEventIn;

export const zPartnerIntOrdersEventHeaders = z.object({
    'Idempotency-Key': z.string().nullish()
});

export const zPartnerIntOrdersEventPath = z.object({
    partner_order_number: z.string().min(1).max(64)
});

/**
 * Successful Response
 */
export const zPartnerIntOrdersEventResponse = zPartnerApiEventOut;
